import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from "react";
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  ReferenceLine, ComposedChart, Cell as RCell } from "recharts";

const API_BASE = window.location.origin;

const THEMES = {
  DARK: {
    bg: "#08090d", panel: "#0d0f17", card: "#0d0f17", border: "#1a1f2e",
    accent: "#00d4ff", accent2: "#7c3aed",
    green: "#00e5a0", red: "#ff4466", yellow: "#f5c542",
    text: "#e2e8f0", muted: "#4a5568", dim: "#2d3748",
    bullish: "#00e5a0", bearish: "#ff4466", warning: "#f5c542",
  },
  ORANGE: {
    bg: "#000000", panel: "#0d0d0d", card: "#0d0d0d", border: "#2a2a2a",
    accent: "#FF6600", accent2: "#FFB800",
    green: "#00CC44", red: "#FF2D55", yellow: "#FFB800",
    text: "#ffffff", muted: "#777777", dim: "#1a1a1a",
    bullish: "#00CC44", bearish: "#FF2D55", warning: "#FFB800",
  },
  MATRIX: {
    bg: "#000000", panel: "#001400", card: "#001400", border: "#003800",
    accent: "#00FF41", accent2: "#00CC33",
    green: "#00FF41", red: "#FF4444", yellow: "#CCFF00",
    text: "#00CC33", muted: "#005500", dim: "#001a00",
    bullish: "#00FF41", bearish: "#FF4444", warning: "#CCFF00",
  },
};
let C = THEMES[localStorage.getItem("quantos_theme") || "DARK"];

const api = {
  token: null,
  role: null,
  async login(username, password) {
    const res = await fetch(`${API_BASE}/auth/token`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: `username=${username}&password=${password}`
    });
    if (!res.ok) throw new Error("Credenziali errate");
    const data = await res.json();
    api.token = data.access_token;
    api.role = data.role;
    localStorage.setItem("quantos_token", data.access_token);
    localStorage.setItem("quantos_role", data.role);
    return data;
  },
  async get(path) {
    const res = await fetch(`${API_BASE}${path}`, {
      headers: { "Authorization": `Bearer ${api.token}` }
    });
    if (res.status === 401) {
      api.token = null;
      localStorage.removeItem("quantos_token");
      window.location.reload();
    }
    return res.json();
  },
  async post(path) {
    const res = await fetch(`${API_BASE}${path}`, {
      method: "POST",
      headers: { "Authorization": `Bearer ${api.token}` }
    });
    return res.json();
  },
  async post_json(path, body) {
    const res = await fetch(`${API_BASE}${path}`, {
      method: "POST",
      headers: { "Authorization": `Bearer ${api.token}`, "Content-Type": "application/json" },
      body: JSON.stringify(body)
    });
    return res.json();
  },
  async patch(path, body) {
    const res = await fetch(`${API_BASE}${path}`, {
      method: "PATCH",
      headers: { "Authorization": `Bearer ${api.token}`, "Content-Type": "application/json" },
      body: JSON.stringify(body)
    });
    return res.json();
  },
  async delete(path) {
    const res = await fetch(`${API_BASE}${path}`, {
      method: "DELETE",
      headers: { "Authorization": `Bearer ${api.token}` }
    });
    return res.json();
  }
};

function Panel({ children, style = {} }) {
  return (
    <div style={{
      background: C.panel, border: `1px solid ${C.border}`,
      borderRadius: 8, padding: 16, ...style
    }}>{children}</div>
  );
}

function Label({ children, color = C.muted, size = 10 }) {
  return (
    <div style={{
      fontSize: size, fontFamily: "'JetBrains Mono', monospace",
      letterSpacing: "0.12em", textTransform: "uppercase",
      color, marginBottom: 4
    }}>{children}</div>
  );
}

function Metric({ label, value, color = C.accent }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Label>{label}</Label>
      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 22, fontWeight: 700, color, lineHeight: 1 }}>{value}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    LIVE: { bg: "#00e5a015", color: C.green, dot: C.green },
    PENDING: { bg: "#f5c54215", color: C.yellow, dot: C.yellow },
    CLOSED: { bg: "#ffffff10", color: C.muted, dot: C.muted },
  };
  const s = map[status] || map.CLOSED;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      background: s.bg, color: s.color, fontSize: 10,
      fontFamily: "'JetBrains Mono', monospace",
      letterSpacing: "0.1em", padding: "2px 8px", borderRadius: 4,
    }}>
      <span style={{
        width: 5, height: 5, borderRadius: "50%", background: s.dot,
        boxShadow: status === "LIVE" ? `0 0 6px ${s.dot}` : "none",
        animation: status === "LIVE" ? "pulse 2s infinite" : "none"
      }} />
      {status}
    </span>
  );
}


// ─── AI ANALYST ───────────────────────────────────────────────────────────────
function AIAnalyst({ page, contextData }) {
  const [open, setOpen] = useState(false);
  const [analysis, setAnalysis] = useState("");
  const [loading, setLoading] = useState(false);

  const analyze = async () => {
    setOpen(true);
    setLoading(true);
    setAnalysis("");
    try {
      const res = await api.post_json("/api/ai/analyze", {
        page,
        context: typeof contextData === "string" ? contextData : JSON.stringify(contextData, null, 2),
        data: {}
      });
      setAnalysis(res.analysis || "Nessuna analisi disponibile");
    } catch (e) {
      setAnalysis("Errore: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button onClick={analyze} style={{
        display: "flex", alignItems: "center", gap: 6,
        background: "linear-gradient(135deg, #7c3aed20, #00d4ff20)",
        border: "1px solid #7c3aed60", color: C.accent2,
        fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
        padding: "5px 14px", borderRadius: 4, cursor: "pointer", letterSpacing: "0.1em"
      }}>◈ AI ANALYST</button>

      {open && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)",
          zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center"
        }} onClick={() => setOpen(false)}>
          <div style={{
            background: "#0d0f17", border: "1px solid #7c3aed60",
            borderRadius: 12, padding: 28, maxWidth: 620, width: "90%",
            boxShadow: "0 0 40px #7c3aed30"
          }} onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <div style={{ fontFamily: "JetBrains Mono", fontSize: 12, color: C.accent2, letterSpacing: "0.15em" }}>◈ AI ANALYST — {page}</div>
              <button onClick={() => setOpen(false)} style={{ background: "transparent", border: "none", color: "#4a5568", cursor: "pointer", fontSize: 18 }}>✕</button>
            </div>
            {loading ? (
              <div style={{ fontFamily: "JetBrains Mono", fontSize: 11, color: "#4a5568", textAlign: "center", padding: 32 }}>
                Analisi in corso con Llama 3.3 70B...
              </div>
            ) : (
              <div style={{ fontFamily: "JetBrains Mono", fontSize: 12, color: "#e2e8f0", lineHeight: 1.8, whiteSpace: "pre-wrap" }}>
                {analysis}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}


// ─── TICKER TAPE ─────────────────────────────────────────────────────────────
function TickerTape({ tickers }) {
  const items = tickers.length > 0 ? [...tickers, ...tickers] :
    ["AMD", "SOFI", "PLTR", "MARA", "RIOT", "RIVN", "BAC", "F", "NIO", "PLUG",
     "AMD", "SOFI", "PLTR", "MARA", "RIOT", "RIVN", "BAC", "F", "NIO", "PLUG"];

  return (
    <div style={{
      background: "#0a0800", borderBottom: "1px solid #3d2800",
      height: 24, overflow: "hidden", position: "relative"
    }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 0,
        animation: "ticker 40s linear infinite",
        whiteSpace: "nowrap", height: "100%"
      }}>
        {items.map((item, i) => {
          const isObj = typeof item === "object";
          const sym   = isObj ? item.symbol : item;
          const price = isObj && item.price > 0 ? item.price.toFixed(2) : "—";
          const chg   = isObj && item.change_pct ? item.change_pct : null;
          const chgColor = chg === null ? C.muted : chg >= 0 ? C.green : C.red;
          const chgStr  = chg !== null ? ` ${chg >= 0 ? "+" : ""}${chg}%` : "";
          return (
            <span key={i} style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 10, padding: "0 16px", letterSpacing: "0.1em",
              borderRight: `1px solid ${C.border}`
            }}>
              <span style={{ color: C.text }}>{sym}</span>
              {" "}
              <span style={{ color: C.text }}>{price}</span>
              {chg !== null && <span style={{ color: chgColor, fontSize: 9 }}>{chgStr}</span>}
            </span>
          );
        })}
      </div>
    </div>
  );
}

// ─── TICKER TAPE CONTAINER ───────────────────────────────────────────────────
function TickerTapeContainer() {
  const [prices, setPrices] = useState([]);

  useEffect(() => {
    const fetch_ = () =>
      api.get("/api/prices")
        .then(data => { if (Array.isArray(data) && data.length > 0) setPrices([...data, ...data]); })
        .catch(() => {});
    fetch_();
    const i = setInterval(fetch_, 60000);
    return () => clearInterval(i);
  }, []);

  return <TickerTape tickers={prices} />;
}

// ─── LOGIN ────────────────────────────────────────────────────────────────────
function LoginPage({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api.login(username, password);
      onLogin();
    } catch {
      setError("Credenziali errate");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ height: "100vh", overflow: "hidden", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ width: 360 }}>
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <div style={{
            width: 48, height: 48, borderRadius: 12,
            background: `linear-gradient(135deg, ${C.accent}, ${C.accent2})`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 20, fontWeight: 900, color: "#fff",
            fontFamily: "'JetBrains Mono', monospace", margin: "0 auto 16px"
          }}>Q</div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 20, fontWeight: 700, color: C.text, letterSpacing: "0.15em" }}>QUANTOS</div>
          <div style={{ fontSize: 11, color: C.muted, letterSpacing: "0.2em", marginTop: 4 }}>OPTIONS ENGINE v1.0</div>
        </div>
        <Panel>
          <form onSubmit={handleLogin} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div>
              <Label>Username</Label>
              <input value={username} onChange={e => setUsername(e.target.value)} style={{
                width: "100%", background: C.bg, border: `1px solid ${C.border}`,
                borderRadius: 6, padding: "10px 12px", color: C.text,
                fontFamily: "'JetBrains Mono', monospace", fontSize: 13, outline: "none", boxSizing: "border-box"
              }} />
            </div>
            <div>
              <Label>Password</Label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} style={{
                width: "100%", background: C.bg, border: `1px solid ${C.border}`,
                borderRadius: 6, padding: "10px 12px", color: C.text,
                fontFamily: "'JetBrains Mono', monospace", fontSize: 13, outline: "none", boxSizing: "border-box"
              }} />
            </div>
            {error && <div style={{ fontSize: 11, color: C.red, fontFamily: "'JetBrains Mono', monospace", background: `${C.red}15`, padding: "8px 12px", borderRadius: 4 }}>{error}</div>}
            <button type="submit" disabled={loading} style={{
              background: `linear-gradient(135deg, ${C.accent}, ${C.accent2})`,
              border: "none", borderRadius: 6, padding: "12px", color: "#fff",
              fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 700,
              letterSpacing: "0.15em", cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.7 : 1
            }}>{loading ? "ACCESSO..." : "ACCEDI"}</button>
          </form>
        </Panel>
      </div>
    </div>
  );
}

// ─── HEADER ──────────────────────────────────────────────────────────────────
function Header({ page, setPage, ibConnected, onLogout, role }) {
  const [time, setTime] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const nav = ["DASHBOARD", "OPPORTUNITIES", "SIGNALS", "VOL SURFACE", "CHAIN", "REGIME", "GEX", "MAX PAIN", "VST", "STRATEGIES", "AUTO-STRATEGY", "PAPER", "BACKTEST", "HEDGE", "PAIRS", "CHART", "NEWS", "FILINGS", "SETTINGS"];
  if (role === "owner" || role === "admin") nav.push("PORTFOLIO");

  return (
    <div>
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "0 24px", height: 56, borderBottom: `1px solid ${C.border}`,
      background: C.bg, position: "sticky", top: 0, zIndex: 100,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 180 }}>
        <div style={{
          width: 28, height: 28, background: `linear-gradient(135deg, ${C.accent}, ${C.accent2})`,
          borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 12, fontWeight: 900, color: "#fff", fontFamily: "'JetBrains Mono', monospace"
        }}>Q</div>
        <div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13, fontWeight: 700, color: C.text, letterSpacing: "0.15em" }}>QUANTOS</div>
          <div style={{ fontSize: 9, color: ibConnected ? C.green : C.red, letterSpacing: "0.2em" }}>
            {ibConnected ? "● IB CONNECTED" : "● IB DISCONNECTED"}
          </div>
        </div>
      </div>
      <div style={{ display: "flex", gap: 4 }}>
        {nav.map(n => (
          <button key={n} onClick={() => setPage(n)} style={{
            background: page === n ? `${C.accent}15` : "transparent",
            border: page === n ? `1px solid ${C.accent}40` : "1px solid transparent",
            color: page === n ? C.accent : n === "PORTFOLIO" ? C.yellow : n === "PAPER" ? C.green : n === "STRATEGIES" ? C.accent2 : C.muted,
            fontSize: 10, letterSpacing: "0.12em", fontFamily: "'JetBrains Mono', monospace",
            padding: "4px 12px", borderRadius: 4, cursor: "pointer",
          }}>{n}</button>
        ))}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 16, minWidth: 180, justifyContent: "flex-end" }}>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: C.accent }}>{time.toLocaleTimeString("it-IT")}</div>
          <div style={{ fontSize: 9, color: C.muted, letterSpacing: "0.15em" }}>{time.toLocaleDateString("it-IT")}</div>
        </div>
        <button onClick={onLogout} style={{
          background: "transparent", border: `1px solid ${C.border}`, color: C.muted,
          fontSize: 9, fontFamily: "'JetBrains Mono', monospace",
          padding: "4px 10px", borderRadius: 4, cursor: "pointer", letterSpacing: "0.1em"
        }}>LOGOUT</button>
      </div>
    </div>
    <TickerTapeContainer />
    </div>
  );
}

// ─── DASHBOARD ────────────────────────────────────────────────────────────────
function DashboardPage() {
  const [regime,  setRegime]  = useState(null);
  const [signals, setSignals] = useState([]);
  const [health,  setHealth]  = useState(null);
  const [vst,     setVst]     = useState([]);
  const [paper,   setPaper]   = useState([]);
  const [loading, setLoading] = useState(true);

  const nav = page => window.dispatchEvent(new CustomEvent("quantos:navigate", { detail: { page } }));

  const fetchData = useCallback(async () => {
    try {
      const [r, s, h, v, p] = await Promise.all([
        api.get("/api/regime"),
        api.get("/api/signals"),
        api.get("/health"),
        api.get("/api/vst/scan?mode=instant&min_vst=0.2").catch(() => ({ data: [] })),
        api.get("/api/paper/trades").catch(() => []),
      ]);
      setRegime(r);
      setSignals(Array.isArray(s) ? s : []);
      setHealth(h);
      setVst((v?.data || []).slice(0, 3));
      setPaper(Array.isArray(p) ? p : []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchData();
    const i = setInterval(fetchData, 60000);
    return () => clearInterval(i);
  }, [fetchData]);

  if (loading) return <div style={{ padding: 24, color: C.muted, fontFamily: "JetBrains Mono", fontSize: 12 }}>Caricamento...</div>;

  const openTrades = paper.filter(t => t.status === "OPEN");
  const totalPnl   = openTrades.reduce((acc, t) => acc + (t.pnl_live ?? 0), 0);
  const REGIME_COLORS = { CRISIS: C.red, HIGH_VOL: C.accent, NORMAL: C.yellow, CALM: C.green, PIN: "#9c27b0" };
  const regimeColor   = REGIME_COLORS[regime?.dominant] || C.yellow;
  const dominantPct   = regime?.distribution?.find(d => d.regime === regime?.dominant)?.pct || 0;

  const NavCard = ({ label, value, color, target, sub }) => (
    <div onClick={() => nav(target)} style={{
      background: C.panel, border: `1px solid ${C.border}`,
      borderRadius: 6, padding: "14px 18px", cursor: "pointer",
      transition: "border-color 0.15s",
    }}
      onMouseEnter={e => e.currentTarget.style.borderColor = color}
      onMouseLeave={e => e.currentTarget.style.borderColor = C.border}
    >
      <div style={{ fontFamily: "JetBrains Mono", fontSize: 8, color: C.muted, letterSpacing: "0.12em", marginBottom: 6 }}>{label}</div>
      <div style={{ fontFamily: "JetBrains Mono", fontSize: 20, fontWeight: 900, color }}>{value}</div>
      {sub && <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted, marginTop: 4 }}>{sub}</div>}
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* ── Metric cards ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 12 }}>
        <NavCard label="IB STATUS" value={health?.ib_connected ? "LIVE" : "OFFLINE"}
          color={health?.ib_connected ? C.green : C.red} target="DASHBOARD"
          sub={health?.ib_connected ? "mercato connesso" : "clicca → reconnect"} />
        <NavCard label="REGIME" value={regime?.dominant?.replace(/_/g, " ") || "—"}
          color={regimeColor} target="REGIME"
          sub={`${dominantPct.toFixed(0)}% ticker · amp ${regime?.amplifier?.toFixed(1) || "—"}×`} />
        <NavCard label="AMPLIFIER" value={regime ? `${regime.amplifier?.toFixed(1)}×` : "—"}
          color={C.yellow} target="REGIME" sub={regime?.date || "—"} />
        <NavCard label="VST SEGNALI" value={vst.length > 0 ? vst.length : "—"}
          color={C.accent} target="VST" sub="top segnali attivi" />
        <NavCard label="PAPER P&L"
          value={openTrades.length > 0 ? `${totalPnl >= 0 ? "+" : ""}${totalPnl.toFixed(0)}$` : "—"}
          color={totalPnl >= 0 ? C.green : C.red} target="PAPER"
          sub={`${openTrades.length} posizioni aperte`} />
      </div>

      {/* ── Regime + Top VST ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>

        {/* Regime */}
        {regime && (
          <Panel>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <Label color={C.muted}>REGIME DETECTOR v2</Label>
              <button onClick={() => nav("REGIME")} style={{
                fontFamily: "JetBrains Mono", fontSize: 8, color: C.muted,
                background: "transparent", border: `1px solid ${C.border}`,
                padding: "2px 8px", borderRadius: 3, cursor: "pointer"
              }}>→ REGIME</button>
            </div>
            <div style={{ marginTop: 12 }}>
              {/* Dominant + amplifier */}
              <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 10 }}>
                <div style={{ fontFamily: "JetBrains Mono", fontSize: 16, fontWeight: 700, color: regimeColor }}>
                  {regime.dominant}
                </div>
                <div style={{ fontFamily: "JetBrains Mono", fontSize: 10, color: C.muted }}>
                  amp {regime.amplifier?.toFixed(1)}×
                </div>
                <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted, marginLeft: "auto" }}>
                  {regime.date}
                </div>
              </div>
              {/* Distribution bars */}
              <div style={{ marginBottom: 14 }}>
                {regime.distribution?.map(d => {
                  const col = REGIME_COLORS[d.regime] || C.muted;
                  return (
                    <div key={d.regime} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
                      <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: col, width: 72, flexShrink: 0 }}>{d.regime}</div>
                      <div style={{ flex: 1, height: 3, background: C.dim, borderRadius: 2 }}>
                        <div style={{ width: `${d.pct}%`, height: "100%", background: col, borderRadius: 2 }} />
                      </div>
                      <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted, width: 36, textAlign: "right" }}>{d.pct}%</div>
                    </div>
                  );
                })}
              </div>
              <Label color={C.muted} size={9}>STRATEGIE CONSIGLIATE</Label>
              {regime.recommended_strategies?.map(s => (
                <div key={s} onClick={() => nav("STRATEGIES")} style={{
                  fontFamily: "JetBrains Mono", fontSize: 11, color: C.text,
                  padding: "3px 0", cursor: "pointer",
                }}
                  onMouseEnter={e => e.currentTarget.style.color = C.accent2}
                  onMouseLeave={e => e.currentTarget.style.color = C.text}
                >→ {s}</div>
              ))}
            </div>
          </Panel>
        )}

        {/* Top VST */}
        <Panel>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <Label color={C.muted}>TOP VST SIGNALS</Label>
            <button onClick={() => nav("STRATEGIES")} style={{
              fontFamily: "JetBrains Mono", fontSize: 8, color: C.accent2,
              background: `${C.accent2}10`, border: `1px solid ${C.accent2}40`,
              padding: "2px 8px", borderRadius: 3, cursor: "pointer"
            }}>→ STRATEGIES</button>
          </div>
          {vst.length === 0 ? (
            <div style={{ fontFamily: "JetBrains Mono", fontSize: 10, color: C.muted, textAlign: "center", padding: 20 }}>
              Nessun segnale VST — esegui COMPUTE dalla pagina VST
            </div>
          ) : vst.map((r, i) => {
            const sc = scoreSignal(r);
            const pct = Math.min(sc.score / 2, 1);
            const col = pct >= 0.75 ? "#00e676" : pct >= 0.5 ? C.yellow : C.accent;
            return (
              <div key={i} onClick={() => nav("VST")} style={{
                display: "flex", alignItems: "center", gap: 12,
                padding: "8px 0", borderBottom: i < vst.length - 1 ? `1px solid ${C.border}30` : "none",
                cursor: "pointer",
              }}
                onMouseEnter={e => e.currentTarget.style.background = `${C.accent}08`}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
              >
                <span style={{ fontFamily: "JetBrains Mono", fontSize: 12, color: C.accent, fontWeight: 700, minWidth: 50 }}>{r.ticker}</span>
                <span style={{ fontFamily: "JetBrains Mono", fontSize: 10, color: C.muted }}>${r.strike}</span>
                <span style={{ fontFamily: "JetBrains Mono", fontSize: 9,
                  color: r.right_type === "C" ? C.green : C.red }}>{r.right_type === "C" ? "CALL" : "PUT"}</span>
                <span style={{ fontFamily: "JetBrains Mono", fontSize: 11, color: col, fontWeight: 700, marginLeft: "auto" }}>
                  {r.vst_instant > 0 ? "+" : ""}{r.vst_instant?.toFixed(3)}
                </span>
                <div style={{ display: "flex", gap: 2 }}>
                  {sc.votes.map((v, j) => (
                    <div key={j} style={{ width: 5, height: 12, borderRadius: 1,
                      background: v.ok ? col : C.border }} title={v.label} />
                  ))}
                </div>
              </div>
            );
          })}
        </Panel>
      </div>

      {/* ── Segnali attivi ── */}
      <Panel>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <Label color={C.muted}>SEGNALI ATTIVI</Label>
          <div style={{ display: "flex", gap: 8 }}>
            <AIAnalyst page="DASHBOARD" contextData={`Regime: ${regime?.regime}, VIX: ${regime?.vix}, VRP: ${regime?.vrp_spread}, Segnali attivi: ${signals.length}`} />
            <button onClick={fetchData} style={{
              fontFamily: "JetBrains Mono", fontSize: 10, color: C.accent,
              background: `${C.accent}15`, padding: "3px 10px", borderRadius: 4,
              border: `1px solid ${C.accent}30`, cursor: "pointer"
            }}>↻ AGGIORNA</button>
          </div>
        </div>
        {signals.length === 0 ? (
          <div style={{ fontFamily: "JetBrains Mono", fontSize: 11, color: C.muted, textAlign: "center", padding: 24 }}>
            Nessun segnale attivo — il sistema sta raccogliendo dati
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                {["STRATEGY", "TICKER", "SIGNAL", "STRIKE", "EXPIRY", "EDGE σ", "STATUS"].map(h => (
                  <th key={h} style={{ textAlign: "left", padding: "6px 10px", fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted, letterSpacing: "0.12em", fontWeight: 400 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {signals.map((s, i) => (
                <tr key={s.id || i}
                  onClick={() => nav("VOL SURFACE")}
                  style={{ borderBottom: `1px solid ${C.border}40`, cursor: "pointer" }}
                  onMouseEnter={e => e.currentTarget.style.background = `${C.accent}08`}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                >
                  <td style={{ padding: "10px", fontFamily: "JetBrains Mono", fontSize: 11, color: C.accent }}>{s.strategy}</td>
                  <td style={{ padding: "10px", fontFamily: "JetBrains Mono", fontSize: 11, fontWeight: 700, color: C.text }}>{s.ticker}</td>
                  <td style={{ padding: "10px", fontFamily: "JetBrains Mono", fontSize: 11, color: C.yellow }}>{s.signal_type}</td>
                  <td style={{ padding: "10px", fontFamily: "JetBrains Mono", fontSize: 11, color: C.muted }}>{s.strike || "—"}</td>
                  <td style={{ padding: "10px", fontFamily: "JetBrains Mono", fontSize: 11, color: C.muted }}>{s.expiry || "—"}</td>
                  <td style={{ padding: "10px", fontFamily: "JetBrains Mono", fontSize: 12, color: s.edge_sigma >= 3 ? C.green : C.yellow, fontWeight: 700 }}>{s.edge_sigma}σ</td>
                  <td style={{ padding: "10px" }}><StatusBadge status={s.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </div>
  );
}

// ─── VOL SURFACE ──────────────────────────────────────────────────────────────
function ivHeatColor(iv) {
  if (iv === null || iv === undefined) return { bg: "transparent", color: "#444" };
  const lo = 15, hi = 58;
  const t = Math.max(0, Math.min(1, (iv - lo) / (hi - lo)));
  if (t < 0.35) {
    const g = Math.round(230 - t * (230 - 180));
    return { bg: `rgba(0,${g},80,0.18)`, color: "#00e676" };
  } else if (t < 0.55) {
    return { bg: "rgba(255,215,0,0.10)", color: "#ffd700" };
  } else if (t < 0.75) {
    return { bg: "rgba(255,140,0,0.18)", color: "#ff8c00" };
  } else {
    const r = Math.round(180 + t * 75);
    return { bg: `rgba(${r},40,20,0.22)`, color: "#ff4444" };
  }
}

function VolSurfacePage() {
  const [ticker, setTicker] = useState("AMD");
  const [tickers, setTickers] = useState(["AMD", "SOFI", "PLTR", "F", "BAC", "MARA"]);
  const [data, setData] = useState(() => _volSurfaceCache["AMD"]?.data || null);
  const [grid, setGrid] = useState(() => _volSurfaceCache["AMD"]?.grid || null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/api/tickers").then(res => {
      if (Array.isArray(res)) {
        const active = res.filter(x => x.active).map(x => x.symbol);
        if (active.length > 0) setTickers(active);
      }
    }).catch(() => {});
  }, []);

  const fetchSurface = async (t) => {
    setLoading(true);
    setData(null);
    setGrid(null);
    // Grid: veloce, mostra subito la heatmap
    api.get(`/api/vol-surface/grid/${t}`)
      .then(g => {
        setGrid(g);
        _volSurfaceCache[t] = { ..._volSurfaceCache[t], grid: g };
      })
      .catch(() => {});
    // Surface: può essere lenta (IB live), aspetta per conto suo
    try {
      const surface = await api.get(`/api/vol-surface/${t}`);
      setData(surface);
      _volSurfaceCache[t] = { ..._volSurfaceCache[t], data: surface };
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    const cached = _volSurfaceCache[ticker];
    if (cached?.data && cached?.grid) {
      setData(cached.data);
      setGrid(cached.grid);
    } else {
      fetchSurface(ticker);
    }
  }, [ticker]);

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 20 }}>
        <select
          value={ticker}
          onChange={e => setTicker(e.target.value)}
          style={{
            background: C.panel, border: `1px solid ${C.accent}`,
            color: C.accent, fontFamily: "JetBrains Mono", fontSize: 12,
            padding: "7px 32px 7px 12px", borderRadius: 4, cursor: "pointer",
            outline: "none", appearance: "none",
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23ff8c00' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,
            backgroundRepeat: "no-repeat", backgroundPosition: "right 10px center",
            minWidth: 140,
          }}
        >
          {tickers.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>
      {loading && <div style={{ color: C.muted, fontFamily: "JetBrains Mono", fontSize: 12 }}>Caricamento...</div>}
      {data && !loading && data.data_source === "historical" && (
        <div style={{ marginBottom: 12, padding: "6px 12px", background: "#f6ad2015", border: "1px solid #f6ad2040", borderRadius: 4, fontFamily: "JetBrains Mono", fontSize: 10, color: "#f6ad20" }}>
          MERCATO CHIUSO — dati da snapshot storico ({data.chain_size} opzioni)
        </div>
      )}
      {grid && !loading && grid.grid && Object.keys(grid.grid).length > 0 && (
        <Panel style={{ marginBottom: 16, overflowX: "auto" }}>
          <div style={{ marginBottom: 10, fontFamily: "JetBrains Mono", fontSize: 10, color: C.muted, letterSpacing: 1 }}>
            SUPERFICIE IV — {ticker}
            {grid.spot && <span style={{ color: C.text }}> · SPOT ${parseFloat(grid.spot).toFixed(2)}</span>}
            {grid.snapshot_at && <span> · {grid.snapshot_at.slice(0, 16)}</span>}
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 560 }}>
            <thead>
              <tr>
                <th style={{ fontFamily: "JetBrains Mono", fontSize: 10, color: C.muted, textAlign: "left", padding: "6px 12px", fontWeight: 400, letterSpacing: 1 }}>MONEYNESS</th>
                {(grid.buckets_dte || [7,14,30,45,60,90]).map(d => (
                  <th key={d} style={{ fontFamily: "JetBrains Mono", fontSize: 10, color: C.muted, textAlign: "center", padding: "6px 8px", fontWeight: 400, letterSpacing: 1 }}>{d} DTE</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(grid.buckets_money || []).map(({ label, desc }) => {
                const isAtm = label === "100%";
                return (
                  <tr key={label}>
                    <td style={{
                      fontFamily: "JetBrains Mono", fontSize: 11, padding: "10px 12px",
                      color: isAtm ? C.accent : C.muted,
                      fontWeight: isAtm ? 700 : 400,
                      borderTop: `1px solid ${C.border}`,
                      whiteSpace: "nowrap",
                    }}>
                      {label}{desc ? <span style={{ color: C.muted, fontWeight: 400 }}> ({desc})</span> : ""}
                      {isAtm && <span style={{ marginLeft: 6, color: C.accent }}>★</span>}
                    </td>
                    {(grid.buckets_dte || [7,14,30,45,60,90]).map(d => {
                      const iv = grid.grid[label]?.[String(d)];
                      const { bg, color } = ivHeatColor(iv);
                      return (
                        <td key={d} style={{
                          fontFamily: "JetBrains Mono", fontSize: 12, fontWeight: 600,
                          textAlign: "center", padding: "10px 8px",
                          background: bg, color: color,
                          borderTop: `1px solid ${C.border}`,
                        }}>
                          {iv !== null && iv !== undefined ? `${iv}%` : "—"}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Panel>
      )}

      {data && !loading && (
        <>
          <Panel style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <Label color={C.muted}>VOL SURFACE ANOMALIES — {ticker} ({data.surface?.length || 0} opzioni analizzate)</Label>
              <AIAnalyst page="VOL SURFACE" contextData={`Ticker: ${ticker}, Anomalie: ${data.anomalies?.length}, Top: ${data.anomalies?.[0] ? JSON.stringify(data.anomalies[0]) : "nessuna"}`} />
            </div>
            <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 6 }}>
              {data.anomalies?.length === 0 && <div style={{ fontFamily: "JetBrains Mono", fontSize: 11, color: C.muted }}>Nessuna anomalia rilevata</div>}
              {data.anomalies?.map((a, i) => (
                <div key={i} style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "8px 10px", background: "#ffffff05", borderRadius: 6, border: `1px solid ${C.border}`
                }}>
                  <span style={{ fontFamily: "JetBrains Mono", fontSize: 11, fontWeight: 700, color: C.text, minWidth: 80 }}>K={a.strike}</span>
                  <span style={{ fontFamily: "JetBrains Mono", fontSize: 10, color: C.muted }}>{a.expiry} {a.right}</span>
                  <span style={{ fontFamily: "JetBrains Mono", fontSize: 10, color: C.muted }}>mkt {a.iv_market}%</span>
                  <span style={{ fontFamily: "JetBrains Mono", fontSize: 10, color: C.muted }}>mdl {a.iv_model}%</span>
                  <span style={{ fontFamily: "JetBrains Mono", fontSize: 12, fontWeight: 700, color: a.edge_sigma >= 3 ? C.green : C.yellow }}>{a.edge_sigma}σ</span>
                  {a.iv_rank !== null && a.iv_rank !== undefined && (
                    <span style={{ fontFamily: "JetBrains Mono", fontSize: 10, color: a.iv_rank >= 70 ? C.red : a.iv_rank <= 30 ? C.green : C.muted }}>
                      IVR {a.iv_rank}%
                    </span>
                  )}
                  {a.confirmed && <span style={{ fontSize: 9, fontFamily: "JetBrains Mono", color: C.green }}>✓</span>}
                  <span style={{
                    fontSize: 9, fontFamily: "JetBrains Mono",
                    color: a.signal_type === "SELL VOL" ? C.red : C.green,
                    background: a.signal_type === "SELL VOL" ? `${C.red}15` : `${C.green}15`,
                    padding: "2px 8px", borderRadius: 4
                  }}>{a.signal_type}</span>
                </div>
              ))}
            </div>
          </Panel>
          <Panel>
            <Label color={C.muted}>IV MARKET vs MODEL</Label>
            <div style={{ height: 250, marginTop: 12 }}>
              <ResponsiveContainer>
                <LineChart data={data.surface?.slice(0, 80)}>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                  <XAxis dataKey="moneyness" tick={{ fill: C.muted, fontSize: 9, fontFamily: "JetBrains Mono" }} />
                  <YAxis tick={{ fill: C.muted, fontSize: 9, fontFamily: "JetBrains Mono" }} />
                  <Tooltip contentStyle={{ background: C.panel, border: `1px solid ${C.border}`, fontFamily: "JetBrains Mono", fontSize: 11 }} />
                  <Legend wrapperStyle={{ fontFamily: "JetBrains Mono", fontSize: 10 }} />
                  <Line type="monotone" dataKey="iv_market" stroke={C.accent} strokeWidth={2} dot={false} name="IV Market %" />
                  <Line type="monotone" dataKey="iv_model" stroke={C.accent2} strokeWidth={2} dot={false} strokeDasharray="5 5" name="IV Model %" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Panel>
        </>
      )}
    </div>
  );
}

// ─── REGIME ───────────────────────────────────────────────────────────────────
function RegimePage() {
  const [data, setData]           = useState(null);
  const [selected, setSelected]   = useState(null);
  const [history, setHistory]     = useState([]);
  const [showHistory, setShowHist] = useState(false);

  useEffect(() => {
    api.get("/api/regime").then(d => { setData(d); setSelected(d.dominant); }).catch(console.error);
  }, []);

  useEffect(() => {
    if (showHistory)
      api.get("/api/regime/history?days=90").then(setHistory).catch(console.error);
  }, [showHistory]);

  if (!data) return <div style={{ padding: 24, color: C.muted, fontFamily: "JetBrains Mono", fontSize: 12 }}>Caricamento...</div>;

  const RCFG = {
    CRISIS:   { color: "#FF4444", amp: "1.8×", desc: "IV estrema" },
    HIGH_VOL: { color: C.orange, amp: "1.5×", desc: "Vol elevata" },
    NORMAL:   { color: C.yellow, amp: "1.0×", desc: "Nella norma" },
    CALM:     { color: C.green,  amp: "0.7×", desc: "Bassa vol" },
    PIN:      { color: "#8B5CF6", amp: "0×",  desc: "Max pain" },
  };
  const ALL = ["CRISIS","HIGH_VOL","NORMAL","CALM","PIN"];
  const STRATS = {
    CRISIS:   ["Long Puts","Protective Collar","Evita SHORT naked"],
    HIGH_VOL: ["Iron Condor","Short Straddle","Credit Spread"],
    NORMAL:   ["Vol Surface Long/Short","Debit Spread","Bull Put Spread"],
    CALM:     ["Short Put (VRP)","Bull Put Spread","Cash-secured Put"],
    PIN:      ["Calendar Spread","Butterfly","Theta play ATM"],
  };

  const distMap = {};
  (data.distribution || []).forEach(d => { distMap[d.regime] = d; });
  const filteredTickers = selected ? (data.tickers || []).filter(t => t.regime === selected) : (data.tickers || []);
  const strategies = STRATS[selected] || data.recommended_strategies || [];
  const activeColor = RCFG[selected]?.color || C.text;

  return (
    <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ fontFamily: "JetBrains Mono", fontSize: 11, color: C.muted }}>REGIME MERCATO — {data.date}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontFamily: "JetBrains Mono", fontSize: 10, color: C.muted }}>DOMINANTE</span>
          <span style={{ fontFamily: "JetBrains Mono", fontSize: 13, fontWeight: 700, color: RCFG[data.dominant]?.color, padding: "3px 10px", border: `1px solid ${RCFG[data.dominant]?.color}50`, borderRadius: 4 }}>{data.dominant}</span>
          <span style={{ fontFamily: "JetBrains Mono", fontSize: 11, color: C.muted }}>amp {RCFG[data.dominant]?.amp}</span>
        </div>
      </div>

      {/* 5 Regime Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10 }}>
        {ALL.map(r => {
          const cfg = RCFG[r];
          const d = distMap[r] || { count: 0, pct: 0 };
          const isActive = selected === r;
          return (
            <Panel key={r} onClick={() => setSelected(isActive ? null : r)}
              style={{ cursor: "pointer", borderColor: isActive ? cfg.color : C.border, background: isActive ? `${cfg.color}12` : undefined, transition: "all 0.15s" }}>
              <div style={{ fontFamily: "JetBrains Mono", fontSize: 10, fontWeight: 700, color: cfg.color, marginBottom: 6 }}>{r}</div>
              <div style={{ fontFamily: "JetBrains Mono", fontSize: 24, fontWeight: 700, color: isActive ? cfg.color : C.text }}>
                {d.pct.toFixed(0)}<span style={{ fontSize: 12 }}>%</span>
              </div>
              <div style={{ fontFamily: "JetBrains Mono", fontSize: 10, color: C.muted, marginTop: 2 }}>{d.count} ticker</div>
              <div style={{ marginTop: 8, height: 3, background: C.dim, borderRadius: 2 }}>
                <div style={{ width: `${d.pct}%`, height: "100%", background: cfg.color, borderRadius: 2 }} />
              </div>
              <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted, marginTop: 6 }}>{cfg.amp} · {cfg.desc}</div>
            </Panel>
          );
        })}
      </div>

      {/* Strategies + Ticker table */}
      <div style={{ display: "grid", gridTemplateColumns: "260px 1fr", gap: 12 }}>
        <Panel>
          <Label color={C.muted}>STRATEGIE — {selected || data.dominant}</Label>
          <div style={{ marginTop: 12 }}>
            {strategies.map(s => (
              <div key={s} style={{ fontFamily: "JetBrains Mono", fontSize: 12, color: C.text, padding: "8px 0", borderBottom: `1px solid ${C.border}30`, display: "flex", gap: 8 }}>
                <span style={{ color: activeColor }}>→</span>{s}
              </div>
            ))}
          </div>
        </Panel>

        <Panel>
          <Label color={C.muted}>TICKER {selected ? `— ${selected}` : "(tutti)"} ({filteredTickers.length})</Label>
          <div style={{ marginTop: 12, maxHeight: 280, overflowY: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "JetBrains Mono", fontSize: 11 }}>
              <thead style={{ position: "sticky", top: 0, background: C.panel, zIndex: 1, boxShadow: `0 1px 0 ${C.border}` }}>
                <tr style={{ color: C.muted, fontSize: 10 }}>
                  {["TICKER","REGIME","PCR","HV10","HV30","SPOT","MAX PAIN"].map(h => (
                    <th key={h} style={{ padding: "4px 8px", textAlign: h === "TICKER" ? "left" : "right", fontWeight: 400 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredTickers.map(t => {
                  const tc = RCFG[t.regime] || {};
                  return (
                    <tr key={t.ticker} style={{ borderBottom: `1px solid ${C.border}20` }}>
                      <td style={{ padding: "5px 8px", color: C.text, fontWeight: 700 }}>{t.ticker}</td>
                      <td style={{ padding: "5px 8px", textAlign: "right" }}>
                        <span style={{ color: tc.color, fontSize: 10 }}>{t.regime}</span>
                      </td>
                      <td style={{ padding: "5px 8px", textAlign: "right", color: t.pcr > 1.2 ? "#FF4444" : t.pcr < 0.8 ? C.green : C.text }}>{t.pcr?.toFixed(2) ?? "—"}</td>
                      <td style={{ padding: "5px 8px", textAlign: "right", color: C.muted }}>{t.hv10 ? (t.hv10 > 1 ? t.hv10.toFixed(1) : (t.hv10 * 100).toFixed(1)) + "%" : "—"}</td>
                      <td style={{ padding: "5px 8px", textAlign: "right", color: C.muted }}>{t.hv30 ? (t.hv30 > 1 ? t.hv30.toFixed(1) : (t.hv30 * 100).toFixed(1)) + "%" : "—"}</td>
                      <td style={{ padding: "5px 8px", textAlign: "right", color: C.text }}>{t.spot ? "$" + Number(t.spot).toFixed(2) : "—"}</td>
                      <td style={{ padding: "5px 8px", textAlign: "right", color: C.muted }}>{t.max_pain ? "$" + Number(t.max_pain).toFixed(2) : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>

      {/* Storico collassabile */}
      <Panel>
        <div onClick={() => setShowHist(!showHistory)} style={{ cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Label color={C.muted}>STORICO REGIME (90 giorni)</Label>
          <span style={{ fontFamily: "JetBrains Mono", fontSize: 10, color: C.muted }}>{showHistory ? "▲ CHIUDI" : "▼ ESPANDI"}</span>
        </div>
        {showHistory && (
          <div style={{ marginTop: 12, maxHeight: 260, overflowY: "auto" }}>
            {history.length === 0
              ? <div style={{ fontFamily: "JetBrains Mono", fontSize: 11, color: C.muted }}>Nessun dato storico disponibile.</div>
              : <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "JetBrains Mono", fontSize: 11 }}>
                  <thead>
                    <tr style={{ color: C.muted, fontSize: 10, borderBottom: `1px solid ${C.border}` }}>
                      <th style={{ padding: "4px 8px", textAlign: "left", fontWeight: 400 }}>DATA</th>
                      <th style={{ padding: "4px 8px", textAlign: "center", fontWeight: 400 }}>REGIME</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((h, i) => {
                      const { date: hDate, ...counts } = h;
                      const dominant = Object.entries(counts).sort((a,b) => b[1]-a[1])[0]?.[0] || "—";
                      return (
                        <tr key={i} style={{ borderBottom: `1px solid ${C.border}20` }}>
                          <td style={{ padding: "4px 8px", color: C.muted }}>{String(hDate)}</td>
                          <td style={{ padding: "4px 8px", textAlign: "center", color: RCFG[dominant]?.color || C.text }}>{dominant}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
            }
          </div>
        )}
      </Panel>
    </div>
  );
}

// ─── PORTFOLIO ────────────────────────────────────────────────────────────────
function PositionChart({ symbol, positionData }) {
  const [chartData, setChartData] = useState([]);

  const { strike, right, multiplier = 100, position = 1, cost_basis = 0 } = positionData || {};

  useEffect(() => {
    api.get(`/api/portfolio/chart/${symbol}`).then(data => {
      const enriched = data.map(d => {
        let pnl = null;
        if (strike && right) {
          const intrinsic = right === "P"
            ? Math.max(0, strike - d.close)
            : Math.max(0, d.close - strike);
          const value = intrinsic * multiplier * Math.abs(position);
          pnl = Math.round((position > 0 ? value - cost_basis : cost_basis - value) * 100) / 100;
        }
        return { ...d, pnl };
      });
      setChartData(enriched);
    }).catch(() => {});
  }, [symbol, positionData]);

  if (!chartData.length) return <div style={{ color: C.muted, fontFamily: "JetBrains Mono", fontSize: 11, padding: 16 }}>Caricamento grafico...</div>;

  const closes = chartData.map(d => d.close);
  const currentPrice = closes[closes.length - 1] || 0;
  const allPrices = strike ? closes.concat([strike]) : closes;
  const yMin = Math.floor(Math.min(...allPrices) * 0.96);
  const yMax = Math.ceil(Math.max(...allPrices) * 1.04);
  const pnls = chartData.map(d => d.pnl).filter(v => v !== null);
  const pMin = pnls.length ? Math.floor(Math.min(...pnls) * 1.15) : -100;
  const pMax = pnls.length ? Math.ceil(Math.max(...pnls) * 1.15) : 100;

  const breakeven = strike && right && cost_basis > 0
    ? right === "P"
      ? strike - cost_basis / (multiplier * Math.abs(position))
      : strike + cost_basis / (multiplier * Math.abs(position))
    : null;

  const payoffData = (() => {
    if (!strike || !right) return [];
    const lo = currentPrice * 0.4;
    const hi = currentPrice * 1.6;
    const steps = 60;
    const step = (hi - lo) / steps;
    return Array.from({ length: steps + 1 }, (_, i) => {
      const price = lo + i * step;
      const intrinsic = right === "P"
        ? Math.max(0, strike - price)
        : Math.max(0, price - strike);
      const value = intrinsic * multiplier * Math.abs(position);
      const pnl = Math.round((position > 0 ? value - cost_basis : cost_basis - value) * 100) / 100;
      return { price: Math.round(price * 100) / 100, pnl };
    });
  })();

  const payoffPnls = payoffData.map(d => d.pnl);
  const payMin = payoffPnls.length ? Math.floor(Math.min(...payoffPnls) * 1.15) : -200;
  const payMax = payoffPnls.length ? Math.ceil(Math.max(...payoffPnls) * 1.15) : 200;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 8 }}>
      <div>
        <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted, marginBottom: 4 }}>
          PREZZO STORICO (30gg) + P&L POSIZIONE
          {breakeven !== null && breakeven > 0 &&
            <span style={{ color: C.yellow, marginLeft: 12 }}>BREAKEVEN: ${Math.round(breakeven * 100) / 100}</span>}
        </div>
        <div style={{ height: 220 }}>
          <ResponsiveContainer>
            <ComposedChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis dataKey="date" tick={{ fill: C.muted, fontSize: 9, fontFamily: "JetBrains Mono" }}
                tickFormatter={d => d.slice(5)} />
              <YAxis yAxisId="left" domain={[yMin, yMax]}
                tick={{ fill: C.accent, fontSize: 9, fontFamily: "JetBrains Mono" }}
                tickFormatter={v => `$${v}`} width={45} />
              <YAxis yAxisId="right" orientation="right" domain={[pMin, pMax]}
                tick={{ fill: C.green, fontSize: 9, fontFamily: "JetBrains Mono" }}
                tickFormatter={v => `$${v}`} width={55} />
              <Tooltip
                contentStyle={{ background: C.panel, border: `1px solid ${C.border}`, fontFamily: "JetBrains Mono", fontSize: 11 }}
                formatter={(value, name) => [`$${value}`, name]} />
              <Line yAxisId="left" type="monotone" dataKey="close" stroke={C.accent}
                strokeWidth={2} dot={false} name="Prezzo" />
              {pnls.length > 0 && (
                <Line yAxisId="right" type="monotone" dataKey="pnl" stroke={C.green}
                  strokeWidth={2} dot={false} strokeDasharray="5 5" name="P&L" />
              )}
              {strike && (
                <ReferenceLine yAxisId="left" y={strike} stroke={C.yellow} strokeDasharray="4 4"
                  label={{ value: `K=${strike}`, fill: C.yellow, fontSize: 9, fontFamily: "JetBrains Mono", position: "insideTopRight" }} />
              )}
              {breakeven !== null && breakeven > 0 && (
                <ReferenceLine yAxisId="left" y={breakeven} stroke="#f6ad20" strokeDasharray="2 6"
                  label={{ value: `BE $${Math.round(breakeven*100)/100}`, fill: "#f6ad20", fontSize: 9, fontFamily: "JetBrains Mono", position: "insideBottomRight" }} />
              )}
              <ReferenceLine yAxisId="right" y={0} stroke={C.muted} strokeDasharray="2 2" />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {payoffData.length > 0 && (
        <div>
          <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted, marginBottom: 4 }}>
            PAYOFF A SCADENZA — P&L al variare del prezzo sottostante
            {breakeven !== null && breakeven > 0 &&
              <span style={{ color: C.yellow, marginLeft: 12 }}>BREAKEVEN: ${Math.round(breakeven * 100) / 100}</span>}
          </div>
          <div style={{ height: 200 }}>
            <ResponsiveContainer>
              <ComposedChart data={payoffData}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                <XAxis dataKey="price" tick={{ fill: C.muted, fontSize: 9, fontFamily: "JetBrains Mono" }}
                  tickFormatter={v => `$${v}`} />
                <YAxis domain={[payMin, payMax]}
                  tick={{ fill: C.muted, fontSize: 9, fontFamily: "JetBrains Mono" }}
                  tickFormatter={v => `$${v}`} width={55} />
                <Tooltip
                  contentStyle={{ background: C.panel, border: `1px solid ${C.border}`, fontFamily: "JetBrains Mono", fontSize: 11 }}
                  formatter={(value, name) => [`$${value}`, name]}
                  labelFormatter={v => `Prezzo: $${v}`} />
                <ReferenceLine y={0} stroke={C.muted} strokeDasharray="2 2" />
                {strike && (
                  <ReferenceLine x={strike} stroke={C.yellow} strokeDasharray="4 4"
                    label={{ value: `K=${strike}`, fill: C.yellow, fontSize: 9, fontFamily: "JetBrains Mono", position: "top" }} />
                )}
                {breakeven !== null && breakeven > 0 && (
                  <ReferenceLine x={Math.round(breakeven * 100) / 100} stroke="#f6ad20" strokeDasharray="2 6"
                    label={{ value: `BE`, fill: "#f6ad20", fontSize: 9, fontFamily: "JetBrains Mono", position: "top" }} />
                )}
                <ReferenceLine x={currentPrice} stroke={C.accent} strokeDasharray="3 3"
                  label={{ value: "oggi", fill: C.accent, fontSize: 9, fontFamily: "JetBrains Mono", position: "top" }} />
                <Line type="monotone" dataKey="pnl" stroke={C.green}
                  strokeWidth={2} dot={false} name="P&L a scadenza" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

function PortfolioPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  const fetchPortfolio = async () => {
    setLoading(true);
    try { setData(await api.get("/api/portfolio")); setLastUpdate(new Date()); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchPortfolio();
    const i = setInterval(fetchPortfolio, 300000);
    return () => clearInterval(i);
  }, []);

  if (loading) return <div style={{ padding: 24, color: C.muted, fontFamily: "JetBrains Mono", fontSize: 12 }}>Caricamento posizioni da IB...</div>;

  const pnl = data?.pnl || {};
  const positions = data?.positions || [];
  const totalUnrealized = positions.reduce((sum, p) => sum + (p.unrealized_pnl || 0), 0);
  const sel = selectedIdx !== null ? positions[selectedIdx] : null;

  return (
    <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
        <Panel><Metric label="NET LIQUIDATION" value={`$${(pnl.NetLiquidation || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}`} color={C.accent} /></Panel>
        <Panel><Metric label="UNREALIZED P&L" value={`${totalUnrealized >= 0 ? "+" : ""}$${totalUnrealized.toFixed(2)}`} color={totalUnrealized >= 0 ? C.green : C.red} /></Panel>
        <Panel><Metric label="REALIZED P&L" value={`${(pnl.RealizedPnL || 0) >= 0 ? "+" : ""}$${(pnl.RealizedPnL || 0).toFixed(2)}`} color={(pnl.RealizedPnL || 0) >= 0 ? C.green : C.red} /></Panel>
        <Panel><Metric label="POSIZIONI APERTE" value={positions.length} color={C.yellow} /></Panel>
      </div>

      <Panel>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <Label color={C.muted}>POSIZIONI APERTE — CONTO LIVE IB {sel && <span style={{ color: C.accent }}> — {sel.symbol} selezionato</span>}</Label>
          <div style={{ display: "flex", gap: 8 }}>
            <AIAnalyst page="PORTFOLIO" contextData={`NetLiq: ${pnl.NetLiquidation}, UnrealizedPnL: ${totalUnrealized}, Posizioni: ${JSON.stringify(positions)}`} />
            {lastUpdate && <span style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted }}>
              agg. {lastUpdate.toLocaleTimeString("it-IT")} · next in 5min
            </span>}
            <button onClick={fetchPortfolio} style={{ fontFamily: "JetBrains Mono", fontSize: 10, color: C.accent, background: `${C.accent}15`, padding: "3px 10px", borderRadius: 4, border: `1px solid ${C.accent}30`, cursor: "pointer" }}>↻ AGGIORNA</button>
          </div>
        </div>

        {positions.length === 0 ? (
          <div style={{ fontFamily: "JetBrains Mono", fontSize: 11, color: C.muted, textAlign: "center", padding: 24 }}>Nessuna posizione aperta</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                {["SYMBOL", "QTÀ", "LEG", "EXPIRY", "SPOT NOW", "P&L LIVE", "VST", "REGIME"].map(h => (
                  <th key={h} style={{ textAlign: "left", padding: "6px 10px", fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted, letterSpacing: "0.12em", fontWeight: 400 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {positions.map((p, i) => {
                const isSelected = selectedIdx === i;
                const isOpt = p.sec_type === "OPT" || p.sec_type === "FOP";
                const legDesc = isOpt ? `K${p.strike} ${p.right}` : p.sec_type;
                const expClean = p.expiry ? String(p.expiry).replace(/-/g,'') : null;
                const expFormatted = expClean && expClean.length >= 8
                  ? `${expClean.slice(0,4)}-${expClean.slice(4,6)}-${expClean.slice(6,8)}`
                  : (p.expiry || "—");
                const vstColor = !p.vst ? C.muted : p.vst > 0 ? C.green : C.red;
                const regimeColor = p.regime === "CRISIS" ? C.red : p.regime === "HIGH_VOL" ? C.red
                  : p.regime === "CALM" || p.regime === "PIN" ? C.green : C.yellow;
                return (
                  <React.Fragment key={i}>
                    <tr onClick={() => setSelectedIdx(isSelected ? null : i)}
                      style={{ borderBottom: `1px solid ${C.border}40`, cursor: "pointer",
                        background: isSelected ? `${C.accent}10` : "transparent",
                        transition: "background 0.15s" }}>
                      <td style={{ padding: "12px 10px", fontFamily: "JetBrains Mono", fontSize: 13, fontWeight: 700, color: C.text }}>{p.symbol}</td>
                      <td style={{ padding: "12px 10px", fontFamily: "JetBrains Mono", fontSize: 12, fontWeight: 700,
                        color: p.position > 0 ? C.green : C.red }}>
                        {p.position > 0 ? "+" : ""}{p.position}
                      </td>
                      <td style={{ padding: "12px 10px", fontFamily: "JetBrains Mono", fontSize: 10, color: C.yellow }}>{legDesc}</td>
                      <td style={{ padding: "12px 10px", fontFamily: "JetBrains Mono", fontSize: 11, color: C.yellow }}>{expFormatted}</td>
                      <td style={{ padding: "12px 10px", fontFamily: "JetBrains Mono", fontSize: 11, color: C.text }}>
                        {p.current_spot ? `$${p.current_spot.toFixed(2)}` : "—"}
                      </td>
                      <td style={{ padding: "12px 10px", fontFamily: "JetBrains Mono", fontSize: 12, fontWeight: 700,
                        color: (p.unrealized_pnl || 0) >= 0 ? C.green : C.red }}>
                        {p.unrealized_pnl !== null ? `${p.unrealized_pnl >= 0 ? "+" : ""}$${p.unrealized_pnl}` : "—"}
                      </td>
                      <td style={{ padding: "12px 10px", fontFamily: "JetBrains Mono", fontSize: 11, fontWeight: 700, color: vstColor }}>
                        {p.vst != null ? `${p.vst >= 0 ? "+" : ""}${p.vst.toFixed(2)}` : "—"}
                      </td>
                      <td style={{ padding: "12px 10px", fontFamily: "JetBrains Mono", fontSize: 10, color: regimeColor }}>
                        {p.regime || "—"}
                      </td>
                    </tr>
                    {isSelected && (
                      <tr style={{ background: `${C.accent}08` }}>
                        <td colSpan={8} style={{ padding: "16px 20px" }}>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
                            <div>
                              <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted, marginBottom: 4 }}>COSTO BASE TOTALE</div>
                              <div style={{ fontFamily: "JetBrains Mono", fontSize: 13, color: C.text }}>${p.cost_basis}</div>
                            </div>
                            <div>
                              <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted, marginBottom: 4 }}>PREZZO CORRENTE</div>
                              <div style={{ fontFamily: "JetBrains Mono", fontSize: 13, color: C.text }}>${p.market_price}</div>
                            </div>
                            <div>
                              <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted, marginBottom: 4 }}>P&L REALIZZATO</div>
                              <div style={{ fontFamily: "JetBrains Mono", fontSize: 13, color: (p.realized_pnl || 0) >= 0 ? C.green : C.red }}>
                                {p.realized_pnl >= 0 ? "+" : ""}${p.realized_pnl}
                              </div>
                            </div>
                            {p.sec_type === "OPT" && (
                              <div>
                                <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted, marginBottom: 4 }}>SCADENZA</div>
                                <div style={{ fontFamily: "JetBrains Mono", fontSize: 13, color: C.yellow }}>
                                  {p.expiry?.slice(0,4)}-{p.expiry?.slice(4,6)}-{p.expiry?.slice(6,8)}
                                </div>
                              </div>
                            )}
                          </div>
                          <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted, marginBottom: 6 }}>
                            GRAFICO UNDERLYING — {p.symbol} (30 giorni){p.strike ? ` — linea strike K=${p.strike}` : ""}
                          </div>
                          <PositionChart symbol={p.symbol} positionData={p} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </Panel>
    </div>
  );
}


// ─── OPTION CHAIN ─────────────────────────────────────────────────────────────

function ChainPage() {
  const [tickers, setTickers]   = useState([]);
  const [ticker, setTicker]     = useState("AMD");
  const [info, setInfo]         = useState(null);
  const [expiry, setExpiry]     = useState(null);
  const [chain, setChain]       = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(false);
  const [loadingChain, setLoadingChain] = useState(false);
  const [showMaxPain, setShowMaxPain] = useState(false);

  useEffect(() => {
    api.get("/api/tickers").then(data => {
      const active = (Array.isArray(data) ? data : []).filter(t => t.active).map(t => t.symbol);
      if (active.length > 0) setTickers(active);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    setLoadingInfo(true);
    setChain(null);
    setExpiry(null);
    api.get(`/api/chain/${ticker}`)
      .then(d => { setInfo(d); if (d.expiries?.length > 0) setExpiry(d.expiries[0]); })
      .catch(() => {})
      .finally(() => setLoadingInfo(false));
  }, [ticker]);

  useEffect(() => {
    if (!expiry) return;
    setLoadingChain(true);
    api.get(`/api/chain/${ticker}/${expiry}`)
      .then(setChain)
      .catch(() => {})
      .finally(() => setLoadingChain(false));
  }, [ticker, expiry]);

  const spot = chain?.spot || info?.spot || 0;
  const maxOI = chain ? Math.max(1, ...chain.strikes.flatMap(s => [s.call?.oi || 0, s.put?.oi || 0])) : 1;

  const OIBar = ({ oi, align }) => {
    const pct = Math.round((oi / maxOI) * 100);
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 4, justifyContent: align === "right" ? "flex-end" : "flex-start" }}>
        {align === "right" && <span style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted, minWidth: 40, textAlign: "right" }}>{oi > 0 ? oi.toLocaleString() : "—"}</span>}
        <div style={{ width: 40, height: 6, background: C.border, borderRadius: 3, overflow: "hidden" }}>
          <div style={{ width: `${pct}%`, height: "100%", background: align === "right" ? C.red : C.green, borderRadius: 3 }} />
        </div>
        {align === "left" && <span style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted, minWidth: 40 }}>{oi > 0 ? oi.toLocaleString() : "—"}</span>}
      </div>
    );
  };

  const Cell = ({ val, color, bold }) => (
    <td style={{ padding: "5px 8px", fontFamily: "JetBrains Mono", fontSize: 10,
      color: color || C.text, fontWeight: bold ? 700 : 400, textAlign: "right", whiteSpace: "nowrap" }}>
      {val ?? "—"}
    </td>
  );

  const thStyle = { padding: "5px 8px", fontFamily: "JetBrains Mono", fontSize: 9,
    color: C.muted, letterSpacing: "0.1em", fontWeight: 400, textAlign: "right", borderBottom: `1px solid ${C.border}` };

  return (
    <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {tickers.map(t => (
          <button key={t} onClick={() => setTicker(t)} style={{
            background: ticker === t ? `${C.accent}20` : "transparent",
            border: `1px solid ${ticker === t ? C.accent : C.border}`,
            color: ticker === t ? C.accent : C.muted,
            fontFamily: "JetBrains Mono", fontSize: 11,
            padding: "6px 16px", borderRadius: 4, cursor: "pointer"
          }}>{t}</button>
        ))}
      </div>

      {info && (
        <div style={{ display: "flex", gap: 24, alignItems: "center" }}>
          <span style={{ fontFamily: "JetBrains Mono", fontSize: 16, color: C.text, fontWeight: 700 }}>
            {ticker} <span style={{ color: C.accent }}>${spot || "—"}</span>
          </span>
          {chain?.data_source === "historical" && (
            <span style={{ fontFamily: "JetBrains Mono", fontSize: 10, color: "#f6ad20",
              background: "#f6ad2015", border: "1px solid #f6ad2040", padding: "3px 10px", borderRadius: 4 }}>
              MERCATO CHIUSO — dati storici
            </span>
          )}
          {chain?.max_pain && (
            <button onClick={() => setShowMaxPain(true)} style={{
              fontFamily: "JetBrains Mono", fontSize: 10, color: C.accent2,
              background: `${C.accent2}15`, border: `1px solid ${C.accent2}40`,
              padding: "3px 10px", borderRadius: 4, cursor: "pointer"
            }}>Max Pain: ${chain.max_pain} ↗</button>
          )}
        </div>
      )}

      {info?.expiries?.length > 0 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {info.expiries.map(e => {
            const label = `${e.slice(0,4)}-${e.slice(4,6)}-${e.slice(6,8)}`;
            return (
              <button key={e} onClick={() => setExpiry(e)} style={{
                background: expiry === e ? `${C.accent}20` : "transparent",
                border: `1px solid ${expiry === e ? C.accent : C.border}`,
                color: expiry === e ? C.accent : C.muted,
                fontFamily: "JetBrains Mono", fontSize: 10,
                padding: "4px 14px", borderRadius: 4, cursor: "pointer"
              }}>{label}</button>
            );
          })}
        </div>
      )}

      {loadingInfo && <div style={{ fontFamily: "JetBrains Mono", fontSize: 11, color: C.muted }}>Caricamento scadenze...</div>}
      {loadingChain && <div style={{ fontFamily: "JetBrains Mono", fontSize: 11, color: C.muted }}>Caricamento chain...</div>}

      {chain && !loadingChain && (
        <Panel style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th colSpan={7} style={{ ...thStyle, textAlign: "center", color: C.green, borderRight: `2px solid ${C.border}` }}>CALLS</th>
                <th style={{ ...thStyle, textAlign: "center", color: C.yellow, minWidth: 70 }}>STRIKE</th>
                <th colSpan={7} style={{ ...thStyle, textAlign: "center", color: C.red, borderLeft: `2px solid ${C.border}` }}>PUTS</th>
              </tr>
              <tr style={{ borderBottom: `2px solid ${C.border}` }}>
                <th style={{ ...thStyle, textAlign: "left" }}>OI</th>
                <th style={thStyle}>Vol</th>
                <th style={thStyle}>IV%</th>
                <th style={thStyle}>Θ</th>
                <th style={thStyle}>Δ</th>
                <th style={thStyle}>Bid</th>
                <th style={{ ...thStyle, borderRight: `2px solid ${C.border}` }}>Ask</th>
                <th style={{ ...thStyle, textAlign: "center" }}></th>
                <th style={{ ...thStyle, borderLeft: `2px solid ${C.border}` }}>Bid</th>
                <th style={thStyle}>Ask</th>
                <th style={thStyle}>Δ</th>
                <th style={thStyle}>Θ</th>
                <th style={thStyle}>IV%</th>
                <th style={thStyle}>Vol</th>
                <th style={{ ...thStyle, textAlign: "right" }}>OI</th>
              </tr>
            </thead>
            <tbody>
              {chain.strikes.map((row, i) => {
                const atm = spot > 0 && Math.abs(row.strike - spot) === Math.min(...chain.strikes.map(s => Math.abs(s.strike - spot)));
                const isMaxPain = chain.max_pain != null && row.strike === chain.max_pain;
                const itm_call = spot > 0 && row.strike < spot;
                const itm_put  = spot > 0 && row.strike > spot;
                const c = row.call;
                const p = row.put;
                const rowBg = isMaxPain ? `${C.accent2}15` : atm ? `${C.yellow}15` : i % 2 === 0 ? "transparent" : "#ffffff04";
                return (
                  <tr key={row.strike} style={{ background: rowBg, borderBottom: `1px solid ${C.border}20` }}>
                    <td style={{ padding: "4px 8px" }}>
                      <OIBar oi={c?.oi || 0} align="left" />
                    </td>
                    <Cell val={c?.volume > 0 ? c.volume.toLocaleString() : "—"} color={C.muted} />
                    <Cell val={c?.iv > 0 ? `${c.iv}%` : "—"} color={C.accent2} />
                    <Cell val={c?.theta !== 0 ? c?.theta : null} color={C.red} />
                    <Cell val={c?.delta !== 0 ? c?.delta : null} color={itm_call ? C.green : C.muted} bold={itm_call} />
                    <Cell val={c?.bid > 0 ? `$${c.bid}` : "—"} color={C.green} />
                    <td style={{ padding: "5px 8px", fontFamily: "JetBrains Mono", fontSize: 10,
                      color: C.green, textAlign: "right", borderRight: `2px solid ${C.border}` }}>
                      {c?.ask > 0 ? `$${c.ask}` : "—"}
                    </td>
                    <td style={{ padding: "5px 12px", textAlign: "center", fontFamily: "JetBrains Mono",
                      fontSize: (atm || isMaxPain) ? 12 : 11, fontWeight: (atm || isMaxPain) ? 700 : 400,
                      color: atm ? C.yellow : isMaxPain ? C.accent2 : C.text,
                      background: atm ? `${C.yellow}20` : isMaxPain ? `${C.accent2}20` : "transparent" }}>
                      {row.strike}
                      {atm && <div style={{ fontSize: 7, color: C.yellow, letterSpacing: "0.1em" }}>ATM</div>}
                      {isMaxPain && <div style={{ fontSize: 7, color: C.accent2, letterSpacing: "0.1em" }}>MAX PAIN</div>}
                    </td>
                    <td style={{ padding: "5px 8px", fontFamily: "JetBrains Mono", fontSize: 10,
                      color: C.red, textAlign: "right", borderLeft: `2px solid ${C.border}` }}>
                      {p?.bid > 0 ? `$${p.bid}` : "—"}
                    </td>
                    <Cell val={p?.ask > 0 ? `$${p.ask}` : "—"} color={C.red} />
                    <Cell val={p?.delta !== 0 ? p?.delta : null} color={itm_put ? C.red : C.muted} bold={itm_put} />
                    <Cell val={p?.theta !== 0 ? p?.theta : null} color={C.red} />
                    <Cell val={p?.iv > 0 ? `${p.iv}%` : "—"} color={C.accent2} />
                    <Cell val={p?.volume > 0 ? p.volume.toLocaleString() : "—"} color={C.muted} />
                    <td style={{ padding: "4px 8px" }}>
                      <OIBar oi={p?.oi || 0} align="right" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Panel>
      )}

      {chain && chain.strikes.length === 0 && (
        <div style={{ fontFamily: "JetBrains Mono", fontSize: 11, color: C.muted, textAlign: "center", padding: 24 }}>
          Nessun dato disponibile per questa scadenza
        </div>
      )}

      {showMaxPain && chain?.pain_curve && (
        <div onClick={e => { if (e.target === e.currentTarget) setShowMaxPain(false); }}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.88)", zIndex: 9999,
            display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12,
            padding: 32, width: "min(880px, 95vw)", maxHeight: "90vh", overflowY: "auto",
            display: "flex", flexDirection: "column", gap: 24 }}>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <div style={{ fontFamily: "JetBrains Mono", fontSize: 11, color: C.muted, letterSpacing: "0.12em" }}>
                  MAX PAIN ANALYSIS
                </div>
                <div style={{ fontFamily: "JetBrains Mono", fontSize: 10, color: C.muted, marginTop: 3 }}>
                  {ticker} — {expiry ? `${expiry.slice(0,4)}-${expiry.slice(4,6)}-${expiry.slice(6,8)}` : ""}
                </div>
              </div>
              <button onClick={() => setShowMaxPain(false)} style={{
                background: "transparent", border: `1px solid ${C.border}`, color: C.muted,
                fontFamily: "JetBrains Mono", fontSize: 18, width: 34, height: 34,
                borderRadius: 6, cursor: "pointer"
              }}>×</button>
            </div>

            <div style={{ textAlign: "center", padding: "8px 0 4px" }}>
              <div style={{ fontFamily: "JetBrains Mono", fontSize: 10, color: C.muted, letterSpacing: "0.15em", marginBottom: 8 }}>
                MAX PAIN STRIKE
              </div>
              <div style={{ fontFamily: "JetBrains Mono", fontSize: 52, fontWeight: 700, color: C.accent2, lineHeight: 1 }}>
                ${chain.max_pain}
              </div>
              <div style={{ fontFamily: "JetBrains Mono", fontSize: 10, color: C.muted, marginTop: 10 }}>
                Spot: ${spot}&nbsp;&nbsp;|&nbsp;&nbsp;Distanza: {spot > 0 ? `${((chain.max_pain - spot) / spot * 100).toFixed(2)}%` : "—"}
              </div>
            </div>

            <div>
              <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted, letterSpacing: "0.12em", marginBottom: 10 }}>
                CURVA DEL DOLORE — PAIN TOTALE PER STRIKE
              </div>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={chain.pain_curve} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                  <XAxis dataKey="strike" tick={{ fontFamily: "JetBrains Mono", fontSize: 8, fill: C.muted }}
                    interval={Math.floor(chain.pain_curve.length / 14)} />
                  <YAxis tick={{ fontFamily: "JetBrains Mono", fontSize: 8, fill: C.muted }}
                    tickFormatter={v => v >= 1000000 ? `${(v/1000000).toFixed(1)}M` : v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} width={48} />
                  <Tooltip contentStyle={{ background: C.card, border: `1px solid ${C.border}`, fontFamily: "JetBrains Mono", fontSize: 10 }}
                    formatter={v => [v.toLocaleString(), "Pain"]} labelFormatter={v => `Strike $${v}`} />
                  <Bar dataKey="pain" radius={[2,2,0,0]}>
                    {chain.pain_curve.map((entry, idx) => (
                      <RCell key={idx} fill={entry.strike === chain.max_pain ? C.accent2 : `${C.accent2}35`} />
                    ))}
                  </Bar>
                  <ReferenceLine x={chain.max_pain} stroke={C.accent2} strokeWidth={2} strokeDasharray="4 2" label={{ value: "MP", fill: C.accent2, fontSize: 9, fontFamily: "JetBrains Mono" }} />
                  {spot > 0 && <ReferenceLine x={spot} stroke={C.accent} strokeWidth={1} strokeDasharray="3 3" label={{ value: "Spot", fill: C.accent, fontSize: 9, fontFamily: "JetBrains Mono" }} />}
                </BarChart>
              </ResponsiveContainer>
              <div style={{ display: "flex", gap: 20, justifyContent: "center", marginTop: 6 }}>
                <span style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.accent2 }}>■ Max Pain</span>
                <span style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.accent }}>— Spot</span>
              </div>
            </div>

            <div>
              <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted, letterSpacing: "0.12em", marginBottom: 10 }}>
                TOP STRIKE PER OPEN INTEREST
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                    {["Strike", "Call OI", "Put OI", "OI Totale", "Pain"].map(h => (
                      <th key={h} style={{ padding: "5px 12px", fontFamily: "JetBrains Mono", fontSize: 9,
                        color: C.muted, textAlign: h === "Strike" ? "center" : "right", letterSpacing: "0.1em" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {chain.strikes
                    .map(s => ({
                      strike: s.strike,
                      callOI: s.call?.oi || 0,
                      putOI: s.put?.oi || 0,
                      totalOI: (s.call?.oi || 0) + (s.put?.oi || 0),
                      pain: chain.pain_curve.find(p => p.strike === s.strike)?.pain || 0,
                    }))
                    .sort((a, b) => b.totalOI - a.totalOI)
                    .slice(0, 12)
                    .map(row => (
                      <tr key={row.strike} style={{
                        borderBottom: `1px solid ${C.border}20`,
                        background: row.strike === chain.max_pain ? `${C.accent2}15` : "transparent"
                      }}>
                        <td style={{ padding: "5px 12px", fontFamily: "JetBrains Mono", fontSize: 10,
                          color: row.strike === chain.max_pain ? C.accent2 : C.text,
                          fontWeight: row.strike === chain.max_pain ? 700 : 400, textAlign: "center" }}>
                          ${row.strike}{row.strike === chain.max_pain && <span style={{ marginLeft: 6, fontSize: 8 }}>★</span>}
                        </td>
                        <td style={{ padding: "5px 12px", fontFamily: "JetBrains Mono", fontSize: 10, color: C.green, textAlign: "right" }}>
                          {row.callOI.toLocaleString()}
                        </td>
                        <td style={{ padding: "5px 12px", fontFamily: "JetBrains Mono", fontSize: 10, color: C.red, textAlign: "right" }}>
                          {row.putOI.toLocaleString()}
                        </td>
                        <td style={{ padding: "5px 12px", fontFamily: "JetBrains Mono", fontSize: 10, color: C.text, textAlign: "right" }}>
                          {row.totalOI.toLocaleString()}
                        </td>
                        <td style={{ padding: "5px 12px", fontFamily: "JetBrains Mono", fontSize: 10, color: C.muted, textAlign: "right" }}>
                          {row.pain.toLocaleString()}
                        </td>
                      </tr>
                    ))
                  }
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ─── GEX PAGE ─────────────────────────────────────────────────────────────────
function GexPage() {
  const [mode, setMode]         = useState("scan");   // "scan" | "single"
  const [tickers, setTickers]   = useState([]);
  const [ticker, setTicker]     = useState("AMD");
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(false);
  const [scanData, setScanData] = useState(null);
  const [scanLoading, setScanLoading] = useState(false);
  const [scanFilter, setScanFilter]   = useState("ALL");  // ALL | SHORT_GAMMA | LONG_GAMMA

  useEffect(() => {
    api.get("/api/tickers").then(d => {
      const active = (Array.isArray(d) ? d : []).filter(t => t.active).map(t => t.symbol);
      if (active.length) setTickers(active);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (mode === "scan" && !scanData) loadScan();
  }, [mode]);

  const loadScan = () => {
    setScanLoading(true);
    api.get("/api/gex/scan")
      .then(setScanData).catch(() => {}).finally(() => setScanLoading(false));
  };

  useEffect(() => {
    if (mode !== "single") return;
    setLoading(true);
    api.get(`/api/gex/${ticker}`)
      .then(setData).catch(() => {}).finally(() => setLoading(false));
  }, [ticker, mode]);

  const strikes  = data?.strikes || [];
  const spot     = data?.spot || 0;
  const netGex   = strikes.reduce((s, r) => s + r.gex, 0);
  const maxAbsGex = Math.max(1, ...strikes.map(r => Math.abs(r.gex)));

  let flipStrike = null;
  let cumGex = 0;
  const sorted = [...strikes].sort((a, b) => a.strike - b.strike);
  for (const s of sorted) {
    const prev = cumGex;
    cumGex += s.gex;
    if (prev < 0 && cumGex >= 0) { flipStrike = s.strike; break; }
    if (prev > 0 && cumGex <= 0) { flipStrike = s.strike; break; }
  }

  const walls = [...strikes].sort((a, b) => Math.abs(b.gex) - Math.abs(a.gex)).slice(0, 8);

  const fmtGex = v => {
    const abs = Math.abs(v);
    const s = v < 0 ? "-" : "+";
    if (abs >= 1e9) return `${s}${(abs/1e9).toFixed(1)}B`;
    if (abs >= 1e6) return `${s}${(abs/1e6).toFixed(1)}M`;
    if (abs >= 1e3) return `${s}${(abs/1e3).toFixed(0)}K`;
    return `${s}${abs.toFixed(0)}`;
  };

  const scanRows = useMemo(() => {
    const rows = scanData?.data || [];
    return scanFilter === "ALL" ? rows : rows.filter(r => r.regime === scanFilter);
  }, [scanData, scanFilter]);

  return (
    <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>

      {/* Mode toggle */}
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        {["scan", "single"].map(m => (
          <button key={m} onClick={() => setMode(m)} style={{
            background: mode === m ? `${C.accent}20` : "transparent",
            border: `1px solid ${mode === m ? C.accent : C.border}`,
            color: mode === m ? C.accent : C.muted,
            fontFamily: "JetBrains Mono", fontSize: 11,
            padding: "6px 18px", borderRadius: 4, cursor: "pointer", letterSpacing: "0.08em"
          }}>{m === "scan" ? "◈ SCAN ALL" : "◎ SINGLE TICKER"}</button>
        ))}
      </div>

      {/* ── SCAN MODE ── */}
      {mode === "scan" && (
        <>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {["ALL", "SHORT_GAMMA", "LONG_GAMMA"].map(f => (
              <button key={f} onClick={() => setScanFilter(f)} style={{
                background: scanFilter === f ? `${C.accent}15` : "transparent",
                border: `1px solid ${scanFilter === f ? C.accent : C.border}`,
                color: scanFilter === f ? C.accent : C.muted,
                fontFamily: "JetBrains Mono", fontSize: 10,
                padding: "4px 14px", borderRadius: 4, cursor: "pointer"
              }}>{f === "SHORT_GAMMA" ? "⚡ SHORT γ" : f === "LONG_GAMMA" ? "🛡 LONG γ" : "ALL"}</button>
            ))}
            <button onClick={loadScan} style={{
              marginLeft: "auto", background: "transparent",
              border: `1px solid ${C.border}`, color: C.muted,
              fontFamily: "JetBrains Mono", fontSize: 10,
              padding: "4px 14px", borderRadius: 4, cursor: "pointer"
            }}>↺ REFRESH</button>
            {scanData?.snapshot_at && (
              <span style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted }}>
                ultimo aggiornamento: {scanData.snapshot_at.replace("T", " ").slice(0, 16)}
              </span>
            )}
          </div>

          {scanLoading && <div style={{ fontFamily: "JetBrains Mono", fontSize: 11, color: C.muted }}>Caricamento GEX scan...</div>}

          {!scanLoading && scanRows.length > 0 && (
            <Panel>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                    {["Ticker", "Nome", "Categoria", "GEX Net", "GEX Calls", "GEX Puts", "Regime"].map(h => (
                      <th key={h} style={{ padding: "6px 12px", fontFamily: "JetBrains Mono", fontSize: 9,
                        color: C.muted, textAlign: h === "Ticker" || h === "Nome" || h === "Categoria" ? "left" : "right",
                        letterSpacing: "0.1em" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {scanRows.map(r => (
                    <tr key={r.ticker}
                      onClick={() => { setTicker(r.ticker); setMode("single"); }}
                      style={{ borderBottom: `1px solid ${C.border}15`, cursor: "pointer" }}
                      onMouseEnter={e => e.currentTarget.style.background = `${C.accent}08`}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                      <td style={{ padding: "6px 12px", fontFamily: "JetBrains Mono", fontSize: 11,
                        color: C.accent, fontWeight: 700 }}>{r.ticker}</td>
                      <td style={{ padding: "6px 12px", fontFamily: "JetBrains Mono", fontSize: 10,
                        color: C.text }}>{r.name || "—"}</td>
                      <td style={{ padding: "6px 12px", fontFamily: "JetBrains Mono", fontSize: 9,
                        color: C.muted }}>{r.category || "—"}</td>
                      <td style={{ padding: "6px 12px", fontFamily: "JetBrains Mono", fontSize: 11,
                        color: r.gex_total < 0 ? C.red : C.green, textAlign: "right", fontWeight: 700 }}>
                        {r.gex_total > 0 ? "+" : ""}{r.gex_total.toFixed(1)}M
                      </td>
                      <td style={{ padding: "6px 12px", fontFamily: "JetBrains Mono", fontSize: 10,
                        color: C.green, textAlign: "right" }}>+{r.gex_calls.toFixed(1)}M</td>
                      <td style={{ padding: "6px 12px", fontFamily: "JetBrains Mono", fontSize: 10,
                        color: C.red, textAlign: "right" }}>{r.gex_puts.toFixed(1)}M</td>
                      <td style={{ padding: "6px 12px", textAlign: "right" }}>
                        <span style={{
                          fontFamily: "JetBrains Mono", fontSize: 9,
                          color: r.regime === "SHORT_GAMMA" ? C.red : C.green,
                          background: r.regime === "SHORT_GAMMA" ? `${C.red}15` : `${C.green}15`,
                          border: `1px solid ${r.regime === "SHORT_GAMMA" ? C.red : C.green}40`,
                          padding: "2px 8px", borderRadius: 3
                        }}>
                          {r.regime === "SHORT_GAMMA" ? "⚡ SHORT γ" : "🛡 LONG γ"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Panel>
          )}

          {!scanLoading && scanRows.length === 0 && (
            <div style={{ fontFamily: "JetBrains Mono", fontSize: 11, color: C.muted, textAlign: "center", padding: 24 }}>
              Nessun dato GEX. Esegui compute_gex.py o attendi le 05:00.
            </div>
          )}
        </>
      )}

      {/* ── SINGLE TICKER MODE ── */}
      {mode === "single" && (
        <>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {tickers.map(t => (
              <button key={t} onClick={() => setTicker(t)} style={{
                background: ticker === t ? `${C.accent}20` : "transparent",
                border: `1px solid ${ticker === t ? C.accent : C.border}`,
                color: ticker === t ? C.accent : C.muted,
                fontFamily: "JetBrains Mono", fontSize: 11,
                padding: "6px 16px", borderRadius: 4, cursor: "pointer"
              }}>{t}</button>
            ))}
          </div>

          {loading && <div style={{ fontFamily: "JetBrains Mono", fontSize: 11, color: C.muted }}>Calcolo GEX...</div>}

          {data && !loading && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12 }}>
                <Panel><Metric label="SPOT" value={spot > 0 ? `$${spot}` : "—"} color={C.accent} /></Panel>
                <Panel><Metric label="NET GEX" value={fmtGex(netGex)} color={netGex >= 0 ? C.green : C.red} /></Panel>
                <Panel><Metric label="GEX FLIP" value={flipStrike ? `$${flipStrike}` : "—"} color={C.yellow} /></Panel>
                <Panel><Metric label="DATA SOURCE" value={data.data_source?.toUpperCase() || "—"} color={C.muted} /></Panel>
              </div>

              <Panel>
                <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
                  <div style={{
                    fontFamily: "JetBrains Mono", fontSize: 11, fontWeight: 700,
                    color: netGex >= 0 ? C.green : C.red,
                    background: netGex >= 0 ? `${C.green}15` : `${C.red}15`,
                    border: `1px solid ${netGex >= 0 ? C.green : C.red}40`,
                    padding: "4px 14px", borderRadius: 4
                  }}>
                    {netGex >= 0 ? "▲ LONG GAMMA — Mercato stabilizzante" : "▼ SHORT GAMMA — Mercato destabilizzante"}
                  </div>
                  {flipStrike && spot > 0 && (
                    <div style={{ fontFamily: "JetBrains Mono", fontSize: 10, color: C.muted }}>
                      {spot > flipStrike
                        ? `Spot sopra GEX flip ($${flipStrike}) — dealer comprano i ribassi`
                        : `Spot sotto GEX flip ($${flipStrike}) — dealer amplificano i movimenti`}
                    </div>
                  )}
                </div>
              </Panel>

              {strikes.length > 0 && (
                <Panel>
                  <Label color={C.muted}>GAMMA EXPOSURE PER STRIKE</Label>
                  <div style={{ marginTop: 12 }}>
                    <ResponsiveContainer width="100%" height={280}>
                      <ComposedChart data={strikes} margin={{ top: 4, right: 16, left: 16, bottom: 4 }}>
                        <XAxis dataKey="strike" tick={{ fontFamily: "JetBrains Mono", fontSize: 8, fill: C.muted }}
                          interval={Math.floor(strikes.length / 14)} />
                        <YAxis tick={{ fontFamily: "JetBrains Mono", fontSize: 8, fill: C.muted }} width={56}
                          tickFormatter={v => v >= 1e6 ? `${(v/1e6).toFixed(0)}M` : v >= 1e3 ? `${(v/1e3).toFixed(0)}K` : v} />
                        <Tooltip contentStyle={{ background: C.card, border: `1px solid ${C.border}`, fontFamily: "JetBrains Mono", fontSize: 10 }}
                          formatter={(v, n) => [fmtGex(v), "GEX"]} labelFormatter={v => `Strike $${v}`} />
                        <Bar dataKey="gex" radius={[2,2,0,0]}>
                          {strikes.map((s, i) => (
                            <RCell key={i} fill={s.gex >= 0 ? C.green : C.red}
                              fillOpacity={Math.abs(s.gex) / maxAbsGex * 0.8 + 0.2} />
                          ))}
                        </Bar>
                        {spot > 0 && <ReferenceLine x={spot} stroke={C.accent} strokeWidth={2} strokeDasharray="4 2"
                          label={{ value: "Spot", fill: C.accent, fontSize: 9, fontFamily: "JetBrains Mono" }} />}
                        {flipStrike && <ReferenceLine x={flipStrike} stroke={C.yellow} strokeWidth={1} strokeDasharray="3 3"
                          label={{ value: "Flip", fill: C.yellow, fontSize: 9, fontFamily: "JetBrains Mono" }} />}
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </Panel>
              )}

              <Panel>
                <Label color={C.muted}>GAMMA WALLS — TOP STRIKE PER ESPOSIZIONE</Label>
                <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 12 }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                      {["Strike", "GEX", "Open Interest", "Pin Prob", "Tipo"].map(h => (
                        <th key={h} style={{ padding: "5px 12px", fontFamily: "JetBrains Mono", fontSize: 9,
                          color: C.muted, textAlign: h === "Tipo" ? "center" : "right", letterSpacing: "0.1em" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {walls.map(w => (
                      <tr key={w.strike} style={{ borderBottom: `1px solid ${C.border}20`,
                        background: w.strike === flipStrike ? `${C.yellow}10` : "transparent" }}>
                        <td style={{ padding: "5px 12px", fontFamily: "JetBrains Mono", fontSize: 10,
                          color: C.text, textAlign: "right", fontWeight: 700 }}>${w.strike}</td>
                        <td style={{ padding: "5px 12px", fontFamily: "JetBrains Mono", fontSize: 10,
                          color: w.gex >= 0 ? C.green : C.red, textAlign: "right" }}>{fmtGex(w.gex)}</td>
                        <td style={{ padding: "5px 12px", fontFamily: "JetBrains Mono", fontSize: 10,
                          color: C.muted, textAlign: "right" }}>{(w.oi || 0).toLocaleString()}</td>
                        <td style={{ padding: "5px 12px", fontFamily: "JetBrains Mono", fontSize: 10,
                          color: C.yellow, textAlign: "right" }}>{((w.pin_prob || 0) * 100).toFixed(1)}%</td>
                        <td style={{ padding: "5px 12px", fontFamily: "JetBrains Mono", fontSize: 9,
                          textAlign: "center" }}>
                          <span style={{ color: w.gex >= 0 ? C.green : C.red,
                            background: w.gex >= 0 ? `${C.green}15` : `${C.red}15`,
                            border: `1px solid ${w.gex >= 0 ? C.green : C.red}40`,
                            padding: "2px 8px", borderRadius: 3 }}>
                            {w.gex >= 0 ? "SUPPORTO" : "RESISTENZA"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Panel>
            </>
          )}

          {data && !loading && strikes.length === 0 && (
            <div style={{ fontFamily: "JetBrains Mono", fontSize: 11, color: C.muted, textAlign: "center", padding: 24 }}>
              Nessun dato GEX disponibile — attendi il prossimo ciclo del collector
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── VST HELPERS ──────────────────────────────────────────────────────────────
function recommendStrategy(s) {
  const over     = s.direction === "SHORT" || s.badge === "OVERPRICED";
  const highVol  = ["HIGH_VOL", "CRISIS"].includes(s.regime);
  const nearFlip = s.gex_proximity >= 0.70;
  const K        = s.strike;
  const isCall   = s.right_type === "C";
  const off      = K < 15 ? 1 : K < 50 ? 2 : K < 150 ? 5 : 10;
  const T        = isCall ? "CALL" : "PUT";
  const sigStr   = `σ=${(s.sigma_instant||0).toFixed(2)}`;
  const gexStr   = `GEX prox ${((s.gex_proximity||0)*100).toFixed(0)}%`;

  if (over) {
    if (highVol) return {
      name: "Iron Condor", icon: "🔲", type: "credit",
      legs: [
        { action:"SELL", desc:`PUT $${K-1}` },
        { action:"BUY",  desc:`PUT $${K-1-off}` },
        { action:"SELL", desc:`CALL $${K+1}` },
        { action:"BUY",  desc:`CALL $${K+1+off}` },
      ],
      rationale:`Vol OVERPRICED in regime ${s.regime}. Non vendere opzioni nude — Iron Condor con rischio definito.`,
    };
    if (nearFlip) return {
      name: isCall ? "Bear Call Spread" : "Bull Put Spread", icon: "📉", type: "credit",
      legs: [
        { action:"SELL", desc:`${T} $${K}` },
        { action:"BUY",  desc:`${T} $${isCall ? K+off : K-off}` },
      ],
      rationale:`Vol OVERPRICED (${sigStr}). ${gexStr} → vicino al flip level. Pin risk elevato: spread creditizio con protezione.`,
    };
    return {
      name: isCall ? "Bear Call Spread" : "Short Put Spread", icon: "📊", type: "credit",
      legs: [
        { action:"SELL", desc:`${T} $${K}` },
        { action:"BUY",  desc:`${T} $${isCall ? K+off : K-off}` },
      ],
      rationale:`Vol OVERPRICED (${sigStr}). ${gexStr}. Spread creditizio standard.`,
    };
  } else {
    if (nearFlip || highVol) return {
      name: "Long Straddle", icon: "⚡", type: "debit",
      legs: [
        { action:"BUY", desc:`CALL $${K}` },
        { action:"BUY", desc:`PUT $${K}` },
      ],
      rationale:`Vol UNDERPRICED (${sigStr}). ${gexStr} → ${nearFlip ? "vicino al flip, movimento bidirezionale atteso" : "regime " + s.regime}. Straddle cattura entrambe le direzioni.`,
    };
    return {
      name: isCall ? "Long Call Debit Spread" : "Long Put Debit Spread", icon: "📈", type: "debit",
      legs: [
        { action:"BUY",  desc:`${T} $${K}` },
        { action:"SELL", desc:`${T} $${isCall ? K+off : K-off}` },
      ],
      rationale:`Vol UNDERPRICED (${sigStr}). ${gexStr}. Debit spread direzionale con costo ridotto.`,
    };
  }
}

function vstDrawGauge(canvas, s, mode) {
  const vst = mode === "instant" ? (s.vst_instant||0) : (s.vst_smooth||0);
  const ctx = canvas.getContext("2d");
  const W = canvas.width, H = canvas.height;
  const cx = W/2, cy = H - 14, R = Math.min(W,H)*0.72, ri = R*0.65;
  ctx.clearRect(0,0,W,H);
  // arc bg
  const zones = [
    [Math.PI, Math.PI*1.33,"#0d2a1a"],[Math.PI*1.33,Math.PI*1.5,"#1a2a0d"],
    [Math.PI*1.5,Math.PI*1.67,"#2a1a0d"],[Math.PI*1.67,Math.PI*2,"#2a0d0d"],
  ];
  zones.forEach(([a,b,col])=>{
    ctx.beginPath(); ctx.moveTo(cx+Math.cos(a)*ri, cy+Math.sin(a)*ri);
    ctx.arc(cx,cy,R,a,b); ctx.arc(cx,cy,ri,b,a,true);
    ctx.fillStyle=col; ctx.fill();
  });
  ctx.beginPath(); ctx.arc(cx,cy,R,Math.PI,0); ctx.strokeStyle="#222"; ctx.lineWidth=1; ctx.stroke();
  ctx.beginPath(); ctx.arc(cx,cy,ri,Math.PI,0); ctx.stroke();
  // ticks
  for(let i=0;i<=6;i++){
    const a=Math.PI+(Math.PI*i/6);
    ctx.beginPath(); ctx.moveTo(cx+Math.cos(a)*(R-2),cy+Math.sin(a)*(R-2)); ctx.lineTo(cx+Math.cos(a)*(R+3),cy+Math.sin(a)*(R+3));
    ctx.strokeStyle="#333"; ctx.lineWidth=1; ctx.stroke();
  }
  // labels
  ctx.font=`7px JetBrains Mono`; ctx.fillStyle="#444"; ctx.textAlign="center";
  [[-3,"−3"],[-2,"−2"],[-1,"−1"],[0,"0"],[1,"+1"],[2,"+2"],[3,"+3"]].forEach(([v,lbl])=>{
    const a=Math.PI+(Math.PI*(v+3)/6);
    ctx.fillText(lbl, cx+Math.cos(a)*(R+10), cy+Math.sin(a)*(R+10)+3);
  });
  // needle
  const clamp = Math.max(-3,Math.min(3,vst));
  const ang = Math.PI+(Math.PI*(clamp+3)/6);
  ctx.save(); ctx.shadowColor="rgba(255,102,0,.5)"; ctx.shadowBlur=6;
  ctx.beginPath();
  ctx.moveTo(cx+Math.cos(ang+Math.PI/2)*5,cy+Math.sin(ang+Math.PI/2)*5);
  ctx.lineTo(cx+Math.cos(ang)*(R-6),cy+Math.sin(ang)*(R-6));
  ctx.lineTo(cx+Math.cos(ang-Math.PI/2)*5,cy+Math.sin(ang-Math.PI/2)*5);
  ctx.fillStyle="#FF6600"; ctx.fill(); ctx.restore();
  ctx.beginPath(); ctx.arc(cx,cy,6,0,Math.PI*2); ctx.fillStyle="#FF6600"; ctx.fill();
  // value
  ctx.font=`bold 15px JetBrains Mono`; ctx.fillStyle="#FF6600"; ctx.textAlign="center";
  ctx.fillText((vst>0?"+":"")+vst.toFixed(2), cx, cy-16);
}

function vstDrawConf(canvas, s) {
  const ctx = canvas.getContext("2d");
  const W=canvas.width, H=canvas.height, cx=W/2, cy=H/2, R=W*0.38;
  ctx.clearRect(0,0,W,H);
  const conf = s.coherent&&s.confirmed?"MAX":s.coherent?"ALTA":s.confirmed?"MEDIA":"BASSA";
  const pct  = {MAX:1,ALTA:.75,MEDIA:.5,BASSA:.25}[conf];
  const col  = {MAX:"#00CC88",ALTA:"#FF6600",MEDIA:"#FFD600",BASSA:"#555"}[conf];
  ctx.beginPath(); ctx.arc(cx,cy,R,0,Math.PI*2); ctx.strokeStyle="#1a1a1a"; ctx.lineWidth=8; ctx.stroke();
  ctx.beginPath(); ctx.arc(cx,cy,R,-Math.PI/2,-Math.PI/2+Math.PI*2*pct); ctx.strokeStyle=col; ctx.lineWidth=8; ctx.lineCap="round"; ctx.stroke();
  ctx.font=`bold 15px JetBrains Mono`; ctx.fillStyle=col; ctx.textAlign="center"; ctx.textBaseline="middle";
  ctx.fillText(conf, cx, cy-6);
  ctx.font=`7px JetBrains Mono`; ctx.fillStyle="#555";
  ctx.fillText("CONFIDENCE", cx, cy+10);
}

function vstDrawPayoff(canvas, s, strat) {
  const ctx = canvas.getContext("2d");
  const W=canvas.width, H=canvas.height;
  ctx.clearRect(0,0,W,H);
  const K=s.strike||100, spot=s.spot||K;
  const pMin=K*0.82, pMax=K*1.18;
  const off = K<15?1:K<50?2:K<150?5:10;

  const payoff = price => {
    const n = strat?.name||"";
    if(n.includes("Straddle"))
      return Math.max(0,price-K)+Math.max(0,K-price) - K*0.05;
    if(n.includes("Bull Put")||n.includes("Short Put")) {
      const credit=off*0.4; return Math.min(credit, credit-(Math.max(0,K-price)-Math.max(0,(K-off)-price)));
    }
    if(n.includes("Bear Call")) {
      const credit=off*0.4; return Math.min(credit, credit-(Math.max(0,price-K)-Math.max(0,price-(K+off))));
    }
    if(n.includes("Iron")) {
      const c=off*0.6; const l=Math.max(0,K-1-price)-Math.max(0,K-1-off-price);const r=Math.max(0,price-(K+1))-Math.max(0,price-(K+1+off));
      return c-l-r;
    }
    if(n.includes("Call Debit")||n.includes("Long Call"))
      return Math.max(0,price-K)-Math.max(0,price-(K+off)) - off*0.24;
    if(n.includes("Put Debit")||n.includes("Long Put"))
      return Math.max(0,K-price)-Math.max(0,(K-off)-price) - off*0.24;
    return Math.max(0,price-K) - K*0.03;
  };

  const prices=[]; for(let i=0;i<=80;i++) prices.push(pMin+(pMax-pMin)*i/80);
  const pnls=prices.map(payoff);
  const minP=Math.min(...pnls), maxP=Math.max(...pnls), range=maxP-minP||1;
  const px=i=>i/80*W, py=v=>H-10-(v-minP)/range*(H-18);
  const y0=py(0);

  // fills
  ctx.save(); ctx.beginPath(); prices.forEach((_,i)=>i?ctx.lineTo(px(i),py(Math.max(0,pnls[i]))):ctx.moveTo(px(i),y0));
  ctx.lineTo(W,y0); ctx.lineTo(0,y0); ctx.closePath(); ctx.fillStyle="rgba(0,204,136,.07)"; ctx.fill(); ctx.restore();
  ctx.save(); ctx.beginPath(); prices.forEach((_,i)=>i?ctx.lineTo(px(i),py(Math.min(0,pnls[i]))):ctx.moveTo(px(i),y0));
  ctx.lineTo(W,y0); ctx.lineTo(0,y0); ctx.closePath(); ctx.fillStyle="rgba(255,59,59,.07)"; ctx.fill(); ctx.restore();

  // zero
  ctx.beginPath(); ctx.moveTo(0,y0); ctx.lineTo(W,y0); ctx.strokeStyle="#222"; ctx.lineWidth=1; ctx.stroke();

  // line
  ctx.beginPath(); prices.forEach((_,i)=>i?ctx.lineTo(px(i),py(pnls[i])):ctx.moveTo(px(i),py(pnls[i])));
  const g=ctx.createLinearGradient(0,0,W,0);
  g.addColorStop(0,"rgba(0,204,136,.8)"); g.addColorStop(.5,"#FF6600"); g.addColorStop(1,"rgba(255,59,59,.8)");
  ctx.strokeStyle=g; ctx.lineWidth=2; ctx.stroke();

  // spot line
  const si=Math.round((spot-pMin)/(pMax-pMin)*80);
  if(si>=0&&si<=80){
    ctx.beginPath(); ctx.moveTo(px(si),2); ctx.lineTo(px(si),H-10);
    ctx.strokeStyle="rgba(255,102,0,.35)"; ctx.lineWidth=1; ctx.setLineDash([3,3]); ctx.stroke(); ctx.setLineDash([]);
    ctx.font="7px JetBrains Mono"; ctx.fillStyle="#FF6600"; ctx.textAlign="center"; ctx.fillText("SPOT",px(si),9);
  }
  // labels
  ctx.font="7px JetBrains Mono"; ctx.fillStyle="#333"; ctx.textAlign="center";
  [[0,"$"+pMin.toFixed(0)],[40,"$"+K],[80,"$"+pMax.toFixed(0)]].forEach(([i,l])=>ctx.fillText(l,px(i),H-2));
}

function vstDrawGex(canvas, gexData, s) {
  if(!gexData?.strikes?.length) return;
  const ctx = canvas.getContext("2d");
  const W=canvas.width, H=canvas.height;
  ctx.clearRect(0,0,W,H);
  const data=gexData.strikes;
  const maxG=Math.max(...data.map(d=>Math.abs(d.gex||0)))||1;
  const strikes=data.map(d=>d.strike); const minK=Math.min(...strikes), maxK=Math.max(...strikes);
  const mid=H*0.55, bw=W/data.length*0.65;
  ctx.beginPath(); ctx.moveTo(0,mid); ctx.lineTo(W,mid); ctx.strokeStyle="#222"; ctx.lineWidth=1; ctx.stroke();
  data.forEach((d,i)=>{
    const x=(i+.5)/data.length*W, bh=Math.abs(d.gex||0)/maxG*(H*.45), pos=(d.gex||0)>0;
    ctx.fillStyle=pos?"rgba(0,204,136,.45)":"rgba(255,59,59,.45)";
    if(pos) ctx.fillRect(x-bw/2,mid-bh,bw,bh); else ctx.fillRect(x-bw/2,mid,bw,bh);
    ctx.font="6px JetBrains Mono"; ctx.fillStyle="#333"; ctx.textAlign="center";
    ctx.fillText("$"+d.strike,x,H-1);
  });
  const toX=k=>(k-minK)/(maxK-minK||1)*W;
  const sx=toX(s.spot||0);
  ctx.beginPath(); ctx.moveTo(sx,2); ctx.lineTo(sx,H-10); ctx.strokeStyle="rgba(255,102,0,.7)"; ctx.lineWidth=2; ctx.setLineDash([3,3]); ctx.stroke(); ctx.setLineDash([]);
  ctx.font="bold 7px JetBrains Mono"; ctx.fillStyle="#FF6600"; ctx.textAlign="center"; ctx.fillText("SPOT",sx,9);
  if(s.flip_level){
    const fx=toX(s.flip_level);
    ctx.beginPath(); ctx.moveTo(fx,2); ctx.lineTo(fx,H-10); ctx.strokeStyle="rgba(255,214,0,.7)"; ctx.lineWidth=1.5; ctx.setLineDash([2,4]); ctx.stroke(); ctx.setLineDash([]);
    ctx.font="bold 7px JetBrains Mono"; ctx.fillStyle="#FFD600"; ctx.textAlign="center"; ctx.fillText("FLIP",fx,9);
  }
}

// ─── VST PAGE ─────────────────────────────────────────────────────────────────
function VSTPage() {
  const [mode, setMode]             = useState("instant");
  const [requireConfirmed, setRequireConfirmed] = useState(true);
  const [data, setData]             = useState(null);
  const [loading, setLoading]       = useState(false);
  const [computing, setComputing]   = useState(false);
  const [filterDir, setFilterDir]   = useState("ALL");
  const [selected, setSelected]   = useState(null);
  const [gexData, setGexData]     = useState(null);
  const [surf3DTicker, setSurf3DTicker] = useState(null);
  const [showCorr, setShowCorr]         = useState(false);
  const gaugeRef  = useRef(null);
  const payoffRef = useRef(null);
  const confRef   = useRef(null);
  const gexRef    = useRef(null);

  // Fetch GEX strike data when ticker changes
  useEffect(() => {
    if (!selected) { setGexData(null); return; }
    api.get(`/api/gex/${selected.ticker}`).then(setGexData).catch(() => setGexData(null));
  }, [selected?.ticker]);

  // Draw canvases after render
  useLayoutEffect(() => {
    if (!selected) return;
    const strat = recommendStrategy(selected);
    if (gaugeRef.current)  vstDrawGauge(gaugeRef.current, selected, mode);
    if (payoffRef.current) vstDrawPayoff(payoffRef.current, selected, strat);
    if (confRef.current)   vstDrawConf(confRef.current, selected);
  }, [selected, mode]);

  useLayoutEffect(() => {
    if (!selected || !gexRef.current) return;
    vstDrawGex(gexRef.current, gexData, selected);
  }, [gexData, selected]);

  const load = () => {
    setLoading(true);
    api.get(`/api/vst/scan?mode=${mode}&min_vst=0.2&require_confirmed=${requireConfirmed}`)
      .then(setData).catch(() => {}).finally(() => setLoading(false));
  };

  const compute = async () => {
    setComputing(true);
    await api.post("/api/vst/compute").catch(() => {});
    setComputing(false);
    load();
  };

  useEffect(() => { load(); }, [mode, requireConfirmed]);

  const rows = useMemo(() => {
    const all = data?.data || [];
    return filterDir === "ALL" ? all : all.filter(r => r.direction === filterDir);
  }, [data, filterDir]);

  const fmtVST = v => {
    const sign = v > 0 ? "+" : "";
    return `${sign}${v.toFixed(3)}`;
  };

  const confidenceLabel = (r) => {
    if (r.coherent && r.confirmed) return { label: "MAX", color: C.green };
    if (r.coherent)                return { label: "ALTA", color: C.yellow };
    if (r.confirmed)               return { label: "MEDIA", color: C.accent };
    return                                { label: "BASSA", color: C.muted };
  };

  return (
    <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>

      {/* Header */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ fontFamily: "JetBrains Mono", fontSize: 13, fontWeight: 700, color: C.accent, letterSpacing: "0.1em" }}>
          ◈ VOL SURFACE TENSION
        </div>
        <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted, marginLeft: 8 }}>
          indicatore proprietario Quantos
        </div>

        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          {/* Mode toggle */}
          {["instant", "smooth"].map(m => (
            <button key={m} onClick={() => setMode(m)} style={{
              background: mode === m ? `${C.accent}20` : "transparent",
              border: `1px solid ${mode === m ? C.accent : C.border}`,
              color: mode === m ? C.accent : C.muted,
              fontFamily: "JetBrains Mono", fontSize: 10,
              padding: "5px 14px", borderRadius: 4, cursor: "pointer"
            }}>{m === "instant" ? "⚡ INSTANT" : "〜 SMOOTH"}</button>
          ))}

          {/* Confirmed toggle */}
          <button onClick={() => setRequireConfirmed(v => !v)} style={{
            background: requireConfirmed ? `${C.green}15` : `${C.yellow}15`,
            border: `1px solid ${requireConfirmed ? C.green : C.yellow}60`,
            color: requireConfirmed ? C.green : C.yellow,
            fontFamily: "JetBrains Mono", fontSize: 10,
            padding: "5px 14px", borderRadius: 4, cursor: "pointer"
          }}>{requireConfirmed ? "✓ CONFERMATI" : "⚠ TUTTI"}</button>

          {/* Compute button */}
          <button onClick={compute} disabled={computing} style={{
            background: computing ? `${C.accent}10` : `${C.accent}20`,
            border: `1px solid ${C.accent}60`,
            color: C.accent, fontFamily: "JetBrains Mono", fontSize: 10,
            padding: "5px 16px", borderRadius: 4, cursor: computing ? "wait" : "pointer"
          }}>{computing ? "CALCOLO..." : "▶ COMPUTE"}</button>

          <button onClick={load} style={{
            background: "transparent", border: `1px solid ${C.border}`,
            color: C.muted, fontFamily: "JetBrains Mono", fontSize: 10,
            padding: "5px 12px", borderRadius: 4, cursor: "pointer"
          }}>↺</button>

          <button onClick={() => setShowCorr(v => !v)} style={{
            background: showCorr ? `${C.accent2}20` : "transparent",
            border: `1px solid ${showCorr ? C.accent2 : C.border}`,
            color: showCorr ? C.accent2 : C.muted,
            fontFamily: "JetBrains Mono", fontSize: 10,
            padding: "5px 14px", borderRadius: 4, cursor: "pointer"
          }}>∿ CORRELAZIONE</button>

        </div>
      </div>


      {/* Mode description */}
      <Panel>
        <div style={{ display: "flex", gap: 24 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted, marginBottom: 4 }}>FORMULA</div>
            <div style={{ fontFamily: "JetBrains Mono", fontSize: 10, color: C.text }}>
              VST = σ_momentum × GEX_proximity × regime_amplifier
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted, marginBottom: 4 }}>
              {mode === "instant" ? "INSTANT MODE" : "SMOOTH MODE"}
            </div>
            <div style={{ fontFamily: "JetBrains Mono", fontSize: 10, color: mode === "instant" ? C.yellow : C.green }}>
              {mode === "instant"
                ? "σ_momentum = σ(t) − σ(t-1) — segnale grezzo, massima reattività"
                : "σ_momentum = EMA(σ, α=0.5) — filtrato, conferma ≥2 snapshot"}
            </div>
          </div>
          <div>
            <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted, marginBottom: 4 }}>SEGNALI</div>
            <div style={{ fontFamily: "JetBrains Mono", fontSize: 13, color: C.accent, fontWeight: 700 }}>
              {data?.count ?? "—"}
            </div>
          </div>
        </div>
      </Panel>

      {/* Filters */}
      <div style={{ display: "flex", gap: 8 }}>
        {["ALL", "OVERPRICED", "UNDERPRICED"].map(f => (
          <button key={f} onClick={() => setFilterDir(f)} style={{
            background: filterDir === f ? `${C.accent}15` : "transparent",
            border: `1px solid ${filterDir === f ? C.accent : C.border}`,
            color: filterDir === f ? C.accent : C.muted,
            fontFamily: "JetBrains Mono", fontSize: 10,
            padding: "4px 14px", borderRadius: 4, cursor: "pointer"
          }}>
            {f === "OVERPRICED" ? "▼ OVERPRICED" : f === "UNDERPRICED" ? "▲ UNDERPRICED" : "ALL"}
          </button>
        ))}
      </div>

      {loading && <div style={{ fontFamily: "JetBrains Mono", fontSize: 11, color: C.muted }}>Caricamento...</div>}

      {!loading && rows.length === 0 && (
        <div style={{ fontFamily: "JetBrains Mono", fontSize: 11, color: C.muted, textAlign: "center", padding: 32 }}>
          Nessun segnale VST. Clicca COMPUTE dopo aver eseguito il scan vol surface.
        </div>
      )}

      {/* Main table */}
      {!loading && rows.length > 0 && (
        <Panel>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                  {["Ticker", "Strike", "Type", "Exp", "Dir", "VST", "σ Inst", "σ Smooth", "GEX Prox", "Regime", "Conf", "Grad/K", "Entr", ""].map(h => (
                    <th key={h} style={{ padding: "6px 10px", fontFamily: "JetBrains Mono", fontSize: 9,
                      color: C.muted, textAlign: ["Ticker","Type","Dir","Conf"].includes(h) ? "left" : "right",
                      letterSpacing: "0.08em" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const vst = mode === "instant" ? r.vst_instant : r.vst_smooth;
                  const conf = confidenceLabel(r);
                  const isSelected = selected?.ticker === r.ticker && selected?.strike === r.strike && selected?.expiry === r.expiry && selected?.right_type === r.right_type;
                  return (
                    <tr key={`${r.ticker}-${r.strike}-${r.expiry}-${r.right_type}`} onClick={() => setSelected(isSelected ? null : r)}
                      style={{ borderBottom: `1px solid ${C.border}15`, cursor: "pointer",
                        background: isSelected ? `${C.accent}10` : "transparent" }}
                      onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = `${C.accent}06`; }}
                      onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = "transparent"; }}>
                      <td style={{ padding: "5px 10px", fontFamily: "JetBrains Mono", fontSize: 11,
                        color: C.accent, fontWeight: 700 }}>{r.ticker}</td>
                      <td style={{ padding: "5px 10px", fontFamily: "JetBrains Mono", fontSize: 10,
                        color: C.text, textAlign: "right" }}>${r.strike}</td>
                      <td style={{ padding: "5px 10px" }}>
                        <span style={{ fontFamily: "JetBrains Mono", fontSize: 9, fontWeight: 700,
                          color: r.right_type === "C" ? C.green : C.red,
                          border: `1px solid ${r.right_type === "C" ? C.green : C.red}40`,
                          background: `${r.right_type === "C" ? C.green : C.red}10`,
                          padding: "1px 6px", borderRadius: 3 }}>
                          {r.right_type === "C" ? "CALL" : "PUT"}
                        </span>
                      </td>
                      <td style={{ padding: "5px 10px", fontFamily: "JetBrains Mono", fontSize: 9,
                        color: C.muted, textAlign: "right" }}>{r.expiry}</td>
                      <td style={{ padding: "5px 10px" }}>
                        <span style={{ fontFamily: "JetBrains Mono", fontSize: 9,
                          color: r.direction === "OVERPRICED" ? C.red : C.green }}>
                          {r.direction === "OVERPRICED" ? "▼ OVER" : "▲ UNDER"}
                        </span>
                      </td>
                      <td style={{ padding: "5px 10px", fontFamily: "JetBrains Mono", fontSize: 11,
                        color: Math.abs(vst) >= 1.5 ? C.yellow : Math.abs(vst) >= 0.8 ? C.accent : C.text,
                        textAlign: "right", fontWeight: Math.abs(vst) >= 0.8 ? 700 : 400 }}>
                        {fmtVST(vst)}
                      </td>
                      <td style={{ padding: "5px 10px", fontFamily: "JetBrains Mono", fontSize: 10,
                        color: C.muted, textAlign: "right" }}>{fmtVST(r.sigma_instant)}</td>
                      <td style={{ padding: "5px 10px", fontFamily: "JetBrains Mono", fontSize: 10,
                        color: C.muted, textAlign: "right" }}>
                        {r.confirmed ? fmtVST(r.sigma_smooth) : <span style={{color:C.muted}}>—</span>}
                      </td>
                      <td style={{ padding: "5px 10px", fontFamily: "JetBrains Mono", fontSize: 10,
                        color: C.text, textAlign: "right" }}>{(r.gex_proximity * 100).toFixed(0)}%</td>
                      <td style={{ padding: "5px 10px", fontFamily: "JetBrains Mono", fontSize: 9,
                        color: r.amplifier >= 1.5 ? C.red : r.amplifier <= 0.7 ? C.muted : C.text,
                        textAlign: "right" }}>×{r.amplifier}</td>
                      <td style={{ padding: "5px 10px" }}>
                        <span style={{ fontFamily: "JetBrains Mono", fontSize: 9,
                          color: conf.color, border: `1px solid ${conf.color}40`,
                          background: `${conf.color}10`, padding: "1px 6px", borderRadius: 3 }}>
                          {conf.label}
                        </span>
                      </td>
                      <td style={{ padding: "5px 10px", textAlign: "right", fontFamily: "JetBrains Mono", fontSize: 10 }}>
                        {r.vst_gradient != null ? (
                          <span style={{ color: Math.abs(r.vst_gradient) > 0.05 ? C.yellow : C.muted }}>
                            {r.vst_gradient > 0 ? "+" : ""}{r.vst_gradient.toFixed(3)}
                          </span>
                        ) : <span style={{ color: C.muted }}>—</span>}
                      </td>
                      <td style={{ padding: "5px 10px", textAlign: "right", fontFamily: "JetBrains Mono", fontSize: 10 }}>
                        {r.vst_entropy != null ? (
                          <span style={{ color: r.vst_entropy > 0.7 ? C.muted : r.vst_entropy < 0.3 ? C.green : C.text }}>
                            {r.vst_entropy.toFixed(2)}
                          </span>
                        ) : <span style={{ color: C.muted }}>—</span>}
                      </td>
                      <td style={{ padding: "5px 6px", textAlign: "center" }}>
                        <button
                          onClick={e => { e.stopPropagation(); setSurf3DTicker(r.ticker); }}
                          title="Apri superficie 3D VST"
                          style={{ background: `${C.accent2}18`, border: `1px solid ${C.accent2}40`,
                            color: C.accent2, fontFamily: "JetBrains Mono", fontSize: 9,
                            padding: "2px 8px", borderRadius: 3, cursor: "pointer", lineHeight: 1 }}>
                          🔬
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Panel>
      )}

      {/* Correlazione cross-ticker */}
      {showCorr && <VSTCorrelationPanel C={C} />}

      {/* VST 3D Surface Overlay */}
      {surf3DTicker && (
        <VST3DOverlay ticker={surf3DTicker} onClose={() => setSurf3DTicker(null)} />
      )}

      {/* VST Detail Overlay */}
      {selected && (
        <VSTOverlay
          selected={selected}
          mode={mode}
          gexData={gexData}
          gaugeRef={gaugeRef}
          payoffRef={payoffRef}
          confRef={confRef}
          gexRef={gexRef}
          onClose={() => setSelected(null)}
          onTrade={() => {}}
          C={C}
        />
      )}

    </div>
  );
}

// ─── VST CORRELATION PANEL ───────────────────────────────────────────────────
function VSTCorrelationPanel({ C }) {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [days, setDays]     = useState(30);

  useEffect(() => {
    setLoading(true);
    api.get(`/api/vst/correlation?days=${days}`)
      .then(setData).catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [days]);

  const corrColor = r => {
    const a = Math.abs(r);
    if (a >= 0.7) return "#ff4444";
    if (a >= 0.4) return C.yellow;
    return "#00e676";
  };

  const corrLabel = r => {
    const a = Math.abs(r);
    if (a >= 0.7) return "SETTORE";
    if (a >= 0.4) return "PARZIALE";
    return "IDIOSINCR.";
  };

  const corrDesc = r => {
    const a = Math.abs(r);
    if (a >= 0.7) return "Movimento di settore — meno alpha specifico";
    if (a >= 0.4) return "Correlazione parziale — monitorare contesto";
    return r < 0 ? "Anti-correlata — possibile rotazione settoriale" : "Segnale idiosincratico — più alpha potenziale";
  };

  return (
    <Panel>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div style={{ fontFamily: "JetBrains Mono", fontSize: 11, color: C.accent2, fontWeight: 700 }}>
          ∿ CORRELAZIONE CROSS-TICKER VST
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted }}>PERIODO:</span>
          {[7, 14, 30, 60].map(d => (
            <button key={d} onClick={() => setDays(d)} style={{
              background: days === d ? `${C.accent2}20` : "transparent",
              border: `1px solid ${days === d ? C.accent2 : C.border}`,
              color: days === d ? C.accent2 : C.muted,
              fontFamily: "JetBrains Mono", fontSize: 9,
              padding: "3px 8px", borderRadius: 3, cursor: "pointer"
            }}>{d}g</button>
          ))}
        </div>
      </div>

      {loading && (
        <div style={{ fontFamily: "JetBrains Mono", fontSize: 10, color: C.muted, padding: 16, textAlign: "center" }}>
          Calcolo correlazioni...
        </div>
      )}

      {!loading && data && data.n_days < 3 && (
        <div style={{ fontFamily: "JetBrains Mono", fontSize: 10, color: C.muted, padding: 16, textAlign: "center" }}>
          Dati insufficienti ({data.n_days} giorni). Il cron VST accumula dati ad ogni scan — riprova domani.
        </div>
      )}

      {!loading && data && data.n_days >= 3 && data.pairs.length === 0 && (
        <div style={{ fontFamily: "JetBrains Mono", fontSize: 10, color: C.muted, padding: 16, textAlign: "center" }}>
          Nessuna coppia calcolabile per il periodo selezionato.
        </div>
      )}

      {!loading && data && data.pairs.length > 0 && (
        <>
          <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted, marginBottom: 8 }}>
            {data.pairs.length} coppie · {data.n_days} giorni · {data.tickers.length} ticker
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                {["Coppia", "Pearson r", "Tipo", "Interpretazione"].map(h => (
                  <th key={h} style={{
                    padding: "4px 10px", fontFamily: "JetBrains Mono", fontSize: 9,
                    color: C.muted, textAlign: h === "Pearson r" ? "right" : "left"
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.pairs.map((p, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${C.border}20` }}>
                  <td style={{ padding: "5px 10px", fontFamily: "JetBrains Mono", fontSize: 10, color: C.text }}>
                    <span style={{ color: C.accent }}>{p.t1}</span>
                    <span style={{ color: C.muted }}> / </span>
                    <span style={{ color: C.accent }}>{p.t2}</span>
                  </td>
                  <td style={{ padding: "5px 10px", fontFamily: "JetBrains Mono", fontSize: 11,
                    color: corrColor(p.r), textAlign: "right", fontWeight: 700 }}>
                    {p.r > 0 ? "+" : ""}{p.r.toFixed(3)}
                  </td>
                  <td style={{ padding: "5px 10px", fontFamily: "JetBrains Mono", fontSize: 9 }}>
                    <span style={{
                      background: `${corrColor(p.r)}18`,
                      border: `1px solid ${corrColor(p.r)}40`,
                      color: corrColor(p.r),
                      padding: "2px 6px", borderRadius: 3
                    }}>{corrLabel(p.r)}</span>
                  </td>
                  <td style={{ padding: "5px 10px", fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted }}>
                    {corrDesc(p.r)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </Panel>
  );
}


// ─── VST 3D SURFACE OVERLAY ──────────────────────────────────────────────────
function VST3DOverlay({ ticker, onClose }) {
  const canvasRef    = useRef(null);
  const containerRef = useRef(null);
  const [status, setStatus] = useState('CARICAMENTO...');
  const [paused, setPaused] = useState(false);
  const autoRotRef = useRef(true);
  const threeCtx = useRef(null);

  useEffect(() => {
    let cancelled = false;

    function loadThree() {
      return new Promise((resolve, reject) => {
        if (window.THREE) { resolve(window.THREE); return; }
        const s = document.createElement('script');
        s.src = 'https://cdn.jsdelivr.net/npm/three@0.152.2/build/three.min.js';
        s.onload = () => resolve(window.THREE);
        s.onerror = reject;
        document.head.appendChild(s);
      });
    }

    async function run() {
      try {
        const THREE = await loadThree();
        if (cancelled) return;

        const data = await api.get('/api/vst/scan?mode=instant&min_vst=0.1&require_confirmed=false');
        if (cancelled) return;

        const allSigs = data?.data || data?.signals || [];
        const signals = allSigs.filter(s => s.ticker === ticker);
        if (!signals.length) { setStatus(`Nessun segnale VST per ${ticker}`); return; }

        const canvas = canvasRef.current;
        const container = containerRef.current;
        if (!canvas || !container) return;

        // Aspetta layout risolto
        await new Promise(r => setTimeout(r, 120));
        if (cancelled) return;

        let W = container.clientWidth || window.innerWidth || 900;
        let H = container.clientHeight || window.innerHeight * 0.8 || 600;

        const renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
        renderer.setSize(W, H);
        renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
        renderer.setClearColor(0x000000, 1);

        const scene = new THREE.Scene();
        scene.fog = new THREE.FogExp2(0x000000, 0.015);

        const camera = new THREE.PerspectiveCamera(48, W/H, 0.1, 500);
        camera.position.set(0, 11, 23);
        camera.lookAt(0, 1, 0);

        scene.add(new THREE.AmbientLight(0xffffff, 0.28));
        const sun = new THREE.DirectionalLight(0xff8840, 1.1);
        sun.position.set(8,18,6); scene.add(sun);
        const fill2 = new THREE.DirectionalLight(0x3366ff, 0.65);
        fill2.position.set(-10,8,-6); scene.add(fill2);

        let grp = new THREE.Group(); scene.add(grp);
        let isDrag=false, lmx=0, lmy=0;
        let rotY=0.32, rotX=0.14, targetZ=23;
        let dynLabels=[], waveRings=[], peakPos=null, tick=0;
        let animId;

        // label container (absolute positioned over canvas)
        const labelsDiv = document.createElement('div');
        labelsDiv.style.cssText = 'position:absolute;inset:0;pointer-events:none;overflow:hidden';
        container.appendChild(labelsDiv);

        // peak / spot / flip tags
        function mkTag(css) {
          const d=document.createElement('div');
          d.style.cssText='position:absolute;display:none;font-family:JetBrains Mono,monospace;white-space:nowrap;pointer-events:none;'+css;
          labelsDiv.appendChild(d); return d;
        }
        const peakTag = mkTag('font-size:9px;color:#FF3300;background:rgba(0,0,0,.9);padding:4px 10px;border-radius:3px;border:1px solid #FF330055;letter-spacing:.08em');
        const spotTag = mkTag('font-size:8px;color:#FF6600;background:rgba(0,0,0,.8);padding:2px 7px;border-left:2px solid #FF6600;letter-spacing:.08em');
        const flipTag = mkTag('font-size:8px;color:#FFB800;background:rgba(0,0,0,.8);padding:2px 7px;border-left:2px solid #FFB800;letter-spacing:.08em');

        // helpers
        function getDTE(expiry) {
          if (!expiry) return 30;
          const e = new Date(expiry.slice(0,4),+expiry.slice(4,6)-1,+expiry.slice(6,8));
          return Math.max(1, Math.round((e-new Date())/86400000));
        }
        function vstRGB(v) {
          const t=Math.max(-2,Math.min(2,v));
          if (t>=0){const n=t/2;return[1,Math.pow(1-n,.65),Math.pow(1-n,2.8)];}
          else{const n=-t/2;return[Math.pow(1-n,1.4),Math.pow(1-n,1.1),1];}
        }
        function line3(pts,color,op=0.6){
          return new THREE.Line(
            new THREE.BufferGeometry().setFromPoints(pts.map(([x,y,z])=>new THREE.Vector3(x,y,z))),
            new THREE.LineBasicMaterial({color,transparent:true,opacity:op}));
        }
        function project(worldVec){
          const v=worldVec.clone().applyMatrix4(grp.matrixWorld).project(camera);
          return{x:(v.x+1)/2*W,y:(-v.y+1)/2*H,vis:v.z<1};
        }
        function placeEl(el,worldVec,offX=0,offY=0,tfm='translate(-50%,-50%)'){
          if(!el)return;
          const s=project(worldVec);
          if(s.vis&&s.x>-50&&s.x<W+50&&s.y>0&&s.y<H){
            el.style.display='block';el.style.left=(s.x+offX)+'px';
            el.style.top=(s.y+offY)+'px';el.style.transform=tfm;
          }else{el.style.display='none';}
        }

        // build surface — gestisce anche dati sparsi (1 strike o 1 DTE)
        function buildSurface(sigs){
          let strikeSet=[...new Set(sigs.map(s=>parseFloat(s.strike)))].sort((a,b)=>a-b);
          let dteSet=[...new Set(sigs.map(s=>getDTE(s.expiry)))].sort((a,b)=>a-b);

          // Padding sintetico se dati insufficienti
          if(strikeSet.length<2){
            const k=strikeSet[0]||50;
            const off=k<20?1:k<60?3:k<200?5:10;
            strikeSet=[k-off,k,k+off];
          }
          if(dteSet.length<2){
            const d=dteSet[0]||30;
            dteSet=[Math.max(1,d-14),d,d+14,d+28];
          }

          const STRIKES=strikeSet.slice(0,20),DTES=dteSet.slice(0,8);
          const nZ=STRIKES.length,nX=DTES.length;
          const VST_MAP=Array.from({length:nZ},()=>Array(nX).fill(0));

          sigs.forEach(s=>{
            const sk=parseFloat(s.strike);
            const sd=getDTE(s.expiry);
            // trova indice più vicino
            const zi=STRIKES.reduce((bi,v,i)=>Math.abs(v-sk)<Math.abs(STRIKES[bi]-sk)?i:bi,0);
            const xi=DTES.reduce((bi,v,i)=>Math.abs(v-sd)<Math.abs(DTES[bi]-sd)?i:bi,0);
            const v=parseFloat(s.vst_instant)||0;
            if(Math.abs(v)>Math.abs(VST_MAP[zi][xi]))VST_MAP[zi][xi]=v;
          });

          // Interpolazione bilineare leggera
          for(let zi=0;zi<nZ;zi++)for(let xi=0;xi<nX;xi++){
            if(VST_MAP[zi][xi]===0){let sum=0,cnt=0;
              [[-1,0],[1,0],[0,-1],[0,1]].forEach(([dz,dx])=>{
                const nzi=zi+dz,nxi=xi+dx;
                if(nzi>=0&&nzi<nZ&&nxi>=0&&nxi<nX&&VST_MAP[nzi][nxi]!==0){sum+=VST_MAP[nzi][nxi];cnt++;}
              });if(cnt>0)VST_MAP[zi][xi]=sum/cnt*0.55;}
          }
          return{STRIKES,DTES,VST_MAP,nZ,nX};
        }

        // Upsample bilineare: da griglia sparsa a griglia densa TARGET×TARGET
        function upsample(map, nZ, nX, TARGET=28){
          const out=Array.from({length:TARGET},()=>Array(TARGET).fill(0));
          for(let i=0;i<TARGET;i++){
            for(let j=0;j<TARGET;j++){
              const zi=i/(TARGET-1)*(nZ-1);
              const xi=j/(TARGET-1)*(nX-1);
              const z0=Math.floor(zi),z1=Math.min(z0+1,nZ-1);
              const x0=Math.floor(xi),x1=Math.min(x0+1,nX-1);
              const fz=zi-z0,fx=xi-x0;
              out[i][j]=
                map[z0][x0]*(1-fz)*(1-fx)+
                map[z1][x0]*fz*(1-fx)+
                map[z0][x1]*(1-fz)*fx+
                map[z1][x1]*fz*fx;
            }
          }
          return out;
        }

        // render
        function renderSurface(sigs){
          scene.remove(grp); grp=new THREE.Group(); scene.add(grp);
          dynLabels.forEach(el=>el.remove()); dynLabels=[]; waveRings=[]; peakPos=null;

          const built=buildSurface(sigs);
          if(!built){ setStatus(`Errore costruzione superficie per ${ticker}`); return; }
          const{STRIKES,DTES,VST_MAP:VST_RAW,nZ:nZraw,nX:nXraw}=built;
          const TARGET=28;
          const nZ=TARGET,nX=TARGET;
          const VST_MAP=upsample(VST_RAW,nZraw,nXraw,TARGET);
          const SX=14,SZ=10,YS=2.2,FLOOR=-4.2;
          const Z_FRONT=SZ/2+0.6,X_LEFT=-SX/2-0.6;
          const vX=xi=>(xi/(nX-1)-0.5)*SX;
          const vZ=zi=>(zi/(nZ-1)-0.5)*SZ;
          const vY=(zi,xi)=>VST_MAP[zi][xi]*YS;

          const geom=new THREE.PlaneGeometry(SX,SZ,nX-1,nZ-1);
          geom.rotateX(-Math.PI/2);
          const pos=geom.attributes.position; const cols=[];
          let maxVST=0,maxZI=0,maxXI=0;
          for(let zi=0;zi<nZ;zi++)for(let xi=0;xi<nX;xi++){
            pos.setY(zi*nX+xi,vY(zi,xi));
            const[r,g,b]=vstRGB(VST_MAP[zi][xi]);cols.push(r,g,b);
            if(Math.abs(VST_MAP[zi][xi])>Math.abs(maxVST)){maxVST=VST_MAP[zi][xi];maxZI=zi;maxXI=xi;}
          }
          geom.setAttribute('color',new THREE.Float32BufferAttribute(cols,3));
          geom.computeVertexNormals();
          grp.add(new THREE.Mesh(geom,new THREE.MeshPhongMaterial({vertexColors:true,side:THREE.DoubleSide,shininess:90,transparent:true,opacity:0.92})));
          grp.add(new THREE.Mesh(geom.clone(),new THREE.MeshBasicMaterial({color:0x1a1a1a,wireframe:true,transparent:true,opacity:0.2})));

          const zp=new THREE.Mesh(new THREE.PlaneGeometry(SX+2,SZ+2),new THREE.MeshBasicMaterial({color:0xffffff,side:THREE.DoubleSide,transparent:true,opacity:0.03}));
          zp.rotateX(-Math.PI/2); grp.add(zp);
          grp.add(new THREE.GridHelper(SX+2,16,0x111111,0x0c0c0c));

          grp.add(line3([[-SX/2,FLOOR,Z_FRONT],[SX/2,FLOOR,Z_FRONT]],0x555555,0.9));
          grp.add(line3([[X_LEFT,FLOOR,-SZ/2],[X_LEFT,FLOOR,SZ/2]],0x555555,0.9));
          grp.add(line3([[X_LEFT,-2*YS,Z_FRONT],[X_LEFT,0,Z_FRONT]],0x2255ff,0.8));
          grp.add(line3([[X_LEFT,0,Z_FRONT],[X_LEFT,2*YS,Z_FRONT]],0xff4400,0.8));
          grp.add(line3([[X_LEFT-.3,0,Z_FRONT],[X_LEFT+.3,0,Z_FRONT]],0x888888,0.8));
          grp.add(line3([[X_LEFT-.2,2*YS-.45,Z_FRONT],[X_LEFT,2*YS+.1,Z_FRONT],[X_LEFT+.2,2*YS-.45,Z_FRONT]],0xff4400,0.7));
          DTES.forEach((_,xi)=>grp.add(line3([[vX(xi),FLOOR,Z_FRONT],[vX(xi),FLOOR+.25,Z_FRONT]],0x444444,0.7)));
          STRIKES.forEach((_,zi)=>grp.add(line3([[X_LEFT,FLOOR,vZ(zi)],[X_LEFT+.25,FLOOR,vZ(zi)]],0x444444,0.7)));
          [-2,-1,0,1,2].forEach(v=>{
            const y=v*YS;
            grp.add(line3([[-SX/2,y,-SZ/2],[SX/2,y,-SZ/2],[SX/2,y,SZ/2],[-SX/2,y,SZ/2],[-SX/2,y,-SZ/2]],v===0?0x333333:0x1a1a1a,v===0?.5:.2));
            grp.add(line3([[X_LEFT,y,Z_FRONT],[X_LEFT+.3,y,Z_FRONT]],0x444444,0.7));
          });

          const spot=parseFloat(sigs[0].spot)||0;
          const flipLevel=sigs[0].flip_level?parseFloat(sigs[0].flip_level):null;
          const zFrac=s=>(s-STRIKES[0])/(STRIKES[STRIKES.length-1]-STRIKES[0]);
          const spotZ3D=(zFrac(spot)-0.5)*SZ;
          const flipZ3D=flipLevel?(zFrac(flipLevel)-0.5)*SZ:null;
          [[spotZ3D,0xFF6600],...(flipZ3D!=null?[[flipZ3D,0xFFB800]]:[])].forEach(([z2,col])=>{
            grp.add(line3([[-SX/2,FLOOR,z2],[SX/2,FLOOR,z2],[SX/2,7,z2],[-SX/2,7,z2],[-SX/2,FLOOR,z2]],col,0.45));
            const fp=new THREE.Mesh(new THREE.PlaneGeometry(SX,12),new THREE.MeshBasicMaterial({color:col,side:THREE.DoubleSide,transparent:true,opacity:0.04}));
            fp.rotation.y=Math.PI/2;fp.position.set(0,2,z2);grp.add(fp);
          });

          const pX3=vX(maxXI),pZ3=vZ(maxZI),pY3=vY(maxZI,maxXI);
          peakPos=new THREE.Vector3(pX3,pY3,pZ3);
          const sphere=new THREE.Mesh(new THREE.SphereGeometry(.22,16,16),new THREE.MeshBasicMaterial({color:0xFF2200}));
          sphere.position.set(pX3,pY3,pZ3); grp.add(sphere);
          const ring=new THREE.Mesh(new THREE.RingGeometry(.3,.5,32),new THREE.MeshBasicMaterial({color:0xFF4400,side:THREE.DoubleSide,transparent:true,opacity:.7}));
          ring.position.set(pX3,pY3,pZ3);ring.rotation.x=-Math.PI/2;ring._isPeakRing=true;grp.add(ring);
          grp.add(line3([[pX3,pY3,pZ3],[pX3,0,pZ3]],0xFF4400,0.4));
          waveRings=Array.from({length:3},(_,w)=>{
            const wr=new THREE.Mesh(new THREE.RingGeometry(.1,.2,32),new THREE.MeshBasicMaterial({color:0xFF6600,side:THREE.DoubleSide,transparent:true,opacity:0}));
            wr.position.set(pX3,pY3,pZ3);wr.rotation.x=-Math.PI/2;wr._ph=w/3;grp.add(wr);return wr;
          });

          function mkLabel(text,world,extraStyle=''){
            const el=document.createElement('div');
            el.style.cssText=`position:absolute;font-family:JetBrains Mono,monospace;font-size:10px;color:#aaa;white-space:nowrap;pointer-events:none;transform:translate(-50%,-50%);text-shadow:0 0 6px #000,0 0 12px #000;font-weight:600;${extraStyle}`;
            el.textContent=text; labelsDiv.appendChild(el); el._world=world; dynLabels.push(el); return el;
          }
          DTES.forEach((d,xi)=>mkLabel(d+'d',new THREE.Vector3(vX(xi),FLOOR-.1,Z_FRONT)));
          STRIKES.forEach((k,zi)=>{ const el=mkLabel('$'+k,new THREE.Vector3(X_LEFT-.2,FLOOR-.1,vZ(zi))); el._isStr=true; });
          [-2,-1,0,1,2].forEach(v=>{ const el=mkLabel((v>0?'+':'')+v,new THREE.Vector3(X_LEFT-.1,v*YS,Z_FRONT),`color:${v>0?'#ff6600':v<0?'#4488ff':'#888'};font-weight:700;font-size:11px`); });

          const topSig=sigs.reduce((a,b)=>Math.abs(b.vst_instant)>Math.abs(a.vst_instant)?b:a,sigs[0]);
          peakTag.textContent=`⚡ $${topSig.strike} ${topSig.right_type==='C'?'CALL':'PUT'} · VST ${maxVST>=0?'+':''}${maxVST.toFixed(2)}`;
          spotTag.textContent=`● SPOT $${spot.toFixed(2)}`;
          if(flipLevel){flipTag.textContent=`| FLIP $${flipLevel}`;flipTag.style.display='none';}

          grp._spotZ3D=spotZ3D; grp._flipZ3D=flipZ3D; grp._SX=SX;
        }

        renderSurface(signals);

        // mouse
        const onDown=e=>{isDrag=true;lmx=e.clientX;lmy=e.clientY;};
        const onUp=()=>isDrag=false;
        const onMove=e=>{if(!isDrag)return;rotY+=(e.clientX-lmx)*.009;rotX+=(e.clientY-lmy)*.005;rotX=Math.max(-.55,Math.min(.65,rotX));lmx=e.clientX;lmy=e.clientY;};
        const onWheel=e=>{targetZ=Math.max(8,Math.min(42,targetZ+e.deltaY*.02));};
        canvas.addEventListener('mousedown',onDown);
        window.addEventListener('mouseup',onUp);
        window.addEventListener('mousemove',onMove);
        canvas.addEventListener('wheel',onWheel,{passive:true});

        // resize
        const ro=new ResizeObserver(()=>{
          W=container.clientWidth;H=container.clientHeight;
          renderer.setSize(W,H);camera.aspect=W/H;camera.updateProjectionMatrix();
        });
        ro.observe(container);

        setStatus(null);

        // animate
        function animate(){
          animId=requestAnimationFrame(animate);
          tick+=0.016;
          if(!isDrag&&autoRotRef.current)rotY+=0.0022;
          grp.rotation.y=rotY;grp.rotation.x=rotX;
          const cur=camera.position.length();
          camera.position.multiplyScalar((cur*.93+targetZ*.07)/cur);
          grp.updateMatrixWorld(true);
          grp.children.forEach(c=>{
            if(c._isPeakRing){c.scale.setScalar(1+.65*Math.sin(tick*3.2));c.material.opacity=.2+.5*Math.sin(tick*3.2);}
          });
          waveRings.forEach(wr=>{const ph=(tick*.65+wr._ph)%1;wr.scale.setScalar(.3+ph*5.5);wr.material.opacity=Math.max(0,(1-ph)*.4);});
          dynLabels.forEach(el=>{placeEl(el,el._world,0,0);if(el._isStr)el.style.transform='translate(-100%,-50%)';});
          if(peakPos)placeEl(peakTag,new THREE.Vector3(peakPos.x,peakPos.y+.5,peakPos.z),0,-34,'translate(-50%,-100%)');
          const SX2=grp._SX||14;
          if(grp._spotZ3D!=null)placeEl(spotTag,new THREE.Vector3(SX2/2*.5,2.5,grp._spotZ3D),6,-10,'none');
          if(grp._flipZ3D!=null)placeEl(flipTag,new THREE.Vector3(SX2/2*.5,2.5,grp._flipZ3D),6,-10,'none');
          renderer.render(scene,camera);
        }
        animate();

        threeCtx.current = () => {
          cancelled=true;
          cancelAnimationFrame(animId);
          ro.disconnect();
          renderer.dispose();
          dynLabels.forEach(el=>el.remove());
          labelsDiv.remove();
          canvas.removeEventListener('mousedown',onDown);
          window.removeEventListener('mouseup',onUp);
          window.removeEventListener('mousemove',onMove);
          canvas.removeEventListener('wheel',onWheel);
        };

      } catch(e) {
        if (!cancelled) setStatus('Errore caricamento dati — ' + e.message);
      }
    }

    run();
    return () => { if (threeCtx.current) threeCtx.current(); };
  }, [ticker]);

  return (
    <div style={{ position:'fixed', inset:0, zIndex:10000, background:'#000000bb',
      display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ width:'92%', height:'90%', background:'#050505',
        border:`1px solid ${C.border}`, borderRadius:6,
        display:'flex', flexDirection:'column', overflow:'hidden' }}>

        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
          padding:'9px 18px', borderBottom:`1px solid ${C.border}`, flexShrink:0, background:C.bg }}>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <span style={{ fontFamily:'JetBrains Mono', fontSize:11, color:C.accent,
              fontWeight:700, letterSpacing:'0.1em' }}>🔬 VST 3D STRESS MAP</span>
            <span style={{ fontFamily:'JetBrains Mono', fontSize:16, color:C.accent,
              fontWeight:700 }}>{ticker}</span>
          </div>
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            <span style={{ fontFamily:'JetBrains Mono', fontSize:8, color:C.muted, letterSpacing:'0.1em' }}>
              DRAG→RUOTA · SCROLL→ZOOM
            </span>
            <button onClick={() => {
              const next = !autoRotRef.current;
              autoRotRef.current = next;
              setPaused(!next);
            }} style={{ background: paused ? `${C.accent}20` : 'transparent',
              border:`1px solid ${paused ? C.accent : C.border}`,
              color: paused ? C.accent : C.muted,
              fontFamily:'JetBrains Mono', fontSize:10,
              padding:'3px 14px', borderRadius:3, cursor:'pointer' }}>
              {paused ? '▶ RIPRENDI' : '⏸ PAUSA'}
            </button>
            <button onClick={onClose} style={{ background:'transparent', border:`1px solid ${C.border}`,
              color:C.muted, fontFamily:'JetBrains Mono', fontSize:11,
              padding:'3px 14px', borderRadius:3, cursor:'pointer' }}>✕ CHIUDI</button>
          </div>
        </div>

        {/* Canvas area */}
        <div ref={containerRef} style={{ flex:1, position:'relative', overflow:'hidden', background:'#000' }}>
          {status && (
            <div style={{ position:'absolute', top:'50%', left:'50%', transform:'translate(-50%,-50%)',
              fontFamily:'JetBrains Mono', fontSize:12, color:'#FF6600', letterSpacing:'0.15em',
              background:'rgba(0,0,0,0.8)', padding:'12px 24px', borderRadius:4,
              border:'1px solid #FF660040', textAlign:'center' }}>
              {status}
            </div>
          )}
          <canvas ref={canvasRef}
            style={{ display:status?'none':'block', width:'100%', height:'100%' }} />

          {/* Legenda assi */}
          {!status && (
            <div style={{ position:'absolute', bottom:14, left:'50%', transform:'translateX(-50%)',
              fontFamily:'JetBrains Mono', fontSize:9, color:'#444', letterSpacing:'0.15em',
              textAlign:'center', pointerEvents:'none',
              textShadow:'0 0 8px #000' }}>
              <span style={{color:'#777'}}>X</span> · DTE &nbsp;·&nbsp;
              <span style={{color:'#777'}}>Z</span> · STRIKE &nbsp;·&nbsp;
              <span style={{color:C.accent,fontWeight:700}}>Y</span> · VOL SURFACE TENSION
            </div>
          )}

          {/* Colorbar */}
          {!status && (
            <div style={{ position:'absolute', right:18, top:'50%', transform:'translateY(-50%)',
              display:'flex', flexDirection:'column', alignItems:'center', gap:3,
              fontFamily:'JetBrains Mono', fontSize:8, pointerEvents:'none' }}>
              <span style={{color:'#ff2200'}}>+2</span>
              <div style={{ width:12, height:140, borderRadius:3, border:'1px solid #1e1e1e',
                background:'linear-gradient(to bottom,#ff0000,#ff5500,#ffaa00,#fff,#88aaff,#0033ff)' }} />
              <span style={{color:'#0033ff'}}>−2</span>
              <span style={{color:'#333', marginTop:4}}>VST</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function vstPayoffCalc(selected, strat, price) {
  const K = selected.strike || 100;
  const off = K < 15 ? 1 : K < 50 ? 2 : K < 150 ? 5 : 10;
  const n = strat?.name || "";
  if (n.includes("Straddle"))
    return Math.max(0, price-K) + Math.max(0, K-price) - K*0.05;
  if (n.includes("Bull Put") || n.includes("Short Put")) {
    const c = off*0.4;
    return Math.min(c, c-(Math.max(0,K-price)-Math.max(0,(K-off)-price)));
  }
  if (n.includes("Bear Call")) {
    const c = off*0.4;
    return Math.min(c, c-(Math.max(0,price-K)-Math.max(0,price-(K+off))));
  }
  if (n.includes("Iron")) {
    const c=off*0.6, l=Math.max(0,K-1-price)-Math.max(0,K-1-off-price), r=Math.max(0,price-(K+1))-Math.max(0,price-(K+1+off));
    return c-l-r;
  }
  if (n.includes("Call Debit") || n.includes("Long Call"))
    return Math.max(0,price-K)-Math.max(0,price-(K+off))-off*0.24;
  if (n.includes("Put Debit") || n.includes("Long Put"))
    return Math.max(0,K-price)-Math.max(0,(K-off)-price)-off*0.24;
  return Math.max(0,price-K)-K*0.03;
}

function SizingModal({ selected, strat, onConfirm, onClose, C }) {
  const [sizing, setSizing]   = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/api/paper/sizing?strategy=${encodeURIComponent(strat?.name || "Long Call")}`)
      .then(d => { setSizing(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [strat?.name]);

  const isLong  = sizing ? sizing.is_long : true;
  const tp_mult = sizing?.tp_mult ?? 2.0;
  const sl_pct  = sizing?.sl_pct  ?? 0.0;

  const Kv = ({ label, val, color, bold }) => (
    <div style={{ display:"flex", justifyContent:"space-between", fontFamily:"JetBrains Mono",
      fontSize:13, marginBottom:10 }}>
      <span style={{ color:C.muted }}>{label}</span>
      <span style={{ color: color || C.text, fontWeight: bold ? 700 : 400 }}>{val}</span>
    </div>
  );

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.88)", zIndex:10000,
      display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ width:960, background:C.panel, border:`1px solid ${C.border}`,
        borderRadius:8, padding:48, display:"flex", flexDirection:"column", gap:32 }}>

        <div style={{ fontFamily:"JetBrains Mono", fontSize:12, color:C.muted, letterSpacing:"0.12em" }}>
          📋 CONFERMA POSIZIONE
        </div>

        <div>
          <div style={{ fontFamily:"JetBrains Mono", fontSize:28, fontWeight:700, color:C.accent }}>
            {selected.ticker}
          </div>
          <div style={{ fontFamily:"JetBrains Mono", fontSize:13, color:C.muted, marginTop:6 }}>
            ${selected.strike} {selected.right_type==="C"?"CALL":"PUT"} · scad. {selected.expiry} · {strat?.name}
          </div>
        </div>

        {loading || !sizing ? (
          <div style={{ fontFamily:"JetBrains Mono", fontSize:10, color:C.muted }}>
            {loading ? "Calcolo sizing..." : "Errore caricamento — procedi con parametri default"}
          </div>
        ) : (
          <div style={{ display:"flex", gap:12 }}>
            <div style={{ flex:1, background:C.dim, borderRadius:6, padding:20 }}>
              <div style={{ fontFamily:"JetBrains Mono", fontSize:11, color:C.muted, letterSpacing:"0.1em", marginBottom:16 }}>SIZING KELLY</div>
              <Kv label="WR rolling (20t)" val={`${(sizing.wr*100).toFixed(1)}%`} />
              <Kv label="Fonte" val={sizing.n_trades >= 5 ? `${sizing.n_trades} trade` : "stima backtest"} />
              <Kv label="Kelly raw" val={`${(sizing.kelly_raw*100).toFixed(1)}%`} />
              <Kv label="Fraction" val="×0.25" />
              <div style={{ borderTop:`1px solid ${C.border}`, paddingTop:8, marginTop:8 }}>
                <Kv label="→ Alloca" val={`${(sizing.kelly_pct*100).toFixed(1)}% portafoglio`} color={C.accent} bold />
              </div>
            </div>
            <div style={{ flex:1, background:C.dim, borderRadius:6, padding:20 }}>
              <div style={{ fontFamily:"JetBrains Mono", fontSize:11, color:C.muted, letterSpacing:"0.1em", marginBottom:16 }}>PARAMETRI</div>
              <Kv label="Direzione" val={isLong ? "LONG" : "SHORT"} color={isLong ? C.green : C.red} />
              <Kv label="TP target" val="+100% (2×)" color={C.green} />
              <Kv label="SL" val={isLong ? "nessuno" : "-50% credito"} color={isLong ? C.muted : C.yellow} />
              <Kv label="Regime" val={selected.regime || "—"} />
              <div style={{ borderTop:`1px solid ${C.border}`, paddingTop:8, marginTop:8 }}>
                <div style={{ fontFamily:"JetBrains Mono", fontSize:8, color:C.muted, lineHeight:"1.6" }}>
                  {isLong ? "Max perdita = premio pagato" : "Max profitto = credito ricevuto"}
                </div>
              </div>
            </div>
          </div>
        )}

        <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
          <button onClick={onClose} style={{ background:"transparent", border:`1px solid ${C.border}`,
            color:C.muted, fontFamily:"JetBrains Mono", fontSize:14,
            padding:"12px 32px", borderRadius:4, cursor:"pointer" }}>
            ANNULLA
          </button>
          <button disabled={loading} onClick={() => onConfirm(sl_pct, tp_mult, sizing?.kelly_pct ?? 0)}
            style={{ background:`${C.accent}20`, color:C.accent, border:`1px solid ${C.accent}50`,
              fontFamily:"JetBrains Mono", fontSize:14, padding:"12px 40px", borderRadius:4,
              cursor: loading ? "default" : "pointer", fontWeight:700, opacity: loading ? 0.5 : 1 }}>
            ✅ CONFERMA POSIZIONE
          </button>
        </div>
      </div>
    </div>
  );
}

function VSTOverlay({ selected, mode, gexData, gaugeRef, payoffRef, confRef, gexRef, onClose, onTrade, C }) {
  const strat      = recommendStrategy(selected);
  const vst        = mode === "instant" ? selected.vst_instant : selected.vst_smooth;
  const payHoverRef = useRef(null);
  const gexHoverRef = useRef(null);
  const [payoffTip,      setPayoffTip]      = useState(null);
  const [gexTip,         setGexTip]         = useState(null);
  const [tradeSent,      setTradeSent]      = useState(false);
  const [tradeError,     setTradeError]     = useState(null);
  const [showSizingModal, setShowSizingModal] = useState(false);

  const handleTrade = async (sl_pct = 0, tp_mult = 2.0, kelly_pct = 0) => {
    if (tradeSent) return;
    const spot = selected.spot || 0;
    const iv   = selected.iv_market || 0.25;
    const legData = buildStrategyLegs(strat.name, selected, spot, iv * 100);
    try {
      await api.post_json("/api/paper/trade", {
        ticker:        selected.ticker,
        strategy:      strat.name,
        legs:          legData?.legs || [],
        spot_at_entry: spot,
        iv_at_entry:   iv,
        expiry:        selected.expiry,
        right_type:    selected.right_type,
        vst_instant:   selected.vst_instant,
        vst_smooth:    selected.vst_smooth,
        regime:        selected.regime,
        notes:         `VST ${selected.direction} K=${selected.strike}${selected.right_type} spot=${spot}`,
        sl_pct,
        tp_mult,
        kelly_pct,
      });
      setTradeSent(true);
      setShowSizingModal(false);
      setTradeError(null);
      if (onTrade) onTrade();
    } catch(e) {
      setTradeError("Errore registrazione posizione");
      console.error("Paper trade error:", e);
    }
  };

  const handlePayoffMove = e => {
    const base = payoffRef.current; const over = payHoverRef.current;
    if (!base || !over) return;
    const rect = over.getBoundingClientRect();
    const x = (e.clientX - rect.left) * (over.width / rect.width);
    const K = selected.strike||100, pMin=K*0.82, pMax=K*1.18;
    const price = pMin + (x/over.width)*(pMax-pMin);
    const pnl   = vstPayoffCalc(selected, strat, price);
    const ctx = over.getContext("2d");
    ctx.clearRect(0,0,over.width,over.height);
    ctx.beginPath(); ctx.moveTo(x,0); ctx.lineTo(x,over.height);
    ctx.strokeStyle="rgba(255,255,255,0.25)"; ctx.lineWidth=1; ctx.setLineDash([3,3]); ctx.stroke(); ctx.setLineDash([]);
    ctx.beginPath(); ctx.arc(x, over.height/2, 3, 0, Math.PI*2);
    ctx.fillStyle = pnl>=0 ? C.green : C.red; ctx.fill();
    setPayoffTip({ price: price.toFixed(2), pnl: (pnl*100).toFixed(0), positive: pnl>=0, x: Math.min(x/over.width*100, 75) });
  };
  const handlePayoffLeave = () => {
    if (payHoverRef.current) payHoverRef.current.getContext("2d").clearRect(0,0,9999,9999);
    setPayoffTip(null);
  };

  const handleGexMove = e => {
    if (!gexData?.strikes?.length || !gexHoverRef.current) return;
    const over = gexHoverRef.current;
    const rect = over.getBoundingClientRect();
    const x = (e.clientX - rect.left) * (over.width / rect.width);
    const data = gexData.strikes;
    const idx  = Math.round(x / over.width * (data.length-1));
    const d    = data[Math.max(0,Math.min(data.length-1,idx))];
    const ctx  = over.getContext("2d");
    ctx.clearRect(0,0,over.width,over.height);
    ctx.beginPath(); ctx.moveTo(x,0); ctx.lineTo(x,over.height);
    ctx.strokeStyle="rgba(255,255,255,0.2)"; ctx.lineWidth=1; ctx.setLineDash([2,4]); ctx.stroke(); ctx.setLineDash([]);
    if (d) setGexTip({ strike: d.strike, gex: (d.gex||0).toFixed(2), positive: (d.gex||0)>=0, x: Math.min(x/over.width*100, 72) });
  };
  const handleGexLeave = () => {
    if (gexHoverRef.current) gexHoverRef.current.getContext("2d").clearRect(0,0,9999,9999);
    setGexTip(null);
  };

  const over     = selected.direction === "OVERPRICED";
  const vstColor = Math.abs(vst) >= 1.5 ? C.yellow : Math.abs(vst) >= 0.8 ? C.accent : C.text;
  const compBars = [
    { label:"σ_momentum",   pct:Math.min(100,Math.abs(selected.sigma_instant||0)/3*100), val:(selected.sigma_instant>=0?"+":"")+selected.sigma_instant?.toFixed(2), color:C.accent },
    { label:"GEX_proximity",pct:(selected.gex_proximity||0)*100, val:((selected.gex_proximity||0)*100).toFixed(0)+"%", color:C.green },
    { label:"regime_amp",   pct:((selected.amplifier||1)/1.8)*100, val:"×"+(selected.amplifier||1).toFixed(1), color:"#4488ff" },
  ];
  return (
    <>
    <div onClick={e => { if(e.target===e.currentTarget) onClose(); }}
      style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.88)", zIndex:9999,
        display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ width:"min(960px,95vw)", maxHeight:"92vh", overflowY:"auto",
        background:"#0d0d0d", border:"1px solid #2a2a2a", borderRadius:8, padding:20,
        display:"flex", flexDirection:"column", gap:12 }}>

        {/* Header bar */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <div style={{ fontFamily:"JetBrains Mono", fontSize:9, color:"#555", letterSpacing:"0.12em" }}>VST — DETTAGLIO SEGNALE</div>
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={onClose} style={{ background:"transparent", border:"1px solid #333", color:"#777",
              fontFamily:"JetBrains Mono", fontSize:11, padding:"2px 10px", borderRadius:3, cursor:"pointer" }}>✕ CHIUDI</button>
          </div>
        </div>

        {/* Signal header */}
        <Panel style={{ padding:"12px 16px" }}>
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            <div>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <div style={{ fontFamily:"JetBrains Mono", fontSize:22, fontWeight:700, color:C.accent }}>{selected.ticker}</div>
                <button onClick={() => window.dispatchEvent(new CustomEvent("quantos:navigate", { detail: { page:"CHART", ticker:selected.ticker } }))}
                  style={{ background:"transparent", border:`1px solid ${C.border}`, borderRadius:3, color:C.muted, fontFamily:"JetBrains Mono", fontSize:9, padding:"3px 8px", cursor:"pointer", letterSpacing:"0.08em" }}>
                  📈 CHART
                </button>
              </div>
              <div style={{ fontFamily:"JetBrains Mono", fontSize:11, color:C.muted, marginTop:2 }}>
                ${selected.strike} &nbsp;
                <span style={{ color:selected.right_type==="C"?C.green:C.red, fontWeight:700 }}>
                  {selected.right_type==="C"?"CALL":"PUT"}
                </span>
                &nbsp;· scad. {selected.expiry} &nbsp;· spot {selected.spot>0?`$${selected.spot}`:"—"}
              </div>
              <div style={{ marginTop:8, display:"flex", gap:6 }}>
                <span style={{ fontFamily:"JetBrains Mono", fontSize:9, padding:"3px 8px", borderRadius:3,
                  color:over?C.red:C.green, border:`1px solid ${over?C.red:C.green}40`, background:`${over?C.red:C.green}10` }}>
                  {over?"▼ OVERPRICED":"▲ UNDERPRICED"}
                </span>
                {Math.abs(selected.vst_instant)>=1.5 && <span style={{ fontFamily:"JetBrains Mono", fontSize:9, padding:"3px 8px", borderRadius:3, color:C.yellow, border:`1px solid ${C.yellow}40`, background:`${C.yellow}08` }}>🔥 SETUP ESPLOSIVO</span>}
                {Math.abs(selected.vst_instant)>=0.8 && Math.abs(selected.vst_instant)<1.5 && <span style={{ fontFamily:"JetBrains Mono", fontSize:9, padding:"3px 8px", borderRadius:3, color:C.accent, border:`1px solid ${C.accent}40`, background:`${C.accent}08` }}>⚡ SETUP INTERESSANTE</span>}
              </div>
            </div>
            <div style={{ marginLeft:"auto", textAlign:"right", display:"flex", flexDirection:"column", alignItems:"flex-end", gap:8 }}>
              <div>
                <div style={{ fontFamily:"JetBrains Mono", fontSize:9, color:C.muted }}>VST {mode.toUpperCase()}</div>
                <div style={{ fontFamily:"JetBrains Mono", fontSize:34, fontWeight:700, color:vstColor, lineHeight:1 }}>
                  {vst>=0?"+":""}{(vst||0).toFixed(3)}
                </div>
                <div style={{ fontFamily:"JetBrains Mono", fontSize:9, color:C.muted, marginTop:4 }}>
                  {selected.n_snapshots||1} snapshot · {selected.calculated_at?.slice(0,16)||""}
                </div>
              </div>
              <button onClick={() => { if (!tradeSent) setShowSizingModal(true); }} disabled={tradeSent}
                style={{ fontFamily:"JetBrains Mono", fontSize:11, padding:"8px 20px", borderRadius:4,
                  cursor: tradeSent ? "default" : "pointer", fontWeight:700, letterSpacing:"0.05em",
                  background: tradeSent ? `${C.green}20` : `${C.accent}20`,
                  color: tradeSent ? C.green : C.accent,
                  border: `1px solid ${tradeSent ? C.green : C.accent}50`,
                  transition:"all .3s" }}>
                {tradeSent ? "✅ POSIZIONE REGISTRATA" : "📋 PRENDO POSIZIONE"}
              </button>
              {tradeError && <div style={{ fontFamily:"JetBrains Mono", fontSize:9, color:C.red }}>{tradeError}</div>}
            </div>
          </div>
        </Panel>

        {/* Two columns */}
        <div style={{ display:"flex", gap:14, alignItems:"flex-start" }}>

          {/* LEFT */}
          <div style={{ flex:1, display:"flex", flexDirection:"column", gap:12 }}>
            <div style={{ display:"flex", gap:10 }}>
              <Panel style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center" }}>
                <div style={{ fontFamily:"JetBrains Mono", fontSize:8, color:C.muted, letterSpacing:"0.1em", marginBottom:4 }}>VST GAUGE</div>
                <canvas ref={gaugeRef} width={180} height={115} style={{ display:"block" }} />
              </Panel>
              <Panel style={{ flex:1.4 }}>
                <div style={{ fontFamily:"JetBrains Mono", fontSize:8, color:C.muted, letterSpacing:"0.1em", marginBottom:12 }}>COMPONENTI</div>
                {compBars.map(b => (
                  <div key={b.label} style={{ marginBottom:12 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", fontFamily:"JetBrains Mono", fontSize:9, color:C.muted, marginBottom:4 }}>
                      <span>{b.label}</span><span style={{ color:C.text, fontWeight:600 }}>{b.val}</span>
                    </div>
                    <div style={{ height:6, background:"#1a1a1a", borderRadius:3, overflow:"hidden" }}>
                      <div style={{ height:"100%", width:`${b.pct}%`, background:b.color, borderRadius:3, transition:"width .5s ease" }} />
                    </div>
                  </div>
                ))}
                <div style={{ borderTop:`1px solid ${C.border}`, paddingTop:8, display:"flex", justifyContent:"space-between", fontFamily:"JetBrains Mono", fontSize:10 }}>
                  <span style={{ color:C.muted }}>σ × GEX × regime =</span>
                  <span style={{ color:vstColor, fontWeight:700 }}>{vst>=0?"+":""}{(vst||0).toFixed(3)}</span>
                </div>
              </Panel>
            </div>

            <Panel>
              <div style={{ fontFamily:"JetBrains Mono", fontSize:8, color:C.muted, letterSpacing:"0.1em", marginBottom:8, display:"flex", justifyContent:"space-between" }}>
                <span>GEX MAP — {selected.ticker}</span>
                {selected.flip_level && <span style={{ color:C.yellow }}>FLIP ${selected.flip_level}</span>}
              </div>
              {gexData?.strikes?.length > 0 && gexTip && (
                <div style={{ fontFamily:"JetBrains Mono", fontSize:9, color: gexTip.positive ? C.green : C.red, marginBottom:4 }}>
                  Strike ${gexTip.strike} → GEX {gexTip.positive?"+":""}{gexTip.gex}M
                </div>
              )}
              {gexData?.strikes?.length > 0
                ? <div style={{ position:"relative" }}>
                    <canvas ref={gexRef} width={400} height={190} style={{ display:"block", width:"100%" }} />
                    <canvas ref={gexHoverRef} width={400} height={190}
                      onMouseMove={handleGexMove} onMouseLeave={handleGexLeave}
                      style={{ position:"absolute", inset:0, width:"100%", height:"100%", cursor:"crosshair" }} />
                  </div>
                : (
                  <div>
                    <div style={{ fontFamily:"JetBrains Mono", fontSize:8, color:C.muted, marginBottom:6 }}>SPOT vs FLIP LEVEL</div>
                    <div style={{ position:"relative", height:24, background:"#0d0d0d", borderRadius:3, overflow:"hidden" }}>
                      <div style={{ height:"100%", width:`${(selected.gex_proximity||0)*100}%`, background:"rgba(255,102,0,.12)", borderRadius:3 }} />
                      <div style={{ position:"absolute", left:`${Math.max(0,Math.min(98,(1-selected.gex_proximity)*100))}%`, width:2, top:0, bottom:0, background:C.yellow, opacity:.8 }} />
                    </div>
                    <div style={{ display:"flex", justifyContent:"space-between", fontFamily:"JetBrains Mono", fontSize:9, color:C.muted, marginTop:6 }}>
                      <span style={{ color:C.accent }}>● SPOT {selected.spot>0?`$${selected.spot}`:""}</span>
                      <span>GEX prox {((selected.gex_proximity||0)*100).toFixed(0)}%</span>
                      <span style={{ color:C.yellow }}>| FLIP {selected.flip_level?`$${selected.flip_level}`:"n/d"}</span>
                    </div>
                  </div>
                )
              }
            </Panel>
          </div>

          {/* RIGHT */}
          <div style={{ flex:1, display:"flex", flexDirection:"column", gap:12 }}>
            <Panel>
              <div style={{ display:"flex", gap:14, alignItems:"center" }}>
                <canvas ref={confRef} width={110} height={110} style={{ display:"block", flexShrink:0 }} />
                <div style={{ flex:1, display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                  {[
                    ["σ INSTANT",(selected.sigma_instant>=0?"+":"")+selected.sigma_instant?.toFixed(2),C.accent],
                    ["σ SMOOTH", selected.confirmed?(selected.sigma_smooth>=0?"+":"")+selected.sigma_smooth?.toFixed(2):"—",C.green],
                    ["REGIME",   selected.regime||"NORMAL", selected.amplifier>=1.5?C.red:"#4488ff"],
                    ["SNAPSHOT", selected.n_snapshots||1, C.text],
                  ].map(([lbl,val,col]) => (
                    <div key={lbl} style={{ background:"#0d0d0d", border:`1px solid ${C.border}`, borderRadius:3, padding:"8px 10px" }}>
                      <div style={{ fontFamily:"JetBrains Mono", fontSize:7, color:C.muted, letterSpacing:"0.08em", marginBottom:4 }}>{lbl}</div>
                      <div style={{ fontFamily:"JetBrains Mono", fontSize:14, fontWeight:700, color:col }}>{val}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ marginTop:10, fontFamily:"JetBrains Mono", fontSize:9, color:C.muted }}>
                {selected.coherent?"✅ INSTANT e SMOOTH concordano":"⚠️ INSTANT e SMOOTH divergono"}
              </div>

              {/* Gradiente VST */}
              <div style={{ marginTop:10, padding:"8px 12px", background:"#0a0a0a",
                border:`1px solid ${C.border}`, borderRadius:3 }}>
                <div style={{ fontFamily:"JetBrains Mono", fontSize:7, color:C.muted, letterSpacing:"0.1em", marginBottom:6 }}>
                  GRADIENTE VST — ΔVST/ΔStrike
                </div>
                {selected.vst_gradient != null ? (() => {
                  const g = selected.vst_gradient;
                  const abs = Math.abs(g);
                  const color = abs > 0.1 ? C.yellow : abs > 0.03 ? C.accent : C.muted;
                  const label = abs > 0.1
                    ? (g > 0 ? "⚡ Pressione concentrata sugli strike bassi — skew asimmetrico verso il basso" : "⚡ Pressione concentrata sugli strike alti — skew asimmetrico verso l'alto")
                    : abs > 0.03
                    ? "〜 Gradiente moderato — anomalia parzialmente localizzata"
                    : "— Anomalia uniforme su tutti gli strike — shift parallelo della vol";
                  return (
                    <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                      <div style={{ fontFamily:"JetBrains Mono", fontSize:18, fontWeight:700, color }}>
                        {g > 0 ? "+" : ""}{g.toFixed(3)}
                      </div>
                      <div style={{ fontFamily:"JetBrains Mono", fontSize:9, color:C.muted, lineHeight:1.5 }}>
                        {label}
                      </div>
                    </div>
                  );
                })() : (
                  <div style={{ fontFamily:"JetBrains Mono", fontSize:9, color:C.muted }}>
                    — calcolabile solo con ≥2 strike per questa scadenza
                  </div>
                )}
              </div>

              {/* Entropia VST */}
              <div style={{ marginTop:10, padding:"8px 12px", background:"#0a0a0a",
                border:`1px solid ${C.border}`, borderRadius:3 }}>
                <div style={{ fontFamily:"JetBrains Mono", fontSize:7, color:C.muted, letterSpacing:"0.1em", marginBottom:6 }}>
                  ENTROPIA VST — distribuzione del segnale
                </div>
                {selected.vst_entropy != null ? (() => {
                  const e = selected.vst_entropy;
                  const color = e < 0.3 ? C.green : e > 0.7 ? C.muted : C.yellow;
                  const label = e < 0.3
                    ? "⬛ Segnale concentrato — anomalia localizzata su pochi strike, pulita"
                    : e > 0.7
                    ? "░░ Segnale diffuso — anomalia distribuita su tutti gli strike, meno specifica"
                    : "▒▒ Segnale parzialmente localizzato — asimmetria moderata";
                  const barWidth = Math.round(e * 100);
                  return (
                    <div>
                      <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:6 }}>
                        <div style={{ fontFamily:"JetBrains Mono", fontSize:18, fontWeight:700, color }}>
                          {e.toFixed(2)}
                        </div>
                        <div style={{ fontFamily:"JetBrains Mono", fontSize:9, color:C.muted, lineHeight:1.5 }}>
                          {label}
                        </div>
                      </div>
                      {/* Barra entropia */}
                      <div style={{ height:4, background:"#1a1a1a", borderRadius:2, overflow:"hidden" }}>
                        <div style={{ width:`${barWidth}%`, height:"100%",
                          background: e < 0.3 ? C.green : e > 0.7 ? "#444" : C.yellow,
                          borderRadius:2, transition:"width 0.3s" }} />
                      </div>
                      <div style={{ display:"flex", justifyContent:"space-between",
                        fontFamily:"JetBrains Mono", fontSize:7, color:"#333", marginTop:2 }}>
                        <span>0 — concentrato</span><span>1 — diffuso</span>
                      </div>
                    </div>
                  );
                })() : (
                  <div style={{ fontFamily:"JetBrains Mono", fontSize:9, color:C.muted }}>
                    — calcolabile solo con ≥2 strike per questa scadenza
                  </div>
                )}
              </div>
            </Panel>

            <Panel>
              <div style={{ fontFamily:"JetBrains Mono", fontSize:8, color:C.muted, letterSpacing:"0.1em", marginBottom:10 }}>STRATEGIA RACCOMANDATA</div>
              <div style={{ fontFamily:"JetBrains Mono", fontSize:16, fontWeight:700, color:C.accent, marginBottom:2 }}>{strat.icon} {strat.name}</div>
              <div style={{ fontFamily:"JetBrains Mono", fontSize:9, color:C.muted, marginBottom:12 }}>
                {strat.type==="credit"?"📥 a credito — incassi premio":"📤 a debito — paghi premio"}
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:12 }}>
                {strat.legs.map((leg,i) => (
                  <div key={i} style={{ display:"flex", alignItems:"center", gap:10, fontFamily:"JetBrains Mono", fontSize:11 }}>
                    <span style={{ padding:"2px 9px", borderRadius:3, fontSize:9, fontWeight:700,
                      color:leg.action==="SELL"?C.red:C.green, border:`1px solid ${leg.action==="SELL"?C.red:C.green}40`,
                      background:`${leg.action==="SELL"?C.red:C.green}10` }}>{leg.action}</span>
                    <span style={{ color:C.text }}>{leg.desc}</span>
                  </div>
                ))}
              </div>
              <div style={{ fontFamily:"JetBrains Mono", fontSize:9, color:C.muted, lineHeight:1.7, borderTop:`1px solid ${C.border}`, paddingTop:10 }}>
                {strat.rationale}
              </div>
            </Panel>

            <Panel>
              <div style={{ fontFamily:"JetBrains Mono", fontSize:8, color:C.muted, letterSpacing:"0.1em", marginBottom:8, display:"flex", justifyContent:"space-between" }}>
                <span>PAYOFF A SCADENZA</span>
                {payoffTip && (
                  <span style={{ fontFamily:"JetBrains Mono", fontSize:9,
                    color: payoffTip.positive ? C.green : C.red }}>
                    ${payoffTip.price} → {payoffTip.positive?"+":""} ${payoffTip.pnl}
                  </span>
                )}
              </div>
              <div style={{ position:"relative" }}>
                <canvas ref={payoffRef} width={400} height={100} style={{ display:"block", width:"100%" }} />
                <canvas ref={payHoverRef} width={400} height={100}
                  onMouseMove={handlePayoffMove} onMouseLeave={handlePayoffLeave}
                  style={{ position:"absolute", inset:0, width:"100%", height:"100%", cursor:"crosshair" }} />
              </div>
            </Panel>
          </div>

        </div>
      </div>
    </div>
    {showSizingModal && (
      <SizingModal
        selected={selected}
        strat={strat}
        onConfirm={handleTrade}
        onClose={() => setShowSizingModal(false)}
        C={C}
      />
    )}
    </>
  );
}

// ─── SIGNALS PAGE ─────────────────────────────────────────────────────────────
const SIGNAL_META = {
  OVERPRICED: {
    color: "#ff4466", bg: "#ff446615",
    label: "OVERPRICED — SELL VOL",
    desc: "La IV di mercato è significativamente sopra il modello. L'opzione è cara: vendere volatilità (es. short call/put, iron condor).",
    icon: "▼",
  },
  UNDERPRICED: {
    color: "#00e5a0", bg: "#00e5a015",
    label: "UNDERPRICED — BUY VOL",
    desc: "La IV di mercato è significativamente sotto il modello. L'opzione è a sconto: comprare volatilità (es. long straddle, calendar spread).",
    icon: "▲",
  },
  "SELL VOL": {
    color: "#ff4466", bg: "#ff446615",
    label: "SELL VOL",
    desc: "IV di mercato sopra modello — vendere volatilità.",
    icon: "▼",
  },
  "BUY VOL": {
    color: "#00e5a0", bg: "#00e5a015",
    label: "BUY VOL",
    desc: "IV di mercato sotto modello — comprare volatilità.",
    icon: "▲",
  },
  LONG_A_SHORT_B: { color: "#00e5a0", bg: "#00e5a015", label: "LONG A / SHORT B", desc: "Spread positivo: long primo ticker, short secondo.", icon: "↑" },
  SHORT_A_LONG_B: { color: "#ff4466", bg: "#ff446615", label: "SHORT A / LONG B", desc: "Spread negativo: short primo ticker, long secondo.", icon: "↓" },
};

// ─── BLACK-SCHOLES UTILITIES ──────────────────────────────────────────────────
function _erf(x) {
  const a1=0.254829592, a2=-0.284496736, a3=1.421413741, a4=-1.453152027, a5=1.061405429, p=0.3275911;
  const sign = x < 0 ? -1 : 1; x = Math.abs(x);
  const t = 1/(1+p*x);
  return sign*(1-(((((a5*t+a4)*t)+a3)*t+a2)*t+a1)*t*Math.exp(-x*x));
}
function _N(x) { return (1+_erf(x/Math.sqrt(2)))/2; }
function bsPrice(S, K, T, r, sigma, type) {
  if (T <= 0.0001) return type==='C' ? Math.max(0,S-K) : Math.max(0,K-S);
  const d1 = (Math.log(S/K)+(r+0.5*sigma*sigma)*T)/(sigma*Math.sqrt(T));
  const d2 = d1 - sigma*Math.sqrt(T);
  if (type==='C') return S*_N(d1) - K*Math.exp(-r*T)*_N(d2);
  return K*Math.exp(-r*T)*_N(-d2) - S*_N(-d1);
}
function getDTE(expiry) {
  if (!expiry) return 30;
  // Supporta sia "20260501" che "2026-05-01"
  const clean = String(expiry).replace(/-/g, '');
  const e = new Date(+clean.slice(0,4), +clean.slice(4,6)-1, +clean.slice(6,8));
  return Math.max(1, Math.round((e - new Date())/(1000*60*60*24)));
}
function calcPayoffAtExpiry(legs, S) {
  return legs.reduce((sum, leg) => {
    const sign = leg.action==='BUY' ? 1 : -1;
    const intr = leg.type==='Call' ? Math.max(0,S-leg.strike) : Math.max(0,leg.strike-S);
    return sum + sign*(intr - leg.premium)*100;
  }, 0);
}
function buildStrategyLegs(stratName, signal, spot, ivMarket) {
  const K = parseFloat(signal.strike);
  const sigma = (ivMarket||30)/100;
  const T = getDTE(signal.expiry)/365;
  const r = 0.05;
  const right = (signal.right_type||'C').toUpperCase();
  const w = K<50?2.5:K<150?5:K<500?10:25;

  const mkLeg = (action, type, strike, sig) => ({
    action, type, strike: Math.round(strike*100)/100,
    premium: Math.round(bsPrice(spot, strike, T, r, sig||sigma, type==='Call'?'C':'P')*100)/100,
    qty: 1,
  });

  if (stratName==='Long Call') {
    const l = mkLeg('BUY','Call',K);
    return { legs:[l], netCost:l.premium*100, maxProfit:Infinity, maxLoss:l.premium*100, breakevenAt:[K+l.premium] };
  }
  if (stratName==='Long Put') {
    const l = mkLeg('BUY','Put',K);
    return { legs:[l], netCost:l.premium*100, maxProfit:(K-l.premium)*100, maxLoss:l.premium*100, breakevenAt:[K-l.premium] };
  }
  if (stratName==='Long Straddle') {
    const c = mkLeg('BUY','Call',K), p = mkLeg('BUY','Put',K);
    const tot = c.premium+p.premium;
    return { legs:[c,p], netCost:tot*100, maxProfit:Infinity, maxLoss:tot*100, breakevenAt:[K+tot,K-tot] };
  }
  if (stratName==='Bear Call Spread') {
    const s1 = mkLeg('SELL','Call',K), s2 = mkLeg('BUY','Call',K+w,sigma*0.92);
    const cr = s1.premium-s2.premium;
    return { legs:[s1,s2], netCost:-cr*100, maxProfit:cr*100, maxLoss:(w-cr)*100, breakevenAt:[K+cr] };
  }
  if (stratName==='Bull Put Spread') {
    const s1 = mkLeg('SELL','Put',K), s2 = mkLeg('BUY','Put',K-w,sigma*0.92);
    const cr = s1.premium-s2.premium;
    return { legs:[s1,s2], netCost:-cr*100, maxProfit:cr*100, maxLoss:(w-cr)*100, breakevenAt:[K-cr] };
  }
  if (stratName==='Iron Condor') {
    const cK1=K*1.03,cK2=K*1.08,pK1=K*0.97,pK2=K*0.92;
    const sc=mkLeg('SELL','Call',cK1,sigma*0.88),bc=mkLeg('BUY','Call',cK2,sigma*0.83);
    const sp=mkLeg('SELL','Put',pK1,sigma*0.88), bp=mkLeg('BUY','Put',pK2,sigma*0.83);
    const cr = sc.premium+sp.premium-bc.premium-bp.premium;
    const mw = Math.max(cK2-cK1, pK1-pK2);
    return { legs:[sc,bc,sp,bp], netCost:-cr*100, maxProfit:cr*100, maxLoss:(mw-cr)*100, breakevenAt:[cK1+cr, pK1-cr] };
  }
  // Calendar spread — simplified
  if (stratName==='Calendar Spread') {
    const T2 = T+30/365;
    const near = mkLeg('SELL', right==='C'?'Call':'Put', K);
    const far  = { ...mkLeg('BUY', right==='C'?'Call':'Put', K), premium: Math.round(bsPrice(spot,K,T2,r,sigma*1.05,right==='C'?'C':'P')*100)/100 };
    const cost = far.premium-near.premium;
    return { legs:[near,far], netCost:cost*100, maxProfit:cost*100*1.5, maxLoss:cost*100, breakevenAt:[K] };
  }
  const l = mkLeg('BUY', right==='C'?'Call':'Put', K);
  return { legs:[l], netCost:l.premium*100, maxProfit:Infinity, maxLoss:l.premium*100, breakevenAt:[K+(right==='C'?1:-1)*l.premium] };
}

// ─── STRATEGY SIMULATOR MODAL ─────────────────────────────────────────────────
function StrategySimulatorModal({ strategy, signal, ivMarket, onClose }) {
  const [spot, setSpot] = useState(null);
  const [tradeSent, setTradeSent] = useState(false);
  const [tradeError, setTradeError] = useState(null);
  const [showSizingModal, setShowSizingModal] = useState(false);

  const handleTrade = async (sl_pct = 0, tp_mult = 2.0, kelly_pct = 0) => {
    if (tradeSent) return;
    const legData = spot ? buildStrategyLegs(strategy.name, signal, spot, ivMarket) : null;
    try {
      await api.post_json("/api/paper/trade", {
        ticker:        signal.ticker,
        strategy:      strategy.name,
        legs:          legData?.legs || [],
        spot_at_entry: spot || 0,
        iv_at_entry:   ivMarket ? ivMarket / 100 : 0.25,
        expiry:        signal.expiry,
        right_type:    signal.right_type,
        vst_instant:   0,
        vst_smooth:    0,
        regime:        signal.regime || 'NORMAL',
        notes:         `SIGNALS ${signal.signal_type} K=${signal.strike}${signal.right_type} spot=${spot}`,
        sl_pct, tp_mult, kelly_pct,
      });
      setTradeSent(true);
      setShowSizingModal(false);
      setTradeError(null);
    } catch(e) {
      setTradeError("Errore registrazione posizione");
      console.error("Paper trade error:", e);
    }
  };

  useEffect(() => {
    api.get('/api/prices').then(data => {
      const p = Array.isArray(data) && data.find(d => d.symbol === signal.ticker);
      if (p && p.price > 0) setSpot(p.price);
    }).catch(()=>{});
  }, [signal.ticker]);

  const legs_data = spot ? buildStrategyLegs(strategy.name, signal, spot, ivMarket) : null;

  const sigma_dollar = useMemo(() => {
    if (!spot || !ivMarket || !signal.expiry) return null;
    const T = getDTE(signal.expiry) / 365;
    return spot * (ivMarket / 100) * Math.sqrt(T);
  }, [spot, ivMarket, signal.expiry]);

  const payoffData = useMemo(() => {
    if (!legs_data || !spot) return [];
    const lo = spot*0.55, hi = spot*1.45, steps = 120;
    const iv = ivMarket ? ivMarket / 100 : 0.3;
    const T = Math.max(getDTE(signal.expiry) / 365, 1/365);
    const mu = -0.5 * iv * iv * T;
    const sig = iv * Math.sqrt(T);
    return Array.from({length:steps+1},(_,i)=>{
      const S = lo + i*(hi-lo)/steps;
      const pnl = calcPayoffAtExpiry(legs_data.legs, S);
      const lnS = Math.log(S / spot);
      const pdf = (1 / (S * sig * Math.sqrt(2*Math.PI))) *
                  Math.exp(-0.5 * Math.pow((lnS - mu) / sig, 2));
      return {
        price: Math.round(S*100)/100,
        pnl: Math.round(pnl*100)/100,
        pdf: Math.round(pdf * 10000) / 10000,
      };
    });
  }, [legs_data, spot, ivMarket, signal.expiry]);

  const meta = SIGNAL_META[signal.signal_type] || { color: C.accent };
  const exp = signal.expiry ? `${signal.expiry.slice(0,4)}-${signal.expiry.slice(4,6)}-${signal.expiry.slice(6,8)}` : '—';
  const dte = getDTE(signal.expiry);

  return (
    <>
    <div style={{
      position:'fixed', inset:0, background:'rgba(0,0,0,0.88)', zIndex:9999,
      display:'flex', alignItems:'center', justifyContent:'center', padding:16,
    }} onClick={e => e.target===e.currentTarget && onClose()}>
      <div style={{
        background:C.panel, border:`1px solid ${C.border}`, borderRadius:12,
        width:'min(1000px,96vw)', maxHeight:'92vh', overflowY:'auto',
        display:'flex', flexDirection:'column', gap:20, padding:28,
      }}>
        {/* Header */}
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
          <div>
            <div style={{fontFamily:'JetBrains Mono',fontSize:11,color:C.muted,letterSpacing:'0.12em',marginBottom:6}}>
              SIMULATORE STRATEGIA
            </div>
            <div style={{fontFamily:'JetBrains Mono',fontSize:20,fontWeight:700,color:C.accent}}>
              {strategy.name}
            </div>
            <div style={{fontFamily:'JetBrains Mono',fontSize:12,color:C.muted,marginTop:4}}>
              {signal.ticker} &nbsp;·&nbsp; K${signal.strike} {signal.right_type}
              &nbsp;·&nbsp; scad. {exp} ({dte}gg)
              &nbsp;·&nbsp; IV mercato {ivMarket?.toFixed(1)}%
            </div>
          </div>
          <button onClick={onClose} style={{
            background:'transparent',border:`1px solid ${C.border}`,color:C.muted,
            fontFamily:'JetBrains Mono',fontSize:18,width:36,height:36,
            borderRadius:6,cursor:'pointer',flexShrink:0,
          }}>×</button>
        </div>

        {!spot ? (
          <div style={{fontFamily:'JetBrains Mono',fontSize:12,color:C.muted,textAlign:'center',padding:32}}>
            Caricamento prezzo corrente...
          </div>
        ) : (
          <>
            {/* Summary metrics */}
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))',gap:10}}>
              {[
                { label:'SPOT ATTUALE',   value:`$${spot}`,  color:C.accent },
                { label:'COSTO / CREDITO', value: legs_data.netCost < 0
                    ? `+$${Math.abs(legs_data.netCost).toFixed(0)} credito`
                    : `-$${legs_data.netCost.toFixed(0)}`,
                  color: legs_data.netCost <= 0 ? C.green : C.red },
                { label:'MAX PROFITTO',   value: legs_data.maxProfit===Infinity ? '∞ (illimitato)' : `$${Math.round(legs_data.maxProfit)}`,  color:C.green },
                { label:'MAX PERDITA',    value: `$${Math.round(Math.abs(legs_data.maxLoss))}`, color:C.red },
                ...legs_data.breakevenAt.map((be,i)=>({
                  label: `BREAKEVEN${legs_data.breakevenAt.length>1?' '+(i+1):''}`,
                  value:`$${be.toFixed(2)}`, color:C.yellow
                })),
                ...(sigma_dollar ? [{
                  label: '±1σ MOSSA ATTESA',
                  value: `±$${sigma_dollar.toFixed(1)} (${((sigma_dollar/spot)*100).toFixed(1)}%)`,
                  color: C.muted,
                }] : []),
              ].map(m=>(
                <Panel key={m.label} style={{padding:'10px 14px'}}>
                  <div style={{fontFamily:'JetBrains Mono',fontSize:8,color:C.muted,marginBottom:4,letterSpacing:'0.1em'}}>{m.label}</div>
                  <div style={{fontFamily:'JetBrains Mono',fontSize:15,fontWeight:700,color:m.color}}>{m.value}</div>
                </Panel>
              ))}
            </div>

            {/* Legs table */}
            <Panel>
              <Label color={C.muted}>LEGS DELLA STRATEGIA — premi stimati con Black-Scholes</Label>
              <table style={{width:'100%',borderCollapse:'collapse',marginTop:10}}>
                <thead>
                  <tr style={{borderBottom:`1px solid ${C.border}`}}>
                    {['AZIONE','TIPO','STRIKE','PREMIO STIMATO','VALORE PER CONTRATTO (×100)'].map(h=>(
                      <th key={h} style={{padding:'5px 10px',fontFamily:'JetBrains Mono',fontSize:9,
                        color:C.muted,textAlign:'left',letterSpacing:'0.1em',fontWeight:400}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {legs_data.legs.map((leg,i)=>(
                    <tr key={i} style={{borderBottom:`1px solid ${C.border}20`}}>
                      <td style={{padding:'8px 10px'}}>
                        <span style={{
                          fontFamily:'JetBrains Mono',fontSize:10,fontWeight:700,
                          color: leg.action==='BUY' ? C.green : C.red,
                          background: leg.action==='BUY' ? `${C.green}20` : `${C.red}20`,
                          borderRadius:4, padding:'2px 8px',
                        }}>{leg.action==='BUY' ? '▲ BUY' : '▼ SELL'}</span>
                      </td>
                      <td style={{padding:'8px 10px',fontFamily:'JetBrains Mono',fontSize:11,color:C.text}}>{leg.type}</td>
                      <td style={{padding:'8px 10px',fontFamily:'JetBrains Mono',fontSize:12,fontWeight:700,color:C.accent}}>${leg.strike}</td>
                      <td style={{padding:'8px 10px',fontFamily:'JetBrains Mono',fontSize:12,color:C.text}}>${leg.premium.toFixed(2)}</td>
                      <td style={{padding:'8px 10px',fontFamily:'JetBrains Mono',fontSize:11,
                        color: leg.action==='BUY' ? C.red : C.green}}>
                        {leg.action==='BUY' ? '-' : '+'}${(leg.premium*100).toFixed(0)}
                      </td>
                    </tr>
                  ))}
                  <tr style={{borderTop:`1px solid ${C.border}`,background:'#ffffff05'}}>
                    <td colSpan={3} style={{padding:'8px 10px',fontFamily:'JetBrains Mono',fontSize:10,color:C.muted,fontWeight:700}}>
                      NETTO (per contratto)
                    </td>
                    <td colSpan={2} style={{padding:'8px 10px',fontFamily:'JetBrains Mono',fontSize:13,fontWeight:700,
                      color: legs_data.netCost<=0 ? C.green : C.red}}>
                      {legs_data.netCost<=0
                        ? `+$${Math.abs(legs_data.netCost).toFixed(0)} CREDITO RICEVUTO`
                        : `-$${legs_data.netCost.toFixed(0)} DEBITO PAGATO`}
                    </td>
                  </tr>
                </tbody>
              </table>
            </Panel>

            {/* Payoff chart */}
            <Panel>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12,flexWrap:'wrap',gap:8}}>
                <Label color={C.muted}>PAYOFF A SCADENZA — P&L al variare del prezzo sottostante</Label>
                <div style={{display:'flex',gap:12,fontFamily:'JetBrains Mono',fontSize:9,flexWrap:'wrap'}}>
                  <span style={{color:C.accent}}>│ Spot</span>
                  <span style={{color:C.yellow}}>┆ Breakeven</span>
                  <span style={{color:C.green}}>― P&L</span>
                  <span style={{color:'#8888ff99'}}>▒ Prob. distribuzione</span>
                  <span style={{color:'#ffffff60'}}>┄ ±1σ / ±2σ</span>
                </div>
              </div>
              <div style={{height:300}}>
                <ResponsiveContainer>
                  <ComposedChart data={payoffData} margin={{top:8,right:50,left:10,bottom:4}}>
                    <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                    <XAxis dataKey="price" tick={{fontFamily:'JetBrains Mono',fontSize:8,fill:C.muted}}
                      tickFormatter={v=>`$${v}`}
                      interval={Math.floor(payoffData.length/10)} />
                    <YAxis yAxisId="pnl" tick={{fontFamily:'JetBrains Mono',fontSize:8,fill:C.muted}} width={60}
                      tickFormatter={v=>v>=0?`+$${v}`:`-$${Math.abs(v)}`} />
                    <YAxis yAxisId="prob" orientation="right" tick={{fontFamily:'JetBrains Mono',fontSize:7,fill:C.muted}}
                      tickFormatter={v=>`${(v*100).toFixed(1)}%`} width={44}
                      domain={[0, dataMax => dataMax * 1.5]} />
                    <Tooltip
                      contentStyle={{background:C.card,border:`1px solid ${C.border}`,fontFamily:'JetBrains Mono',fontSize:10}}
                      labelFormatter={v=>`Prezzo: $${v}`}
                      formatter={(v, name)=> name==='P&L'
                        ? [<span style={{color:v>=0?C.green:C.red,fontWeight:700}}>{v>=0?'+':''}{v.toFixed(0)} $</span>, 'P&L']
                        : [`${(v*100).toFixed(2)}%`, 'Probabilità']
                      } />
                    <ReferenceLine yAxisId="pnl" y={0} stroke={C.muted} strokeWidth={1.5} strokeDasharray="4 2" />
                    <ReferenceLine yAxisId="pnl" x={spot} stroke={C.accent} strokeWidth={2}
                      label={{value:'Spot',fill:C.accent,fontSize:9,fontFamily:'JetBrains Mono',position:'top'}} />
                    {/* σ reference lines */}
                    {sigma_dollar && [1,2,-1,-2].map(n=>{
                      const px = Math.round((spot + n*sigma_dollar)*100)/100;
                      return <ReferenceLine key={`s${n}`} yAxisId="pnl" x={px}
                        stroke={`rgba(255,255,255,${Math.abs(n)===1?0.25:0.12})`}
                        strokeDasharray="4 3"
                        label={{value:`${n>0?'+':''}${n}σ`,fill:`rgba(255,255,255,${Math.abs(n)===1?0.5:0.3})`,fontSize:8,fontFamily:'JetBrains Mono',position:'top'}} />;
                    })}
                    {legs_data.breakevenAt.map((be,i)=>(
                      <ReferenceLine key={i} yAxisId="pnl" x={Math.round(be*100)/100} stroke={C.yellow} strokeDasharray="5 3"
                        label={{value:`BE$${be.toFixed(0)}`,fill:C.yellow,fontSize:8,fontFamily:'JetBrains Mono',position:'top'}} />
                    ))}
                    {legs_data.legs.filter(l=>l.action==='SELL'||legs_data.legs.length===1).map((leg,i)=>(
                      <ReferenceLine key={`k${i}`} yAxisId="pnl" x={leg.strike} stroke={`${meta.color}80`} strokeDasharray="3 3"
                        label={{value:`K${leg.strike}`,fill:meta.color,fontSize:8,fontFamily:'JetBrains Mono'}} />
                    ))}
                    {/* Gaussiana */}
                    <Area yAxisId="prob" type="monotone" dataKey="pdf"
                      fill="rgba(120,120,255,0.15)" stroke="rgba(120,120,255,0.5)"
                      strokeWidth={1.5} dot={false} name="Probabilità" />
                    {/* Payoff line */}
                    <Line yAxisId="pnl" type="monotone" dataKey="pnl"
                      stroke="url(#payoffGrad2)" strokeWidth={2.5} dot={false} name="P&L" />
                    <defs>
                      <linearGradient id="payoffGrad2" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={C.green} />
                        <stop offset="50%" stopColor={C.accent} />
                        <stop offset="100%" stopColor={C.red} />
                      </linearGradient>
                    </defs>
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              <div style={{fontFamily:'JetBrains Mono',fontSize:9,color:C.muted,textAlign:'center',marginTop:6}}>
                ⚠ Premi stimati con Black-Scholes — la curva viola mostra la distribuzione log-normale implicita dalla IV a scadenza
              </div>
            </Panel>

            {/* CTA */}
            <div style={{display:'flex',gap:12,justifyContent:'flex-end'}}>
              <button onClick={onClose} style={{
                background:`${C.muted}15`,color:C.muted,border:`1px solid ${C.muted}40`,
                borderRadius:6,padding:'10px 24px',cursor:'pointer',
                fontFamily:'JetBrains Mono',fontSize:11,
              }}>Chiudi</button>
              <button onClick={() => { if (!tradeSent) setShowSizingModal(true); }} disabled={tradeSent} style={{
                background: tradeSent ? `${C.green}20` : C.accent,
                color: tradeSent ? C.green : '#000', border: tradeSent ? `1px solid ${C.green}50` : 'none',
                borderRadius:6,padding:'10px 28px',cursor: tradeSent ? 'default' : 'pointer',
                fontFamily:'JetBrains Mono',fontSize:12,fontWeight:700,letterSpacing:'0.1em',
              }}>{tradeSent ? "✅ POSIZIONE REGISTRATA" : "▶ PRENDO POSIZIONE"}</button>
              {tradeError && <div style={{fontFamily:'JetBrains Mono',fontSize:9,color:C.red}}>{tradeError}</div>}
            </div>
          </>
        )}
      </div>
    </div>
    {showSizingModal && (
      <SizingModal
        selected={{ ...signal, spot: spot || 0, iv_market: ivMarket ? ivMarket / 100 : 0.25 }}
        strat={strategy}
        onConfirm={handleTrade}
        onClose={() => setShowSizingModal(false)}
        C={C}
      />
    )}
    </>
  );
}

function getStrategies(signalType, rightType, merged = false) {
  const rt = (rightType || "").toUpperCase();

  if (signalType === "UNDERPRICED") {
    if (merged) {
      // Anomalia su entrambe C e P — nessuna view direzionale implicita
      return [
        {
          name: "Long Straddle",
          cost: "💰💰 Medio (2× premio)",
          risk: "Limitato al costo totale",
          reward: "Illimitato — profitto se il titolo si muove molto",
          rr: "Ottimo",
          description: `Acquistare sia la call che la put allo stesso strike e scadenza. La IV è sottovalutata su entrambi i lati: il titolo deve solo muoversi in modo significativo in qualsiasi direzione per generare profitto.`,
          recommended: true,
          tags: ["✓ no direzionale", "✓ max esposizione vol", "✓ adatto a C/P"],
        },
        {
          name: "Long Call",
          cost: "💰 Basso",
          risk: "Limitato al premio pagato",
          reward: "Illimitato (al rialzo)",
          rr: "Ottimo se view rialzista",
          description: `Acquistare solo la call se hai una view rialzista sul titolo. Costo dimezzato rispetto allo straddle, ma richiede che il titolo salga sopra lo strike entro la scadenza.`,
          recommended: false,
          tags: ["view rialzista", "min costo"],
        },
        {
          name: "Long Put",
          cost: "💰 Basso",
          risk: "Limitato al premio pagato",
          reward: "Elevato (fino a strike → 0)",
          rr: "Ottimo se view ribassista",
          description: `Acquistare solo la put se hai una view ribassista sul titolo. Costo dimezzato rispetto allo straddle, ma richiede che il titolo scenda sotto lo strike entro la scadenza.`,
          recommended: false,
          tags: ["view ribassista", "min costo"],
        },
        {
          name: "Calendar Spread",
          cost: "💰 Basso (debito netto ridotto)",
          risk: "Limitato al debito netto",
          reward: "Moderato — beneficia dall'espansione di IV",
          rr: "Moderato",
          description: `Vendere la scadenza più vicina e acquistare la stessa strike su scadenza più lontana. Sfrutta l'espansione di IV con un costo contenuto.`,
          recommended: false,
          tags: ["basso costo", "IV expansion play"],
        },
      ];
    }
    return [
      {
        name: rt === "C" ? "Long Call" : rt === "P" ? "Long Put" : "Long Option",
        cost: "💰 Basso",
        risk: "Limitato al premio pagato",
        reward: rt === "C" ? "Illimitato (al rialzo)" : "Elevato (fino a strike → 0)",
        rr: "Ottimo",
        description: `Acquistare l'opzione segnalata. Strategia più semplice e diretta: il rischio massimo è solo il premio pagato, il guadagno potenziale è molto maggiore.`,
        recommended: true,
        tags: ["✓ min rischio", "✓ min costo", "✓ max R/R"],
      },
      {
        name: "Long Straddle",
        cost: "💰💰 Medio (2× premio)",
        risk: "Limitato al costo totale",
        reward: "Illimitato — profitto se il titolo si muove molto",
        rr: "Buono",
        description: `Acquistare sia la call che la put allo stesso strike e scadenza. Non richiede una view direzionale: basta che il titolo si muova in modo significativo in qualsiasi direzione.`,
        recommended: false,
        tags: ["no direzionale", "max esposizione vol"],
      },
      {
        name: "Calendar Spread",
        cost: "💰 Basso (debito netto ridotto)",
        risk: "Limitato al debito netto",
        reward: "Moderato — beneficia dall'espansione di IV",
        rr: "Moderato",
        description: `Vendere la scadenza più vicina e acquistare la stessa strike su scadenza più lontana. Costo ridotto rispetto alla long pura, guadagna se la IV si espande e il titolo rimane vicino allo strike.`,
        recommended: false,
        tags: ["basso costo", "IV expansion play"],
      },
    ];
  } else if (signalType === "OVERPRICED") {
    if (merged) {
      // Anomalia su entrambe C e P — nessuna view direzionale
      return [
        {
          name: "Iron Condor",
          cost: "💰 Nessun esborso (doppio credito)",
          risk: "Limitato (larghezza spread − premio totale)",
          reward: "Doppio premio incassato",
          rr: "Ottimo",
          description: `Vendere un call spread OTM + un put spread OTM. La IV è sopravvalutata su entrambi i lati: profitti se il titolo rimane in un range laterale. Strategia neutrale ideale quando entrambe C e P sono overpriced.`,
          recommended: true,
          tags: ["✓ no direzionale", "✓ doppio credito", "✓ adatto a C/P"],
        },
        {
          name: "Bear Call Spread",
          cost: "💰 Nessun esborso (ricevi premio)",
          risk: "Limitato (larghezza spread − premio incassato)",
          reward: "Premio netto incassato",
          rr: "Buono se view ribassista",
          description: `Vendere la call + acquistare una call OTM con strike più alto. Strategia bearish a rischio definito se hai una view ribassista.`,
          recommended: false,
          tags: ["view ribassista", "rischio definito"],
        },
        {
          name: "Bull Put Spread",
          cost: "💰 Nessun esborso (ricevi premio)",
          risk: "Limitato (larghezza spread − premio incassato)",
          reward: "Premio netto incassato",
          rr: "Buono se view rialzista",
          description: `Vendere la put + acquistare una put OTM con strike più basso. Strategia bullish a rischio definito se hai una view rialzista.`,
          recommended: false,
          tags: ["view rialzista", "rischio definito"],
        },
        {
          name: "Short Strangle",
          cost: "💰 Nessun esborso (ricevi premio)",
          risk: "Elevato se il titolo si muove molto",
          reward: "Doppio premio OTM incassato",
          rr: "Buono in mercato laterale",
          description: `Vendere una call OTM e una put OTM. Costo inferiore all'iron condor ma con rischio maggiore se il titolo esce dal range. Solo per trader esperti.`,
          recommended: false,
          tags: ["alto rischio", "solo esperti"],
        },
      ];
    }
    return [
      {
        name: rt === "C" ? "Bear Call Spread" : "Bull Put Spread",
        cost: "💰 Nessun esborso (ricevi premio)",
        risk: "Limitato (larghezza spread − premio incassato)",
        reward: "Premio netto incassato",
        rr: "Ottimo per risk management",
        description: rt === "C"
          ? `Vendere la call segnalata + acquistare una call OTM con strike più alto. Incassi subito un premio, il rischio massimo è la differenza tra i due strike meno il credito ricevuto. Strategia bearish a rischio definito.`
          : `Vendere la put segnalata + acquistare una put OTM con strike più basso. Incassi subito un premio, il rischio massimo è la differenza tra i due strike meno il credito ricevuto. Strategia bullish a rischio definito.`,
        recommended: true,
        tags: ["✓ min rischio", "✓ rischio definito", "✓ nessun esborso"],
      },
      {
        name: "Iron Condor",
        cost: "💰 Nessun esborso (doppio credito)",
        risk: "Limitato (larghezza spread − premio totale)",
        reward: "Doppio premio incassato",
        rr: "Buono se titolo in range",
        description: `Vendere un call spread OTM + un put spread OTM. Profitti se il titolo rimane in un range laterale. Strategia neutrale che beneficia da alta IV che scende (theta positivo).`,
        recommended: false,
        tags: ["neutrale", "doppia vendita vol"],
      },
      {
        name: rt === "C" ? "Short Call (naked)" : "Short Put (cash-secured)",
        cost: rt === "C" ? "⚠️ Richiede alto margine" : "💰💰 Richiede capitale (put × 100)",
        risk: rt === "C" ? "Illimitato ⚠️" : "Elevato (strike × 100 − premio)",
        reward: "Premio incassato",
        rr: "Basso — rischio/rendimento sfavorevole",
        description: rt === "C"
          ? `Vendere la call segnalata senza copertura. Massimizza il premio incassato ma espone a perdite teoricamente illimitate. Sconsigliata senza gestione attiva del rischio.`
          : `Vendere la put segnalata con la liquidità necessaria ad acquistare il titolo. Adatto solo se si è disposti a comprare il titolo allo strike in caso di esercizio.`,
        recommended: false,
        tags: ["alto rischio", "solo esperti"],
      },
    ];
  }
  return [];
}

function SignalCard({ s, onUpdateStatus }) {
  const [expanded, setExpanded] = useState(false);
  const [simStrategy, setSimStrategy] = useState(null);
  const [hvData, setHvData] = useState(null);
  const meta = SIGNAL_META[s.signal_type] || { color: C.muted, bg: C.border+"20", label: s.signal_type, desc: "", icon: "●" };

  useEffect(() => {
    if (expanded && !hvData) {
      api.get(`/api/hv/${s.ticker}`).then(setHvData).catch(() => {});
    }
  }, [expanded]);
  const STATUS_COLOR = { PENDING: C.yellow, LIVE: C.green, CLOSED: C.muted, CANCELLED: C.red };
  const exp = s.expiry ? `${s.expiry.slice(0,4)}-${s.expiry.slice(4,6)}-${s.expiry.slice(6,8)}` : "—";

  // Dati IV da notes JSON
  let ivMarket = null, ivModel = null, ivRank = null;
  try {
    if (s.notes) {
      const n = JSON.parse(s.notes);
      ivMarket = n.iv_market;
      ivModel  = n.iv_model;
      ivRank   = n.iv_rank;
    }
  } catch {}

  const ivDiff = (ivMarket != null && ivModel != null) ? (ivMarket - ivModel).toFixed(1) : null;
  const strategies = getStrategies(s.signal_type, s.right_type, s.merged);

  return (
    <div style={{
      border: `1px solid ${meta.color}40`,
      borderLeft: `3px solid ${meta.color}`,
      borderRadius: 8, background: meta.bg,
      padding: "14px 16px", cursor: "pointer",
    }} onClick={() => setExpanded(e => !e)}>

      {/* Header row */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <span style={{ fontFamily: "JetBrains Mono", fontSize: 18, color: meta.color }}>{meta.icon}</span>
        <span
          style={{ fontFamily: "JetBrains Mono", fontSize: 15, fontWeight: 700, color: C.accent, minWidth: 50, cursor: "pointer" }}
          title="Apri in Chart"
          onClick={e => { e.stopPropagation(); window.dispatchEvent(new CustomEvent("quantos:navigate", { detail: { page: "CHART", ticker: s.ticker } })); }}
        >{s.ticker}</span>
        <span style={{
          fontFamily: "JetBrains Mono", fontSize: 10, fontWeight: 700,
          color: meta.color, background: meta.color+"20",
          border: `1px solid ${meta.color}50`, borderRadius: 4, padding: "2px 8px"
        }}>{meta.label}</span>
        {s.strike && (
          <span style={{ fontFamily: "JetBrains Mono", fontSize: 11, color: C.text }}>
            K <strong>${s.strike}</strong>{" "}
            {s.merged
              ? <span style={{ color: C.yellow, fontWeight: 700 }}>C/P</span>
              : (s.right_type || "")}
          </span>
        )}
        {s.expiry && (
          <span style={{ fontFamily: "JetBrains Mono", fontSize: 10, color: C.muted }}>scad. {exp}</span>
        )}
        {ivMarket != null && ivModel != null && (
          <span style={{ fontFamily: "JetBrains Mono", fontSize: 10, color: C.muted }}>
            IV <span style={{ color: meta.color }}>{ivMarket.toFixed(1)}%</span>
            {" vs "}
            <span style={{ color: C.accent }}>{ivModel.toFixed(1)}%</span>
          </span>
        )}
        <span style={{
          marginLeft: "auto", fontFamily: "JetBrains Mono", fontSize: 13, fontWeight: 700,
          color: s.edge_sigma >= 2.5 ? C.red : s.edge_sigma >= 1.8 ? C.yellow : C.muted
        }}>
          {s.edge_sigma != null ? `${Number(s.edge_sigma).toFixed(2)}σ` : "—"}
        </span>
        <span style={{
          fontFamily: "JetBrains Mono", fontSize: 9, fontWeight: 700,
          background: `${STATUS_COLOR[s.status] || C.muted}25`,
          color: STATUS_COLOR[s.status] || C.muted,
          borderRadius: 4, padding: "3px 8px",
        }}>{s.status}</span>
        <span style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted }}>{expanded ? "▲" : "▼"}</span>
      </div>

      {/* Expanded */}
      {expanded && (
        <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 14 }}
          onClick={e => e.stopPropagation()}>

          {/* Nota superficie IV — solo se segnale merged C+P */}
          {s.merged && (
            <div style={{
              background: `${C.yellow}12`, border: `1px solid ${C.yellow}40`,
              borderRadius: 6, padding: "8px 14px",
              fontFamily: "JetBrains Mono", fontSize: 10, color: C.yellow,
              lineHeight: 1.6,
            }}>
              ⚠️ Anomalia rilevata sia sulla Call che sulla Put a questo strike.<br />
              <span style={{ color: C.muted }}>
                Il segnale riguarda il <strong style={{ color: C.text }}>punto della superficie IV</strong>, non un contratto specifico.
                Scegli in base alla tua view: <strong style={{ color: C.green }}>Call</strong> se rialzista,{" "}
                <strong style={{ color: C.red }}>Put</strong> se ribassista,{" "}
                <strong style={{ color: C.accent }}>Straddle</strong> se neutrale sulla direzione.
              </span>
            </div>
          )}

          {/* IV comparison */}
          {ivMarket != null && ivModel != null && (
            <div style={{
              background: "#ffffff06", borderRadius: 8, padding: "12px 16px",
              display: "flex", gap: 24, flexWrap: "wrap", alignItems: "center"
            }}>
              <div>
                <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted, marginBottom: 4 }}>IV MERCATO</div>
                <div style={{ fontFamily: "JetBrains Mono", fontSize: 20, fontWeight: 700, color: meta.color }}>{ivMarket.toFixed(1)}%</div>
              </div>
              <div style={{ fontFamily: "JetBrains Mono", fontSize: 18, color: C.border }}>vs</div>
              <div>
                <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted, marginBottom: 4 }}>IV MODELLO</div>
                <div style={{ fontFamily: "JetBrains Mono", fontSize: 20, fontWeight: 700, color: C.accent }}>{ivModel.toFixed(1)}%</div>
              </div>
              <div>
                <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted, marginBottom: 4 }}>DIFFERENZA</div>
                <div style={{ fontFamily: "JetBrains Mono", fontSize: 20, fontWeight: 700, color: meta.color }}>
                  {ivDiff > 0 ? "+" : ""}{ivDiff}%
                </div>
              </div>
              {ivRank != null && (
                <div>
                  <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted, marginBottom: 4 }}>IV RANK (storico)</div>
                  <div style={{ fontFamily: "JetBrains Mono", fontSize: 20, fontWeight: 700,
                    color: ivRank >= 70 ? C.red : ivRank <= 30 ? C.green : C.yellow }}>
                    {ivRank}°pct
                  </div>
                </div>
              )}
              <div style={{ flex: 1, minWidth: 160 }}>
                <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted, marginBottom: 6 }}>ANOMALIA</div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ flex: 1, height: 6, background: C.dim, borderRadius: 3 }}>
                    <div style={{
                      width: `${Math.min(100, (s.edge_sigma / 5) * 100)}%`,
                      height: "100%", background: meta.color, borderRadius: 3
                    }} />
                  </div>
                  <span style={{ fontFamily: "JetBrains Mono", fontSize: 11, color: meta.color, fontWeight: 700 }}>
                    {Number(s.edge_sigma).toFixed(2)}σ
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Volatilità storica realizzata */}
          {hvData && (
            <div style={{
              background: "#ffffff06", borderRadius: 8, padding: "12px 16px",
            }}>
              <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted, letterSpacing: "0.12em", marginBottom: 10 }}>
                VOLATILITÀ STORICA REALIZZATA (HV) — confronto con IV mercato {ivMarket != null ? `${ivMarket.toFixed(1)}%` : ""}
              </div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {[
                  { label: "3g",   key: "hv_3d",   short: true },
                  { label: "5g",   key: "hv_5d",   short: true },
                  { label: "10g",  key: "hv_10d",  short: false },
                  { label: "30g",  key: "hv_30d",  short: false },
                  { label: "60g",  key: "hv_60d",  short: false },
                  { label: "90g",  key: "hv_90d",  short: false },
                  { label: "120g", key: "hv_120d", short: false },
                ].map(({ label, key, short }) => {
                  const val = hvData[key];
                  const diff = (val != null && ivMarket != null) ? ivMarket - val : null;
                  const color = diff == null ? C.muted : diff > 5 ? C.red : diff < -5 ? C.green : C.yellow;
                  return (
                    <div key={key} style={{
                      background: "#ffffff06", borderRadius: 6, padding: "8px 12px", minWidth: 70, textAlign: "center",
                      border: short ? `1px solid ${C.accent}30` : `1px solid ${C.border}`,
                    }}>
                      <div style={{ fontFamily: "JetBrains Mono", fontSize: 8, color: C.muted, marginBottom: 4 }}>
                        HV {label}{short ? " ⚡" : ""}
                      </div>
                      <div style={{ fontFamily: "JetBrains Mono", fontSize: 14, fontWeight: 700, color }}>
                        {val != null ? `${val}%` : "—"}
                      </div>
                      {diff != null && (
                        <div style={{ fontFamily: "JetBrains Mono", fontSize: 8, color, marginTop: 2 }}>
                          {diff > 0 ? `IV+${diff.toFixed(1)}` : `IV${diff.toFixed(1)}`}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted, marginTop: 8 }}>
                ⚡ = breve termine &nbsp;·&nbsp; IV - HV &gt; 0: la IV è cara rispetto alla vol realizzata (favorevole a vendere vol)
              </div>
            </div>
          )}

          {/* Strategy simulator */}
          {strategies.length > 0 && (
            <div>
              <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted, letterSpacing: "0.12em", marginBottom: 10 }}>
                STRATEGIE SUGGERITE — ordinate per rischio/rendimento
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {strategies.map((st, i) => (
                  <div key={i} style={{
                    border: `1px solid ${st.recommended ? C.accent : C.border}`,
                    borderRadius: 8, padding: "12px 16px",
                    background: st.recommended ? `${C.accent}08` : "#ffffff04",
                    position: "relative",
                  }}>
                    {st.recommended && (
                      <div style={{
                        position: "absolute", top: -10, left: 16,
                        fontFamily: "JetBrains Mono", fontSize: 9, fontWeight: 700,
                        color: "#000", background: C.accent,
                        padding: "2px 10px", borderRadius: 4, letterSpacing: "0.1em"
                      }}>★ CONSIGLIATA</div>
                    )}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontFamily: "JetBrains Mono", fontSize: 13, fontWeight: 700,
                          color: st.recommended ? C.accent : C.text, marginBottom: 6 }}>
                          {st.name}
                        </div>
                        <div style={{ fontFamily: "JetBrains Mono", fontSize: 11, color: C.muted, lineHeight: 1.7, marginBottom: 8 }}>
                          {st.description}
                        </div>
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                          {st.tags.map(t => (
                            <span key={t} style={{
                              fontFamily: "JetBrains Mono", fontSize: 9,
                              color: t.startsWith("✓") ? C.green : t.startsWith("⚠") ? C.red : C.muted,
                              background: t.startsWith("✓") ? `${C.green}15` : t.startsWith("⚠") ? `${C.red}15` : `${C.muted}15`,
                              borderRadius: 3, padding: "2px 7px"
                            }}>{t}</span>
                          ))}
                          <button
                            onClick={e => { e.stopPropagation(); setSimStrategy(st); }}
                            style={{
                              marginLeft: "auto",
                              fontFamily: "JetBrains Mono", fontSize: 9, fontWeight: 700,
                              color: C.accent, background: `${C.accent}18`,
                              border: `1px solid ${C.accent}60`,
                              borderRadius: 5, padding: "4px 14px", cursor: "pointer",
                              letterSpacing: "0.08em",
                            }}>
                            🔬 SIMULA
                          </button>
                        </div>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, minWidth: 260 }}>
                        {[
                          { label: "COSTO/ESBORSO", value: st.cost },
                          { label: "RISCHIO MAX",   value: st.risk },
                          { label: "GUADAGNO MAX",  value: st.reward },
                          { label: "RISK/REWARD",   value: st.rr },
                        ].map(m => (
                          <div key={m.label} style={{
                            background: "#ffffff06", borderRadius: 6, padding: "8px 10px"
                          }}>
                            <div style={{ fontFamily: "JetBrains Mono", fontSize: 8, color: C.muted, marginBottom: 3 }}>{m.label}</div>
                            <div style={{ fontFamily: "JetBrains Mono", fontSize: 10, color: C.text }}>{m.value}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          {["PENDING", "LIVE"].includes(s.status) && (
            <div style={{ display: "flex", gap: 8, paddingTop: 4 }}>
              {s.status === "PENDING" && (
                <button onClick={() => onUpdateStatus(s.id, "LIVE")} style={{
                  background: `${C.green}20`, color: C.green, border: `1px solid ${C.green}50`,
                  borderRadius: 5, padding: "8px 20px", cursor: "pointer",
                  fontFamily: "JetBrains Mono", fontSize: 10, fontWeight: 700
                }}>▶ PRENDO POSIZIONE</button>
              )}
              <button onClick={() => onUpdateStatus(s.id, "CLOSED")} style={{
                background: `${C.muted}15`, color: C.muted, border: `1px solid ${C.muted}40`,
                borderRadius: 5, padding: "8px 20px", cursor: "pointer",
                fontFamily: "JetBrains Mono", fontSize: 10
              }}>✓ Chiudi</button>
              <button onClick={() => onUpdateStatus(s.id, "CANCELLED")} style={{
                background: `${C.red}10`, color: C.red, border: `1px solid ${C.red}30`,
                borderRadius: 5, padding: "8px 20px", cursor: "pointer",
                fontFamily: "JetBrains Mono", fontSize: 10
              }}>✕ Scarta</button>
            </div>
          )}

          <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted }}>
            Rilevato: {s.created_at ? new Date(s.created_at).toLocaleString("it-IT").slice(0,16) : "—"}
            &nbsp;·&nbsp;Strategia: {s.strategy}
          </div>
        </div>
      )}

      {/* Strategy Simulator Modal */}
      {simStrategy && (
        <StrategySimulatorModal
          strategy={simStrategy}
          signal={s}
          ivMarket={ivMarket}
          onClose={() => setSimStrategy(null)}
        />
      )}
    </div>
  );
}

function SignalsPage() {
  const [signals, setSignals]               = useState([]);
  const [loading, setLoading]               = useState(true);
  const [scanning, setScanning]             = useState(false);
  const [scanResult, setScanResult]         = useState(null);
  const [filterStrategy, setFilterStrategy] = useState("");
  const [filterStatus, setFilterStatus]     = useState("");
  const [filterTicker, setFilterTicker]     = useState("");
  const [sortBy, setSortBy]                 = useState("sigma_desc");
  const [moneynessFilter, setMoneynessFilter] = useState(10);
  const [dteMin, setDteMin]                   = useState(14);
  const [dteMax, setDteMax]                   = useState(90);

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (filterStrategy) p.append("strategy", filterStrategy);
      if (filterStatus)   p.append("status",   filterStatus);
      if (filterTicker)   p.append("ticker",   filterTicker);
      p.append("limit", 200);
      const data = await api.get(`/api/signals?${p}`);
      setSignals(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const scan = async () => {
    setScanning(true);
    setScanResult(null);
    try {
      const res = await api.post(`/api/signals/scan?moneyness_pct=${moneynessFilter}&dte_min=${dteMin}&dte_max=${dteMax}`);
      setScanResult(res);
      await load();
    } catch (e) { console.error(e); }
    setScanning(false);
  };

  useEffect(() => { load(); }, [filterStrategy, filterStatus, filterTicker]);

  const updateStatus = async (id, newStatus) => {
    try {
      await api.patch(`/api/signals/${id}/status`, { status: newStatus });
      load();
    } catch (e) { console.error(e); }
  };

  const selStyle = {
    background: C.card, color: C.text,
    border: `1px solid ${C.border}`, borderRadius: 6,
    padding: "6px 12px", fontSize: 12,
    fontFamily: "JetBrains Mono",
  };

  const pendingCount = signals.filter(s => s.status === "PENDING").length;
  const liveCount    = signals.filter(s => s.status === "LIVE").length;

  const sortedSignals = useMemo(() => {
    const today = new Date(); today.setHours(0,0,0,0);

    // Filtro DTE
    const filtered = signals.filter(s => {
      if (!s.expiry) return true;
      const exp = String(s.expiry).replace(/-/g, "");
      if (exp.length < 8) return true;
      const expDate = new Date(+exp.slice(0,4), +exp.slice(4,6)-1, +exp.slice(6,8));
      const dte = Math.round((expDate - today) / 86400000);
      return dte >= dteMin;
    });

    // Dedup C/P: raggruppa per (ticker, strike, expiry)
    // Se esistono sia C che P → merge in un unico record con merged:true
    const groups = {};
    for (const s of filtered) {
      const key = `${s.ticker}|${s.strike}|${s.expiry}`;
      if (!groups[key]) {
        groups[key] = { ...s, merged: false };
      } else {
        // Stesso punto superficie — tieni sigma più alto, segna come merged
        const best = (s.edge_sigma || 0) > (groups[key].edge_sigma || 0) ? s : groups[key];
        groups[key] = { ...best, merged: true };
      }
    }
    const arr = Object.values(groups);

    if (sortBy === "sigma_desc") arr.sort((a,b) => (b.edge_sigma||0) - (a.edge_sigma||0));
    else if (sortBy === "sigma_asc") arr.sort((a,b) => (a.edge_sigma||0) - (b.edge_sigma||0));
    else if (sortBy === "ticker_asc") arr.sort((a,b) => (a.ticker||"").localeCompare(b.ticker||""));
    else if (sortBy === "ticker_desc") arr.sort((a,b) => (b.ticker||"").localeCompare(a.ticker||""));
    else if (sortBy === "date_desc") arr.sort((a,b) => new Date(b.created_at||0) - new Date(a.created_at||0));
    return arr;
  }, [signals, sortBy, dteMin]);

  return (
    <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>

      {/* Scan CTA */}
      <Panel style={{ background: `${C.accent}08`, border: `1px solid ${C.accent}30` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: "JetBrains Mono", fontSize: 13, fontWeight: 700, color: C.accent, marginBottom: 4 }}>
              🔍 SCANSIONE OPPORTUNITÀ
            </div>
            <div style={{ fontFamily: "JetBrains Mono", fontSize: 11, color: C.muted }}>
              Analizza la vol surface di tutti i ticker attivi e rileva opzioni mispriced.
              {scanResult && (
                <span style={{ color: C.green, marginLeft: 12 }}>
                  ✓ Ultima scansione: {scanResult.scanned}/{scanResult.total_symbols} ticker — {scanResult.signals_found} segnali trovati
                </span>
              )}
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 200 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontFamily: "JetBrains Mono", fontSize: 10, color: C.muted }}>
              <span>FILTRO MONEYNESS</span>
              <span style={{ color: C.accent, fontWeight: 700 }}>±{moneynessFilter}%</span>
            </div>
            <input type="range" min={3} max={30} step={1} value={moneynessFilter}
              onChange={e => setMoneynessFilter(Number(e.target.value))}
              style={{ width: "100%", accentColor: C.accent, cursor: "pointer" }} />
            <div style={{ display: "flex", justifyContent: "space-between", fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted }}>
              <span>3% solo ATM</span><span>30% ampio</span>
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 200 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontFamily: "JetBrains Mono", fontSize: 10, color: C.muted }}>
              <span>DTE MIN</span>
              <span style={{ color: C.accent, fontWeight: 700 }}>{dteMin}gg</span>
            </div>
            <input type="range" min={8} max={45} step={1} value={dteMin}
              onChange={e => setDteMin(Number(e.target.value))}
              style={{ width: "100%", accentColor: C.accent, cursor: "pointer" }} />
            <div style={{ display: "flex", justifyContent: "space-between", fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted }}>
              <span>8gg</span><span>45gg</span>
            </div>
          </div>
          <button onClick={scan} disabled={scanning} style={{
            background: scanning ? `${C.accent}20` : C.accent,
            color: scanning ? C.accent : "#000",
            border: `1px solid ${C.accent}`,
            borderRadius: 6, padding: "10px 24px",
            cursor: scanning ? "not-allowed" : "pointer",
            fontFamily: "JetBrains Mono", fontSize: 12, fontWeight: 700,
            letterSpacing: "0.1em", minWidth: 160,
          }}>
            {scanning ? "⏳ Scansione..." : "▶ SCANSIONA ORA"}
          </button>
        </div>
      </Panel>

      {/* Legenda */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        {[
          { color: "#ff4466", icon: "▼", label: "OVERPRICED", desc: "Vendi volatilità — IV cara rispetto al modello" },
          { color: "#00e5a0", icon: "▲", label: "UNDERPRICED", desc: "Compra volatilità — IV a sconto rispetto al modello" },
        ].map(l => (
          <div key={l.label} style={{
            display: "flex", alignItems: "center", gap: 8,
            background: l.color+"12", border: `1px solid ${l.color}30`,
            borderRadius: 6, padding: "6px 14px",
            fontFamily: "JetBrains Mono", fontSize: 10,
          }}>
            <span style={{ color: l.color, fontSize: 14 }}>{l.icon}</span>
            <div>
              <div style={{ color: l.color, fontWeight: 700 }}>{l.label}</div>
              <div style={{ color: C.muted, fontSize: 9 }}>{l.desc}</div>
            </div>
          </div>
        ))}
        <div style={{ marginLeft: "auto", display: "flex", gap: 16, alignItems: "center", fontFamily: "JetBrains Mono", fontSize: 11 }}>
          <span style={{ color: C.yellow }}>⏳ {pendingCount} PENDING</span>
          <span style={{ color: C.green }}>▶ {liveCount} LIVE</span>
        </div>
      </div>

      {/* Filtri */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <select value={filterStrategy} onChange={e => setFilterStrategy(e.target.value)} style={selStyle}>
          <option value="">Tutte le strategie</option>
          <option value="VOL_SURFACE">VOL SURFACE</option>
          <option value="KALMAN_PAIR">KALMAN PAIR</option>
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={selStyle}>
          <option value="">Tutti gli status</option>
          <option value="PENDING">PENDING</option>
          <option value="LIVE">LIVE</option>
          <option value="CLOSED">CLOSED</option>
          <option value="CANCELLED">CANCELLED</option>
        </select>
        <input
          placeholder="Filtra ticker..."
          value={filterTicker}
          onChange={e => setFilterTicker(e.target.value.toUpperCase())}
          style={{ ...selStyle, width: 130 }}
        />
        <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={selStyle}>
          <option value="sigma_desc">↓ σ più alto</option>
          <option value="sigma_asc">↑ σ più basso</option>
          <option value="ticker_asc">Ticker A→Z</option>
          <option value="ticker_desc">Ticker Z→A</option>
          <option value="date_desc">Più recenti</option>
        </select>
        <button onClick={load} style={{
          background: `${C.accent}15`, border: `1px solid ${C.accent}40`,
          color: C.accent, borderRadius: 6, padding: "6px 14px",
          cursor: "pointer", fontSize: 11, fontFamily: "JetBrains Mono",
        }}>↻</button>
        <span style={{ color: C.muted, fontSize: 11, fontFamily: "JetBrains Mono" }}>
          {signals.length} segnali
        </span>
      </div>

      {/* Lista segnali */}
      {loading ? (
        <div style={{ color: C.muted, fontFamily: "JetBrains Mono", fontSize: 12, padding: 16 }}>Caricamento...</div>
      ) : signals.length === 0 ? (
        <Panel>
          <div style={{ textAlign: "center", padding: 40 }}>
            <div style={{ fontFamily: "JetBrains Mono", fontSize: 32, color: C.border, marginBottom: 12 }}>📡</div>
            <div style={{ fontFamily: "JetBrains Mono", fontSize: 13, color: C.text, marginBottom: 8 }}>Nessun segnale trovato</div>
            <div style={{ fontFamily: "JetBrains Mono", fontSize: 11, color: C.muted, marginBottom: 20 }}>
              Premi <strong style={{ color: C.accent }}>SCANSIONA ORA</strong> per analizzare tutti i ticker attivi<br />
              e trovare opzioni mispriced rispetto al modello di volatilità.
            </div>
            <button onClick={scan} disabled={scanning} style={{
              background: C.accent, color: "#000", border: "none",
              borderRadius: 6, padding: "10px 28px", cursor: "pointer",
              fontFamily: "JetBrains Mono", fontSize: 12, fontWeight: 700
            }}>{scanning ? "⏳ Scansione..." : "▶ SCANSIONA ORA"}</button>
          </div>
        </Panel>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {sortedSignals.map(s => (
            <SignalCard key={s.id} s={s} onUpdateStatus={updateStatus} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── STRATEGIES PAGE ──────────────────────────────────────────────────────────
function scoreSignal(r) {
  const votes = [
    { label: "VST",    desc: "Segnale forte",              ok: Math.abs(r.vst_instant || 0) >= 0.8 },
    { label: "SMOOTH", desc: "Confermato nel tempo",       ok: r.confirmed === true },
    { label: "COER",   desc: "Instant e Smooth concordi",  ok: r.coherent === true },
    { label: "GEX",    desc: "Vicino al flip GEX",         ok: (r.gex_proximity || 0) >= 0.5 },
    { label: "REGIME", desc: "Regime amplifica (>×1)",     ok: (r.amplifier || 1) > 1.0 },
    { label: "GRAD",   desc: "Pressione strike-specifica", ok: r.vst_gradient !== null && Math.abs(r.vst_gradient || 0) >= 0.05 },
    { label: "ENTR",   desc: "Segnale localizzato",        ok: r.vst_entropy !== null && (r.vst_entropy || 1) <= 0.4 },
    { label: "PERS",   desc: "Persistente (≥3 snap)",      ok: (r.n_snapshots || 0) >= 3 },
  ];
  const score = votes.filter(v => v.ok).length;
  return { votes, score, total: votes.length };
}

function StrategiesPage() {
  const [data,        setData]      = useState(_strategiesCache?.data || null);
  const [lastFetched, setLastFetched] = useState(_strategiesCache?.timestamp || null);
  const [loading,     setLoading]   = useState(false);
  const [minScore,    setMinScore]  = useState(0.05);
  const [selected,    setSelected]  = useState(null);
  const [gexData,     setGexData]   = useState(null);
  // MP Gravity
  const [mpData,      setMpData]    = useState(null);
  const [mpLoading,   setMpLoading] = useState(false);
  const [mpMinDist,   setMpMinDist] = useState(5.0);
  const [mpSelected,  setMpSelected] = useState(null);
  // MP Gravity — PRENDO POSIZIONE
  const [mpTradeContract, setMpTradeContract] = useState(null);
  const [mpTradeLoading,  setMpTradeLoading]  = useState(false);
  const [mpShowSizing,    setMpShowSizing]    = useState(false);
  const [mpTradeSent,     setMpTradeSent]     = useState({});
  const [mpTradeError,    setMpTradeError]    = useState(null);
  const gaugeRef  = useRef(null);
  const payoffRef = useRef(null);
  const confRef   = useRef(null);
  const gexRef    = useRef(null);

  const fetchSignals = (forceRefresh = false) => {
    setLoading(true);
    api.get(`/api/strategies/live?min_score=${minScore}&refresh=${forceRefresh}`)
      .then(result => {
        const ts = new Date();
        _strategiesCache = { data: result, timestamp: ts };
        setData(result);
        setLastFetched(ts);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const fetchMpSignals = () => {
    setMpLoading(true);
    api.get(`/api/max-pain/signals?min_dist=${mpMinDist}`)
      .then(r => setMpData(r))
      .catch(() => setMpData(null))
      .finally(() => setMpLoading(false));
  };

  // MP Gravity segnala solo "BUY CALL/PUT ATM", non un contratto preciso.
  // Recupera dalla chain reale lo strike più vicino allo spot + IV corrente.
  const openMpTrade = async (r) => {
    setMpTradeError(null);
    setMpTradeLoading(true);
    try {
      const chain = await api.get(`/api/chain/${r.ticker}/${r.expiry}`);
      const strikes = chain?.strikes || [];
      if (!strikes.length) throw new Error("no strikes");
      const spot = r.spot || chain.spot || 0;
      let best = strikes[0], bestDist = Infinity;
      for (const s of strikes) {
        const d = Math.abs(s.strike - spot);
        if (d < bestDist) { bestDist = d; best = s; }
      }
      const isCall  = r.direction === "LONG";
      const legInfo = isCall ? best.call : best.put;
      const ivPct   = (legInfo && legInfo.iv) || (r.atm_iv ? r.atm_iv * 100 : 30);
      setMpTradeContract({
        ticker: r.ticker,
        strike: best.strike,
        right_type: isCall ? "C" : "P",
        expiry: r.expiry,
        spot,
        iv_market: ivPct / 100,
        direction: r.direction,
        regime: null,
        _mp: r,
      });
      setMpShowSizing(true);
    } catch (e) {
      setMpTradeError("Errore recupero contratto ATM per " + r.ticker);
    } finally {
      setMpTradeLoading(false);
    }
  };

  const handleMpTrade = async (sl_pct = 0, tp_mult = 2.0, kelly_pct = 0) => {
    const c = mpTradeContract;
    if (!c) return;
    const stratName = c.direction === "LONG" ? "Long Call" : "Long Put";
    const legData = buildStrategyLegs(stratName, c, c.spot, c.iv_market * 100);
    try {
      await api.post_json("/api/paper/trade", {
        ticker:        c.ticker,
        strategy:      stratName,
        legs:          legData?.legs || [],
        spot_at_entry: c.spot,
        iv_at_entry:   c.iv_market,
        expiry:        c.expiry,
        right_type:    c.right_type,
        regime:        c.regime,
        notes:         `MP GRAVITY ${c.direction} K=${c.strike}${c.right_type} maxpain=${c._mp?.max_pain}`,
        sl_pct, tp_mult, kelly_pct,
      });
      setMpTradeSent(prev => ({ ...prev, [`${c.ticker}_${c.expiry}`]: true }));
      setMpShowSizing(false);
      setMpTradeError(null);
    } catch (e) {
      setMpTradeError("Errore registrazione posizione");
    }
  };

  // Auto-fetch solo se cache vuota
  useEffect(() => { if (!_strategiesCache) fetchSignals(); }, []);
  useEffect(() => { fetchMpSignals(); }, [mpMinDist]);

  useEffect(() => {
    if (!selected) { setGexData(null); return; }
    api.get(`/api/gex/${selected.ticker}`).then(setGexData).catch(() => setGexData(null));
  }, [selected?.ticker]);

  useLayoutEffect(() => {
    if (!selected) return;
    const strat = recommendStrategy(selected);
    if (gaugeRef.current)  vstDrawGauge(gaugeRef.current, selected, "instant");
    if (payoffRef.current) vstDrawPayoff(payoffRef.current, selected, strat);
    if (confRef.current)   vstDrawConf(confRef.current, selected);
  }, [selected]);

  useLayoutEffect(() => {
    if (!selected || !gexRef.current) return;
    vstDrawGex(gexRef.current, gexData, selected);
  }, [gexData, selected]);

  const signals = data?.signals || [];
  const filtered = signals.filter(s => s.score >= minScore);

  const confBar = score => {
    const pct = Math.min(score / 2, 1);
    const col = pct >= 0.75 ? "#00e676" : pct >= 0.5 ? C.yellow : pct >= 0.375 ? C.accent : C.muted;
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <div style={{ width: 72, height: 6, background: `${C.border}`, borderRadius: 3, overflow: "hidden" }}>
          <div style={{ width: `${pct * 100}%`, height: "100%", background: col, borderRadius: 3 }} />
        </div>
        <span style={{ fontFamily: "JetBrains Mono", fontSize: 11, color: col, fontWeight: 700 }}>
          {typeof score === "number" ? score.toFixed(2) : score}
        </span>
      </div>
    );
  };

  // ── MP Gravity card renderer ──────────────────────────────────────────────
  const mpCard = (r, i) => {
    const MP_COLOR = "#e040fb";
    const scorePct  = Math.min((r.score || 0) / 3, 1);
    const scoreColor = scorePct >= 0.7 ? "#00e676" : scorePct >= 0.4 ? C.yellow : MP_COLOR;
    const dirColor  = r.direction === "LONG" ? C.green : C.red;
    const dirLabel  = r.direction === "LONG" ? "▲ LONG" : "▼ SHORT";
    const strategy  = r.direction === "LONG" ? "BUY CALL" : "BUY PUT";
    const distColor = r.dist_pct >= 10 ? "#00e676" : r.dist_pct >= 7 ? C.yellow : MP_COLOR;
    const mpKey     = `${r.ticker}_${r.expiry}`;
    const isSent    = !!mpTradeSent[mpKey];
    return (
      <div key={i}
        onClick={() => setMpSelected(mpSelected?.ticker === r.ticker && mpSelected?.expiry === r.expiry ? null : r)}
        style={{
          background: C.panel, border: `1px solid ${MP_COLOR}30`,
          borderLeft: `3px solid ${scoreColor}`,
          borderRadius: 6, padding: "12px 14px", cursor: "pointer",
          marginBottom: 6,
          boxShadow: mpSelected?.ticker === r.ticker && mpSelected?.expiry === r.expiry
            ? `0 0 0 1px ${MP_COLOR}60` : "none",
        }}>
        {/* Row 1: score + ticker + direction */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <div style={{ minWidth: 70 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <div style={{ width: 48, height: 5, background: C.border, borderRadius: 3, overflow: "hidden" }}>
                <div style={{ width: `${scorePct*100}%`, height: "100%", background: scoreColor, borderRadius: 3 }} />
              </div>
              <span style={{ fontFamily: "JetBrains Mono", fontSize: 10, color: scoreColor, fontWeight: 700 }}>
                {(r.score||0).toFixed(2)}
              </span>
            </div>
            <div style={{ fontFamily: "JetBrains Mono", fontSize: 7, color: C.muted, marginTop: 2 }}>σ-DIST</div>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <span style={{ fontFamily: "JetBrains Mono", fontSize: 13, color: C.accent, fontWeight: 700 }}>{r.ticker}</span>
              <span style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: dirColor, fontWeight: 700 }}>{dirLabel}</span>
              <span style={{
                fontFamily: "JetBrains Mono", fontSize: 8,
                background: `${MP_COLOR}18`, border: `1px solid ${MP_COLOR}50`,
                color: MP_COLOR, padding: "1px 5px", borderRadius: 2
              }}>{strategy}</span>
            </div>
            <div style={{ fontFamily: "JetBrains Mono", fontSize: 8, color: C.muted, marginTop: 2 }}>
              <span style={{ color: C.text }}>EXP</span> {r.expiry}
              &nbsp;·&nbsp; <span style={{ color: C.text }}>DTE</span> {r.dte}
              {r.atm_iv ? <>&nbsp;·&nbsp; <span style={{ color: C.text }}>IV</span> {(r.atm_iv*100).toFixed(0)}%</> : null}
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontFamily: "JetBrains Mono", fontSize: 13, color: distColor, fontWeight: 700 }}>
              {(r.dist_pct||0).toFixed(1)}%
            </div>
            <div style={{ fontFamily: "JetBrains Mono", fontSize: 7, color: C.muted }}>DISTANZA</div>
          </div>
        </div>
        {/* Row 2: max pain vs spot */}
        <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
          <div>
            <span style={{ fontFamily: "JetBrains Mono", fontSize: 8, color: C.muted }}>SPOT </span>
            <span style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.text }}>${(r.spot||0).toFixed(2)}</span>
          </div>
          <span style={{ fontFamily: "JetBrains Mono", fontSize: 10, color: MP_COLOR }}>→</span>
          <div>
            <span style={{ fontFamily: "JetBrains Mono", fontSize: 8, color: C.muted }}>MAX PAIN </span>
            <span style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: MP_COLOR, fontWeight: 700 }}>
              ${(r.max_pain||0).toFixed(2)}
            </span>
          </div>
          <span style={{ fontFamily: "JetBrains Mono", fontSize: 8, color: C.muted, marginLeft: "auto" }}>
            gravità {r.direction === "LONG" ? "▲" : "▼"}
          </span>
        </div>
        {/* Expanded detail */}
        {mpSelected?.ticker === r.ticker && mpSelected?.expiry === r.expiry && (
          <div style={{
            marginTop: 10, paddingTop: 10,
            borderTop: `1px solid ${C.border}`,
            fontFamily: "JetBrains Mono", fontSize: 8, color: C.muted, lineHeight: 1.7
          }}>
            <div style={{ color: MP_COLOR, fontWeight: 700, marginBottom: 4 }}>⊕ MP GRAVITY SETUP</div>
            <div>Spot {r.direction === "LONG" ? "sotto" : "sopra"} il max pain di <span style={{color:distColor}}>{(r.dist_pct||0).toFixed(2)}%</span></div>
            <div>σ-distance = <span style={{color:scoreColor}}>{(r.score||0).toFixed(3)}</span> — normalizzata per IV×√(DTE/365)</div>
            <div style={{ marginTop: 6 }}>
              <span style={{color:C.accent2}}>THESIS:</span> i market maker hanno OI concentrato su ${(r.max_pain||0).toFixed(0)}.
              Con DTE {r.dte} giorni, esiste pressione per riportare lo spot verso quel livello.
            </div>
            <div style={{ marginTop: 4 }}>
              <span style={{color:C.accent2}}>TRADE:</span> {strategy} ATM vicino a ${(r.spot||0).toFixed(0)}, DTE 21-30gg, TP 2x, SL -50%.
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); if (!isSent && !mpTradeLoading) openMpTrade(r); }}
              disabled={isSent || mpTradeLoading}
              style={{
                marginTop: 10,
                background: isSent ? "transparent" : `${MP_COLOR}20`,
                color: isSent ? C.muted : MP_COLOR,
                border: `1px solid ${isSent ? C.border : MP_COLOR + "60"}`,
                fontFamily: "JetBrains Mono", fontSize: 10, fontWeight: 700,
                padding: "8px 18px", borderRadius: 4,
                cursor: isSent || mpTradeLoading ? "default" : "pointer",
                opacity: mpTradeLoading ? 0.6 : 1,
              }}>
              {isSent ? "✅ POSIZIONE REGISTRATA" : mpTradeLoading ? "…" : "📋 PRENDO POSIZIONE"}
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <div style={{ fontFamily: "JetBrains Mono", fontSize: 18, color: C.accent2, fontWeight: 900, letterSpacing: "0.1em" }}>
            STRATEGIES
          </div>
          <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted, marginTop: 2 }}>
            CONFLUENZA INDICATORI — ogni indicatore vota YES/NO indipendentemente
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {data?.date && <span style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted }}>DATI: {data.date}</span>}
          {lastFetched && (
            <span style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted }}>
              · aggiornato {lastFetched.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
            </span>
          )}
          <span style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted, marginLeft: 8 }}>MIN SCORE:</span>
          {[0.05, 0.1, 0.15, 0.2, 0.3].map(n => (
            <button key={n} onClick={() => setMinScore(n)} style={{
              background: minScore === n ? `${C.accent2}20` : "transparent",
              border: `1px solid ${minScore === n ? C.accent2 : C.border}`,
              color: minScore === n ? C.accent2 : C.muted,
              fontFamily: "JetBrains Mono", fontSize: 10,
              padding: "4px 10px", borderRadius: 3, cursor: "pointer"
            }}>{n}</button>
          ))}
          <button onClick={() => fetchSignals(true)} style={{
            background: "transparent", border: `1px solid ${C.border}`,
            color: C.muted, fontFamily: "JetBrains Mono", fontSize: 10,
            padding: "4px 10px", borderRadius: 3, cursor: "pointer"
          }}>↺</button>
        </div>
      </div>

      {/* Legend */}
      <Panel>
        <div style={{ display: "flex", gap: 24, flexWrap: "wrap", alignItems: "center" }}>
          <span style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted }}>
            Score = Q × F × regime_weight — stessa formula del backtest walk-forward
          </span>
          {[
            { label: "Q", desc: "z_surface × iv_factor × dte_factor × snap_factor" },
            { label: "F", desc: "gain/loss ratio (TP/SL)" },
            { label: "RW", desc: "regime weight: CRISIS×1.5 HIGH_VOL×1.3 CALM×0.6" },
            { label: "PIN", desc: "trade bloccato se spot vicino a max pain" },
          ].map(v => (
            <div key={v.label} style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <span style={{ fontFamily: "JetBrains Mono", fontSize: 8,
                background: `${C.accent2}18`, border: `1px solid ${C.accent2}40`,
                color: C.accent2, padding: "1px 5px", borderRadius: 2 }}>{v.label}</span>
              <span style={{ fontFamily: "JetBrains Mono", fontSize: 8, color: C.muted }}>{v.desc}</span>
            </div>
          ))}
        </div>
      </Panel>

      {/* ── Two-column layout ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, alignItems: "start" }}>

        {/* ── LEFT: VST Signals ── */}
        <div>
          {/* Column header */}
          <div style={{
            fontFamily: "JetBrains Mono", fontSize: 10, color: C.accent2,
            letterSpacing: "0.08em", marginBottom: 10, paddingBottom: 6,
            borderBottom: `1px solid ${C.accent2}30`,
            display: "flex", justifyContent: "space-between", alignItems: "center"
          }}>
            <span>◆ VST — VOL SURFACE TENSION</span>
            <span style={{ color: C.muted, fontSize: 9 }}>{filtered.length} segnali</span>
          </div>

          {loading && <div style={{ fontFamily: "JetBrains Mono", fontSize: 11, color: C.muted }}>Caricamento...</div>}
          {!loading && filtered.length === 0 && (
            <div style={{ fontFamily: "JetBrains Mono", fontSize: 10, color: C.muted, padding: "16px 0" }}>
              Nessun segnale con score ≥ {minScore}.
            </div>
          )}

          {filtered.map((r, i) => {
            const strat = recommendStrategy(r);
            const fmtVST = v => `${v > 0 ? "+" : ""}${(v||0).toFixed(3)}`;
            const scorePct = Math.min(r.score / 0.5, 1);
            const scoreColor = scorePct >= 0.7 ? "#00e676" : scorePct >= 0.4 ? C.yellow : C.accent;
            const dirColor = r.direction === "LONG" ? C.green : C.red;
            const dirLabel = r.direction === "LONG" ? "▲ LONG" : "▼ SHORT";
            return (
              <div key={i}
                onClick={() => setSelected(r)}
                style={{
                  background: C.panel, border: `1px solid ${scoreColor}40`,
                  borderLeft: `3px solid ${scoreColor}`,
                  borderRadius: 6, padding: "12px 14px", cursor: "pointer",
                  marginBottom: 6,
                  transition: "border-color 0.15s",
                }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
                  <div style={{ minWidth: 80 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                      <div style={{ width: 52, height: 5, background: C.border, borderRadius: 3, overflow: "hidden" }}>
                        <div style={{ width: `${scorePct*100}%`, height: "100%", background: scoreColor, borderRadius: 3 }} />
                      </div>
                      <span style={{ fontFamily: "JetBrains Mono", fontSize: 10, color: scoreColor, fontWeight: 700 }}>
                        {r.score.toFixed(3)}
                      </span>
                    </div>
                    <div style={{ fontFamily: "JetBrains Mono", fontSize: 7, color: C.muted, marginTop: 2 }}>SCORE</div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <span style={{ fontFamily: "JetBrains Mono", fontSize: 13, color: C.accent, fontWeight: 700 }}>{r.ticker}</span>
                      <span style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.text }}>${r.strike}</span>
                      <span style={{
                        fontFamily: "JetBrains Mono", fontSize: 8,
                        background: r.right_type === "C" ? `${C.green}20` : `${C.red}20`,
                        border: `1px solid ${r.right_type === "C" ? C.green : C.red}40`,
                        color: r.right_type === "C" ? C.green : C.red,
                        padding: "1px 5px", borderRadius: 2
                      }}>{r.right_type === "C" ? "CALL" : "PUT"}</span>
                      <span style={{ fontFamily: "JetBrains Mono", fontSize: 8, color: C.muted }}>{r.expiry}</span>
                      <span style={{ fontFamily: "JetBrains Mono", fontSize: 8, color: dirColor, fontWeight: 700 }}>{dirLabel}</span>
                    </div>
                    <div style={{ fontFamily: "JetBrains Mono", fontSize: 8, color: C.muted, marginTop: 2 }}>
                      VST <span style={{ color: Math.abs(r.vst||0) >= 0.5 ? C.yellow : C.text }}>{fmtVST(r.vst)}</span>
                      &nbsp;·&nbsp; IVR <span style={{ color: C.text }}>{((r.iv_rank||0)*100).toFixed(0)}%</span>
                      &nbsp;·&nbsp; <span style={{ color: C.text }}>{r.regime}</span>
                      &nbsp;·&nbsp; DTE {r.dte}
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.accent2, fontWeight: 700 }}>{strat.name}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* ── RIGHT: MP Gravity Signals ── */}
        <div>
          {/* Column header */}
          <div style={{
            fontFamily: "JetBrains Mono", fontSize: 10, color: "#e040fb",
            letterSpacing: "0.08em", marginBottom: 10, paddingBottom: 6,
            borderBottom: "1px solid #e040fb30",
            display: "flex", justifyContent: "space-between", alignItems: "center"
          }}>
            <span>◆ MP GRAVITY — MAX PAIN ATTRACTION</span>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <span style={{ color: C.muted, fontSize: 8 }}>MIN DIST%:</span>
              {[4, 5, 7, 10].map(n => (
                <button key={n} onClick={() => setMpMinDist(n)} style={{
                  background: mpMinDist === n ? "#e040fb20" : "transparent",
                  border: `1px solid ${mpMinDist === n ? "#e040fb" : C.border}`,
                  color: mpMinDist === n ? "#e040fb" : C.muted,
                  fontFamily: "JetBrains Mono", fontSize: 9,
                  padding: "2px 7px", borderRadius: 3, cursor: "pointer"
                }}>{n}%</button>
              ))}
              <button onClick={fetchMpSignals} style={{
                background: "transparent", border: `1px solid ${C.border}`,
                color: C.muted, fontFamily: "JetBrains Mono", fontSize: 9,
                padding: "2px 7px", borderRadius: 3, cursor: "pointer"
              }}>↺</button>
              <span style={{ color: C.muted, fontSize: 9 }}>{(mpData?.signals||[]).length} segnali</span>
            </div>
          </div>

          {/* Legend MP */}
          <div style={{
            fontFamily: "JetBrains Mono", fontSize: 8, color: C.muted,
            marginBottom: 10, padding: "6px 10px",
            background: "#e040fb08", border: "1px solid #e040fb20", borderRadius: 4,
            lineHeight: 1.6
          }}>
            <span style={{ color: "#e040fb" }}>σ-DIST</span> = dist% / (IV × √DTE/365) · più alto = più lontano in sigma dal max pain ·
            i market maker hanno interesse a riportare il prezzo sul livello di max pain entro la scadenza
          </div>

          {mpLoading && <div style={{ fontFamily: "JetBrains Mono", fontSize: 11, color: C.muted }}>Caricamento...</div>}

          {!mpLoading && (mpData?.signals||[]).length === 0 && (
            <div style={{ fontFamily: "JetBrains Mono", fontSize: 10, color: C.muted, padding: "16px 0" }}>
              {mpData === null
                ? "Tabella max_pain_signals non ancora popolata — disponibile dal cron 05:00 di domani."
                : `Nessun segnale con distanza ≥ ${mpMinDist}%.`}
            </div>
          )}

          {(mpData?.signals||[]).map(mpCard)}
        </div>

      </div>{/* end two-column */}

      {/* Detail overlay VST — full width */}
      {selected && (
        <VSTOverlay
          selected={selected}
          mode="instant"
          gexData={gexData}
          gaugeRef={gaugeRef}
          payoffRef={payoffRef}
          confRef={confRef}
          gexRef={gexRef}
          onClose={() => setSelected(null)}
          onTrade={() => {}}
          C={C}
        />
      )}

      {mpTradeError && (
        <div style={{ fontFamily: "JetBrains Mono", fontSize: 10, color: C.red }}>
          {mpTradeError}
        </div>
      )}

      {mpShowSizing && mpTradeContract && (
        <SizingModal
          selected={mpTradeContract}
          strat={{ name: mpTradeContract.direction === "LONG" ? "Long Call" : "Long Put" }}
          onConfirm={handleMpTrade}
          onClose={() => setMpShowSizing(false)}
          C={C}
        />
      )}

    </div>
  );
}


// ─── OPPORTUNITIES — classifica combinata VST + MP GRAVITY + PAIRS ───────────
const OPP_SOURCE_META = {
  VST:        { label: "VST",        color: C.accent },
  MP_GRAVITY: { label: "MP GRAVITY", color: "#e040fb" },
  PAIRS:      { label: "PAIRS",      color: C.accent2 },
};

function OpportunitiesPage() {
  const [opps, setOpps]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [expanded, setExpanded]   = useState(null);
  const [tradeContract, setTradeContract] = useState(null);
  const [tradeLoading, setTradeLoading]   = useState(false);
  const [showSizing, setShowSizing]       = useState(false);
  const [tradeSent, setTradeSent]         = useState({});
  const [tradeError, setTradeError]       = useState(null);

  const fetchOpportunities = () => {
    setLoading(true); setError(null);
    api.get("/api/opportunities")
      .then(d => setOpps(d.opportunities || []))
      .catch(() => setError("Errore caricamento opportunità"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchOpportunities(); }, []);

  const goChart = (ticker) => window.dispatchEvent(new CustomEvent("quantos:navigate", { detail: { page: "CHART", ticker } }));
  const goPairs = () => window.dispatchEvent(new CustomEvent("quantos:navigate", { detail: { page: "PAIRS" } }));

  // MP GRAVITY segnala solo "BUY CALL/PUT ATM": recupera dalla chain reale lo strike più vicino allo spot.
  const openTrade = async (o) => {
    setTradeError(null);
    if (o.source === "VST") {
      const sig = o.detail;
      setTradeContract({
        ticker: o.ticker, strike: sig.strike, right_type: sig.right_type,
        expiry: sig.expiry, spot: sig.spot,
        iv_market: sig.iv_market ?? sig.iv ?? 0.3,
        direction: o.direction, regime: sig.regime,
        source: "VST",
      });
      setShowSizing(true);
      return;
    }
    if (o.source === "MP_GRAVITY") {
      setTradeLoading(true);
      try {
        const chain = await api.get(`/api/chain/${o.ticker}/${o.detail.expiry}`);
        const strikes = chain?.strikes || [];
        if (!strikes.length) throw new Error("no strikes");
        const spot = o.detail.spot || chain.spot || 0;
        let best = strikes[0], bestDist = Infinity;
        for (const s of strikes) {
          const d = Math.abs(s.strike - spot);
          if (d < bestDist) { bestDist = d; best = s; }
        }
        const isCall  = o.direction === "LONG";
        const legInfo = isCall ? best.call : best.put;
        const ivPct   = (legInfo && legInfo.iv) || (o.detail.atm_iv ? o.detail.atm_iv * 100 : 30);
        setTradeContract({
          ticker: o.ticker, strike: best.strike, right_type: isCall ? "C" : "P",
          expiry: o.detail.expiry, spot,
          iv_market: ivPct / 100,
          direction: o.direction, regime: null,
          source: "MP_GRAVITY",
        });
        setShowSizing(true);
      } catch (e) {
        setTradeError("Errore recupero contratto ATM per " + o.ticker);
      } finally {
        setTradeLoading(false);
      }
    }
    // PAIRS: nessun trade diretto qui — rimanda alla pagina PAIRS (apertura a 2 gambe)
  };

  const handleConfirmTrade = async (sl_pct = 0, tp_mult = 2.0, kelly_pct = 0) => {
    const c = tradeContract;
    if (!c) return;
    const stratName = c.direction === "LONG" ? "Long Call" : "Long Put";
    const legData = buildStrategyLegs(stratName, c, c.spot, c.iv_market * 100);
    try {
      await api.post_json("/api/paper/trade", {
        ticker:        c.ticker,
        strategy:      stratName,
        legs:          legData?.legs || [],
        spot_at_entry: c.spot,
        iv_at_entry:   c.iv_market,
        expiry:        c.expiry,
        right_type:    c.right_type,
        regime:        c.regime,
        notes:         `OPPORTUNITIES ${c.source} ${c.direction} K=${c.strike}${c.right_type}`,
        sl_pct, tp_mult, kelly_pct,
      });
      setTradeSent(prev => ({ ...prev, [`${c.ticker}_${c.expiry}_${c.source}`]: true }));
      setShowSizing(false);
      setTradeError(null);
    } catch (e) {
      setTradeError("Errore registrazione posizione");
    }
  };

  const weightBadge = (w) => {
    if (w >= 1.1) return { icon: "🟢", color: C.green,  label: "storico buono" };
    if (w <  0.8) return { icon: "🔴", color: C.red,    label: "storico debole" };
    return               { icon: "⚪", color: C.muted,  label: "senza storico / neutro" };
  };

  return (
    <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ fontFamily: "JetBrains Mono", fontSize: 20, fontWeight: 900, color: C.text }}>
            📊 OPPORTUNITIES
          </div>
          <div style={{ fontFamily: "JetBrains Mono", fontSize: 11, color: C.muted, marginTop: 4 }}>
            Classifica combinata su tutto l'universo ticker — VST + MP GRAVITY + PAIRS, pesata per bontà storica del ticker (backtest walk-forward).
          </div>
        </div>
        <button onClick={fetchOpportunities} disabled={loading}
          style={{
            background: `${C.accent}20`, color: C.accent, border: `1px solid ${C.accent}60`,
            fontFamily: "JetBrains Mono", fontSize: 11, fontWeight: 700,
            padding: "8px 16px", borderRadius: 4, cursor: loading ? "default" : "pointer",
          }}>
          {loading ? "…" : "↻ AGGIORNA"}
        </button>
      </div>

      <div style={{ fontFamily: "JetBrains Mono", fontSize: 10, color: C.muted, background: `${C.yellow}10`, border: `1px solid ${C.yellow}40`, borderRadius: 4, padding: "8px 12px" }}>
        ⚠ Il peso storico riflette oggi solo il backtest della strategia VST/vol-surface (l'unica simulata dal motore). Per MP GRAVITY e PAIRS è un proxy generico di qualità del ticker, non una verifica della strategia specifica.
      </div>

      {error && (
        <div style={{ fontFamily: "JetBrains Mono", fontSize: 11, color: C.red }}>{error}</div>
      )}
      {tradeError && (
        <div style={{ fontFamily: "JetBrains Mono", fontSize: 10, color: C.red }}>{tradeError}</div>
      )}

      {loading ? (
        <div style={{ fontFamily: "JetBrains Mono", fontSize: 12, color: C.muted, padding: 40, textAlign: "center" }}>Caricamento…</div>
      ) : opps.length === 0 ? (
        <div style={{ fontFamily: "JetBrains Mono", fontSize: 12, color: C.muted, padding: 40, textAlign: "center" }}>Nessuna opportunità al momento.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {opps.map((o, i) => {
            const meta   = OPP_SOURCE_META[o.source] || { label: o.source, color: C.text };
            const wb     = weightBadge(o.historical_weight);
            const key    = `${o.ticker}_${o.ticker_b || ""}_${o.source}_${i}`;
            const sentKey = `${o.ticker}_${o.detail?.expiry}_${o.source}`;
            const isSent = !!tradeSent[sentKey];
            const isOpen = expanded === key;
            return (
              <div key={key}
                onClick={() => setExpanded(isOpen ? null : key)}
                style={{
                  background: C.panel, border: `1px solid ${isOpen ? meta.color + "60" : C.border}`,
                  borderRadius: 6, padding: "10px 14px", cursor: "pointer",
                }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  <span style={{
                    fontFamily: "JetBrains Mono", fontSize: 9, fontWeight: 700,
                    color: meta.color, border: `1px solid ${meta.color}60`, borderRadius: 3,
                    padding: "2px 6px", minWidth: 68, textAlign: "center",
                  }}>{meta.label}</span>

                  <span
                    onClick={e => { e.stopPropagation(); goChart(o.ticker); }}
                    style={{ fontFamily: "JetBrains Mono", fontSize: 13, fontWeight: 700, color: C.text, cursor: "pointer", textDecoration: "underline" }}>
                    {o.ticker}{o.ticker_b ? ` / ${o.ticker_b}` : ""}
                  </span>

                  <span style={{ fontFamily: "JetBrains Mono", fontSize: 10, color: o.direction?.includes("SHORT") || o.direction?.includes("PUT") ? C.red : C.green }}>
                    {o.direction}
                  </span>

                  <span style={{ marginLeft: "auto", fontFamily: "JetBrains Mono", fontSize: 10, color: C.muted }}>
                    score <b style={{ color: C.text }}>{o.final_score.toFixed(3)}</b>
                  </span>

                  <span title={wb.label} style={{ fontFamily: "JetBrains Mono", fontSize: 10, color: wb.color }}>
                    {wb.icon} {o.historical_weight.toFixed(2)}×
                  </span>
                </div>

                {isOpen && (
                  <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${C.border}`, fontFamily: "JetBrains Mono", fontSize: 10, color: C.muted, display: "flex", flexDirection: "column", gap: 4 }}>
                    <div>raw_score: {o.raw_score} · norm_score: {o.norm_score} · peso storico: {o.historical_weight}</div>
                    {o.source === "MP_GRAVITY" && (
                      <div>spot ${o.detail.spot?.toFixed(2)} → max pain ${o.detail.max_pain?.toFixed(2)} ({o.detail.dist_pct?.toFixed(1)}% dist) · DTE {o.detail.dte} · ATM IV {(o.detail.atm_iv * 100)?.toFixed(1)}%</div>
                    )}
                    {o.source === "VST" && (
                      <div>K={o.detail.strike}{o.detail.right_type} · scad. {o.detail.expiry} · regime {o.detail.regime} · badge {o.detail.badge}</div>
                    )}
                    {o.source === "PAIRS" && (
                      <div>corr20d {o.detail.corr_20d?.toFixed(2)} · z_spread {o.detail.z_spread?.toFixed(2)} · z_a {o.detail.z_a?.toFixed(2)} · z_b {o.detail.z_b?.toFixed(2)} · peso gambe {(o.detail.weight_a*100)?.toFixed(0)}% / {(o.detail.weight_b*100)?.toFixed(0)}%</div>
                    )}

                    {o.source === "PAIRS" ? (
                      <button onClick={e => { e.stopPropagation(); goPairs(); }}
                        style={{
                          marginTop: 6, alignSelf: "flex-start",
                          background: `${meta.color}20`, color: meta.color, border: `1px solid ${meta.color}60`,
                          fontFamily: "JetBrains Mono", fontSize: 10, fontWeight: 700,
                          padding: "6px 14px", borderRadius: 4, cursor: "pointer",
                        }}>
                        ↗ APRI IN PAIRS (2 gambe)
                      </button>
                    ) : (
                      <button
                        onClick={e => { e.stopPropagation(); if (!isSent && !tradeLoading) openTrade(o); }}
                        disabled={isSent || tradeLoading}
                        style={{
                          marginTop: 6, alignSelf: "flex-start",
                          background: isSent ? "transparent" : `${meta.color}20`,
                          color: isSent ? C.muted : meta.color,
                          border: `1px solid ${isSent ? C.border : meta.color + "60"}`,
                          fontFamily: "JetBrains Mono", fontSize: 10, fontWeight: 700,
                          padding: "6px 14px", borderRadius: 4,
                          cursor: isSent || tradeLoading ? "default" : "pointer",
                          opacity: tradeLoading ? 0.6 : 1,
                        }}>
                        {isSent ? "✅ POSIZIONE REGISTRATA" : tradeLoading ? "…" : "📋 PRENDO POSIZIONE"}
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showSizing && tradeContract && (
        <SizingModal
          selected={tradeContract}
          strat={{ name: tradeContract.direction === "LONG" ? "Long Call" : "Long Put" }}
          onConfirm={handleConfirmTrade}
          onClose={() => setShowSizing(false)}
          C={C}
        />
      )}
    </div>
  );
}


// ─── AUTO-STRATEGY — wizard capitale/rischio/orizzonte/obiettivo + esecuzione automatica ──
const RISK_OPTS      = [["LOW","BASSO"],["MEDIUM","MEDIO"],["HIGH","ALTO"]];
const HORIZON_OPTS   = [["WEEKS","SETTIMANE"],["MONTHS","MESI"],["LONG_TERM","OLTRE 1 ANNO"]];
const OBJECTIVE_OPTS = [["PROFIT","PROFITTO"],["BALANCED","BILANCIATO"],["RISK_ADJUSTED","RISK-ADJUSTED"]];
const STRAT_STATUS_COLOR = { ACTIVE: C.green, PAUSED: C.yellow, STOPPED: C.red };

function ChoiceGroup({ label, value, options, onChange }) {
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
      <span style={{ fontSize:10, color:C.muted, letterSpacing:1 }}>{label}</span>
      <div style={{ display:"flex", gap:8 }}>
        {options.map(([v, lbl]) => (
          <button key={v} onClick={() => onChange(v)}
            style={{
              padding: "8px 16px", borderRadius: 4, cursor: "pointer",
              fontFamily: "JetBrains Mono", fontSize: 12, fontWeight: 700,
              background: value === v ? C.accent : "transparent",
              color: value === v ? "#000" : C.muted,
              border: `1px solid ${value === v ? C.accent : C.border}`,
            }}>{lbl}</button>
        ))}
      </div>
    </div>
  );
}

function StrategyBuilderPage() {
  const [capital, setCapital]     = useState(10000);
  const [risk, setRisk]           = useState("MEDIUM");
  const [horizon, setHorizon]     = useState("MONTHS");
  const [objective, setObjective] = useState("BALANCED");
  const [name, setName]           = useState("");
  const [preview, setPreview]     = useState(null);
  const [btResult, setBtResult]   = useState(null);
  const [btLoading, setBtLoading] = useState(false);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState(null);
  const [strategies, setStrategies] = useState([]);

  const fetchStrategies = () => {
    api.get("/api/strategies").then(d => setStrategies(d.strategies || [])).catch(() => {});
  };
  useEffect(() => { fetchStrategies(); }, []);

  useEffect(() => {
    setBtResult(null); setError(null);
    api.post_json("/api/strategies/preview", { risk_level: risk, horizon, objective })
      .then(setPreview).catch(() => setPreview(null));
  }, [risk, horizon, objective]);

  const runBacktest = async () => {
    setBtLoading(true); setError(null); setBtResult(null);
    try {
      const r = await api.post_json("/api/strategies/backtest", {
        capital: parseFloat(capital) || 10000, risk_level: risk, horizon, objective,
      });
      if (r.detail) throw new Error(r.detail);
      setBtResult(r);
    } catch (e) {
      setError(e.message || "Errore backtest");
    } finally { setBtLoading(false); }
  };

  const activate = async () => {
    if (!btResult) return;
    setSaving(true); setError(null);
    try {
      const r = await api.post_json("/api/strategies", {
        name: name || `${risk}/${horizon}/${objective}`,
        capital: parseFloat(capital) || 10000,
        risk_level: risk, horizon, objective,
        backtest_snapshot: btResult.summary,
      });
      if (r.detail) throw new Error(r.detail);
      setBtResult(null); setName("");
      fetchStrategies();
    } catch (e) {
      setError(e.message || "Errore attivazione strategia");
    } finally { setSaving(false); }
  };

  const setStatus = async (id, status) => {
    await api.patch(`/api/strategies/${id}/status`, { status });
    fetchStrategies();
  };

  const pnlColor = v => v >= 0 ? C.green : C.red;

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: "0 auto", display:"flex", flexDirection:"column", gap: 20 }}>
      <div>
        <Label color={C.accent} size={18}>AUTO-STRATEGY</Label>
        <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
          Capitale, rischio, orizzonte, obiettivo — il sistema traduce il profilo in parametri
          tecnici, lo backtesta, e (se attivato) lo esegue in automatico sul paper trading.
        </div>
      </div>

      <div style={{ fontFamily:"JetBrains Mono", fontSize: 10, color: C.muted, background: `${C.yellow}10`, border: `1px solid ${C.yellow}40`, borderRadius: 4, padding: "8px 12px" }}>
        ⚠ Esecuzione automatica in <b>paper trading</b> (denaro simulato), senza conferma manuale
        per apertura/rotazione. Le posizioni PAIRS restano sempre gestite manualmente dalla pagina PAIRS.
      </div>

      <Panel>
        <Label>1. PROFILO</Label>
        <div style={{ display:"flex", flexDirection:"column", gap:16, marginTop: 10 }}>
          <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
            <span style={{ fontSize:10, color:C.muted, letterSpacing:1 }}>CAPITALE (€)</span>
            <input type="number" min={100} step={100} value={capital}
              onChange={e => setCapital(e.target.value)}
              style={{ width:160, background:C.bg, border:`1px solid ${C.border}`, color:C.text,
                       padding:"8px 10px", borderRadius:4, fontFamily:"JetBrains Mono", fontSize:13 }} />
          </div>
          <ChoiceGroup label="RISCHIO AMMISSIBILE" value={risk} options={RISK_OPTS} onChange={setRisk} />
          <ChoiceGroup label="ORIZZONTE" value={horizon} options={HORIZON_OPTS} onChange={setHorizon} />
          <ChoiceGroup label="OBIETTIVO" value={objective} options={OBJECTIVE_OPTS} onChange={setObjective} />
        </div>
      </Panel>

      {preview && (
        <Panel>
          <Label>2. PARAMETRI RISULTANTI (SOLA LETTURA)</Label>
          <div style={{ fontFamily:"JetBrains Mono", fontSize: 10, color: C.yellow, marginTop: 8, marginBottom: 8 }}>
            ⚠ La rotazione è sempre attiva in AUTO-STRATEGY: nessuna delle combinazioni possibili,
            baseline inclusa, è stata validata individualmente con la rotazione accesa. Il backtest
            qui sotto usa esattamente questi parametri — nessuna approssimazione — quindi guardalo
            sempre prima di attivare.
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))", gap:10, marginTop: 8 }}>
            {[
              ["Budget %", preview.budget_pct + "%"],
              ["Max trade simultanei", preview.max_trades],
              ["SL long (x)", preview.sl_mult],
              ["SL short (x premio)", preview.sl_short_mult],
              ["Kelly fraction", preview.kelly_fraction],
              ["Ruota solo se in utile", preview.rotate_only_if_profitable ? "SI" : "NO"],
              ["DTE min/max", preview.dte_min + " / " + preview.dte_max],
              ["Hold minimo (gg)", preview.rotate_min_hold_days],
              ["Soglia rotazione (x)", preview.rotate_threshold],
              ["Take Profit (x)", preview.tp_mult],
            ].map(([k,v]) => (
              <div key={k} style={{ fontFamily:"JetBrains Mono", fontSize: 11 }}>
                <div style={{ color: C.muted, fontSize: 9 }}>{k}</div>
                <div style={{ color: C.text, fontWeight: 700 }}>{v}</div>
              </div>
            ))}
          </div>
        </Panel>
      )}

      <div style={{ display:"flex", gap:12 }}>
        <button onClick={runBacktest} disabled={btLoading}
          style={{ padding:"10px 24px", background:C.accent, color:"#000", border:"none",
                   borderRadius:4, fontFamily:"JetBrains Mono", fontSize:13, fontWeight:700,
                   cursor: btLoading ? "default" : "pointer" }}>
          {btLoading ? "CALCOLO..." : "▶ BACKTESTA QUESTA STRATEGIA"}
        </button>
      </div>

      {error && (
        <div style={{ color: C.red, fontFamily:"JetBrains Mono", fontSize: 12 }}>{error}</div>
      )}

      {btResult && (
        <Panel>
          <Label>3. RISULTATO BACKTEST (2024-04-08 → oggi)</Label>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))", gap:12, margin:"14px 0" }}>
            {[
              { label:"CAPITALE FINALE", value:"€"+btResult.summary.final_equity.toLocaleString("it-IT"), color:pnlColor(btResult.summary.total_pnl) },
              { label:"P&L TOTALE",      value:(btResult.summary.total_pnl>=0?"+":"")+"€"+btResult.summary.total_pnl.toLocaleString("it-IT"), color:pnlColor(btResult.summary.total_pnl) },
              { label:"WIN RATE",        value:btResult.summary.win_rate+"%", color:btResult.summary.win_rate>=50?C.green:C.red },
              { label:"SHARPE",          value:(btResult.summary.sharpe??0).toFixed(2), color:(btResult.summary.sharpe??0)>=0?C.green:C.red },
              { label:"MAX DRAWDOWN",    value:(btResult.summary.max_drawdown_pct??0).toFixed(1)+"%", color:C.red },
              { label:"ROTAZIONI",       value:btResult.summary.n_rotations??0, color:C.accent2 },
            ].map(m => (
              <div key={m.label} style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:4, padding:10 }}>
                <div style={{ fontSize:9, color:C.muted, marginBottom:4 }}>{m.label}</div>
                <div style={{ fontSize:16, fontWeight:700, color:m.color }}>{m.value}</div>
              </div>
            ))}
          </div>
          <AreaChart width={800} height={180} data={btResult.equity_curve} style={{ maxWidth:"100%" }}>
            <defs>
              <linearGradient id="stratEqGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={C.accent} stopOpacity={0.3}/>
                <stop offset="95%" stopColor={C.accent} stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
            <XAxis dataKey="date" tick={{ fontSize:9, fill:C.muted }} minTickGap={40} />
            <YAxis tick={{ fontSize:9, fill:C.muted }} />
            <Tooltip contentStyle={{ background:C.panel, border:`1px solid ${C.border}`, fontSize:11 }} />
            <Area type="monotone" dataKey="equity" stroke={C.accent} fill="url(#stratEqGrad)" />
          </AreaChart>

          <div style={{ display:"flex", gap:12, marginTop:16, alignItems:"center" }}>
            <input placeholder="Nome strategia (opzionale)" value={name} onChange={e=>setName(e.target.value)}
              style={{ flex:1, maxWidth:300, background:C.bg, border:`1px solid ${C.border}`, color:C.text,
                       padding:"8px 10px", borderRadius:4, fontFamily:"JetBrains Mono", fontSize:12 }} />
            <button onClick={activate} disabled={saving}
              style={{ padding:"10px 24px", background:C.green, color:"#000", border:"none",
                       borderRadius:4, fontFamily:"JetBrains Mono", fontSize:13, fontWeight:700,
                       cursor: saving ? "default" : "pointer" }}>
              {saving ? "..." : "✓ ATTIVA STRATEGIA"}
            </button>
          </div>
        </Panel>
      )}

      <Panel>
        <Label>LE MIE STRATEGIE</Label>
        {strategies.length === 0 ? (
          <div style={{ color:C.muted, fontFamily:"JetBrains Mono", fontSize:12, marginTop:8 }}>Nessuna strategia attivata.</div>
        ) : (
          <div style={{ overflowX:"auto", marginTop:10 }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontFamily:"JetBrains Mono", fontSize:11 }}>
              <thead>
                <tr style={{ color:C.muted, textAlign:"left" }}>
                  <th style={{ padding:8 }}>NOME</th>
                  <th style={{ padding:8 }}>PROFILO</th>
                  <th style={{ padding:8 }}>CAPITALE</th>
                  <th style={{ padding:8 }}>EQUITY</th>
                  <th style={{ padding:8 }}>POSIZIONI APERTE</th>
                  <th style={{ padding:8 }}>STATO</th>
                  <th style={{ padding:8 }}>AZIONI</th>
                </tr>
              </thead>
              <tbody>
                {strategies.map(s => (
                  <tr key={s.id} style={{ borderTop:`1px solid ${C.border}` }}>
                    <td style={{ padding:8, color:C.text }}>{s.name}</td>
                    <td style={{ padding:8, color:C.muted }}>{s.risk_level}/{s.horizon}/{s.objective}</td>
                    <td style={{ padding:8 }}>€{Number(s.capital).toLocaleString("it-IT")}</td>
                    <td style={{ padding:8, color:pnlColor(Number(s.current_equity)-Number(s.capital)) }}>€{Number(s.current_equity).toLocaleString("it-IT")}</td>
                    <td style={{ padding:8 }}>{s.open_positions}</td>
                    <td style={{ padding:8, color: STRAT_STATUS_COLOR[s.status] || C.muted, fontWeight:700 }}>{s.status}</td>
                    <td style={{ padding:8, display:"flex", gap:6 }}>
                      {s.status !== "PAUSED" && <button onClick={()=>setStatus(s.id,"PAUSED")} style={{ background:"transparent", border:`1px solid ${C.yellow}`, color:C.yellow, borderRadius:4, padding:"4px 8px", cursor:"pointer", fontFamily:"JetBrains Mono", fontSize:10 }}>⏸ PAUSA</button>}
                      {s.status === "PAUSED" && <button onClick={()=>setStatus(s.id,"ACTIVE")} style={{ background:"transparent", border:`1px solid ${C.green}`, color:C.green, borderRadius:4, padding:"4px 8px", cursor:"pointer", fontFamily:"JetBrains Mono", fontSize:10 }}>▶ RIPRENDI</button>}
                      {s.status !== "STOPPED" && <button onClick={()=>setStatus(s.id,"STOPPED")} style={{ background:"transparent", border:`1px solid ${C.red}`, color:C.red, borderRadius:4, padding:"4px 8px", cursor:"pointer", fontFamily:"JetBrains Mono", fontSize:10 }}>⏹ FERMA</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}


// ─── PAIRS SIGNAL MODAL ───────────────────────────────────────────────────────
function PairsSignalModal({ signal, onClose }) {
  const [data, setData]     = useState(null);
  const [loading, setLoad]  = useState(true);
  const canvasRef           = useRef(null);
  const canvas2Ref          = useRef(null);

  useEffect(() => {
    setLoad(true);
    api.get(`/api/pairs/chart/${signal.ticker_a}/${signal.ticker_b}?days=60`)
      .then(d => { setData(d); setLoad(false); })
      .catch(() => setLoad(false));
  }, [signal.ticker_a, signal.ticker_b]);

  // Disegna grafico prezzi normalizzati
  useEffect(() => {
    if (!data || !canvasRef.current) return;
    const cv = canvasRef.current;
    const ctx = cv.getContext("2d");
    const W = cv.width, H = cv.height;
    const pad = { t: 24, r: 16, b: 32, l: 48 };
    const iW = W - pad.l - pad.r, iH = H - pad.t - pad.b;
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = "#111"; ctx.fillRect(0, 0, W, H);

    const normA = data.norm_a.filter(v => v !== null);
    const normB = data.norm_b.filter(v => v !== null);
    const allV  = [...normA, ...normB];
    const yMin  = Math.min(...allV) - 1, yMax = Math.max(...allV) + 1;
    const n     = data.norm_a.length;

    const toX = i => pad.l + (i / (n - 1)) * iW;
    const toY = v => pad.t + iH - ((v - yMin) / (yMax - yMin)) * iH;

    // Griglia
    ctx.strokeStyle = "#333"; ctx.lineWidth = 0.5;
    [-10, -5, 0, 5, 10].forEach(v => {
      if (v < yMin || v > yMax) return;
      const y = toY(v);
      ctx.beginPath(); ctx.moveTo(pad.l, y); ctx.lineTo(W - pad.r, y); ctx.stroke();
      ctx.fillStyle = "#555"; ctx.font = "9px JetBrains Mono";
      ctx.fillText(`${v > 0 ? "+" : ""}${v}%`, 2, y + 3);
    });
    // Zero line
    ctx.strokeStyle = "#444"; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(pad.l, toY(0)); ctx.lineTo(W - pad.r, toY(0)); ctx.stroke();

    // Linea A
    ctx.strokeStyle = "#ff8c00"; ctx.lineWidth = 2; ctx.beginPath();
    data.norm_a.forEach((v, i) => { if (v === null) return; i === 0 || data.norm_a[i-1] === null ? ctx.moveTo(toX(i), toY(v)) : ctx.lineTo(toX(i), toY(v)); });
    ctx.stroke();

    // Linea B
    ctx.strokeStyle = "#00e676"; ctx.lineWidth = 2; ctx.beginPath();
    data.norm_b.forEach((v, i) => { if (v === null) return; i === 0 || data.norm_b[i-1] === null ? ctx.moveTo(toX(i), toY(v)) : ctx.lineTo(toX(i), toY(v)); });
    ctx.stroke();

    // Labels asse X (ogni ~10 giorni)
    ctx.fillStyle = "#555"; ctx.font = "8px JetBrains Mono";
    data.dates.forEach((d, i) => {
      if (i % 10 === 0) ctx.fillText(d.slice(5), toX(i) - 12, H - 4);
    });

    // Legenda
    ctx.fillStyle = "#ff8c00"; ctx.font = "bold 10px JetBrains Mono";
    ctx.fillText(signal.ticker_a, pad.l, 14);
    ctx.fillStyle = "#00e676";
    ctx.fillText(signal.ticker_b, pad.l + 60, 14);
    ctx.fillStyle = "#555"; ctx.font = "9px JetBrains Mono";
    ctx.fillText("β=" + data.beta, pad.l + 120, 14);
  }, [data]);

  // Disegna grafico z-score spread
  useEffect(() => {
    if (!data || !canvas2Ref.current) return;
    const cv = canvas2Ref.current;
    const ctx = cv.getContext("2d");
    const W = cv.width, H = cv.height;
    const pad = { t: 20, r: 16, b: 28, l: 48 };
    const iW = W - pad.l - pad.r, iH = H - pad.t - pad.b;
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = "#111"; ctx.fillRect(0, 0, W, H);

    const zs  = data.z_spread.filter(v => v !== null);
    const za  = data.z_a.filter(v => v !== null);
    const zb  = data.z_b.filter(v => v !== null);
    const all = [...zs, ...za, ...zb];
    const yMin = Math.min(...all) - 0.3, yMax = Math.max(...all) + 0.3;
    const n   = data.z_spread.length;

    const toX = i => pad.l + (i / (n - 1)) * iW;
    const toY = v => pad.t + iH - ((v - yMin) / (yMax - yMin)) * iH;

    // Soglie ±2.5σ
    [-2.5, 0, 2.5].forEach(v => {
      if (v < yMin || v > yMax) return;
      ctx.strokeStyle = v === 0 ? "#444" : "#ff440066";
      ctx.lineWidth   = v === 0 ? 1 : 1.5;
      ctx.setLineDash(v === 0 ? [] : [4, 3]);
      ctx.beginPath(); ctx.moveTo(pad.l, toY(v)); ctx.lineTo(W - pad.r, toY(v)); ctx.stroke();
      ctx.setLineDash([]);
      if (v !== 0) {
        ctx.fillStyle = "#ff4400"; ctx.font = "8px JetBrains Mono";
        ctx.fillText(`${v > 0 ? "+" : ""}${v}σ`, 2, toY(v) + 3);
      }
      ctx.fillStyle = "#555"; ctx.font = "8px JetBrains Mono";
      if (v === 0) ctx.fillText("0", 2, toY(v) + 3);
    });

    // z_spread (bianco principale)
    ctx.strokeStyle = "#ffffff"; ctx.lineWidth = 2; ctx.beginPath();
    data.z_spread.forEach((v, i) => { if (v === null) return; i === 0 || data.z_spread[i-1] === null ? ctx.moveTo(toX(i), toY(v)) : ctx.lineTo(toX(i), toY(v)); });
    ctx.stroke();

    // z_a (arancio tratteggiato)
    ctx.strokeStyle = "#ff8c00aa"; ctx.lineWidth = 1.5; ctx.setLineDash([3,2]); ctx.beginPath();
    data.z_a.forEach((v, i) => { if (v === null) return; i === 0 || data.z_a[i-1] === null ? ctx.moveTo(toX(i), toY(v)) : ctx.lineTo(toX(i), toY(v)); });
    ctx.stroke(); ctx.setLineDash([]);

    // z_b (verde tratteggiato)
    ctx.strokeStyle = "#00e67699"; ctx.lineWidth = 1.5; ctx.setLineDash([3,2]); ctx.beginPath();
    data.z_b.forEach((v, i) => { if (v === null) return; i === 0 || data.z_b[i-1] === null ? ctx.moveTo(toX(i), toY(v)) : ctx.lineTo(toX(i), toY(v)); });
    ctx.stroke(); ctx.setLineDash([]);

    // Legenda
    ctx.font = "bold 9px JetBrains Mono";
    ctx.fillStyle = "#fff";    ctx.fillText("z SPREAD", pad.l, 14);
    ctx.fillStyle = "#ff8c00"; ctx.fillText(`z ${signal.ticker_a}`, pad.l + 72, 14);
    ctx.fillStyle = "#00e676"; ctx.fillText(`z ${signal.ticker_b}`, pad.l + 72 + 60, 14);

    // Date asse X
    ctx.fillStyle = "#555"; ctx.font = "8px JetBrains Mono";
    data.dates.forEach((d, i) => { if (i % 10 === 0) ctx.fillText(d.slice(5), toX(i) - 12, H - 4); });
  }, [data]);

  const dirColor = d => d === "PUT" ? "#ff4444" : "#00e676";
  const Kv = ({ label, val, color }) => (
    <div style={{ display:"flex", justifyContent:"space-between", fontFamily:"JetBrains Mono", fontSize:12, marginBottom:8 }}>
      <span style={{ color:C.muted }}>{label}</span>
      <span style={{ color: color || C.text, fontWeight:700 }}>{val ?? "—"}</span>
    </div>
  );

  const LegBox = ({ leg, ticker, weight, spot }) => {
    if (!leg) return (
      <div style={{ background:C.dim, border:`1px solid ${C.border}`, borderRadius:6, padding:16 }}>
        <div style={{ fontFamily:"JetBrains Mono", fontSize:11, color:C.muted }}>
          {ticker} — nessun dato opzioni live
        </div>
      </div>
    );
    return (
      <div style={{ background:C.dim, border:`1px solid ${dirColor(leg.direction)}44`, borderRadius:6, padding:16 }}>
        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:12 }}>
          <span style={{ fontFamily:"JetBrains Mono", fontSize:20, fontWeight:700, color:C.accent }}>{ticker}</span>
          <span style={{ fontFamily:"JetBrains Mono", fontSize:18, fontWeight:700, color:dirColor(leg.direction) }}>{leg.direction}</span>
        </div>
        <Kv label="Strike"   val={`$${leg.strike}`} />
        <Kv label="Scadenza" val={leg.expiry ? `${leg.expiry.slice(0,4)}-${leg.expiry.slice(4,6)}-${leg.expiry.slice(6)}` : null} />
        <Kv label="DTE"      val={leg.dte ? `${leg.dte} giorni` : null} />
        <Kv label="Premio"   val={leg.premium ? `$${leg.premium}` : null} color={C.yellow} />
        <Kv label="Delta"    val={leg.delta} />
        <Kv label="IV"       val={leg.iv ? `${leg.iv}%` : null} />
        <Kv label="Spot now" val={`$${spot.toFixed(2)}`} color={C.muted} />
        <div style={{ marginTop:10, height:6, background:"#333", borderRadius:3 }}>
          <div style={{ width:`${Math.round(weight*100)}%`, height:"100%", background:dirColor(leg.direction), borderRadius:3 }} />
        </div>
        <div style={{ fontFamily:"JetBrains Mono", fontSize:9, color:C.muted, marginTop:4 }}>
          Budget: {Math.round(weight*100)}%
        </div>
      </div>
    );
  };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.92)", zIndex:10000,
      display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ width:"min(1100px,96vw)", maxHeight:"92vh", overflowY:"auto",
        background:C.panel, border:`1px solid ${C.border}`, borderRadius:10,
        display:"flex", flexDirection:"column", gap:0 }}>

        {/* Header */}
        <div style={{ padding:"18px 24px", borderBottom:`1px solid ${C.border}`,
          display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div>
            <span style={{ fontFamily:"JetBrains Mono", fontSize:22, fontWeight:700, color:C.accent }}>
              {signal.ticker_a}
            </span>
            <span style={{ fontFamily:"JetBrains Mono", fontSize:16, color:C.muted, margin:"0 10px" }}>×</span>
            <span style={{ fontFamily:"JetBrains Mono", fontSize:22, fontWeight:700, color:C.text }}>
              {signal.ticker_b}
            </span>
            <span style={{ fontFamily:"JetBrains Mono", fontSize:11, color:C.muted, marginLeft:18 }}>
              ρ {signal.corr_20d?.toFixed(2)} · z spread {signal.z_spread > 0 ? "+" : ""}{signal.z_spread?.toFixed(2)}σ · score {signal.score?.toFixed(2)}
            </span>
          </div>
          <button onClick={onClose} style={{ background:"transparent", border:"none",
            color:C.muted, fontSize:22, cursor:"pointer", fontFamily:"JetBrains Mono" }}>×</button>
        </div>

        {loading ? (
          <div style={{ padding:60, textAlign:"center", fontFamily:"JetBrains Mono", fontSize:12, color:C.muted }}>
            Caricamento dati...
          </div>
        ) : !data ? (
          <div style={{ padding:60, textAlign:"center", fontFamily:"JetBrains Mono", fontSize:12, color:C.muted }}>
            Dati non disponibili
          </div>
        ) : (
          <div style={{ padding:20, display:"flex", flexDirection:"column", gap:16 }}>

            {/* Grafici */}
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              <div style={{ fontFamily:"JetBrains Mono", fontSize:9, color:C.muted, letterSpacing:"0.1em" }}>
                PREZZI NORMALIZZATI (% change 60d)
              </div>
              <canvas ref={canvasRef} width={1050} height={180}
                style={{ width:"100%", height:180, borderRadius:4, border:`1px solid ${C.border}30` }} />
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              <div style={{ fontFamily:"JetBrains Mono", fontSize:9, color:C.muted, letterSpacing:"0.1em" }}>
                Z-SCORE — SPREAD (bianco) · {signal.ticker_a} (arancio) · {signal.ticker_b} (verde) · soglia ±2.5σ
              </div>
              <canvas ref={canvas2Ref} width={1050} height={150}
                style={{ width:"100%", height:150, borderRadius:4, border:`1px solid ${C.border}30` }} />
            </div>

            {/* Legs */}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
              <LegBox
                leg={data.leg_a} ticker={signal.ticker_a}
                weight={data.signal?.weight_a ?? signal.weight_a}
                spot={data.prices_a[data.prices_a.length-1]}
              />
              <LegBox
                leg={data.leg_b} ticker={signal.ticker_b}
                weight={data.signal?.weight_b ?? signal.weight_b}
                spot={data.prices_b[data.prices_b.length-1]}
              />
            </div>

            {/* Exit */}
            <div style={{ background:C.dim, border:`1px solid ${C.border}`, borderRadius:6, padding:14 }}>
              <div style={{ fontFamily:"JetBrains Mono", fontSize:9, color:C.muted, letterSpacing:"0.1em", marginBottom:10 }}>
                STRATEGIA USCITA
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12 }}>
                {[
                  { label:"Target uscita",   val:"z spread < 0.5σ", color:C.green  },
                  { label:"Take Profit",     val:"Premio ×2",        color:C.yellow },
                  { label:"Stop Loss",       val:"Premio -50%",      color:C.red    },
                ].map(({ label, val, color }) => (
                  <div key={label} style={{ textAlign:"center" }}>
                    <div style={{ fontFamily:"JetBrains Mono", fontSize:9, color:C.muted, marginBottom:4 }}>{label}</div>
                    <div style={{ fontFamily:"JetBrains Mono", fontSize:13, fontWeight:700, color }}>{val}</div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        )}
      </div>
    </div>
  );
}

// ─── PAIRS MAP ────────────────────────────────────────────────────────────────
const DEFAULT_PAIRS_TICKERS = [
  "AAPL","MSFT","NVDA","AMZN","GOOGL","META","TSLA","AMD","AVGO","QCOM",
  "JPM","BAC","GS","V","MA","JNJ","UNH","XOM","CVX","WMT",
  "HD","CAT","NFLX","INTC","CRM",
];

function PairsPage() {
  const [selected,      setSelected]      = useState(DEFAULT_PAIRS_TICKERS);
  const [heatmap,       setHeatmap]       = useState(null);
  const [listData,      setListData]      = useState(null);
  const [loading,       setLoading]       = useState(false);
  const [tab,           setTab]           = useState("heatmap");
  const [addSearch,     setAddSearch]     = useState("");
  const [allTickers,    setAllTickers]    = useState([]);
  const [minCorr,       setMinCorr]       = useState(0.7);
  const [listSearch,    setListSearch]    = useState("");
  const [pairsSignals,  setPairsSignals]  = useState(null);
  const [sigLoading,    setSigLoading]    = useState(false);
  const [minZ,          setMinZ]          = useState(2.5);
  const [modalSignal,   setModalSignal]   = useState(null);

  const nav = page => window.dispatchEvent(new CustomEvent("quantos:navigate", { detail: { page } }));

  // Carica lista completa ticker
  useEffect(() => {
    api.get("/api/tickers")
      .then(r => {
        const list = Array.isArray(r) ? r : (r.tickers || []);
        setAllTickers(list.filter(t => t.active).map(t => t.symbol).sort());
      }).catch(() => {});
  }, []);

  // Fetch heatmap quando cambiano i ticker selezionati
  useEffect(() => {
    if (selected.length < 2) return;
    setLoading(true);
    api.get(`/api/correlations/heatmap?tickers=${selected.join(",")}`)
      .then(r => setHeatmap(r))
      .catch(() => setHeatmap(null))
      .finally(() => setLoading(false));
  }, [selected]);

  // Fetch lista coppie
  useEffect(() => {
    if (tab !== "correlated" && tab !== "decorrelated") return;
    api.get(`/api/correlations/map?min_corr=${minCorr}&top_n=200`)
      .then(r => setListData(r))
      .catch(() => {});
  }, [tab, minCorr]);

  // Fetch segnali pairs trading
  useEffect(() => {
    if (tab !== "signals") return;
    setSigLoading(true);
    api.get(`/api/pairs/signals?min_z=${minZ}&min_corr=${minCorr}`)
      .then(r => setPairsSignals(r))
      .catch(() => setPairsSignals(null))
      .finally(() => setSigLoading(false));
  }, [tab, minZ, minCorr]);

  // ── Colore cella heatmap ──
  const cellStyle = (v) => {
    if (v === null || v === undefined) return { background: C.dim, color: C.muted };
    if (v === 1.0) return { background: "#1a4a2a", color: "#00e676", fontWeight: 700 };
    if (v > 0) {
      const a = Math.pow(v, 0.7);
      const r = Math.round(10 + 20 * (1 - a));
      const g = Math.round(60 + 170 * a);
      const b = Math.round(10 + 20 * (1 - a));
      const textCol = a > 0.55 ? "#000" : C.text;
      return { background: `rgb(${r},${g},${b})`, color: textCol };
    } else {
      const a = Math.pow(-v, 0.7);
      const r = Math.round(60 + 170 * a);
      const g = Math.round(10 + 20 * (1 - a));
      const b = Math.round(10 + 20 * (1 - a));
      const textCol = a > 0.55 ? "#000" : C.text;
      return { background: `rgb(${r},${g},${b})`, color: textCol };
    }
  };

  const removeTicker = t => setSelected(sel => sel.filter(s => s !== t));
  const addTicker    = t => {
    if (!selected.includes(t) && selected.length < 35) {
      setSelected(sel => [...sel, t]);
    }
    setAddSearch("");
  };

  const corrColor = v => {
    if (v == null) return C.muted;
    if (v >=  0.90) return "#00e676";
    if (v >=  0.80) return C.yellow;
    if (v >=  0.60) return C.accent;
    if (v <= -0.60) return C.red;
    if (v <= -0.40) return "#ff6b35";
    return C.muted;
  };

  const filterSuggestions = allTickers.filter(t =>
    t.includes(addSearch.toUpperCase()) && !selected.includes(t)
  ).slice(0, 8);

  const filterListRows = rows => {
    if (!rows || !listSearch) return rows || [];
    const s = listSearch.toUpperCase();
    return rows.filter(r => r.ticker_a.includes(s) || r.ticker_b.includes(s));
  };

  // ── HEATMAP ──
  const HeatmapView = () => {
    if (!heatmap) return (
      <div style={{ padding: 40, textAlign: "center", fontFamily: "JetBrains Mono", fontSize: 11, color: C.muted }}>
        {loading ? "Caricamento heatmap..." : "Seleziona almeno 2 ticker"}
      </div>
    );
    const tks = heatmap.tickers;
    const CELL_W = 52, CELL_H = 26, LABEL_W = 60;
    return (
      <div style={{ overflowX: "auto", overflowY: "auto", maxHeight: 680 }}>
        <div style={{ display: "inline-block", minWidth: "max-content" }}>
          {/* Header row */}
          <div style={{ display: "flex", marginLeft: LABEL_W }}>
            {tks.map(t => (
              <div key={t} style={{
                width: CELL_W, flexShrink: 0,
                fontFamily: "JetBrains Mono", fontSize: 8, color: C.muted,
                textAlign: "center", padding: "4px 2px",
                writingMode: "vertical-rl", transform: "rotate(180deg)",
                height: 52, display: "flex", alignItems: "center", justifyContent: "center",
              }}>{t}</div>
            ))}
          </div>
          {/* Rows */}
          {tks.map(rowT => (
            <div key={rowT} style={{ display: "flex", alignItems: "center" }}>
              {/* Row label */}
              <div style={{
                width: LABEL_W, flexShrink: 0,
                fontFamily: "JetBrains Mono", fontSize: 9, color: C.text,
                fontWeight: 700, paddingRight: 8, textAlign: "right",
              }}>{rowT}</div>
              {/* Cells */}
              {tks.map(colT => {
                const val = heatmap.matrix[rowT]?.[colT];
                const v   = val?.c60 ?? null;
                const cs  = cellStyle(v);
                return (
                  <div key={colT} title={`${rowT} × ${colT}: ρ60=${v?.toFixed(3) ?? "—"}`}
                    style={{
                      width: CELL_W, height: CELL_H, flexShrink: 0,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontFamily: "JetBrains Mono", fontSize: 9,
                      cursor: "default", border: "1px solid #11111180",
                      ...cs,
                    }}>
                    {v !== null && v !== undefined ? v.toFixed(2) : "—"}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ── LISTA COPPIE ──
  const PairRow = ({ r, i }) => {
    const cc = corrColor(r.corr_60d);
    return (
      <div style={{
        display: "grid",
        gridTemplateColumns: "80px 80px 65px 65px 65px 80px 80px 1fr",
        gap: 8, alignItems: "center", padding: "8px 12px",
        borderBottom: `1px solid ${C.border}20`,
        fontFamily: "JetBrains Mono", fontSize: 11,
        background: i % 2 === 0 ? "transparent" : `${C.dim}40`,
      }}>
        <span style={{ color: C.accent, fontWeight: 700 }}>{r.ticker_a}</span>
        <span style={{ color: C.text,   fontWeight: 700 }}>{r.ticker_b}</span>
        <span style={{ color: cc, fontWeight: 700 }}>{r.corr_60d?.toFixed(3)}</span>
        <span style={{ color: C.muted }}>{r.corr_20d?.toFixed(3)}</span>
        <span style={{ color: C.muted }}>{r.beta?.toFixed(2)}</span>
        <span style={{ color: C.muted }}>{r.spot_a ? `$${r.spot_a.toFixed(0)}` : "—"}</span>
        <span style={{ color: C.muted }}>{r.spot_b ? `$${r.spot_b.toFixed(0)}` : "—"}</span>
        <div style={{ height: 3, background: C.dim, borderRadius: 2 }}>
          <div style={{ width: `${Math.abs(r.corr_60d||0)*100}%`, height: "100%", background: cc, borderRadius: 2 }} />
        </div>
      </div>
    );
  };

  const inputSt = {
    background: C.dim, border: `1px solid ${C.border}`, color: C.text,
    fontFamily: "JetBrains Mono", fontSize: 11, padding: "5px 9px",
    borderRadius: 4, outline: "none",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

      {/* ── Controlli ── */}
      <Panel>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
          <div>
            <Label color={C.muted}>PAIRS MAP — Matrice di Correlazione 60d</Label>
            <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted, marginTop: 4 }}>
              {heatmap?.computed_date ? `Dati al ${heatmap.computed_date} · ${selected.length} ticker selezionati` : `${selected.length} ticker selezionati`}
            </div>
          </div>
          {/* Aggiungi ticker */}
          <div style={{ position: "relative" }}>
            <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted, marginBottom: 4 }}>AGGIUNGI TICKER</div>
            <input value={addSearch} onChange={e => setAddSearch(e.target.value.toUpperCase())}
              placeholder="cerca..." style={{ ...inputSt, width: 120 }} />
            {addSearch && filterSuggestions.length > 0 && (
              <div style={{
                position: "absolute", top: "100%", left: 0, zIndex: 99,
                background: C.panel, border: `1px solid ${C.border}`,
                borderRadius: 4, minWidth: 120, boxShadow: "0 4px 12px #0008",
              }}>
                {filterSuggestions.map(t => (
                  <div key={t} onClick={() => addTicker(t)} style={{
                    padding: "6px 10px", fontFamily: "JetBrains Mono", fontSize: 11,
                    color: C.text, cursor: "pointer",
                  }}
                    onMouseEnter={e => e.currentTarget.style.background = C.dim}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                  >{t}</div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Tag ticker selezionati */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 12 }}>
          {selected.map(t => (
            <div key={t} style={{
              fontFamily: "JetBrains Mono", fontSize: 10, color: C.text,
              background: C.dim, border: `1px solid ${C.border}`,
              borderRadius: 3, padding: "3px 8px", display: "flex", alignItems: "center", gap: 6,
            }}>
              {t}
              <span onClick={() => removeTicker(t)} style={{ color: C.muted, cursor: "pointer", fontSize: 12, lineHeight: 1 }}>×</span>
            </div>
          ))}
        </div>

        {/* Tab selector */}
        <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
          {[
            { id: "heatmap",      label: "🔥 HEATMAP",      color: C.accent  },
            { id: "correlated",   label: "⚡ CORRELATI",     color: C.green   },
            { id: "decorrelated", label: "🛡 DECORRELATI",   color: C.red     },
            { id: "signals",      label: "📡 SEGNALI PAIRS", color: C.yellow  },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              fontFamily: "JetBrains Mono", fontSize: 10, fontWeight: tab === t.id ? 700 : 400,
              color: tab === t.id ? t.color : C.muted,
              background: tab === t.id ? `${t.color}18` : "transparent",
              border: `1px solid ${tab === t.id ? t.color : C.border}`,
              padding: "5px 14px", borderRadius: 4, cursor: "pointer",
            }}>{t.label}</button>
          ))}
          {/* Controlli contestuali */}
          {(tab === "correlated" || tab === "decorrelated") && (
            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted }}>
                MIN ρ: <span style={{ color: C.accent }}>{minCorr.toFixed(2)}</span>
              </span>
              <input type="range" min="0.5" max="0.95" step="0.05" value={minCorr}
                onChange={e => setMinCorr(+e.target.value)}
                style={{ width: 100, accentColor: C.accent }} />
              <input value={listSearch} onChange={e => setListSearch(e.target.value.toUpperCase())}
                placeholder="filtra ticker" style={{ ...inputSt, width: 110 }} />
            </div>
          )}
          {tab === "signals" && (
            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted }}>
                MIN |z|: <span style={{ color: C.yellow }}>{minZ.toFixed(1)}</span>
              </span>
              <input type="range" min="2.0" max="4.0" step="0.5" value={minZ}
                onChange={e => setMinZ(+e.target.value)}
                style={{ width: 100, accentColor: C.yellow }} />
              <span style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted }}>
                MIN ρ: <span style={{ color: C.accent }}>{minCorr.toFixed(2)}</span>
              </span>
              <input type="range" min="0.5" max="0.95" step="0.05" value={minCorr}
                onChange={e => setMinCorr(+e.target.value)}
                style={{ width: 80, accentColor: C.accent }} />
            </div>
          )}
        </div>
      </Panel>

      {/* ── Contenuto tab ── */}
      {tab === "heatmap" && (
        <Panel style={{ padding: 16 }}>
          {loading
            ? <div style={{ padding: 40, textAlign: "center", fontFamily: "JetBrains Mono", fontSize: 11, color: C.muted }}>Caricamento...</div>
            : <HeatmapView />
          }
        </Panel>
      )}

      {(tab === "correlated" || tab === "decorrelated") && (
        <Panel style={{ padding: 0, overflow: "hidden" }}>
          {/* Header */}
          <div style={{
            display: "grid", gridTemplateColumns: "80px 80px 65px 65px 65px 80px 80px 1fr",
            gap: 8, padding: "8px 12px", borderBottom: `1px solid ${C.border}`,
            fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted,
            letterSpacing: "0.1em", position: "sticky", top: 0, background: C.panel,
          }}>
            {["TICKER A","TICKER B","ρ 60D","ρ 20D","BETA","SPOT A","SPOT B",""].map(h=><span key={h}>{h}</span>)}
          </div>
          <div style={{ maxHeight: 620, overflowY: "auto" }}>
            {filterListRows(listData?.[tab]).map((r, i) => <PairRow key={`${r.ticker_a}-${r.ticker_b}`} r={r} i={i} />)}
          </div>
        </Panel>
      )}

      {/* ── SEGNALI PAIRS ── */}
      {tab === "signals" && (
        <Panel style={{ padding: 0, overflow: "hidden" }}>
          {sigLoading ? (
            <div style={{ padding: 40, textAlign: "center", fontFamily: "JetBrains Mono", fontSize: 11, color: C.muted }}>
              Caricamento segnali...
            </div>
          ) : !pairsSignals || pairsSignals.signals?.length === 0 ? (
            <div style={{ padding: 40, textAlign: "center", fontFamily: "JetBrains Mono", fontSize: 11, color: C.muted }}>
              {pairsSignals?.note || "Nessun segnale attivo. Abbassa il filtro z o esegui compute_pairs_signals.py"}
            </div>
          ) : (
            <>
              {/* Header */}
              <div style={{
                display: "grid",
                gridTemplateColumns: "90px 90px 60px 60px 55px 55px 55px 55px 90px 90px 55px",
                gap: 6, padding: "8px 14px", borderBottom: `1px solid ${C.border}`,
                fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted,
                letterSpacing: "0.08em", position: "sticky", top: 0, background: C.panel,
              }}>
                {["TICKER A","TICKER B","ρ 20D","z SPREAD","z A","z B","SPOT A","SPOT B","LEG A","LEG B","SCORE"].map(h => <span key={h}>{h}</span>)}
              </div>
              <div style={{ maxHeight: 620, overflowY: "auto" }}>
                {pairsSignals.signals.map((s, i) => {
                  const zAbs = Math.abs(s.z_spread);
                  const zColor = zAbs >= 3.5 ? C.red : zAbs >= 3.0 ? C.yellow : C.accent;
                  const corrColor = Math.abs(s.corr_20d) >= 0.85 ? C.green : Math.abs(s.corr_20d) >= 0.70 ? C.accent : C.muted;
                  const dirColor  = d => d === "PUT" ? C.red : C.green;
                  // Barre sizing: la gamba più pesante è evidenziata
                  const WBar = ({ w, color }) => (
                    <div style={{ position: "relative", height: 16, display: "flex", alignItems: "center", gap: 4 }}>
                      <div style={{
                        width: `${Math.round(w * 60)}px`, height: 6,
                        background: color, borderRadius: 3, opacity: 0.8,
                        minWidth: 4,
                      }} />
                      <span style={{ fontFamily: "JetBrains Mono", fontSize: 9, color }}>{(w*100).toFixed(0)}%</span>
                    </div>
                  );
                  const rowBg = i % 2 === 0 ? "transparent" : `${C.dim}40`;
                  return (
                    <div key={i} onClick={() => setModalSignal(s)}
                    onMouseEnter={e => e.currentTarget.style.background = `${C.accent}18`}
                    onMouseLeave={e => e.currentTarget.style.background = rowBg}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "90px 90px 60px 60px 55px 55px 55px 55px 90px 90px 55px",
                      gap: 6, alignItems: "center", padding: "9px 14px",
                      borderBottom: `1px solid ${C.border}20`,
                      background: rowBg,
                      fontFamily: "JetBrains Mono", fontSize: 11,
                      cursor: "pointer",
                    }}>
                      {/* Ticker A */}
                      <div>
                        <span style={{ color: C.accent, fontWeight: 700 }}>{s.ticker_a}</span>
                        <div style={{ fontSize: 9, color: C.muted }}>${s.spot_a.toFixed(1)}</div>
                      </div>
                      {/* Ticker B */}
                      <div>
                        <span style={{ color: C.text, fontWeight: 700 }}>{s.ticker_b}</span>
                        <div style={{ fontSize: 9, color: C.muted }}>${s.spot_b.toFixed(1)}</div>
                      </div>
                      {/* ρ 20d */}
                      <span style={{ color: corrColor, fontWeight: 700 }}>{s.corr_20d.toFixed(2)}</span>
                      {/* z spread */}
                      <span style={{ color: zColor, fontWeight: 700 }}>
                        {s.z_spread > 0 ? "+" : ""}{s.z_spread.toFixed(2)}σ
                      </span>
                      {/* z A */}
                      <span style={{ color: Math.abs(s.z_a) > 1.5 ? C.yellow : C.muted, fontSize: 10 }}>
                        {s.z_a > 0 ? "+" : ""}{s.z_a.toFixed(2)}
                      </span>
                      {/* z B */}
                      <span style={{ color: Math.abs(s.z_b) > 1.5 ? C.yellow : C.muted, fontSize: 10 }}>
                        {s.z_b > 0 ? "+" : ""}{s.z_b.toFixed(2)}
                      </span>
                      {/* Spot A */}
                      <span style={{ color: C.muted, fontSize: 10 }}>${s.spot_a.toFixed(1)}</span>
                      {/* Spot B */}
                      <span style={{ color: C.muted, fontSize: 10 }}>${s.spot_b.toFixed(1)}</span>
                      {/* Leg A: direzione + sizing */}
                      <div>
                        <span style={{ color: dirColor(s.direction_a), fontWeight: 700, fontSize: 10 }}>
                          {s.direction_a}
                        </span>
                        <WBar w={s.weight_a} color={dirColor(s.direction_a)} />
                      </div>
                      {/* Leg B: direzione + sizing */}
                      <div>
                        <span style={{ color: dirColor(s.direction_b), fontWeight: 700, fontSize: 10 }}>
                          {s.direction_b}
                        </span>
                        <WBar w={s.weight_b} color={dirColor(s.direction_b)} />
                      </div>
                      {/* Score */}
                      <span style={{ color: C.accent, fontWeight: 700 }}>{s.score.toFixed(2)}</span>
                    </div>
                  );
                })}
              </div>
              <div style={{ padding: "8px 14px", borderTop: `1px solid ${C.border}`, fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted }}>
                {pairsSignals.count} segnali · dati al {pairsSignals.computed_date} · LEG sizing proporzionale a |z| individuale
              </div>
            </>
          )}
        </Panel>
      )}

      {/* Modal segnale pairs */}
      {modalSignal && (
        <PairsSignalModal signal={modalSignal} onClose={() => setModalSignal(null)} />
      )}

      {/* Legenda colori */}
      <Panel>
        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          {[
            ["#1a4a2a","#00e676","1.00 diagonale"],
            ["rgb(10,230,10)","#000","0.90+"],
            ["rgb(10,180,10)","#000","0.70"],
            ["rgb(10,130,10)","#eee","0.50"],
            ["rgb(50,50,50)","#aaa","0.00"],
            ["rgb(130,10,10)","#eee","-0.50"],
            ["rgb(180,10,10)","#000","-0.70"],
            ["rgb(230,10,10)","#000","-0.90+"],
          ].map(([bg,fg,label]) => (
            <div key={label} style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <div style={{ width: 28, height: 16, background: bg, borderRadius: 2, border: "1px solid #333" }} />
              <span style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted }}>{label}</span>
            </div>
          ))}
        </div>
      </Panel>

    </div>
  );
}


// ─── HEDGE ENGINE ─────────────────────────────────────────────────────────────
function HedgePage() {
  const [ticker,     setTicker]     = useState("");
  const [size,       setSize]       = useState(1);
  const [leverage,   setLeverage]   = useState(1);
  const [direction,  setDirection]  = useState("LONG");
  const [entryPrice, setEntryPrice] = useState("");
  const [autoSpot,   setAutoSpot]   = useState(null);
  const [spotLoading,setSpotLoading]= useState(false);
  const [data,       setData]       = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [tickers,    setTickerList] = useState([]);
  const [error,      setError]      = useState(null);

  useEffect(() => {
    api.get("/api/tickers")
      .then(r => {
        const list = Array.isArray(r) ? r : (r.tickers || []);
        setTickerList(list.filter(t => t.active).map(t => t.symbol).sort());
      })
      .catch(() => {});
  }, []);

  // Auto-fetch spot quando cambia il ticker
  useEffect(() => {
    if (!ticker) { setAutoSpot(null); return; }
    setSpotLoading(true);
    setAutoSpot(null);
    api.get(`/api/spot/${ticker}`)
      .then(r => setAutoSpot(r.spot || null))
      .catch(() => setAutoSpot(null))
      .finally(() => setSpotLoading(false));
  }, [ticker]);

  const compute = async () => {
    if (!ticker) return;
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const effectiveSize = size * leverage;
      const params = new URLSearchParams({ ticker, size: effectiveSize, direction });
      if (autoSpot) params.append("price_a", autoSpot);
      const d = await api.get(`/api/correlations/hedge?${params}`);
      setData(d);
    } catch (e) {
      setError(e?.response?.data?.detail || "Errore nel calcolo hedge");
    } finally {
      setLoading(false);
    }
  };

  const REGIME_COL = { CRISIS: C.red, HIGH_VOL: C.accent, NORMAL: C.yellow, CALM: C.green, PIN: "#9c27b0" };

  const corrColor = v => {
    if (v === null || v === undefined) return C.muted;
    if (v >=  0.8) return "#00e676";
    if (v >=  0.6) return C.yellow;
    if (v <= -0.6) return C.red;
    if (v <= -0.4) return "#ff6b35";
    return C.muted;
  };

  const inputSt = {
    background: C.dim, border: `1px solid ${C.border}`, color: C.text,
    fontFamily: "JetBrains Mono", fontSize: 12, padding: "6px 10px",
    borderRadius: 4, outline: "none", width: 100,
  };
  const selectSt = { ...inputSt, width: 130, cursor: "pointer" };
  const btnSt = {
    background: C.accent, color: "#000", fontFamily: "JetBrains Mono",
    fontSize: 11, fontWeight: 700, padding: "7px 18px", borderRadius: 4,
    border: "none", cursor: "pointer", letterSpacing: "0.08em",
    opacity: (!ticker || loading) ? 0.5 : 1,
  };

  // Componente riga singola per ogni ticker suggerito
  const HedgeRow = ({ r, inverse }) => {
    const dir = inverse
      ? (direction === "LONG" ? "SHORT" : "LONG")
      : direction;
    const dirCol = dir === "LONG" ? C.green : C.red;
    const cc     = corrColor(r.corr_60d);
    return (
      <div style={{
        borderBottom: `1px solid ${C.border}30`, padding: "10px 0",
        fontFamily: "JetBrains Mono",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{r.ticker}</span>
          <span style={{
            fontSize: 9, color: dirCol, background: `${dirCol}20`,
            padding: "2px 7px", borderRadius: 3, letterSpacing: "0.08em",
          }}>{dir}</span>
        </div>
        <div style={{ display: "flex", gap: 14, marginTop: 5 }}>
          <span style={{ fontSize: 10, color: cc }}>ρ60 {r.corr_60d?.toFixed(2)}</span>
          <span style={{ fontSize: 10, color: C.muted }}>β {r.beta?.toFixed(2)}</span>
          <span style={{ fontSize: 10, color: C.muted }}>${r.spot?.toFixed(0) || "—"}</span>
        </div>
        {r.size_suggest != null && (
          <div style={{ marginTop: 5, fontSize: 11, color: C.accent }}>
            → {r.size_suggest} azioni &nbsp;·&nbsp; copre ~{r.coverage_pct}%
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* ── Controlli ── */}
      <Panel>
        <Label color={C.muted}>HEDGE ENGINE — correlazione & decorrelazione</Label>
        <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted, marginTop: 4, marginBottom: 14 }}>
          Dato un ticker in posizione, trova i migliori candidati per sterilizzare, fare leva o accelerare il recupero.
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div>
            <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted, marginBottom: 4 }}>TICKER</div>
            <select value={ticker} onChange={e => setTicker(e.target.value)} style={{ ...selectSt, width: 140 }}>
              <option value="">— seleziona —</option>
              {tickers.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted, marginBottom: 4 }}>SIZE</div>
            <input type="number" min="1" value={size}
              onChange={e => setSize(Math.max(1, +e.target.value))} style={inputSt} />
          </div>
          <div>
            <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted, marginBottom: 4 }}>DIREZIONE</div>
            <select value={direction} onChange={e => setDirection(e.target.value)} style={selectSt}>
              <option value="LONG">LONG</option>
              <option value="SHORT">SHORT</option>
            </select>
          </div>
          <div>
            <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted, marginBottom: 4 }}>LEVA</div>
            <input type="number" min="1" max="100" step="0.5" value={leverage}
              onChange={e => setLeverage(Math.max(1, +e.target.value))}
              style={{ ...inputSt, width: 70 }} />
          </div>
          <div>
            <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted, marginBottom: 4 }}>INGRESSO $</div>
            <input type="number" step="0.01" value={entryPrice}
              onChange={e => setEntryPrice(e.target.value)}
              style={{ ...inputSt, width: 90 }} placeholder="0.00" />
          </div>
          {/* Spot auto-fetched — display only */}
          <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
            <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted, marginBottom: 4 }}>SPOT ORA</div>
            <div style={{
              ...inputSt, width: 90, display: "flex", alignItems: "center",
              color: autoSpot ? C.yellow : C.muted, borderColor: C.border,
            }}>
              {spotLoading ? "..." : autoSpot ? `$${autoSpot.toFixed(2)}` : "—"}
            </div>
          </div>
          <button onClick={compute} disabled={!ticker || loading} style={btnSt}>
            {loading ? "CALCOLO..." : "CALCOLA HEDGE"}
          </button>
        </div>
        {error && (
          <div style={{ marginTop: 12, fontFamily: "JetBrains Mono", fontSize: 11, color: C.red }}>
            ⚠ {error}
          </div>
        )}
      </Panel>

      {/* ── Risultati ── */}
      {data && (
        <>
          {/* Summary cards */}
          {(() => {
            const spot    = data.spot_a || autoSpot;
            const entry   = entryPrice ? +entryPrice : null;
            const expTot  = spot ? size * leverage * spot : null;
            const pnlRaw  = (spot && entry)
              ? (direction === "LONG" ? spot - entry : entry - spot) * size * leverage
              : null;
            const pnlPct  = (pnlRaw != null && entry && size * leverage * entry !== 0)
              ? (pnlRaw / (size * leverage * entry) * 100)
              : null;
            const pnlCol  = pnlRaw == null ? C.muted : pnlRaw >= 0 ? C.green : C.red;
            const fmt     = v => v.toLocaleString("en-US", { maximumFractionDigits: 0 });
            return (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 12 }}>
                <Panel>
                  <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted, marginBottom: 6 }}>TICKER</div>
                  <div style={{ fontFamily: "JetBrains Mono", fontSize: 18, fontWeight: 700, color: C.accent }}>{data.ticker}</div>
                  <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted, marginTop: 3 }}>{data.direction}</div>
                </Panel>
                <Panel>
                  <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted, marginBottom: 6 }}>SIZE × LEVA</div>
                  <div style={{ fontFamily: "JetBrains Mono", fontSize: 18, fontWeight: 700, color: C.text }}>{size}</div>
                  <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: leverage > 1 ? C.accent : C.muted, marginTop: 3 }}>× {leverage}× leva</div>
                </Panel>
                <Panel>
                  <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted, marginBottom: 6 }}>INGRESSO</div>
                  <div style={{ fontFamily: "JetBrains Mono", fontSize: 18, fontWeight: 700, color: C.text }}>
                    {entry ? `$${entry.toFixed(2)}` : "—"}
                  </div>
                </Panel>
                <Panel>
                  <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted, marginBottom: 6 }}>SPOT ORA</div>
                  <div style={{ fontFamily: "JetBrains Mono", fontSize: 18, fontWeight: 700, color: C.yellow }}>
                    {spot ? `$${spot.toFixed(2)}` : "—"}
                  </div>
                </Panel>
                <Panel>
                  <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted, marginBottom: 6 }}>ESPOSIZIONE</div>
                  <div style={{ fontFamily: "JetBrains Mono", fontSize: 18, fontWeight: 700, color: C.green }}>
                    {expTot ? `$${fmt(expTot)}` : "—"}
                  </div>
                  {leverage > 1 && spot && (
                    <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted, marginTop: 3 }}>
                      {size} × {leverage}× × ${spot.toFixed(0)}
                    </div>
                  )}
                </Panel>
                <Panel>
                  <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted, marginBottom: 6 }}>P&L UNREALIZZATO</div>
                  <div style={{ fontFamily: "JetBrains Mono", fontSize: 18, fontWeight: 700, color: pnlCol }}>
                    {pnlRaw != null ? `${pnlRaw >= 0 ? "+" : ""}$${fmt(pnlRaw)}` : "—"}
                  </div>
                  {pnlPct != null && (
                    <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: pnlCol, marginTop: 3 }}>
                      {pnlPct >= 0 ? "+" : ""}{pnlPct.toFixed(1)}%
                    </div>
                  )}
                </Panel>
              </div>
            );
          })()}

          {/* Tre colonne */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>

            {/* STERILIZZA */}
            <Panel>
              <div style={{ fontFamily: "JetBrains Mono", fontSize: 10, fontWeight: 700,
                color: C.red, marginBottom: 4, letterSpacing: "0.1em" }}>🛡 STERILIZZA</div>
              <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted, marginBottom: 10 }}>
                Decorrelati (ρ &lt; -0.4). Prendi posizione inversa per neutralizzare l'esposizione.
              </div>
              {data.hedge.length === 0
                ? <div style={{ fontFamily: "JetBrains Mono", fontSize: 10, color: C.muted }}>Nessun ticker decorrelato trovato</div>
                : data.hedge.map((r, i) => <HedgeRow key={i} r={r} inverse={true} />)
              }
            </Panel>

            {/* LEVA */}
            <Panel>
              <div style={{ fontFamily: "JetBrains Mono", fontSize: 10, fontWeight: 700,
                color: C.green, marginBottom: 4, letterSpacing: "0.1em" }}>⚡ LEVA</div>
              <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted, marginBottom: 10 }}>
                Correlati (ρ &gt; 0.6). Stessa direzione: aumenta l'esposizione senza fare prezzo medio.
              </div>
              {data.leverage.length === 0
                ? <div style={{ fontFamily: "JetBrains Mono", fontSize: 10, color: C.muted }}>Nessun ticker correlato trovato</div>
                : data.leverage.map((r, i) => <HedgeRow key={i} r={r} inverse={false} />)
              }
            </Panel>

            {/* ACCELERA */}
            <Panel>
              <div style={{ fontFamily: "JetBrains Mono", fontSize: 10, fontWeight: 700,
                color: C.accent, marginBottom: 4, letterSpacing: "0.1em" }}>🚀 ACCELERA</div>
              <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted, marginBottom: 10 }}>
                Correlati con β alto. Stesso senso, si muove più velocemente: recupero perdita accelerato.
              </div>
              {data.accelerate.length === 0
                ? <div style={{ fontFamily: "JetBrains Mono", fontSize: 10, color: C.muted }}>Nessun fast mover trovato</div>
                : data.accelerate.map((r, i) => <HedgeRow key={i} r={r} inverse={false} />)
              }
            </Panel>
          </div>

          {/* Formula legenda */}
          <Panel>
            <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted }}>
              <span style={{ color: C.accent }}>HEDGE RATIO</span>
              {" "}— azioni_B = size_A × |β(B|A)| × (spot_A / spot_B)
              &nbsp;&nbsp;·&nbsp;&nbsp;
              <span style={{ color: C.accent }}>COPERTURA %</span>
              {" "}≈ |ρ60d| × 100 &nbsp;&nbsp;·&nbsp;&nbsp;
              dati aggiornati ogni giorno lavorativo alle 05:30
            </div>
          </Panel>
        </>
      )}
    </div>
  );
}


// ─── NEWS FEED ────────────────────────────────────────────────────────────────
const DEFAULT_WATCHLIST = ["AAPL","MSFT","NVDA","TSLA","AMZN","META","GOOGL","SPY","QQQ","VIX"];

function NewsModal({ item, onClose }) {
  const [aiSummary,    setAiSummary]    = useState(null);
  const [loadingAI,    setLoadingAI]    = useState(false);
  const [aiError,      setAiError]      = useState(null);

  useEffect(() => {
    setAiSummary(null);
    setAiError(null);
    setLoadingAI(false);
  }, [item]);

  if (!item) return null;

  const fetchAISummary = async () => {
    setLoadingAI(true); setAiError(null); setAiSummary(null);
    try {
      const qs = new URLSearchParams({
        url:     item.link    || "",
        title:   item.title   || "",
        ticker:  item.ticker  || "",
        summary: item.summary || "",
      });
      const data = await api.get(`/api/news/ai-summary?${qs}`);
      if (data.summary) {
        setAiSummary(data.summary);
      } else {
        setAiError(data.detail || "Nessun contenuto generato.");
      }
    } catch (e) {
      setAiError("Errore Grok API — riprova.");
    } finally { setLoadingAI(false); }
  };

  const formColor = item.form_type === "8-K" ? C.red
    : item.form_type === "4"    ? C.yellow
    : item.form_type === "10-Q" ? C.green
    : item.form_type === "10-K" ? C.accent : C.muted;
  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "#000000cc",
      zIndex: 9000, display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: "#111", border: `1px solid ${C.border}`,
        borderRadius: 8, padding: "28px 32px", maxWidth: 680, width: "90%",
        maxHeight: "80vh", overflowY: "auto",
      }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ fontSize: 9, color: C.accent2, letterSpacing: "0.1em" }}>{item.source}</span>
            {item.ticker && <span style={{ fontSize: 9, color: C.accent, border: `1px solid ${C.accent}40`, borderRadius: 3, padding: "1px 6px" }}>{item.ticker}</span>}
            {item.form_type && <span style={{ fontSize: 9, color: formColor, border: `1px solid ${formColor}40`, borderRadius: 3, padding: "1px 6px" }}>{item.form_type}</span>}
            <span style={{ fontSize: 9, color: C.dim }}>{item.published}</span>
          </div>
          <button onClick={onClose} style={{
            background: "transparent", border: "none", color: C.muted,
            fontSize: 18, cursor: "pointer", lineHeight: 1, padding: "0 4px",
          }}>×</button>
        </div>
        {/* Titolo */}
        <div style={{ fontSize: 15, fontWeight: 700, color: C.text, lineHeight: 1.5, marginBottom: 16 }}>
          {item.title}
        </div>
        {/* Summary */}
        {item.summary && (
          <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.7, marginBottom: 20 }}>
            {item.summary}
          </div>
        )}
        {/* AI Summary */}
        {!aiSummary && !loadingAI && (
          <button onClick={fetchAISummary} style={{
            background: "#1a1a2e", border: `1px solid #6644aa`,
            color: "#aa88ff", fontSize: 10, padding: "7px 18px", borderRadius: 4,
            cursor: "pointer", fontFamily: "'JetBrains Mono', monospace",
            letterSpacing: "0.1em", marginBottom: 10,
          }}>⚡ AI SUMMARY</button>
        )}
        {loadingAI && (
          <div style={{ fontSize: 11, color: "#aa88ff", marginBottom: 10, animation: "pulse 1.5s infinite" }}>
            ⚡ Groq sta analizzando...
          </div>
        )}
        {aiError && <div style={{ fontSize: 11, color: C.red, marginBottom: 10 }}>{aiError}</div>}
        {aiSummary && (
          <div style={{
            background: "#0d0d1a", border: `1px solid #6644aa44`,
            borderRadius: 6, padding: "14px 16px", marginBottom: 14,
          }}>
            <div style={{ fontSize: 9, color: "#aa88ff", letterSpacing: "0.15em", marginBottom: 8 }}>
              ⚡ AI SUMMARY
            </div>
            <div style={{ fontSize: 11, color: C.text, lineHeight: 1.8, whiteSpace: "pre-wrap" }}>
              {aiSummary}
            </div>
          </div>
        )}
        {/* Link esterno */}
        {item.link && (
          <a href={item.link} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
            <button style={{
              background: `${C.accent}20`, border: `1px solid ${C.accent}50`,
              color: C.accent, fontSize: 10, padding: "7px 18px", borderRadius: 4,
              cursor: "pointer", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em",
            }}>APRI ARTICOLO ORIGINALE ↗</button>
          </a>
        )}
      </div>
    </div>
  );
}

// Source label + colore per stile Bloomberg
function srcInfo(source) {
  if (!source) return { label: "──", color: C.dim };
  if (source.includes("Yahoo"))       return { label: "YAHOO",      color: "#9966ff" };
  if (source.includes("MarketWatch")) return { label: "MKTWATCH",   color: "#4da6ff" };
  if (source.includes("CNBC"))        return { label: "CNBC",       color: "#ff6600" };
  if (source.includes("Investing"))   return { label: "INVESTING",  color: "#00cc66" };
  if (source.includes("Benzinga"))     return { label: "BENZINGA",   color: "#ff4444" };
  if (source.includes("EDGAR"))       return { label: "SEC EDGAR",  color: C.yellow };
  if (source.includes("YF/"))         return { label: source.split("/")[1] || "YAHOO", color: "#9966ff" };
  return { label: source.slice(0, 9).toUpperCase(), color: C.dim };
}

function NewsRow({ item, idx, onOpen }) {
  const [hovered, setHovered] = useState(false);
  const formColor = item.form_type === "8-K" ? C.red
    : item.form_type === "4"    ? C.yellow
    : item.form_type === "10-Q" ? C.green
    : item.form_type === "10-K" ? C.accent
    : null;

  const time = item.published ? item.published.slice(11, 16) || item.published.slice(0, 10) : "";
  const src  = srcInfo(item.source);

  return (
    <div
      onClick={() => onOpen && onOpen(item)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "grid",
        gridTemplateColumns: "42px 76px 1fr",
        gap: "0 10px",
        padding: "5px 10px",
        background: hovered ? "#ffffff0a" : idx % 2 === 0 ? "#0a0a0a" : "transparent",
        borderBottom: `1px solid ${C.border}30`,
        cursor: "pointer",
        alignItems: "start",
      }}
    >
      <span style={{ fontSize: 10, color: "#888", lineHeight: 1.6, whiteSpace: "nowrap" }}>{time}</span>
      <span style={{ fontSize: 9, color: src.color, lineHeight: 1.6, whiteSpace: "nowrap",
        fontWeight: 600, letterSpacing: "0.05em" }}>{src.label}</span>
      <div>
        <span style={{ fontSize: 11, fontWeight: 600, color: hovered ? C.accent : C.text, lineHeight: 1.5 }}>
          {formColor && (
            <span style={{ color: formColor, marginRight: 6, fontSize: 10 }}>[{item.form_type}]</span>
          )}
          {item.ticker && !formColor && (
            <span style={{ color: C.accent, marginRight: 6, fontSize: 10 }}>{item.ticker}</span>
          )}
          {item.title}
        </span>
        {item.summary && (
          <div style={{ fontSize: 10, color: C.muted, lineHeight: 1.5, marginTop: 1 }}>
            {item.summary.length > 160 ? item.summary.slice(0, 160) + "…" : item.summary}
          </div>
        )}
      </div>
    </div>
  );
}

function NewsPage() {
  const [tab,        setTab]        = useState("FEED");
  const [feedItems,  setFeedItems]  = useState([]);
  const [wlItems,    setWlItems]    = useState([]);
  const [edgarItems, setEdgarItems] = useState([]);
  const [wlTickers,  setWlTickers]  = useState(DEFAULT_WATCHLIST);
  const [wlInput,    setWlInput]    = useState("");
  const [edgarTicker,setEdgarTicker]= useState("");
  const [edgarMode,  setEdgarMode]  = useState("ticker"); // "ticker" | "scan"
  const [loadingFeed,  setLoadingFeed]  = useState(false);
  const [loadingWl,    setLoadingWl]    = useState(false);
  const [loadingEdgar, setLoadingEdgar] = useState(false);
  const [edgarScanMsg, setEdgarScanMsg] = useState(null);
  const [selectedNews, setSelectedNews] = useState(null);
  const [lastUpdate,   setLastUpdate]   = useState(null);
  const [filterSource, setFilterSource] = useState("ALL");

  // ── Tab FEED ──────────────────────────────────────────────────────────────
  const fetchFeed = async () => {
    setLoadingFeed(true);
    try {
      const data = await api.get("/api/news?limit=80");
      setFeedItems(Array.isArray(data) ? data : []);
      setLastUpdate(new Date().toLocaleTimeString("it-IT"));
    } catch (e) { console.error("News feed:", e); }
    finally { setLoadingFeed(false); }
  };

  useEffect(() => {
    if (tab === "FEED") {
      fetchFeed();
      const iv = setInterval(fetchFeed, 60000);
      return () => clearInterval(iv);
    }
  }, [tab]);

  // ── Tab WATCHLIST ─────────────────────────────────────────────────────────
  const fetchWatchlist = async (tickers) => {
    if (!tickers || tickers.length === 0) return;
    setLoadingWl(true);
    try {
      const qs = tickers.join(",");
      const data = await api.get(`/api/news/watchlist?tickers=${qs}`);
      setWlItems(Array.isArray(data) ? data : []);
    } catch (e) { console.error("News watchlist:", e); }
    finally { setLoadingWl(false); }
  };

  useEffect(() => {
    if (tab === "WATCHLIST") fetchWatchlist(wlTickers);
  }, [tab]);

  const addWlTicker = () => {
    const t = wlInput.trim().toUpperCase();
    if (!t || wlTickers.includes(t)) { setWlInput(""); return; }
    const newList = [...wlTickers, t];
    setWlTickers(newList);
    setWlInput("");
    fetchWatchlist(newList);
  };

  const removeWlTicker = (t) => {
    const newList = wlTickers.filter(x => x !== t);
    setWlTickers(newList);
    setWlItems(wlItems.filter(i => i.ticker !== t));
  };

  // ── Tab EDGAR ─────────────────────────────────────────────────────────────
  const fetchEdgar = async () => {
    setLoadingEdgar(true); setEdgarScanMsg(null);
    try {
      let data;
      if (edgarMode === "scan") {
        // Legge i ticker dalle Strategies live
        const strategiesResp = await api.get("/api/strategies/live");
        const signalsList = strategiesResp?.signals || [];
        if (signalsList.length === 0) {
          setEdgarScanMsg("Nessun segnale attivo in Strategies. Fai girare Strategies prima.");
          setLoadingEdgar(false); return;
        }
        const tickers = [...new Set(signalsList.map(s => s.ticker))].join(",");
        data = await api.get(`/api/news/edgar/scan?tickers=${tickers}`);
        if (!data || data.length === 0) {
          setEdgarScanMsg(`Nessun filing recente trovato per i ticker di Strategies (${signalsList.length} segnali).`);
          setLoadingEdgar(false); return;
        }
      } else {
        if (!edgarTicker.trim()) { setLoadingEdgar(false); return; }
        data = await api.get(`/api/news/edgar/${edgarTicker.trim().toUpperCase()}`);
      }
      setEdgarItems(Array.isArray(data) ? data : []);
    } catch (e) { console.error("EDGAR:", e); }
    finally { setLoadingEdgar(false); }
  };

  useEffect(() => {
    if (tab === "EDGAR" && edgarMode === "scan") fetchEdgar();
  }, [tab, edgarMode]);

  // ── Sources uniche per filtro ──────────────────────────────────────────────
  const sources = ["ALL", ...Array.from(new Set(feedItems.map(i => i.source)))];
  const filteredFeed = filterSource === "ALL"
    ? feedItems
    : feedItems.filter(i => i.source === filterSource);

  const tabStyle = (t) => ({
    padding: "6px 18px", fontSize: 10, letterSpacing: "0.1em",
    fontFamily: "'JetBrains Mono', monospace", cursor: "pointer",
    background: tab === t ? `${C.accent}20` : "transparent",
    border: tab === t ? `1px solid ${C.accent}50` : `1px solid ${C.border}`,
    color: tab === t ? C.accent : C.muted, borderRadius: 4,
  });

  return (
    <div style={{ padding: "24px 32px", maxWidth: 1100, margin: "0 auto" }}>
      <NewsModal item={selectedNews} onClose={() => setSelectedNews(null)} />
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 11, color: C.muted, letterSpacing: "0.15em" }}>NEWS FEED</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: C.text }}>Market Intelligence</div>
        </div>
        <div style={{ fontSize: 9, color: C.dim }}>
          {lastUpdate && `Ultimo aggiornamento: ${lastUpdate}`}
          <span style={{ marginLeft: 8, color: C.green }}>● auto 60s</span>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        <button style={tabStyle("FEED")}     onClick={() => setTab("FEED")}>📰 FEED</button>
        <button style={tabStyle("WATCHLIST")}onClick={() => setTab("WATCHLIST")}>📡 WATCHLIST</button>
        <button style={tabStyle("EDGAR")}    onClick={() => setTab("EDGAR")}>📋 EDGAR / SEC</button>
      </div>

      {/* ── Tab FEED ── */}
      {tab === "FEED" && (
        <div>
          {/* Filtro source */}
          <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
            {sources.map(s => (
              <button key={s} onClick={() => setFilterSource(s)} style={{
                padding: "3px 10px", fontSize: 9, borderRadius: 3, cursor: "pointer",
                background: filterSource === s ? `${C.accent}25` : "transparent",
                border: `1px solid ${filterSource === s ? C.accent : C.border}`,
                color: filterSource === s ? C.accent : "#888",
              }}>{s}</button>
            ))}
            <button onClick={fetchFeed} style={{
              marginLeft: "auto", padding: "3px 12px", fontSize: 9, borderRadius: 3,
              cursor: "pointer", background: "transparent",
              border: `1px solid ${C.border}`, color: C.muted,
            }}>{loadingFeed ? "..." : "↻ REFRESH"}</button>
          </div>
          <div style={{ border: `1px solid ${C.border}`, borderRadius: 4, overflow: "hidden" }}>
            {loadingFeed && filteredFeed.length === 0
              ? <div style={{ color: C.muted, fontSize: 11, padding: 16 }}>Caricamento...</div>
              : filteredFeed.length === 0
                ? <div style={{ color: C.dim, fontSize: 11, padding: 16 }}>Nessun articolo. Verifica la connessione.</div>
                : filteredFeed.map((item, i) => <NewsRow key={i} item={item} idx={i} onOpen={setSelectedNews} />)
            }
          </div>
        </div>
      )}

      {/* ── Tab WATCHLIST ── */}
      {tab === "WATCHLIST" && (
        <div>
          {/* Ticker chips */}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14, alignItems: "center" }}>
            {wlTickers.map(t => (
              <div key={t} style={{
                display: "flex", alignItems: "center", gap: 4,
                background: `${C.accent}15`, border: `1px solid ${C.accent}40`,
                borderRadius: 4, padding: "2px 8px", fontSize: 10, color: C.accent,
              }}>
                {t}
                <span onClick={() => removeWlTicker(t)}
                  style={{ cursor: "pointer", color: C.dim, fontSize: 11, lineHeight: 1 }}>×</span>
              </div>
            ))}
            <div style={{ display: "flex", gap: 6 }}>
              <input value={wlInput} onChange={e => setWlInput(e.target.value.toUpperCase())}
                onKeyDown={e => e.key === "Enter" && addWlTicker()}
                placeholder="AGGIUNGI TICKER" style={{
                  background: C.card, border: `1px solid ${C.border}`, color: C.text,
                  fontSize: 10, padding: "4px 10px", borderRadius: 4, width: 140,
                  fontFamily: "'JetBrains Mono', monospace",
                }} />
              <button onClick={addWlTicker} style={{
                background: `${C.accent}20`, border: `1px solid ${C.accent}40`,
                color: C.accent, fontSize: 10, padding: "4px 12px", borderRadius: 4, cursor: "pointer",
              }}>+</button>
              <button onClick={() => fetchWatchlist(wlTickers)} style={{
                background: "transparent", border: `1px solid ${C.border}`,
                color: C.muted, fontSize: 10, padding: "4px 12px", borderRadius: 4, cursor: "pointer",
              }}>{loadingWl ? "..." : "↻"}</button>
            </div>
          </div>
          <div style={{ border: `1px solid ${C.border}`, borderRadius: 4, overflow: "hidden" }}>
            {loadingWl
              ? <div style={{ color: C.muted, fontSize: 11, padding: 16 }}>Caricamento...</div>
              : wlItems.length === 0
                ? <div style={{ color: C.dim, fontSize: 11, padding: 16 }}>Nessun articolo. Premi ↻ per caricare.</div>
                : wlItems.map((item, i) => <NewsRow key={i} item={item} idx={i} onOpen={setSelectedNews} />)
            }
          </div>
        </div>
      )}

      {/* ── Tab EDGAR ── */}
      {tab === "EDGAR" && (
        <div>
          {/* Mode toggle + search */}
          <div style={{ display: "flex", gap: 10, marginBottom: 16, alignItems: "center", flexWrap: "wrap" }}>
            <button onClick={() => setEdgarMode("scan")} style={{
              ...tabStyle(edgarMode === "scan" ? "EDGAR" : ""),
              background: edgarMode === "scan" ? `${C.accent}20` : "transparent",
            }}>SCAN SISTEMA (14gg)</button>
            <button onClick={() => setEdgarMode("ticker")} style={{
              ...tabStyle(edgarMode === "ticker" ? "EDGAR" : ""),
              background: edgarMode === "ticker" ? `${C.accent}20` : "transparent",
            }}>TICKER SINGOLO</button>
            {edgarMode === "ticker" && (
              <>
                <input value={edgarTicker}
                  onChange={e => setEdgarTicker(e.target.value.toUpperCase())}
                  onKeyDown={e => e.key === "Enter" && fetchEdgar()}
                  placeholder="es. AAPL"
                  style={{
                    background: C.card, border: `1px solid ${C.border}`, color: C.text,
                    fontSize: 10, padding: "4px 10px", borderRadius: 4, width: 120,
                    fontFamily: "'JetBrains Mono', monospace",
                  }} />
                <button onClick={fetchEdgar} style={{
                  background: `${C.accent}20`, border: `1px solid ${C.accent}40`,
                  color: C.accent, fontSize: 10, padding: "4px 14px", borderRadius: 4, cursor: "pointer",
                }}>{loadingEdgar ? "..." : "CERCA"}</button>
              </>
            )}
            {edgarMode === "scan" && (
              <button onClick={fetchEdgar} style={{
                background: "transparent", border: `1px solid ${C.border}`,
                color: C.muted, fontSize: 10, padding: "4px 12px", borderRadius: 4, cursor: "pointer",
              }}>{loadingEdgar ? "..." : "↻ REFRESH"}</button>
            )}
          </div>
          {/* Form-type legend */}
          <div style={{ display: "flex", gap: 16, marginBottom: 14, fontSize: 9, color: C.dim }}>
            <span><span style={{ color: C.red    }}>■</span> 8-K  eventi significativi</span>
            <span><span style={{ color: C.yellow }}>■</span> 4    transazioni insider</span>
            <span><span style={{ color: C.green  }}>■</span> 10-Q trimestrale</span>
            <span><span style={{ color: C.accent }}>■</span> 10-K annuale</span>
          </div>
          <div style={{ border: `1px solid ${C.border}`, borderRadius: 4, overflow: "hidden" }}>
            {loadingEdgar
              ? <div style={{ color: C.muted, fontSize: 11, padding: 16 }}>Ricerca EDGAR...</div>
              : edgarScanMsg
                ? <div style={{ color: C.yellow, fontSize: 11, padding: 16 }}>⚠ {edgarScanMsg}</div>
              : edgarItems.length === 0
                ? <div style={{ color: C.dim, fontSize: 11, padding: 16 }}>
                    {edgarMode === "ticker" ? "Inserisci un ticker e premi CERCA." : "Nessun filing trovato."}
                  </div>
                : edgarItems.map((item, i) => <NewsRow key={i} item={item} idx={i} onOpen={setSelectedNews} />)
            }
          </div>
        </div>
      )}
    </div>
  );
}

// ─── SEC FILINGS ──────────────────────────────────────────────────────────────
function FilingsPage() {
  const FORM_TYPES = ["ALL", "8-K", "SC 13D", "SC 13G", "Form 4", "S-1"];
  const SCORES     = ["ALL", "HIGH", "MEDIUM", "LOW"];
  const TIME_OPTS  = [
    { label: "OGGI",  days: 1 },
    { label: "3GG",   days: 3 },
    { label: "7GG",   days: 7 },
    { label: "30GG",  days: 30 },
    { label: "TUTTI", days: 0 },
  ];

  const [items,         setItems]         = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [filter,        setFilter]        = useState("ALL");
  const [scoreFilter,   setScoreFilter]   = useState("ALL");
  const [timeFilter,    setTimeFilter]    = useState(7);
  const [inUniverse,    setInUniverse]    = useState(false);
  const [expanded,      setExpanded]      = useState(null);
  const [aiResults,     setAiResults]     = useState({});
  const [aiLoading,     setAiLoading]     = useState({});
  const [lastUpdate,    setLastUpdate]    = useState(null);

  const fetchFilings = async (forceRefresh = false) => {
    setLoading(true);
    try {
      const data = await api.get(`/api/filings?refresh=${forceRefresh ? 1 : 0}`);
      setItems(Array.isArray(data) ? data : []);
      setLastUpdate(new Date().toLocaleTimeString("it-IT"));
    } catch (e) { console.error("Filings:", e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchFilings(); }, []);

  const analyzeAI = async (e, item) => {
    e.stopPropagation();
    const id = item.id;
    setAiLoading(p => ({ ...p, [id]: true }));
    try {
      const qs = new URLSearchParams({
        cik:       item.cik       || "",
        form_type: item.form_type || "8-K",
        title:     item.title     || "",
        company:   item.company   || "",
      }).toString();
      const res = await api.get(`/api/filings/analyze?${qs}`);
      const badge = res.doc_fetched ? "📄 documento reale" : "📋 titolo+summary";
      setAiResults(p => ({ ...p, [id]: `[${badge}]\n\n${res.summary}` }));
      setExpanded(id);
    } catch (err) {
      setAiResults(p => ({ ...p, [id]: "Errore analisi AI." }));
    }
    finally { setAiLoading(p => ({ ...p, [id]: false })); }
  };

  const scoreColor = s => s === "HIGH" ? C.red : s === "MEDIUM" ? C.yellow : C.muted;
  const formColor  = f => {
    if (f === "SC 13D" || f === "SC 13G") return C.red;
    if (f === "S-1")   return C.accent;
    if (f === "8-K")   return C.yellow;
    return C.muted;
  };

  const nowTs = Date.now() / 1000;
  const filtered = items
    .filter(i => timeFilter === 0 || (nowTs - (i.published_ts || 0)) <= timeFilter * 86400)
    .filter(i => filter === "ALL" || i.form_type === filter)
    .filter(i => scoreFilter === "ALL" || i.score === scoreFilter)
    .filter(i => !inUniverse || i.in_universe);

  const pillStyle = (active, color) => ({
    padding: "4px 12px", fontSize: 10, cursor: "pointer", borderRadius: 4,
    fontFamily: "'JetBrains Mono', monospace",
    background: active ? `${color}20` : "transparent",
    border: active ? `1px solid ${color}50` : `1px solid ${C.border}`,
    color: active ? color : C.muted,
  });

  return (
    <div style={{ padding: "24px 32px", maxWidth: 1200, margin: "0 auto" }}>

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 11, color: C.muted, letterSpacing: "0.15em" }}>SEC EDGAR</div>
          <div style={{ fontSize: 22, fontWeight: 900, color: C.text }}>FILINGS MONITOR</div>
          <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>
            8-K · SC 13D/G · Form 4 · S-1 — tutti i mercati USA
            {lastUpdate && ` · aggiornato ${lastUpdate}`}
          </div>
        </div>
        <button
          onClick={() => fetchFilings(true)} disabled={loading}
          style={{ padding: "8px 20px", background: C.accent, color: C.bg, border: "none",
                   borderRadius: 4, fontSize: 10, fontWeight: 700, cursor: "pointer",
                   fontFamily: "'JetBrains Mono', monospace" }}
        >
          {loading ? "CARICAMENTO..." : "↻ REFRESH"}
        </button>
      </div>

      {/* Filtri riga 1 — periodo temporale */}
      <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 8 }}>
        <span style={{ fontSize: 9, color: C.muted, marginRight: 4, letterSpacing: "0.1em" }}>PERIODO</span>
        {TIME_OPTS.map(({ label, days }) => (
          <button key={label} style={pillStyle(timeFilter === days, C.accent2)} onClick={() => setTimeFilter(days)}>{label}</button>
        ))}
      </div>

      {/* Filtri riga 2 — form type, score, universe */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginBottom: 16 }}>
        {FORM_TYPES.map(ft => (
          <button key={ft} style={pillStyle(filter === ft, C.accent)} onClick={() => setFilter(ft)}>{ft}</button>
        ))}
        <div style={{ width: 1, height: 18, background: C.border, margin: "0 4px" }} />
        {SCORES.map(s => (
          <button key={s} style={pillStyle(scoreFilter === s, scoreColor(s))} onClick={() => setScoreFilter(s)}>{s}</button>
        ))}
        <div style={{ width: 1, height: 18, background: C.border, margin: "0 4px" }} />
        <button style={pillStyle(inUniverse, C.green)} onClick={() => setInUniverse(!inUniverse)}>★ IN UNIVERSE</button>
        <span style={{ marginLeft: "auto", fontSize: 10, color: C.muted }}>{filtered.length} filing{filtered.length !== 1 ? "s" : ""}</span>
      </div>

      {/* Tabella */}
      {loading && items.length === 0 ? (
        <div style={{ textAlign: "center", color: C.muted, fontSize: 12, padding: 60 }}>Caricamento filing EDGAR...</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: "center", color: C.muted, fontSize: 12, padding: 60 }}>Nessun filing trovato</div>
      ) : (
        <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 8, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.border}`, background: `${C.dim}80` }}>
                {["SCORE", "FORM", "COMPANY", "FILED", "SUMMARY", "AZIONI"].map(h => (
                  <th key={h} style={{ padding: "10px 14px", fontSize: 9, color: C.muted,
                                       letterSpacing: "0.1em", textAlign: "left", fontWeight: 700 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((item, idx) => {
                const id        = item.id || item.link || idx;
                const isOpen    = expanded === id;
                const aiText    = aiResults[id];
                const isLoading = aiLoading[id];
                return (
                  <React.Fragment key={id}>
                    <tr
                      onClick={() => setExpanded(isOpen ? null : id)}
                      style={{ borderBottom: `1px solid ${C.border}`, cursor: "pointer",
                               background: isOpen ? `${C.accent}08` : "transparent" }}
                    >
                      {/* Score */}
                      <td style={{ padding: "10px 14px", whiteSpace: "nowrap" }}>
                        <span style={{ fontSize: 9, padding: "2px 8px", borderRadius: 3, fontWeight: 700,
                          background: `${scoreColor(item.score)}20`, color: scoreColor(item.score) }}>
                          {item.score || "—"}
                        </span>
                        {item.in_universe && (
                          <span style={{ fontSize: 8, marginLeft: 5, color: C.green }} title="Nel tuo universo 136">★</span>
                        )}
                      </td>
                      {/* Form */}
                      <td style={{ padding: "10px 14px" }}>
                        <span style={{ fontSize: 9, padding: "2px 8px", borderRadius: 3, fontWeight: 700,
                          background: `${formColor(item.form_type)}20`, color: formColor(item.form_type) }}>
                          {item.form_type || "—"}
                        </span>
                      </td>
                      {/* Company */}
                      <td style={{ padding: "10px 14px", maxWidth: 260 }}>
                        {item.ticker && (
                          <span style={{ fontSize: 10, color: C.accent, fontWeight: 700, marginRight: 6 }}>{item.ticker}</span>
                        )}
                        <span style={{ fontSize: 10, color: C.text }}>
                          {(item.company || item.title || "").slice(0, 55)}
                        </span>
                      </td>
                      {/* Filed */}
                      <td style={{ padding: "10px 14px", fontSize: 10, color: C.muted, whiteSpace: "nowrap" }}>
                        {item.published || "—"}
                      </td>
                      {/* Summary */}
                      <td style={{ padding: "10px 14px", maxWidth: 300 }}>
                        <span style={{ fontSize: 10, color: C.muted }}>
                          {(item.summary || "").slice(0, 90)}{(item.summary || "").length > 90 ? "…" : ""}
                        </span>
                      </td>
                      {/* Azioni */}
                      <td style={{ padding: "10px 14px" }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: "flex", gap: 6 }}>
                          <button
                            onClick={e => analyzeAI(e, item)} disabled={isLoading}
                            style={{ padding: "4px 10px", fontSize: 9, cursor: "pointer",
                              background: aiText ? `${C.green}20` : `${C.accent}20`,
                              color: aiText ? C.green : C.accent,
                              border: `1px solid ${aiText ? C.green : C.accent}50`,
                              borderRadius: 3, fontFamily: "'JetBrains Mono', monospace" }}
                          >
                            {isLoading ? "…" : aiText ? "✓ AI" : "⚡ AI"}
                          </button>
                          {item.link && (
                            <a href={item.link} target="_blank" rel="noopener noreferrer"
                              style={{ padding: "4px 10px", fontSize: 9, color: C.muted,
                                border: `1px solid ${C.border}`, borderRadius: 3,
                                textDecoration: "none", fontFamily: "'JetBrains Mono', monospace" }}>
                              SEC ↗
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                    {/* Riga espansa con AI summary */}
                    {isOpen && aiText && (
                      <tr style={{ background: `${C.accent}05`, borderBottom: `1px solid ${C.border}` }}>
                        <td colSpan={6} style={{ padding: "12px 24px" }}>
                          <div style={{ fontSize: 10, color: C.text, lineHeight: 1.8,
                                        borderLeft: `2px solid ${C.accent}`, paddingLeft: 12,
                                        whiteSpace: "pre-wrap" }}>
                            {aiText}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── SETTINGS ─────────────────────────────────────────────────────────────────
function SettingsPage() {
  const [tickers, setTickers] = useState([]);
  const [newTicker, setNewTicker] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const fetchTickers = async () => {
    try {
      const data = await api.get("/api/tickers");
      setTickers(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchTickers(); }, []);

  const toggleTicker = async (symbol) => {
    setSaving(symbol);
    try { await api.post(`/api/tickers/${symbol}/toggle`); await fetchTickers(); }
    catch (e) { console.error(e); }
    finally { setSaving(null); }
  };

  const addTicker = async () => {
    if (!newTicker.trim()) return;
    setSaving("new");
    try { await api.post(`/api/tickers/${newTicker.trim().toUpperCase()}`); setNewTicker(""); await fetchTickers(); }
    catch (e) { console.error(e); }
    finally { setSaving(null); }
  };

  const deleteTicker = async (symbol) => {
    if (!window.confirm(`Eliminare ${symbol}?`)) return;
    setSaving(symbol);
    try { await api.delete(`/api/tickers/${symbol}`); await fetchTickers(); }
    catch (e) { console.error(e); }
    finally { setSaving(null); }
  };

  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/api/stats").then(setStats).catch(console.error);
  }, []);

  const active = tickers.filter(t => t.active);
  const inactive = tickers.filter(t => !t.active);

  if (loading) return <div style={{ padding: 24, color: C.muted, fontFamily: "JetBrains Mono", fontSize: 12 }}>Caricamento...</div>;

  const currentTheme = localStorage.getItem("quantos_theme") || "DARK";
  const changeTheme = (name) => {
    localStorage.setItem("quantos_theme", name);
    window.location.reload();
  };

  return (
    <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
      <Panel>
        <Label color={C.muted}>TEMA INTERFACCIA</Label>
        <div style={{ display: "flex", gap: 12, marginTop: 14, flexWrap: "wrap" }}>
          {[
            { id: "DARK",   label: "DARK",          preview: ["#08090d","#00d4ff","#00e5a0","#ff4466"], desc: "Default — navy scuro" },
            { id: "ORANGE", label: "QUANTOS ORANGE", preview: ["#000000","#FF6600","#33FF66","#FF3333"], desc: "Ambra su nero — stile terminale" },
            { id: "MATRIX", label: "MATRIX",         preview: ["#000000","#00FF41","#00FF41","#FF4444"], desc: "Verde fosforescente" },
          ].map(t => (
            <button key={t.id} onClick={() => changeTheme(t.id)} style={{
              background: currentTheme === t.id ? `${C.accent}15` : "transparent",
              border: `1px solid ${currentTheme === t.id ? C.accent : C.border}`,
              borderRadius: 8, padding: "12px 18px", cursor: "pointer",
              display: "flex", flexDirection: "column", gap: 8, minWidth: 160,
            }}>
              <div style={{ display: "flex", gap: 5 }}>
                {t.preview.map((col, i) => (
                  <div key={i} style={{ width: 16, height: 16, borderRadius: 3, background: col }} />
                ))}
              </div>
              <div style={{ fontFamily: "JetBrains Mono", fontSize: 11,
                color: currentTheme === t.id ? C.accent : C.text, fontWeight: 700 }}>
                {t.label}
              </div>
              <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted }}>
                {t.desc}
              </div>
              {currentTheme === t.id && (
                <div style={{ fontFamily: "JetBrains Mono", fontSize: 8,
                  color: C.accent, letterSpacing: "0.1em" }}>✓ ATTIVO</div>
              )}
            </button>
          ))}
        </div>
      </Panel>
      <Panel>
        <Label color={C.muted}>AGGIUNGI TICKER</Label>
        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <input value={newTicker} onChange={e => setNewTicker(e.target.value.toUpperCase())}
            onKeyDown={e => e.key === "Enter" && addTicker()} placeholder="Es. TSLA"
            style={{ flex: 1, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, padding: "8px 12px", color: C.text, fontFamily: "'JetBrains Mono', monospace", fontSize: 13, outline: "none" }} />
          <button onClick={addTicker} disabled={saving === "new"} style={{
            background: `${C.accent}20`, border: `1px solid ${C.accent}40`, color: C.accent,
            fontFamily: "'JetBrains Mono', monospace", fontSize: 11, padding: "8px 20px", borderRadius: 6, cursor: "pointer"
          }}>{saving === "new" ? "..." : "+ AGGIUNGI"}</button>
        </div>
      </Panel>
      <Panel>
        <Label color={C.muted}>TICKER ATTIVI — {active.length} in raccolta dati</Label>
        <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 8 }}>
          {active.map(t => (
            <div key={t.symbol} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: `${C.green}10`, border: `1px solid ${C.green}30`, borderRadius: 6, padding: "8px 12px" }}>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 700, color: C.green }}>{t.symbol}</span>
              <div style={{ display: "flex", gap: 6 }}>
                <button onClick={() => toggleTicker(t.symbol)} disabled={saving === t.symbol} style={{ background: "transparent", border: "none", color: C.yellow, cursor: "pointer", fontSize: 14 }}>⏸</button>
                <button onClick={() => deleteTicker(t.symbol)} disabled={saving === t.symbol} style={{ background: "transparent", border: "none", color: C.red, cursor: "pointer", fontSize: 14 }}>✕</button>
              </div>
            </div>
          ))}
        </div>
      </Panel>
      {inactive.length > 0 && (
        <Panel>
          <Label color={C.muted}>TICKER INATTIVI — {inactive.length} disponibili</Label>
          <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 8 }}>
            {inactive.map(t => (
              <div key={t.symbol} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "#ffffff05", border: `1px solid ${C.border}`, borderRadius: 6, padding: "8px 12px" }}>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: C.muted }}>{t.symbol}</span>
                <div style={{ display: "flex", gap: 6 }}>
                  <button onClick={() => toggleTicker(t.symbol)} disabled={saving === t.symbol} style={{ background: "transparent", border: "none", color: C.green, cursor: "pointer", fontSize: 14 }}>▶</button>
                  <button onClick={() => deleteTicker(t.symbol)} disabled={saving === t.symbol} style={{ background: "transparent", border: "none", color: C.red, cursor: "pointer", fontSize: 14 }}>✕</button>
                </div>
              </div>
            ))}
          </div>
        </Panel>
      )}

      <Panel>
        <Label color={"#4a5568"}>DATABASE STATUS</Label>
        {!stats ? (
          <div style={{ fontFamily: "JetBrains Mono", fontSize: 11, color: "#4a5568", marginTop: 8 }}>Caricamento...</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 12 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid #1a1f2e" }}>
                {["TABELLA", "RIGHE", "ULTIMO RECORD", "STATUS"].map(h => (
                  <th key={h} style={{ textAlign: "left", padding: "4px 10px", fontFamily: "JetBrains Mono", fontSize: 9, color: "#4a5568", letterSpacing: "0.12em", fontWeight: 400 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(stats).map(([table, info]) => (
                <tr key={table} style={{ borderBottom: "1px solid #1a1f2e30" }}>
                  <td style={{ padding: "6px 10px", fontFamily: "JetBrains Mono", fontSize: 11, color: "#00d4ff" }}>{table}</td>
                  <td style={{ padding: "6px 10px", fontFamily: "JetBrains Mono", fontSize: 11, fontWeight: 700, color: info.rows > 0 ? "#00e5a0" : "#4a5568" }}>{info.rows?.toLocaleString() || 0}</td>
                  <td style={{ padding: "6px 10px", fontFamily: "JetBrains Mono", fontSize: 10, color: "#4a5568" }}>{info.last_record ? info.last_record.substring(0, 16) : "—"}</td>
                  <td style={{ padding: "6px 10px" }}>
                    <span style={{
                      fontSize: 9, fontFamily: "JetBrains Mono",
                      color: info.error ? "#ff4466" : info.rows > 0 ? "#00e5a0" : "#f5c542",
                      background: info.error ? "#ff446615" : info.rows > 0 ? "#00e5a015" : "#f5c54215",
                      padding: "2px 8px", borderRadius: 4
                    }}>
                      {info.error ? "ERROR" : info.rows > 0 ? "ACTIVE" : "EMPTY"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </div>
  );
}

// ─── PLACEHOLDER ─────────────────────────────────────────────────────────────
function PlaceholderPage({ title }) {
  return (
    <div style={{ padding: 24, display: "flex", alignItems: "center", justifyContent: "center", height: "60vh" }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontFamily: "JetBrains Mono", fontSize: 48, color: C.border, fontWeight: 900 }}>{title}</div>
        <div style={{ fontFamily: "JetBrains Mono", fontSize: 12, color: C.muted, marginTop: 12 }}>In sviluppo</div>
      </div>
    </div>
  );
}

// ─── PAPER PORTFOLIO PAGE ─────────────────────────────────────────────────────
// Cache segnali Strategies — persiste tra navigazioni, si azzera al refresh browser
let _strategiesCache = null; // { data, timestamp }
let _volSurfaceCache = {}; // { [ticker]: { data, grid } }

const DEFAULT_BACKTEST_PARAMS = {
  initial: 10000, budget_pct: 45, max_trades: 2,
  tp_mult: 2.0, sl_mult: 0,
  sl_short_mult: 0.5,
  friction_entry: 2.5, friction_exit: 2.5,
  vst_threshold: 0.3,
  dte_min: 20, dte_max: 60,
  moneyness_pct: 15, min_snapshots: 3,
  trail_pct: 0, long_only: false,
  min_score: 0.05, iv_rank_threshold: 0.70,
  kelly_fraction: 0.0, max_contracts: "", long_iv_rank_thr: 0.40,
  date_from: "2024-04-08", date_to: "2025-12-31",
  split_date: "2025-10-08",
  rotate_enabled: false, rotate_threshold: 1.5,
  rotate_min_hold_days: 5, rotate_only_if_profitable: true
};

function BacktestPage() {
  const abortRef = React.useRef(null);
  const [params, setParams] = React.useState({...DEFAULT_BACKTEST_PARAMS});
  const [result, setResult]   = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [progress, setProgress] = React.useState({current:0, total:0});
  const [error, setError]     = React.useState(null);

  

  const stopBacktest = () => {
    if (abortRef.current) { abortRef.current.abort(); }
    setLoading(false);
  };

  const runBacktest = async () => {
    setLoading(true); setError(null); setResult(null); setProgress({current:0,total:0});
    const token = localStorage.getItem("quantos_token");
    const pollId = setInterval(async()=>{ try{ const pr=await fetch("/api/backtest/progress",{headers:{"Authorization":"Bearer "+token}}); const p=await pr.json(); setProgress(p); }catch(_){} },2000);
    setTimeout(()=>clearInterval(pollId),600000);
    try {
      const token = localStorage.getItem("quantos_token");
      const payload = {...params};
      payload.max_contracts = (payload.max_contracts === "" || payload.max_contracts === null) ? null : parseInt(payload.max_contracts);
      const r = await fetch("/api/backtest/run", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": "Bearer " + token },
        body: JSON.stringify(payload)
      });
      if (!r.ok) throw new Error(await r.text());
      setResult(await r.json());
    } catch(e) {
      if (e.name === "AbortError") {
        setError("Simulazione interrotta dall utente.");
      } else {
        setError(e.message);
      }
    }
    finally { setLoading(false); abortRef.current = null; }
  };

  const P = ({ label, field, step=0.1, min=0 }) => (
    <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
      <span style={{ fontSize:10, color:C.muted }}>{label}</span>
      <input type="number" step={step} min={min}
        value={params[field]}
        onChange={e => setParams(p => ({...p, [field]: parseFloat(e.target.value) || 0}))}
        style={{ background:C.panel, border:"1px solid "+C.dim, color:C.text,
                 padding:"6px 10px", borderRadius:4, fontSize:12, width:110 }} />
    </div>
  );

  const D = ({ label, field }) => (
    <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
      <span style={{ fontSize:10, color:C.muted }}>{label}</span>
      <input type="date" value={params[field]}
        onChange={e => setParams(p => ({...p, [field]: e.target.value}))}
        style={{ background:C.panel, border:"1px solid "+C.dim, color:C.text,
                 padding:"6px 10px", borderRadius:4, fontSize:12, colorScheme:'dark' }} />
    </div>
  );

  const pnlColor = v => v >= 0 ? C.green : C.red;

  return (
    <div style={{ padding:24, maxWidth:1400, margin:"0 auto" }}>
      <Label color={C.accent} style={{ fontSize:18, letterSpacing:3 }}>BACKTEST WALK-FORWARD</Label>
      <div style={{ fontSize:11, color:C.muted, marginTop:4, marginBottom:24 }}>
        Simulazione cronologica giorno per giorno — nessun lookahead
      </div>

      {/* Parametri */}
      <Panel style={{ marginBottom:20 }}>
        <Label color={C.muted} style={{ fontSize:11, marginBottom:16 }}>PARAMETRI</Label>
        <div style={{ display:"flex", flexWrap:"wrap", gap:16, alignItems:"flex-end" }}>
          <P label="Capitale iniziale €"   field="initial"       step={1000} min={1000} />
          <P label="Budget % capitale"     field="budget_pct"    step={5}    min={5} />
          <P label="Max trade simultanei"  field="max_trades"    step={1}    min={1} />
          <P label="Take Profit (x)"       field="tp_mult"       step={0.5}  min={1} />
          <P label="Stop Loss (x, 0=exp)"  field="sl_mult"       step={0.1}  min={0} />
          <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
            <span style={{ fontSize:10, color:C.muted }}>SL Short (x premio, 0=kamikaze)</span>
            <select value={params.sl_short_mult}
              onChange={e => setParams(p => ({...p, sl_short_mult: parseFloat(e.target.value)}))}
              style={{ background:C.panel, border:"1px solid "+C.dim, color:C.text,
                       padding:"6px 10px", borderRadius:4, fontSize:12, width:110 }}>
              <option value={0}>0 — kamikaze (nessun SL)</option>
              {[0.1,0.2,0.3,0.4,0.5,0.6,0.7,0.8,0.9,1,1.5,2,2.5,3,3.5,4,4.5,5].map(v => (
                <option key={v} value={v}>{v}x — perdo {Math.round(v*100)}% premio</option>
              ))}
            </select>
          </div>
          <P label="Friction entrata %"    field="friction_entry" step={0.5} min={0} />
          <P label="Friction uscita %"     field="friction_exit"  step={0.5} min={0} />
          <P label="VST soglia"            field="vst_threshold"  step={0.05} min={0.1} />
          <P label="DTE min" field="dte_min" step={1} min={5}/>
          <P label="DTE max" field="dte_max" step={1} min={10}/>
          <P label="Moneyness %" field="moneyness_pct" step={1} min={1}/>
          <P label="Min Snapshots" field="min_snapshots" step={1} min={1}/>
          <P label="Trail Stop %" field="trail_pct" step={0.05} min={0}/>
          <P label="Score Min (0-1)" field="min_score" step={0.05} min={0} max={1}/>
          <P label="Kelly Fraction (0=off)" field="kelly_fraction" step={0.1} min={0} max={1}/>
          <div style={{display:"flex",flexDirection:"column",gap:4}}>
            <label style={{color:C.muted,fontSize:11}}>MAX CONTRATTI (vuoto=no cap)</label>
            <input type="number" min={1} step={1}
              value={params.max_contracts}
              onChange={e=>setParams(p=>({...p,max_contracts:e.target.value}))}
              placeholder="es. 5"
              style={{width:110,background:"#111",color:C.text,border:"1px solid "+C.border,
                      borderRadius:4,padding:"6px 8px",fontFamily:"JetBrains Mono",fontSize:12}}/>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:8,marginTop:4}}>
            <input type="checkbox" id="lo" checked={params.long_only||false} onChange={e=>setParams(p=>({...p,long_only:e.target.checked}))}/>
            <label htmlFor="lo" style={{color:C.muted,fontSize:11,cursor:"pointer"}}>SOLO LONG</label>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:8,marginTop:4}}>
            <input type="checkbox" id="rot" checked={params.rotate_enabled||false} onChange={e=>setParams(p=>({...p,rotate_enabled:e.target.checked}))}/>
            <label htmlFor="rot" style={{color:C.muted,fontSize:11,cursor:"pointer"}}>🔄 ROTAZIONE ATTIVA</label>
          </div>
          {params.rotate_enabled && (
            <>
              <P label="Soglia rotazione (x)" field="rotate_threshold" step={0.1} min={1}/>
              <P label="Hold minimo (gg)" field="rotate_min_hold_days" step={1} min={1}/>
              <div style={{display:"flex",alignItems:"center",gap:8,marginTop:4}}>
                <input type="checkbox" id="rop" checked={params.rotate_only_if_profitable!==false} onChange={e=>setParams(p=>({...p,rotate_only_if_profitable:e.target.checked}))}/>
                <label htmlFor="rop" style={{color:C.muted,fontSize:11,cursor:"pointer"}}>Ruota solo se in utile</label>
              </div>
            </>
          )}
          <D label="Da"  field="date_from" />
          <D label="A"   field="date_to" />
          <D label="Split OOS" field="split_date" />
          <button onClick={runBacktest} disabled={loading}
            style={{ padding:"10px 28px", background:C.accent, color:"#000",
                     border:"none", borderRadius:4, fontFamily:"JetBrains Mono",
                     fontSize:13, fontWeight:700, cursor:"pointer", alignSelf:"flex-end" }}>
            {loading ? "CALCOLO..." : "▶ AVVIA BACKTEST"}
          </button>
          {!loading && (
            <button onClick={()=>setParams({...DEFAULT_BACKTEST_PARAMS})}
              style={{ padding:"10px 20px", background:"transparent", color:"#888",
                       border:"1px solid #555", borderRadius:4, fontFamily:"JetBrains Mono",
                       fontSize:13, fontWeight:700, cursor:"pointer", alignSelf:"flex-end" }}>
              RESET
            </button>
          )}
          {loading && (
            <button onClick={stopBacktest}
              style={{ padding:"10px 20px", background:"transparent", color:C.red,
                       border:"1px solid "+C.red, borderRadius:4, fontFamily:"JetBrains Mono",
                       fontSize:13, fontWeight:700, cursor:"pointer", alignSelf:"flex-end" }}>
              ■ ARRESTA
            </button>
          )}
        </div>
      </Panel>

      {error && <div style={{ color:C.red, marginBottom:16, fontSize:12 }}>ERRORE: {error}</div>}
      {loading && <div style={{marginBottom:16}}><div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:C.muted,marginBottom:4}}><span>{progress.phase==="preload"?"CARICAMENTO DATI...":"ELABORAZIONE IN CORSO..."}</span><span>{progress.total>0?Math.round(progress.current/progress.total*100)+"%":"..."}</span></div><div style={{height:4,background:"#1a1a1a",borderRadius:2}}><div style={{height:4,background:C.accent,borderRadius:2,width:progress.total>0?(Math.round(progress.current/progress.total*100))+"%":"0%",transition:"width 0.5s ease"}}></div></div><div style={{fontSize:10,color:C.muted,marginTop:4}}>{progress.total>0?progress.current+" / "+progress.total+" giorni":""}</div></div>}

      {result && <>
        {/* IS / OOS Walk-Forward Comparison */}
        {result.is && result.oos && (() => {
          const is  = result.is.summary;
          const oos = result.oos.summary;
          const wrDelta = (oos.win_rate || 0) - (is.win_rate || 0);
          const confirmed = oos.total_pnl > 0 && wrDelta >= -15;
          const col = confirmed ? C.green : C.red;
          const verdict = confirmed ? "✓ ALPHA CONFERMATO" : "⚠ ATTENZIONE OVERFITTING";
          const kpi = (label, isVal, oosVal, fmt, goodIfPositive=true) => {
            const delta = oosVal - isVal;
            const dc = delta >= 0 ? C.green : C.red;
            return (
              <tr key={label} style={{ borderBottom: `1px solid ${C.border}20` }}>
                <td style={{ padding:"5px 10px", color:C.muted, fontSize:10 }}>{label}</td>
                <td style={{ padding:"5px 10px", color:C.text, fontWeight:700, textAlign:"right" }}>{fmt(isVal)}</td>
                <td style={{ padding:"5px 10px", color:oosVal > 0 || !goodIfPositive ? C.green : C.red, fontWeight:700, textAlign:"right" }}>{fmt(oosVal)}</td>
                <td style={{ padding:"5px 10px", color:dc, textAlign:"right", fontSize:10 }}>{delta >= 0 ? "+" : ""}{fmt(delta)}</td>
              </tr>
            );
          };
          return (
            <Panel style={{ marginBottom:20, border:`1px solid ${col}55` }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
                <Label color={C.muted} style={{ fontSize:11 }}>WALK-FORWARD IS / OOS</Label>
                <span style={{ fontFamily:"JetBrains Mono", fontSize:12, fontWeight:700, color:col,
                               background:col+"18", border:`1px solid ${col}44`, borderRadius:4, padding:"4px 14px" }}>
                  {verdict}
                </span>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:14 }}>
                <div>
                  <div style={{ fontSize:9, color:C.muted, marginBottom:4 }}>IN-SAMPLE (IS) — {result.split_date ? params.date_from + " → " + result.split_date : ""}</div>
                  <div style={{ fontSize:10, color:C.muted }}>Trade: <span style={{ color:C.text }}>{is.n_trades}</span> &nbsp;|&nbsp; WR: <span style={{ color:is.win_rate>=50?C.green:C.red }}>{is.win_rate}%</span> &nbsp;|&nbsp; P&L: <span style={{ color:is.total_pnl>=0?C.green:C.red }}>{is.total_pnl>=0?"+":""}{is.total_pnl?.toLocaleString("it-IT")}€</span></div>
                </div>
                <div>
                  <div style={{ fontSize:9, color:C.muted, marginBottom:4 }}>OUT-OF-SAMPLE (OOS) — {result.split_date ? result.split_date + " → " + params.date_to : ""}</div>
                  <div style={{ fontSize:10, color:C.muted }}>Trade: <span style={{ color:C.text }}>{oos.n_trades}</span> &nbsp;|&nbsp; WR: <span style={{ color:oos.win_rate>=50?C.green:C.red }}>{oos.win_rate}%</span> &nbsp;|&nbsp; P&L: <span style={{ color:oos.total_pnl>=0?C.green:C.red }}>{oos.total_pnl>=0?"+":""}{oos.total_pnl?.toLocaleString("it-IT")}€</span></div>
                </div>
              </div>
              <div style={{ overflowX:"auto" }}>
                <table style={{ width:"100%", borderCollapse:"collapse", fontFamily:"JetBrains Mono", fontSize:11 }}>
                  <thead>
                    <tr style={{ borderBottom:`1px solid ${C.border}` }}>
                      {["KPI","IS","OOS","Δ"].map(h => (
                        <th key={h} style={{ textAlign: h==="KPI"?"left":"right", padding:"5px 10px", color:C.muted, fontSize:9 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {kpi("Win Rate", is.win_rate||0, oos.win_rate||0, v => v.toFixed(1)+"%")}
                    {kpi("P&L Totale", is.total_pnl||0, oos.total_pnl||0, v => (v>=0?"+":"")+v?.toLocaleString("it-IT")+"€")}
                    {kpi("Rendimento", is.total_pnl_pct||0, oos.total_pnl_pct||0, v => (v>=0?"+":"")+v?.toFixed(1)+"%")}
                    {kpi("N° Trade", is.n_trades||0, oos.n_trades||0, v => v, false)}
                  </tbody>
                </table>
              </div>
              {wrDelta < -15 && (
                <div style={{ marginTop:10, fontSize:10, color:C.red }}>
                  ⚠ Win Rate OOS cala di {Math.abs(wrDelta).toFixed(1)}% rispetto all&apos;IS — probabile overfitting. Riduci i parametri o allarga i vincoli.
                </div>
              )}
            </Panel>
          );
        })()}
        {/* Parametri test */}
        <Panel style={{marginBottom:20}}>
          <div style={{fontSize:10,color:C.muted,marginBottom:10,letterSpacing:1}}>PARAMETRI TEST ESEGUITO</div>
          <div style={{display:'flex',flexWrap:'wrap',gap:'8px 24px'}}>
            {Object.entries(params).map(([k,v])=>(
              <span key={k} style={{fontSize:11,color:C.muted}}>{k}: <span style={{color:C.text,fontWeight:700}}>{v.toString()}</span></span>
            ))}
          </div>
        </Panel>
        {/* CSV Download */}
        <div style={{display:'flex',justifyContent:'flex-end',marginBottom:12}}>
          <button onClick={()=>{
            const hdr=['underlying','expiry','strike','right_type','direction','entry_date','exit_date','entry_price','exit_price','pnl','win','reason','confluence'];
            const pline=Object.entries(params).map(([k,v])=>"# "+k+": "+v).join("\n");
            const rows=result.trades.map(t=>hdr.map(h=>t[h]!=null?t[h]:'').join(','));
            const csv=pline+"\n\n"+hdr.join(',')+"\n"+rows.join("\n");
            const a=document.createElement('a');
            a.href=URL.createObjectURL(new Blob([csv],{type:'text/csv'}));
            a.download='backtest_'+params.date_from+'_'+params.date_to+'.csv';
            a.click();
          }} style={{padding:'8px 16px',background:'transparent',color:C.accent,border:'1px solid '+C.accent,borderRadius:4,fontFamily:'JetBrains Mono',fontSize:12,cursor:'pointer'}}>
            SCARICA CSV TRADE
          </button>
        </div>
        {/* Summary */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))", gap:12, marginBottom:20 }}>
          {[
            { label:"CAPITALE FINALE", value:"€"+result.summary.final_equity.toLocaleString("it-IT"), color:pnlColor(result.summary.total_pnl) },
            { label:"P&L TOTALE",      value:(result.summary.total_pnl>=0?"+":"")+"€"+result.summary.total_pnl.toLocaleString("it-IT"), color:pnlColor(result.summary.total_pnl) },
            { label:"RENDIMENTO",      value:(result.summary.total_pnl_pct>=0?"+":"")+result.summary.total_pnl_pct+"%", color:pnlColor(result.summary.total_pnl) },
            { label:"TRADE TOTALI",    value:result.summary.n_trades, color:C.text },
            { label:"WIN RATE",        value:result.summary.win_rate+"%", color:result.summary.win_rate>=50?C.green:C.red },
            { label:"SHARPE",          value:(result.summary.sharpe??0).toFixed(2), color:(result.summary.sharpe??0)>=0?C.green:C.red },
            { label:"MAX DRAWDOWN",    value:(result.summary.max_drawdown_pct??0).toFixed(1)+"%", color:C.red },
            { label:"ROTAZIONI",       value:result.summary.n_rotations??0, color:C.accent2 },
          ].map(m => (
            <Panel key={m.label}>
              <div style={{ fontSize:10, color:C.muted, marginBottom:6 }}>{m.label}</div>
              <div style={{ fontSize:20, fontWeight:700, color:m.color }}>{m.value}</div>
            </Panel>
          ))}
        </div>

        {/* Equity Curve */}
        <Panel style={{ marginBottom:20 }}>
          <Label color={C.muted} style={{ fontSize:11, marginBottom:12 }}>EQUITY CURVE</Label>
          <AreaChart width={900} height={220} data={result.equity_curve}
            style={{ maxWidth:"100%" }}>
            <defs>
              <linearGradient id="eqGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor={C.accent} stopOpacity={0.3}/>
                <stop offset="95%" stopColor={C.accent} stopOpacity={0}/>
              </linearGradient>
            </defs>
            <XAxis dataKey="date" tick={{ fontSize:9, fill:C.muted }}
              tickFormatter={v => v.slice(2,7)} interval={30} />
            <YAxis tick={{ fontSize:9, fill:C.muted }}
              tickFormatter={v => "€"+v.toLocaleString("it-IT")} width={80} />
            <CartesianGrid strokeDasharray="3 3" stroke={C.dim} />
            <Tooltip formatter={v => "€"+v.toLocaleString("it-IT")} labelStyle={{ color:C.muted }} />
            <ReferenceLine y={params.initial} stroke={C.dim} strokeDasharray="4 4" />
            <Area type="monotone" dataKey="equity" stroke={C.accent}
              fill="url(#eqGrad)" strokeWidth={2} dot={false} />
          </AreaChart>
        </Panel>

        {/* Monthly P&L */}
        <Panel style={{ marginBottom:20 }}>
          <Label color={C.muted} style={{ fontSize:11, marginBottom:12 }}>P&L MENSILE</Label>
          <BarChart width={900} height={180} data={result.monthly} style={{ maxWidth:"100%" }}>
            <XAxis dataKey="month" tick={{ fontSize:9, fill:C.muted }} />
            <YAxis tick={{ fontSize:9, fill:C.muted }} tickFormatter={v => "€"+v} width={70} />
            <CartesianGrid strokeDasharray="3 3" stroke={C.dim} />
            <Tooltip formatter={(v,n) => [n==="pnl"?"€"+v:v+"%", n==="pnl"?"P&L":"Win Rate"]} />
            <ReferenceLine y={0} stroke={C.dim} />
            <Bar dataKey="pnl" radius={[2,2,0,0]}>
              {result.monthly.map((m,i) => (
                <RCell key={i} fill={m.pnl >= 0 ? C.green : C.red} />
              ))}
            </Bar>
          </BarChart>
        </Panel>

        {/* Tabella mensile */}
        <Panel style={{ marginBottom:20 }}>
          <Label color={C.muted} style={{ fontSize:11, marginBottom:12 }}>RIEPILOGO MENSILE</Label>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
            <thead>
              <tr>{["MESE","TRADE","WIN","WIN RATE","P&L €","P&L %"].map(h => (
                <th key={h} style={{ padding:"6px 12px", textAlign:"right",
                  borderBottom:"1px solid "+C.dim, color:C.muted, fontSize:10 }}>{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {result.monthly.map((m,i) => {
                const pct = result.equity_curve.find(e => e.date.startsWith(m.month));
                const base = params.initial;
                const pnlPct = (m.pnl / base * 100).toFixed(2);
                return (
                  <tr key={i} style={{ borderBottom:"1px solid "+C.dim+"33" }}>
                    <td style={{ padding:"6px 12px", color:C.text }}>{m.month}</td>
                    <td style={{ padding:"6px 12px", textAlign:"right", color:C.muted }}>{m.trades}</td>
                    <td style={{ padding:"6px 12px", textAlign:"right", color:C.muted }}>{m.wins}</td>
                    <td style={{ padding:"6px 12px", textAlign:"right",
                      color:m.wr>=50?C.green:C.red }}>{m.wr}%</td>
                    <td style={{ padding:"6px 12px", textAlign:"right",
                      color:pnlColor(m.pnl) }}>{m.pnl>=0?"+":""}{m.pnl.toLocaleString("it-IT")}</td>
                    <td style={{ padding:"6px 12px", textAlign:"right",
                      color:pnlColor(m.pnl) }}>{pnlPct>=0?"+":""}{pnlPct}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Panel>

        {/* Trade list */}
        <Panel>
          <Label color={C.muted} style={{ fontSize:11, marginBottom:12 }}>
            LISTA TRADE ({result.trades.length})
          </Label>
          <div style={{ overflowX:"auto" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:11 }}>
              <thead>
                <tr>{["TICKER","DIR","TIPO","STRIKE","EXPIRY","ENTRATA","USCITA","ENTRY $","EXIT $","P&L €","MOTIVO"].map(h => (
                  <th key={h} style={{ padding:"5px 10px", textAlign:"right",
                    borderBottom:"1px solid "+C.dim, color:C.muted, fontSize:9 }}>{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {result.trades.slice(0,200).map((t,i) => (
                  <tr key={i} style={{ borderBottom:"1px solid "+C.dim+"22" }}>
                    <td style={{ padding:"5px 10px", color:C.accent }}>{t.underlying}</td>
                    <td style={{ padding:"5px 10px", textAlign:"right",
                      color:t.direction==="SHORT"?C.red:C.green }}>{t.direction||"LONG"}</td>
                    <td style={{ padding:"5px 10px", textAlign:"right",
                      color:t.right_type==="C"?C.green:C.red }}>{t.right_type==="C"?"CALL":"PUT"}</td>
                    <td style={{ padding:"5px 10px", textAlign:"right" }}>${t.strike}</td>
                    <td style={{ padding:"5px 10px", textAlign:"right", color:C.muted }}>{t.expiry}</td>
                    <td style={{ padding:"5px 10px", textAlign:"right", color:C.muted }}>{t.entry_date}</td>
                    <td style={{ padding:"5px 10px", textAlign:"right", color:C.muted }}>{t.exit_date}</td>
                    <td style={{ padding:"5px 10px", textAlign:"right" }}>${t.entry_price}</td>
                    <td style={{ padding:"5px 10px", textAlign:"right" }}>${t.exit_price}</td>
                    <td style={{ padding:"5px 10px", textAlign:"right",
                      color:pnlColor(t.pnl), fontWeight:700 }}>
                      {t.pnl>=0?"+":""}{t.pnl.toLocaleString("it-IT")}
                    </td>
                    <td style={{ padding:"5px 10px", textAlign:"right",
                      color:t.reason==="TP"?C.green:t.reason==="SL"?C.red:t.reason==="ROTATE"?C.accent2:C.muted }}>{t.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      </>}
    </div>
  );
}

// ─── PAPER PORTFOLIO PAGE ─────────────────────────────────────────────────────
function PaperPortfolioPage() {
  const [trades,   setTrades]   = useState([]);
  const [summary,  setSummary]  = useState({});
  const [loading,  setLoading]  = useState(true);
  const [closing,  setClosing]  = useState(null);
  const [closeVal, setCloseVal] = useState("");

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [t, s] = await Promise.all([
        api.get("/api/paper/trades"),
        api.get("/api/paper/pnl"),
      ]);
      setTrades(t.trades || []);
      setSummary(s || {});
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); const i = setInterval(fetchAll, 60000); return () => clearInterval(i); }, []);

  const openTrades   = trades.filter(t => t.status === "OPEN");
  const closedTrades = trades.filter(t => t.status === "CLOSED");

  const calcLivePnl = (trade) => {
    if (!trade.current_spot || !trade.legs?.length) return null;
    const S   = trade.current_spot;
    const dte = getDTE(trade.expiry);
    const iv  = trade.iv_at_entry || 0.25;
    return trade.legs.reduce((sum, leg) => {
      const entry = leg.premium || 0;
      const curr  = bsPrice(S, leg.strike, Math.max(0.001, dte / 365), 0.05, iv,
                            leg.type === "Call" ? "C" : "P");
      const sign  = leg.action === "BUY" ? 1 : -1;
      return sum + sign * (curr - entry) * 100 * (leg.qty || 1);
    }, 0);
  };

  const handleClose = async (id) => {
    const pnl = parseFloat(closeVal) || 0;
    await api.patch(`/api/paper/trades/${id}/close`, { pnl });
    setClosing(null);
    setCloseVal("");
    await fetchAll();
  };

  const totalLivePnl = openTrades.reduce((s, t) => s + (calcLivePnl(t) || 0), 0);

  const TH = ({ children }) => (
    <th style={{ textAlign:"left", padding:"6px 10px", fontFamily:"JetBrains Mono", fontSize:9, color:C.muted, letterSpacing:"0.1em", fontWeight:400 }}>{children}</th>
  );
  const TD = ({ children, color, bold }) => (
    <td style={{ padding:"10px 10px", fontFamily:"JetBrains Mono", fontSize:bold?13:11, fontWeight:bold?700:400, color:color||C.text }}>{children}</td>
  );

  return (
    <div style={{ padding:24, display:"flex", flexDirection:"column", gap:16 }}>

      {/* Summary */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12 }}>
        <Panel><Metric label="POSIZIONI APERTE"   value={summary.open_count   || 0}    color={C.yellow} /></Panel>
        <Panel><Metric label="P&L LIVE (EST.)"    value={`${totalLivePnl>=0?"+":""}$${totalLivePnl.toFixed(2)}`} color={totalLivePnl>=0?C.green:C.red} /></Panel>
        <Panel><Metric label="P&L REALIZZATO"     value={`${(summary.realized_pnl||0)>=0?"+":""}$${(summary.realized_pnl||0).toFixed(2)}`} color={(summary.realized_pnl||0)>=0?C.green:C.red} /></Panel>
        <Panel><Metric label="WIN RATE"            value={`${summary.win_rate||0}%  (${summary.wins||0}W / ${summary.losses||0}L)`} color={C.accent} /></Panel>
      </div>

      {/* Open positions */}
      <Panel>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
          <Label color={C.muted}>POSIZIONI APERTE — PAPER TRADING</Label>
          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
            <span style={{ fontFamily:"JetBrains Mono", fontSize:9, color:C.muted }}>P&L live calcolato via Black-Scholes · aggiorna ogni 60s</span>
            <button onClick={fetchAll} style={{ fontFamily:"JetBrains Mono", fontSize:10, color:C.accent, background:`${C.accent}15`,
              padding:"3px 10px", borderRadius:4, border:`1px solid ${C.accent}30`, cursor:"pointer" }}>↻</button>
          </div>
        </div>
        {loading && openTrades.length === 0 ? (
          <div style={{ fontFamily:"JetBrains Mono", fontSize:11, color:C.muted, padding:16 }}>Caricamento...</div>
        ) : openTrades.length === 0 ? (
          <div style={{ fontFamily:"JetBrains Mono", fontSize:11, color:C.muted, textAlign:"center", padding:32 }}>
            Nessuna posizione aperta — usa <span style={{ color:C.accent }}>📋 PRENDO POSIZIONE</span> nel pannello VST
          </div>
        ) : (
          <table style={{ width:"100%", borderCollapse:"collapse" }}>
            <thead>
              <tr style={{ borderBottom:`1px solid ${C.border}` }}>
                <TH>TICKER</TH><TH>STRATEGIA</TH><TH>EXPIRY</TH><TH>SPOT ENTRY</TH>
                <TH>SPOT NOW</TH><TH>P&L LIVE</TH><TH>VST</TH><TH>REGIME</TH><TH>DATA</TH><TH></TH>
              </tr>
            </thead>
            <tbody>
              {openTrades.map(t => {
                const pnl       = calcLivePnl(t);
                const isClosing = closing === t.id;
                return (
                  <React.Fragment key={t.id}>
                    <tr style={{ borderBottom:`1px solid ${C.border}40` }}>
                      <TD bold color={C.accent}>{t.ticker}</TD>
                      <TD>{t.strategy}</TD>
                      <TD color={C.yellow}>{t.expiry ? (t.expiry.includes('-') ? t.expiry.slice(0,10) : `${t.expiry.slice(0,4)}-${t.expiry.slice(4,6)}-${t.expiry.slice(6,8)}`) : "—"}</TD>
                      <TD color={C.muted}>${(t.spot_at_entry||0).toFixed(2)}</TD>
                      <TD>{t.current_spot ? `$${t.current_spot.toFixed(2)}` : "—"}</TD>
                      <td style={{ padding:"10px 10px", fontFamily:"JetBrains Mono", fontSize:13, fontWeight:700,
                        color: pnl == null ? C.muted : pnl >= 0 ? C.green : C.red }}>
                        {pnl == null ? "—" : `${pnl >= 0 ? "+" : ""}$${pnl.toFixed(2)}`}
                      </td>
                      <TD color={C.accent}>{t.vst_instant != null ? `${t.vst_instant>=0?"+":""}${Number(t.vst_instant).toFixed(2)}` : "—"}</TD>
                      <TD color={C.muted}>{t.regime || "—"}</TD>
                      <TD color={C.muted}>{t.created_at?.slice(0,16) || "—"}</TD>
                      <td style={{ padding:"10px 10px" }}>
                        <button onClick={() => { setClosing(isClosing ? null : t.id); setCloseVal(pnl != null ? pnl.toFixed(2) : "0"); }}
                          style={{ fontFamily:"JetBrains Mono", fontSize:9, padding:"3px 9px", borderRadius:3, cursor:"pointer",
                            background: isClosing ? `${C.red}20` : "transparent",
                            color: isClosing ? C.red : C.muted,
                            border: `1px solid ${isClosing ? C.red + "50" : C.border}` }}>
                          {isClosing ? "↩" : "✕ CHIUDI"}
                        </button>
                      </td>
                    </tr>
                    {isClosing && (
                      <tr style={{ background:`${C.red}08` }}>
                        <td colSpan={10} style={{ padding:"12px 20px" }}>
                          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                            <span style={{ fontFamily:"JetBrains Mono", fontSize:9, color:C.muted }}>P&L FINALE ($):</span>
                            <input value={closeVal} onChange={e => setCloseVal(e.target.value)} type="number" step="0.01"
                              style={{ fontFamily:"JetBrains Mono", fontSize:12, width:110, background:"#0a0a0a",
                                border:`1px solid ${C.border}`, color:C.text, padding:"4px 8px", borderRadius:3 }} />
                            <button onClick={() => handleClose(t.id)}
                              style={{ fontFamily:"JetBrains Mono", fontSize:10, padding:"5px 16px", borderRadius:3, cursor:"pointer",
                                background:`${C.red}20`, color:C.red, border:`1px solid ${C.red}40`, fontWeight:700 }}>
                              CONFERMA CHIUSURA
                            </button>
                            {pnl != null && (
                              <span style={{ fontFamily:"JetBrains Mono", fontSize:9, color:C.muted }}>
                                P&L stimato: <span style={{ color: pnl>=0?C.green:C.red }}>{pnl>=0?"+":""}${pnl.toFixed(2)}</span>
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </Panel>

      {/* Closed positions */}
      {closedTrades.length > 0 && (
        <Panel>
          <Label color={C.muted} style={{ marginBottom:16 }}>STORICO — POSIZIONI CHIUSE</Label>
          <table style={{ width:"100%", borderCollapse:"collapse" }}>
            <thead>
              <tr style={{ borderBottom:`1px solid ${C.border}` }}>
                <TH>TICKER</TH><TH>STRATEGIA</TH><TH>EXPIRY</TH>
                <TH>P&L REALIZ.</TH><TH>VST ENTRY</TH><TH>DATA APERTURA</TH><TH>DATA CHIUSURA</TH>
              </tr>
            </thead>
            <tbody>
              {closedTrades.map(t => {
                const pnl = parseFloat(t.pnl_at_close) || 0;
                return (
                  <tr key={t.id} style={{ borderBottom:`1px solid ${C.border}30` }}>
                    <TD bold>{t.ticker}</TD>
                    <TD color={C.muted}>{t.strategy}</TD>
                    <TD color={C.yellow}>{t.expiry ? `${t.expiry.slice(0,4)}-${t.expiry.slice(4,6)}-${t.expiry.slice(6,8)}` : "—"}</TD>
                    <td style={{ padding:"10px 10px", fontFamily:"JetBrains Mono", fontSize:14, fontWeight:700,
                      color: pnl >= 0 ? C.green : C.red }}>
                      {pnl >= 0 ? "+" : ""}${pnl.toFixed(2)}
                    </td>
                    <TD color={C.accent}>{t.vst_instant != null ? `${t.vst_instant>=0?"+":""}${Number(t.vst_instant).toFixed(2)}` : "—"}</TD>
                    <TD color={C.muted}>{t.created_at?.slice(0,16) || "—"}</TD>
                    <TD color={C.muted}>{t.closed_at?.slice(0,16) || "—"}</TD>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Panel>
      )}
    </div>
  );
}

// ─── CHART PAGE ───────────────────────────────────────────────────────────────

// LWC CDN loader — nessun npm install richiesto
let _lwcLoaded = false;
let _lwcCbs    = [];
function ensureLWC(cb) {
  if (window.LightweightCharts) { cb(window.LightweightCharts); return; }
  _lwcCbs.push(cb);
  if (_lwcLoaded) return;
  _lwcLoaded = true;
  const s = document.createElement("script");
  s.src = "https://unpkg.com/lightweight-charts@3.8.0/dist/lightweight-charts.standalone.development.js";
  s.onload  = () => { const cbs = _lwcCbs; _lwcCbs = []; cbs.forEach(fn => fn(window.LightweightCharts)); };
  s.onerror = () => { _lwcLoaded = false; };
  document.head.appendChild(s);
}

// Ticker globale condiviso con altri componenti
let _chartTicker = "AAPL";

function ChartPage() {
  const [ticker,      setTicker]      = useState(_chartTicker);
  const [inputVal,    setInputVal]    = useState(_chartTicker);
  const [period,      setPeriod]      = useState("6M");
  const [data,        setData]        = useState(null);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState(null);
  const [lwc,         setLwc]         = useState(null);
  const [showMA20,    setShowMA20]    = useState(true);
  const [showMA50,    setShowMA50]    = useState(true);
  const [showMA200,   setShowMA200]   = useState(false);
  const [showMaxPain, setShowMaxPain] = useState(true);
  const [showFlipGex, setShowFlipGex] = useState(true);

  const mainRef   = useRef(null);
  const rsiRef    = useRef(null);
  const chartsRef = useRef(null);

  // Carica LWC una sola volta
  useEffect(() => { ensureLWC(lib => setLwc(lib)); }, []);

  // Ascolta navigazione con ticker da altre pagine
  useEffect(() => {
    const h = e => {
      if (e.detail?.ticker) {
        _chartTicker = e.detail.ticker;
        setTicker(e.detail.ticker);
        setInputVal(e.detail.ticker);
      }
    };
    window.addEventListener("quantos:chart-ticker", h);
    return () => window.removeEventListener("quantos:chart-ticker", h);
  }, []);

  // Fetch dati
  const fetchData = useCallback(async (sym, per) => {
    setLoading(true); setError(null);
    try {
      const d = await api.get(`/api/chart/${sym}?period=${per}`);
      if (d.detail) throw new Error(d.detail);
      setData(d);
    } catch (e) { setError(e.message || "Errore"); setData(null); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(ticker, period); }, [ticker, period]);

  // Crea/ricrea chart quando cambiano dati o opzioni
  useEffect(() => {
    if (!lwc || !data?.candles?.length || !mainRef.current || !rsiRef.current) return;

    // Distruggi chart precedenti
    if (chartsRef.current) {
      try { chartsRef.current.ro?.disconnect(); } catch {}
      try { chartsRef.current.main?.remove(); } catch {}
      try { chartsRef.current.rsi?.remove(); } catch {}
      chartsRef.current = null;
    }

    const bgColor    = C.panel;
    const borderClr  = C.border;
    const textClr    = C.muted;
    const fontFam    = "'JetBrains Mono', monospace";

    const baseOpts = (h) => ({
      layout:          { background: { color: bgColor }, textColor: textClr, fontFamily: fontFam, fontSize: 10 },
      grid:            { vertLines: { color: borderClr + "50" }, horzLines: { color: borderClr + "50" } },
      crosshair:       { mode: 1 },
      rightPriceScale: { borderColor: borderClr },
      timeScale:       { borderColor: borderClr, timeVisible: true, secondsVisible: false },
      handleScroll:    { mouseWheel: true, pressedMouseMove: true },
      handleScale:     { mouseWheel: true, pinch: true },
      width:           mainRef.current.clientWidth,
      height:          h,
    });

    const main = lwc.createChart(mainRef.current, baseOpts(400));
    const rsi  = lwc.createChart(rsiRef.current,  {
      ...baseOpts(120),
      timeScale: { borderColor: borderClr, timeVisible: false, secondsVisible: false },
    });

    // Serie candlestick
    const candle = main.addCandlestickSeries({
      upColor: C.green, downColor: C.red,
      borderVisible: false, wickUpColor: C.green, wickDownColor: C.red,
    });

    // Volume (pannello inferiore 20%)
    const volSeries = main.addHistogramSeries({
      priceFormat: { type: "volume" },
      priceScaleId: "vol",
    });
    main.priceScale("vol").applyOptions({ scaleMargins: { top: 0.82, bottom: 0 } });

    // MA lines
    const ma20s  = showMA20  ? main.addLineSeries({ color: "#2196F3",  lineWidth: 1, priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false }) : null;
    const ma50s  = showMA50  ? main.addLineSeries({ color: C.accent,   lineWidth: 1, priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false }) : null;
    const ma200s = showMA200 ? main.addLineSeries({ color: C.red,      lineWidth: 1, lineStyle: 2, priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false }) : null;

    // RSI
    const rsiSeries = rsi.addLineSeries({ color: C.accent2 || "#7c3aed", lineWidth: 1.5, priceLineVisible: false, lastValueVisible: true });
    rsiSeries.createPriceLine({ price: 70, color: C.red   + "70", lineWidth: 1, lineStyle: 2, axisLabelVisible: true,  title: "OB" });
    rsiSeries.createPriceLine({ price: 30, color: C.green + "70", lineWidth: 1, lineStyle: 2, axisLabelVisible: true,  title: "OS" });
    rsiSeries.createPriceLine({ price: 50, color: textClr + "50", lineWidth: 1, lineStyle: 4, axisLabelVisible: false, title: "" });

    // Imposta dati candlestick e volume
    candle.setData(data.candles.map(c => ({ time: c.time, open: c.open, high: c.high, low: c.low, close: c.close })));
    volSeries.setData(data.candles.map(c => ({ time: c.time, value: c.volume, color: c.close >= c.open ? C.green + "99" : C.red + "99" })));

    // MA data
    if (ma20s)  ma20s.setData( data.candles.filter(c => c.ma20  != null).map(c => ({ time: c.time, value: c.ma20  })));
    if (ma50s)  ma50s.setData( data.candles.filter(c => c.ma50  != null).map(c => ({ time: c.time, value: c.ma50  })));
    if (ma200s) ma200s.setData(data.candles.filter(c => c.ma200 != null).map(c => ({ time: c.time, value: c.ma200 })));

    // RSI data
    rsiSeries.setData(data.candles.filter(c => c.rsi != null).map(c => ({ time: c.time, value: c.rsi })));

    // Linee Max Pain (punteggiate viola)
    if (showMaxPain && data.max_pain_levels?.length) {
      data.max_pain_levels.forEach(mp => {
        candle.createPriceLine({ price: mp.level, color: "#e040fb", lineWidth: 1, lineStyle: 2, axisLabelVisible: true, title: `MP ${mp.expiry.slice(5)}` });
      });
    }

    // Flip GEX (punteggiato giallo)
    if (showFlipGex && data.flip_gex) {
      candle.createPriceLine({ price: data.flip_gex, color: C.yellow, lineWidth: 1, lineStyle: 2, axisLabelVisible: true, title: "FLIP GEX" });
    }

    // Sincronizza scroll/zoom tra main e RSI
    let _syncing = false;
    main.timeScale().subscribeVisibleTimeRangeChange(range => {
      if (_syncing || !range) return;
      _syncing = true;
      try { rsi.timeScale().setVisibleRange(range); } catch {}
      _syncing = false;
    });
    rsi.timeScale().subscribeVisibleTimeRangeChange(range => {
      if (_syncing || !range) return;
      _syncing = true;
      try { main.timeScale().setVisibleRange(range); } catch {}
      _syncing = false;
    });

    main.timeScale().fitContent();

    // Resize automatico
    const ro = new ResizeObserver(() => {
      if (mainRef.current) main.applyOptions({ width: mainRef.current.clientWidth });
      if (rsiRef.current)  rsi.applyOptions({  width: rsiRef.current.clientWidth  });
    });
    if (mainRef.current) ro.observe(mainRef.current);
    if (rsiRef.current)  ro.observe(rsiRef.current);

    chartsRef.current = { main, rsi, ro };

    return () => {
      if (chartsRef.current) {
        try { chartsRef.current.ro?.disconnect(); } catch {}
        try { chartsRef.current.main?.remove(); } catch {}
        try { chartsRef.current.rsi?.remove(); } catch {}
        chartsRef.current = null;
      }
    };
  }, [lwc, data, showMA20, showMA50, showMA200, showMaxPain, showFlipGex]);

  const handleSearch = (e) => {
    if (e.key === "Enter" || e.type === "click") {
      const sym = inputVal.trim().toUpperCase();
      if (sym) { _chartTicker = sym; setTicker(sym); }
    }
  };

  const PERIODS = ["1M", "3M", "6M", "1Y", "2Y", "4Y"];
  const REGIME_COLORS = { CRISIS: C.red, HIGH_VOL: C.yellow, NORMAL: C.green, CALM: "#64b4ff", PIN: "#c864ff" };
  const last     = data?.candles?.[data.candles.length - 1];
  const changePos = (data?.change_pct ?? 0) >= 0;

  const overlayBtns = [
    { label: "MA20",     state: showMA20,    set: setShowMA20,    color: "#2196F3" },
    { label: "MA50",     state: showMA50,    set: setShowMA50,    color: C.accent  },
    { label: "MA200",    state: showMA200,   set: setShowMA200,   color: C.red     },
    { label: "MAX PAIN", state: showMaxPain, set: setShowMaxPain, color: "#e040fb" },
    { label: "FLIP GEX", state: showFlipGex, set: setShowFlipGex, color: C.yellow  },
  ];

  return (
    <div style={{ padding: "20px 24px" }}>

      {/* ── Controlli ─ */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
        {/* Ticker input */}
        <div style={{ display: "flex", gap: 6 }}>
          <input
            value={inputVal}
            onChange={e => setInputVal(e.target.value.toUpperCase())}
            onKeyDown={handleSearch}
            style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 4, padding: "6px 12px", color: C.text, fontFamily: "'JetBrains Mono', monospace", fontSize: 13, width: 110, outline: "none", letterSpacing: "0.08em" }}
            placeholder="TICKER"
          />
          <button onClick={handleSearch} style={{ background: C.accent, border: "none", borderRadius: 4, color: "#000", fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, padding: "6px 14px", cursor: "pointer", letterSpacing: "0.1em" }}>
            GO
          </button>
        </div>

        {/* Periodo */}
        <div style={{ display: "flex", gap: 3 }}>
          {PERIODS.map(p => (
            <button key={p} onClick={() => setPeriod(p)} style={{
              background: period === p ? C.accent + "18" : "transparent",
              border: `1px solid ${period === p ? C.accent : C.border}`,
              color: period === p ? C.accent : C.muted,
              fontFamily: "'JetBrains Mono', monospace", fontSize: 10, letterSpacing: "0.08em",
              padding: "5px 10px", borderRadius: 3, cursor: "pointer",
            }}>{p}</button>
          ))}
        </div>

        {/* Overlay toggles */}
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {overlayBtns.map(({ label, state, set, color }) => (
            <button key={label} onClick={() => set(v => !v)} style={{
              background: state ? color + "18" : "transparent",
              border: `1px solid ${state ? color : C.border}`,
              color: state ? color : C.muted,
              fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
              padding: "5px 10px", borderRadius: 3, cursor: "pointer",
              display: "flex", alignItems: "center", gap: 5,
            }}>
              <span style={{ width: 10, height: 2, background: state ? color : C.muted, display: "inline-block", borderRadius: 1 }} />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Stats bar ─ */}
      {data && last && (
        <div style={{ display: "flex", alignItems: "center", gap: 18, marginBottom: 10, padding: "8px 14px", background: C.panel, borderRadius: 6, border: `1px solid ${C.border}`, flexWrap: "wrap" }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: C.accent, letterSpacing: "0.12em" }}>{data.ticker}</span>
          <span style={{ fontSize: 15, fontWeight: 700, color: C.text }}>${last.close.toFixed(2)}</span>
          <span style={{ fontSize: 13, fontWeight: 600, color: changePos ? C.green : C.red }}>
            {changePos ? "▲" : "▼"} {changePos ? "+" : ""}{data.change_pct}%
          </span>
          <span style={{ fontSize: 11, color: C.muted }}>O:<span style={{ color: C.text }}> ${last.open.toFixed(2)}</span></span>
          <span style={{ fontSize: 11, color: C.muted }}>H:<span style={{ color: C.green }}> ${last.high.toFixed(2)}</span></span>
          <span style={{ fontSize: 11, color: C.muted }}>L:<span style={{ color: C.red }}> ${last.low.toFixed(2)}</span></span>
          <span style={{ fontSize: 11, color: C.muted }}>Vol:<span style={{ color: C.text }}> {last.volume >= 1e6 ? (last.volume / 1e6).toFixed(1) + "M" : (last.volume / 1e3).toFixed(0) + "K"}</span></span>
          {last.hv30 != null && <span style={{ fontSize: 11, color: C.muted }}>HV30:<span style={{ color: C.yellow }}> {last.hv30}%</span></span>}
          {data.regime && (
            <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 3,
              background: (REGIME_COLORS[data.regime] || C.muted) + "18",
              color: REGIME_COLORS[data.regime] || C.muted,
              border: `1px solid ${(REGIME_COLORS[data.regime] || C.muted)}40` }}>
              {data.regime}
            </span>
          )}
          {data.flip_gex && showFlipGex && (
            <span style={{ fontSize: 11, color: C.muted }}>Flip GEX:<span style={{ color: C.yellow, fontWeight: 700 }}> ${data.flip_gex}</span></span>
          )}
          <span style={{ fontSize: 10, color: C.muted, marginLeft: "auto" }}>{data.count} barre · {data.period}</span>
        </div>
      )}

      {/* ── Stato caricamento ─ */}
      {loading && <div style={{ textAlign: "center", padding: 80, color: C.muted, fontSize: 12, letterSpacing: "0.1em" }}>CARICAMENTO DATI...</div>}
      {error   && <div style={{ textAlign: "center", padding: 60, color: C.red,  fontSize: 12 }}>{error}</div>}
      {!lwc && !loading && <div style={{ textAlign: "center", padding: 60, color: C.muted, fontSize: 11 }}>Caricamento libreria grafico...</div>}

      {/* ── Grafici ─ */}
      {!loading && !error && (
        <>
          <div ref={mainRef} style={{ width: "100%", borderRadius: "6px 6px 0 0", overflow: "hidden", border: `1px solid ${C.border}`, borderBottom: "none" }} />
          <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderTop: `1px solid ${C.border}40`, padding: "2px 10px", fontSize: 9, color: C.muted, letterSpacing: "0.12em" }}>
            RSI (14) · <span style={{ color: C.red }}>OB 70</span> · <span style={{ color: C.green }}>OS 30</span>
          </div>
          <div ref={rsiRef} style={{ width: "100%", borderRadius: "0 0 6px 6px", overflow: "hidden", border: `1px solid ${C.border}`, borderTop: "none" }} />
        </>
      )}

      {/* ── Legenda Max Pain ─ */}
      {data?.max_pain_levels?.length > 0 && showMaxPain && (
        <div style={{ marginTop: 10, display: "flex", gap: 6, flexWrap: "wrap" }}>
          {data.max_pain_levels.map(mp => (
            <span key={mp.expiry} style={{ fontSize: 10, color: "#e040fb", background: "rgba(224,64,251,0.06)", border: "1px solid rgba(224,64,251,0.22)", padding: "2px 8px", borderRadius: 3 }}>
              ── MP {mp.expiry} · ${mp.level}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}


// ─── MAX PAIN PAGE ────────────────────────────────────────────────────────────
function MaxPainPage() {
  const [ticker, setTicker]           = useState("AAPL");
  const [tickers, setTickers]         = useState([]);
  const [data, setData]               = useState(null);
  const [loading, setLoading]         = useState(false);
  const [selectedExpiry, setSelectedExpiry] = useState(null);

  useEffect(() => {
    api.get("/api/tickers").then(res => {
      if (Array.isArray(res)) {
        const active = res.filter(x => x.active).map(x => x.symbol);
        if (active.length > 0) setTickers(active);
      }
    }).catch(() => {});
  }, []);

  const fetchData = async (t, exp) => {
    setLoading(true);
    try {
      const params = exp ? `?selected_expiry=${exp}` : "";
      const result = await api.get(`/api/max-pain/${t}${params}`);
      setData(result);
      if (!exp && result.term_structure?.length > 0) {
        setSelectedExpiry(result.term_structure[0].expiry);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(ticker, null); }, [ticker]);

  const handleExpiry = (exp) => {
    setSelectedExpiry(exp);
    fetchData(ticker, exp);
  };

  const spot    = data?.spot || 0;
  const ts      = data?.term_structure || [];
  const varData = data?.variance || [];
  const pc      = data?.pain_curve || [];
  const mpScore = data?.mp_variance_score;
  const selExp  = data?.selected_expiry;

  const selItem = ts.find(x => x.expiry === selExp);

  // Pain curve: mostra solo strikes vicino al max pain (±30%)
  const mpStrike   = selItem?.max_pain || 0;
  const pcFiltered = pc.filter(x => !mpStrike || Math.abs(x.strike - mpStrike) / mpStrike < 0.30);

  // Colore distanza max pain
  const distColor = (dist) => {
    if (dist === null || dist === undefined) return C.muted;
    const abs = Math.abs(dist);
    return abs < 3 ? "#00e676" : abs < 8 ? C.yellow : C.accent;
  };

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20, flexWrap: "wrap" }}>
        <select
          value={ticker}
          onChange={e => setTicker(e.target.value)}
          style={{
            background: C.panel, border: `1px solid ${C.accent}`,
            color: C.accent, fontFamily: "JetBrains Mono", fontSize: 12,
            padding: "7px 32px 7px 12px", borderRadius: 4, cursor: "pointer",
            outline: "none", appearance: "none",
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23ff8c00' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,
            backgroundRepeat: "no-repeat", backgroundPosition: "right 10px center", minWidth: 120,
          }}>
          {tickers.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        {spot > 0 && (
          <span style={{ fontFamily: "JetBrains Mono", fontSize: 16, color: C.accent, fontWeight: 700 }}>
            ${spot.toFixed(2)}
          </span>
        )}
        {mpScore !== null && mpScore !== undefined && (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontFamily: "JetBrains Mono", fontSize: 10, color: C.muted }}>MP VARIANCE SCORE</span>
            <span style={{ fontFamily: "JetBrains Mono", fontSize: 14, fontWeight: 700,
              color: mpScore < 1 ? "#00e676" : mpScore < 3 ? C.yellow : C.accent }}>
              ${mpScore}
            </span>
            <span style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted }}>
              {mpScore < 1 ? "STABILE → PIN PROBABILE" : mpScore < 3 ? "MODERATA" : "VOLATILE"}
            </span>
          </div>
        )}
        {loading && <span style={{ fontFamily: "JetBrains Mono", fontSize: 10, color: C.muted }}>CARICAMENTO...</span>}
      </div>

      {/* Term Structure */}
      {ts.length > 0 && (
        <Panel style={{ marginBottom: 16 }}>
          <div style={{ fontFamily: "JetBrains Mono", fontSize: 10, color: C.muted, marginBottom: 12, letterSpacing: "0.1em" }}>
            TERM STRUCTURE — MAX PAIN PER SCADENZA
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <ComposedChart data={ts} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
              <XAxis dataKey="expiry" tick={{ fill: C.muted, fontSize: 9, fontFamily: "JetBrains Mono" }} />
              <YAxis tick={{ fill: C.muted, fontSize: 9, fontFamily: "JetBrains Mono" }}
                domain={['auto', 'auto']} />
              <Tooltip
                contentStyle={{ background: C.panel, border: `1px solid ${C.border}`, fontFamily: "JetBrains Mono", fontSize: 11 }}
                formatter={(v, n) => [`$${v}`, n === "max_pain" ? "Max Pain" : n]}
                labelFormatter={l => `Expiry: ${l}`}
              />
              <ReferenceLine y={spot} stroke={C.yellow} strokeDasharray="4 2"
                label={{ value: `SPOT $${spot}`, fill: C.yellow, fontSize: 9, fontFamily: "JetBrains Mono" }} />
              <Bar dataKey="max_pain" name="Max Pain" radius={[3,3,0,0]}>
                {ts.map((entry, idx) => (
                  <RCell key={idx}
                    fill={entry.expiry === selExp ? C.accent2 : `${C.accent2}70`}
                    style={{ cursor: "pointer" }}
                  />
                ))}
              </Bar>
              <Line type="monotone" dataKey="max_pain" stroke={C.accent2} strokeWidth={2} dot={{ fill: C.accent2, r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
          {/* Click su expiry per selezionarla */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
            {ts.map(item => (
              <button key={item.expiry} onClick={() => handleExpiry(item.expiry)}
                style={{
                  background: item.expiry === selExp ? C.accent2 : C.panel,
                  border: `1px solid ${item.expiry === selExp ? C.accent2 : C.border}`,
                  color: item.expiry === selExp ? "#000" : C.muted,
                  fontFamily: "JetBrains Mono", fontSize: 10, padding: "4px 10px",
                  borderRadius: 4, cursor: "pointer",
                }}>
                {item.expiry} · ${item.max_pain} · {item.distance_pct !== null ? `${item.distance_pct > 0 ? "+" : ""}${item.distance_pct}%` : "—"}
              </button>
            ))}
          </div>
        </Panel>
      )}

      {/* Dettaglio expiry selezionata */}
      {selExp && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
          {/* Pain Curve */}
          <Panel>
            <div style={{ fontFamily: "JetBrains Mono", fontSize: 10, color: C.muted, marginBottom: 12, letterSpacing: "0.1em" }}>
              CURVA DEL DOLORE — {selExp}
            </div>
            {pcFiltered.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={pcFiltered} margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
                  <XAxis dataKey="strike" tick={{ fill: C.muted, fontSize: 8, fontFamily: "JetBrains Mono" }}
                    tickFormatter={v => `$${v}`} />
                  <YAxis hide />
                  <Tooltip
                    contentStyle={{ background: C.panel, border: `1px solid ${C.border}`, fontFamily: "JetBrains Mono", fontSize: 10 }}
                    formatter={(v) => [v.toLocaleString(), "Pain"]}
                    labelFormatter={l => `Strike $${l}`}
                  />
                  <Bar dataKey="pain" radius={[2,2,0,0]}>
                    {pcFiltered.map((entry, idx) => (
                      <RCell key={idx}
                        fill={entry.strike === mpStrike ? C.accent2 : `${C.accent2}40`}
                      />
                    ))}
                  </Bar>
                  {mpStrike > 0 && (
                    <ReferenceLine x={mpStrike} stroke={C.accent2} strokeWidth={2} strokeDasharray="4 2"
                      label={{ value: "MP", fill: C.accent2, fontSize: 9, fontFamily: "JetBrains Mono" }} />
                  )}
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ fontFamily: "JetBrains Mono", fontSize: 10, color: C.muted, textAlign: "center", padding: 40 }}>
                Nessun dato disponibile
              </div>
            )}
          </Panel>

          {/* Variance chart */}
          <Panel>
            <div style={{ fontFamily: "JetBrains Mono", fontSize: 10, color: C.muted, marginBottom: 12, letterSpacing: "0.1em" }}>
              MAX PAIN VARIANCE — EVOLUZIONE NEL TEMPO · {selExp}
            </div>
            {varData.length > 1 ? (
              <>
                <ResponsiveContainer width="100%" height={160}>
                  <LineChart data={varData} margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
                    <XAxis dataKey="date" tick={{ fill: C.muted, fontSize: 8, fontFamily: "JetBrains Mono" }}
                      tickFormatter={v => v.slice(5)} interval="preserveStartEnd" />
                    <YAxis tick={{ fill: C.muted, fontSize: 8, fontFamily: "JetBrains Mono" }}
                      domain={['auto','auto']} tickFormatter={v => `$${v}`} />
                    <Tooltip
                      contentStyle={{ background: C.panel, border: `1px solid ${C.border}`, fontFamily: "JetBrains Mono", fontSize: 10 }}
                      formatter={(v) => [`$${v}`, "Max Pain"]}
                      labelFormatter={l => l}
                    />
                    <ReferenceLine y={spot} stroke={C.yellow} strokeDasharray="3 3"
                      label={{ value: "SPOT", fill: C.yellow, fontSize: 8, fontFamily: "JetBrains Mono" }} />
                    <Line type="monotone" dataKey="max_pain" stroke={C.accent2} strokeWidth={2}
                      dot={false} />
                  </LineChart>
                </ResponsiveContainer>
                {mpScore !== null && mpScore !== undefined && (
                  <div style={{ marginTop: 8, fontFamily: "JetBrains Mono", fontSize: 10, color: C.muted }}>
                    Variazione media giornaliera: <span style={{ color: mpScore < 1 ? "#00e676" : mpScore < 3 ? C.yellow : C.accent, fontWeight: 700 }}>${mpScore}</span>
                    {" · "}{varData.length} osservazioni storiche
                  </div>
                )}
              </>
            ) : (
              <div style={{ fontFamily: "JetBrains Mono", fontSize: 10, color: C.muted, textAlign: "center", padding: 40 }}>
                Dati storici non disponibili per questa scadenza
                <div style={{ marginTop: 8, fontSize: 9 }}>(historical_options: Apr 2022 – Dic 2025)</div>
              </div>
            )}
          </Panel>
        </div>
      )}

      {/* Tabella term structure */}
      {ts.length > 0 && (
        <Panel>
          <div style={{ fontFamily: "JetBrains Mono", fontSize: 10, color: C.muted, marginBottom: 12, letterSpacing: "0.1em" }}>
            DETTAGLIO SCADENZE
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {["SCADENZA", "DTE", "MAX PAIN", "DIST SPOT", "CALL OI", "PUT OI", "PCR"].map(h => (
                  <th key={h} style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: C.muted,
                    padding: "6px 8px", textAlign: "right", borderBottom: `1px solid ${C.border}`,
                    letterSpacing: "0.05em" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ts.map((item, i) => (
                <tr key={i} onClick={() => handleExpiry(item.expiry)}
                  style={{ cursor: "pointer",
                    background: item.expiry === selExp ? `${C.accent2}10` : "transparent" }}>
                  <td style={{ fontFamily: "JetBrains Mono", fontSize: 11, color: item.expiry === selExp ? C.accent2 : C.accent,
                    padding: "8px", fontWeight: item.expiry === selExp ? 700 : 400 }}>{item.expiry}</td>
                  <td style={{ fontFamily: "JetBrains Mono", fontSize: 11, color: C.muted,
                    padding: "8px", textAlign: "right" }}>{item.dte}g</td>
                  <td style={{ fontFamily: "JetBrains Mono", fontSize: 12, color: C.accent2,
                    padding: "8px", textAlign: "right", fontWeight: 700 }}>${item.max_pain}</td>
                  <td style={{ fontFamily: "JetBrains Mono", fontSize: 11, padding: "8px", textAlign: "right",
                    color: distColor(item.distance_pct), fontWeight: 700 }}>
                    {item.distance_pct !== null ? `${item.distance_pct > 0 ? "+" : ""}${item.distance_pct}%` : "—"}
                  </td>
                  <td style={{ fontFamily: "JetBrains Mono", fontSize: 11, color: "#00e676",
                    padding: "8px", textAlign: "right" }}>{item.call_oi?.toLocaleString()}</td>
                  <td style={{ fontFamily: "JetBrains Mono", fontSize: 11, color: C.accent,
                    padding: "8px", textAlign: "right" }}>{item.put_oi?.toLocaleString()}</td>
                  <td style={{ fontFamily: "JetBrains Mono", fontSize: 11,
                    color: item.pcr > 1 ? C.accent : "#00e676",
                    padding: "8px", textAlign: "right", fontWeight: 700 }}>
                    {item.pcr ?? "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}

      {!loading && ts.length === 0 && (
        <Panel>
          <div style={{ fontFamily: "JetBrains Mono", fontSize: 12, color: C.muted, textAlign: "center", padding: 40 }}>
            Nessun dato disponibile per {ticker}
          </div>
        </Panel>
      )}
    </div>
  );
}

// ─── APP ROOT ─────────────────────────────────────────────────────────────────
export default function App() {
  const [loggedIn, setLoggedIn] = useState(false);
  const [page, setPage] = useState("DASHBOARD");
  const [ibConnected, setIbConnected] = useState(false);
  const [role, setRole] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("quantos_token");
    const savedRole = localStorage.getItem("quantos_role");
    if (token) { api.token = token; api.role = savedRole; setRole(savedRole); setLoggedIn(true); }
  }, []);

  useEffect(() => {
    if (!loggedIn) return;
    const checkHealth = async () => {
      try { const h = await api.get("/health"); setIbConnected(h.ib_connected); } catch {}
    };
    checkHealth();
    const i = setInterval(checkHealth, 10000);
    return () => clearInterval(i);
  }, [loggedIn]);

  // Navigazione globale via evento custom
  useEffect(() => {
    const handler = e => {
      if (e.detail?.ticker) _chartTicker = e.detail.ticker;
      setPage(e.detail.page);
    };
    window.addEventListener("quantos:navigate", handler);
    return () => window.removeEventListener("quantos:navigate", handler);
  }, []);

  const handleLogout = () => {
    api.token = null; api.role = null;
    localStorage.removeItem("quantos_token");
    localStorage.removeItem("quantos_role");
    setLoggedIn(false); setRole(null);
  };

  const handleLogin = () => {
    setRole(api.role);
    setLoggedIn(true);
  };

  const pages = {
    "DASHBOARD":   <DashboardPage />,
    "OPPORTUNITIES": <OpportunitiesPage />,
    "SIGNALS":     <SignalsPage />,
    "VOL SURFACE": <VolSurfacePage />,
    "REGIME":      <RegimePage />,
    "GEX":         <GexPage />,
    "MAX PAIN":    <MaxPainPage />,
    "VST":         <VSTPage />,
    "CHAIN":       <ChainPage />,
    "STRATEGIES":  <StrategiesPage />,
    "AUTO-STRATEGY": <StrategyBuilderPage />,
    "PAPER":       <PaperPortfolioPage />,
    "BACKTEST":    <BacktestPage />,
    "HEDGE":       <HedgePage />,
    "PAIRS":       <PairsPage />,
    "CHART":       <ChartPage />,
    "NEWS":        <NewsPage />,
    "FILINGS":     <FilingsPage />,
    "SETTINGS":    <SettingsPage />,
    "PORTFOLIO":   (role === "owner" || role === "admin") ? <PortfolioPage /> : <PlaceholderPage title="ACCESS DENIED" />,
  };

  if (!loggedIn) return <LoginPage onLogin={handleLogin} />;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;700;900&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: ${C.bg}; color: ${C.text}; font-family: 'JetBrains Mono', monospace; margin: 0; }
        ::-webkit-scrollbar { width: 6px; } ::-webkit-scrollbar-track { background: ${C.bg}; } ::-webkit-scrollbar-thumb { background: ${C.dim}; border-radius: 3px; }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }
        @keyframes ticker { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        tr:hover { background: #ffffff08 !important; }
        input:focus { border-color: ${C.accent} !important; }
        button:hover { opacity: 0.8; }
      `}</style>
      <div style={{ minHeight: "100vh", background: C.bg }}>
        <Header page={page} setPage={setPage} ibConnected={ibConnected} onLogout={handleLogout} role={role} />
        <main>{pages[page]}</main>
      </div>
    </>
  );
}
